import { useState, useRef, useEffect, useCallback, useMemo, Fragment } from "react";
import { Bookmark, ChevronDown, ChevronRight, Info, Pencil, Search, Settings, ThumbsUp, Trash2 } from "lucide-react";
import { useAuth } from "./hooks/useAuth";
import { supabase } from "./supabase";
import { SermonList } from "./components/SermonUpload";
import EncouragementUpload from "./components/EncouragementUpload";
import InstallBanner from "./components/InstallBanner";
import ChurchOnboarding from "./components/ChurchOnboarding";
import SermonsTab from "./components/SermonsTab";
import ThisWeekTab from "./components/ThisWeekTab";
import LeadersPage from "./components/LeadersPage";
import ReminderPromptSheet from "./components/ReminderPromptSheet";
import CommunityTab from "./components/CommunityTab";
import ChurchProfilePage from "./components/ChurchProfilePage";
import { CircleLogoOrInitials, CIRCLE_PASTELS } from "./components/ExploreTab";
import FullSermonSheet from "./components/FullSermonSheet";
import ReadingPlanDaySheet from "./components/ReadingPlanDaySheet";
import PlansTab from "./components/PlansTab";
import EventEditorSheet from "./components/EventEditorSheet";
import YouTubeEmbed, { fmtClock as fmtClipClock, clipBoundsFor } from "./components/YouTubeEmbed";
import { useSermons, useSermonAdmin, useVerseSermonRefs } from "./hooks/useSermons";
import { FREE_SCAN_LIMIT } from "./lib/scanLimits";
import { usePrayers, useEncouragements, useVerseComments, useNotifications } from "./hooks/useData";
import { useReadingSettings } from "./hooks/useReadingSettings";
import { parseBibleRefs, expandVerseRef, canonicalizeBook } from "./lib/bibleRefs";
import { groupSermonsBySeries, displayTitleOf } from "./lib/sermonSeries";
import { takeDeepLink, clearDeepLink, parseDeepLinkFromSearch } from "./deepLink";
import { shareTextViaBestChannel } from "./lib/shareInvite";
import Median from "median-js-bridge";

function getInitials(name) {
  return (name || "")
    .split(/\s+/).filter(Boolean).slice(0,2)
    .map(w => w[0].toUpperCase()).join("") || "·";
}

// This Week header greeting — "Morning"/"Afternoon"/"Evening" by local hour.
function getHourGreeting() {
  const h = new Date().getHours();
  return h < 12 ? "Morning" : h < 18 ? "Afternoon" : "Evening";
}

// Standard like + comment controls (YouTube/Facebook style): an outline
// thumbs-up that fills white when liked, count beside it; a speech-bubble for
// comments/replies. Used across verse comments, the community board, and
// encouragements so the interaction looks the same everywhere.
function LikeButton({ liked, count, onClick, size = 16 }) {
  return (
    <button type="button"
      onClick={e => { e.stopPropagation(); onClick?.(); }}
      aria-label={liked ? "Remove like" : "Like"}
      style={{
        display:"inline-flex", alignItems:"center", gap:5,
        background:"transparent", border:"none", padding:"4px 4px", cursor:"pointer",
        color: liked ? "var(--gold-ink)" : "var(--stone)",
        fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:600, lineHeight:1,
      }}>
      <ThumbsUp size={size} strokeWidth={1.8} fill={liked ? "currentColor" : "none"}/>
      {count > 0 && <span>{count}</span>}
    </button>
  );
}
// Small YouTube glyph for "Watch the moment" links.
function YtGlyph({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0, display: "block" }}>
      <rect x="1" y="4" width="22" height="16" rx="4.5" fill="#FF0000"/>
      <path d="M10 8.5l6 3.5-6 3.5z" fill="#fff"/>
    </svg>
  );
}

// ── AVATAR CROP SHEET ────────────────────────────────────────
// Lightweight in-app cropper: pan with pointer drag, zoom with the
// range slider, circular mask over the viewport. On Save we render
// the visible region to a 512x512 JPEG blob and hand it back.
function AvatarCropSheet({ file, onCancel, onSave, saving, error }) {
  const VIEW = 280;  // viewport edge length in CSS px
  const OUT  = 512;  // output edge length in actual px
  const imgRef = useRef(null);
  const [imgUrl,     setImgUrl]     = useState("");
  const [natural,    setNatural]    = useState(null); // { w, h }
  const [zoom,       setZoom]       = useState(1);
  const [pan,        setPan]        = useState({ x: 0, y: 0 });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const coverScale = natural
    ? Math.max(VIEW / natural.w, VIEW / natural.h)
    : 1;

  function clampPan(px, py, z) {
    if (!natural) return { x: 0, y: 0 };
    const dispW = coverScale * z * natural.w;
    const dispH = coverScale * z * natural.h;
    const maxX = Math.max(0, (dispW - VIEW) / 2);
    const maxY = Math.max(0, (dispH - VIEW) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, px)),
      y: Math.max(-maxY, Math.min(maxY, py)),
    };
  }

  function onPointerDown(e) {
    dragRef.current = {
      active: true,
      startX: e.clientX, startY: e.clientY,
      baseX: pan.x,      baseY: pan.y,
    };
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
  }
  function onPointerMove(e) {
    if (!dragRef.current.active) return;
    const nx = dragRef.current.baseX + (e.clientX - dragRef.current.startX);
    const ny = dragRef.current.baseY + (e.clientY - dragRef.current.startY);
    setPan(clampPan(nx, ny, zoom));
  }
  function onPointerUp(e) {
    dragRef.current.active = false;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch {}
  }

  function onZoomChange(nextZ) {
    setZoom(nextZ);
    setPan(p => clampPan(p.x, p.y, nextZ));
  }

  // Local lock so the user can't fire the canvas-to-blob conversion twice
  // before the parent's `saving` prop flips on (the parent only knows we're
  // saving once it receives the blob).
  const [localSaving, setLocalSaving] = useState(false);
  useEffect(() => { if (error) setLocalSaving(false); }, [error]);

  async function handleSaveCrop() {
    if (localSaving || saving) return;
    const img = imgRef.current;
    if (!img || !natural) return;
    setLocalSaving(true);
    try {
      const effScale = coverScale * zoom;
      const visSize  = VIEW / effScale; // size in source pixels
      const cx = natural.w / 2 - pan.x / effScale;
      const cy = natural.h / 2 - pan.y / effScale;
      const srcX = cx - visSize / 2;
      const srcY = cy - visSize / 2;

      const canvas = document.createElement('canvas');
      canvas.width = OUT; canvas.height = OUT;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, OUT, OUT);
      ctx.drawImage(img, srcX, srcY, visSize, visSize, 0, 0, OUT, OUT);
      const blob = await new Promise(resolve =>
        canvas.toBlob(resolve, 'image/jpeg', 0.9)
      );
      if (blob) onSave(blob);
      else setLocalSaving(false); // canvas failed, allow retry
    } catch (e) {
      setLocalSaving(false);
    }
  }

  // Image rendered at "cover" size so the natural aspect ratio is preserved
  // but the viewport is fully filled at zoom=1.
  const dispW = natural ? coverScale * natural.w : VIEW;
  const dispH = natural ? coverScale * natural.h : VIEW;

  return (
    <>
      <div className="overlay" style={{ zIndex: 250 }} onClick={saving ? undefined : onCancel}/>
      <div className="sheet" style={{ zIndex: 251, maxHeight:"90vh", display:"flex", flexDirection:"column" }}>
        <div className="shandle"/>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 16px 0",flexShrink:0}}>
          <div className="shd" style={{padding:0}}>Crop photo</div>
          <button onClick={saving ? undefined : onCancel}
            style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:20,cursor:"pointer",padding:4}}>✕</button>
        </div>
        <div style={{padding:"16px", display:"flex", flexDirection:"column", alignItems:"center", gap:14}}>
          <div style={{
            position:"relative", width:VIEW, height:VIEW,
            overflow:"hidden", touchAction:"none",
            background:"#000", borderRadius:8,
            cursor: dragRef.current.active ? "grabbing" : "grab",
          }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}>
            {imgUrl && (
              <img
                ref={imgRef}
                src={imgUrl}
                alt=""
                draggable={false}
                onLoad={e => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                style={{
                  position:"absolute", left:"50%", top:"50%",
                  width: dispW, height: dispH,
                  transform:`translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px)) scale(${zoom})`,
                  transformOrigin:"center",
                  userSelect:"none", pointerEvents:"none",
                  WebkitUserDrag: "none",
                }}
              />
            )}
            {/* Circular mask — dim everything outside the round crop area */}
            <div style={{
              position:"absolute", inset:0,
              boxShadow:"0 0 0 9999px rgba(0,0,0,0.55) inset",
              borderRadius:"50%",
              border:"2px solid rgba(255,255,255,0.4)",
              pointerEvents:"none",
            }}/>
          </div>

          <div style={{display:"flex", alignItems:"center", gap:10, width:"100%", maxWidth:VIEW}}>
            <span style={{fontSize:14, color:"var(--stone-dim)"}}>−</span>
            <input type="range" min="1" max="3" step="0.02"
              value={zoom}
              onChange={e => onZoomChange(parseFloat(e.target.value))}
              style={{flex:1, accentColor:"#E8C87A"}}/>
            <span style={{fontSize:14, color:"var(--stone-dim)"}}>+</span>
          </div>

          <div style={{fontSize:11, color:"var(--stone-dim)", textAlign:"center"}}>
            Drag to position · pinch the slider to zoom
          </div>

          {error && (
            <div style={{
              width:"100%", maxWidth:VIEW,
              background:"rgba(249,112,102,0.1)",
              border:"1px solid rgba(249,112,102,0.3)",
              borderRadius:8, padding:"8px 10px",
              fontSize:12, color:"#C8372A", lineHeight:1.4,
              wordBreak:"break-word",
            }}>
              {error}
            </div>
          )}

          <div style={{display:"flex", gap:8, width:"100%", maxWidth:VIEW, marginTop:4}}>
            {(() => {
              const busy = saving || localSaving;
              return (
                <>
                  <button onClick={onCancel} disabled={busy}
                    style={{flex:1, background:"transparent", color:"var(--stone)", border:"1px solid var(--border-strong)", borderRadius:10, padding:"11px", fontSize:14, fontWeight:600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.5 : 1, fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                    Cancel
                  </button>
                  <button onClick={handleSaveCrop} disabled={busy || !natural}
                    style={{flex:1, background:"var(--gold)", color:"var(--ink)", border:"none", borderRadius:10, padding:"11px", fontSize:14, fontWeight:800, cursor: (busy || !natural) ? "default" : "pointer", opacity: (busy || !natural) ? 0.5 : 1, fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                    {busy ? "Saving…" : "Save photo"}
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </>
  );
}

// Reusable circular avatar. Renders the image when `url` is set;
// falls back to an initials chip in the existing app palette.
// ── SERIES FOLDER TILE ──────────────────────────────────────────────
// Rendered in the Sermons tab to group a series of videos into a single
// tappable "folder". The 2 ghost cards behind the front tile hint at the
// pile of videos inside — they shift in slightly on hover.
function SeriesFolder({ name, count, thumb, isAll, onClick }) {
  const accent = isAll ? "rgba(232,200,122,0.55)" : "var(--border-strong)";
  const ratio  = "16 / 9";
  const radius = 10;
  return (
    <div onClick={onClick}
      style={{
        position:"relative", cursor:"pointer", paddingTop:14,
        // Reserve room for the two stacked shadows behind the cover image.
        paddingRight:10,
      }}>
      {/* Back card (deepest) */}
      <div style={{
        position:"absolute", top:0, left:14, right:0, aspectRatio:ratio,
        background:"var(--white)", border:`1px solid ${accent}`,
        borderRadius:radius, opacity:0.32, transform:"rotate(2.5deg)",
        boxShadow:"0 2px 10px rgba(0,0,0,0.4)",
      }}/>
      {/* Middle card */}
      <div style={{
        position:"absolute", top:7, left:7, right:7, aspectRatio:ratio,
        background:"var(--white)", border:`1px solid ${accent}`,
        borderRadius:radius, opacity:0.6, transform:"rotate(-1.5deg)",
        boxShadow:"0 2px 10px rgba(0,0,0,0.4)",
      }}/>
      {/* Front card with cover image */}
      <div style={{
        position:"relative", aspectRatio:ratio, width:"100%",
        background:"#222", border:`1px solid ${isAll ? "var(--gold)" : "var(--border-strong)"}`,
        borderRadius:radius, overflow:"hidden",
        boxShadow:"0 6px 22px rgba(0,0,0,0.55)",
      }}>
        {thumb ? (
          <img src={thumb} alt=""
            style={{width:"100%", height:"100%", objectFit:"cover"}}
            onError={e=>{e.target.style.display="none"}}/>
        ) : (
          <div style={{
            width:"100%", height:"100%", display:"flex", alignItems:"center",
            justifyContent:"center", fontSize:32, color:"var(--stone-dim)",
          }}>🎙️</div>
        )}
        {/* darken bottom for legibility of count chip */}
        <div style={{
          position:"absolute", inset:0,
          background:"linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 55%)",
        }}/>
        <div style={{
          position:"absolute", left:8, bottom:8, right:8,
          display:"flex", alignItems:"flex-end", justifyContent:"space-between", gap:6,
        }}>
          {isAll && (
            <span style={{
              fontSize:9, fontWeight:800, letterSpacing:"0.10em",
              textTransform:"uppercase", color:"var(--gold-ink)",
              background:"rgba(0,0,0,0.55)", padding:"2px 6px", borderRadius:5,
              border:"1px solid rgba(232,200,122,0.35)",
            }}>All</span>
          )}
          <span style={{
            marginLeft:"auto",
            fontSize:11, fontWeight:700, color:"#fff",
            background:"rgba(0,0,0,0.7)", padding:"3px 8px", borderRadius:10,
            border:"1px solid rgba(255,255,255,0.18)",
          }}>{count} video{count===1?"":"s"}</span>
        </div>
      </div>
      <div style={{
        marginTop:10, fontSize:13, fontWeight:700, color:"var(--ink)",
        lineHeight:1.3,
        overflow:"hidden", textOverflow:"ellipsis",
        display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical",
      }}>{name}</div>
    </div>
  );
}

// "+" tile shown in the sermon-library grid for admins/elders to add more
// sermons. Sized to sit alongside the SeriesFolder cards in the 2-col grid.
function AddSermonTile({ onClick }) {
  return (
    <div onClick={onClick}
      style={{ cursor:"pointer", paddingTop:14, paddingRight:10 }}>
      <div style={{
        position:"relative", aspectRatio:"16 / 9", width:"100%",
        background:"rgba(232,200,122,0.06)",
        border:"1.5px dashed rgba(232,200,122,0.5)",
        borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center",
      }}>
        <div style={{
          width:36, height:36, borderRadius:"50%",
          background:"var(--gold)", color:"var(--ink)",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:24, fontWeight:800, lineHeight:1,
        }}>+</div>
      </div>
      <div style={{
        marginTop:10, fontSize:13, fontWeight:700, color:"var(--gold-ink)", lineHeight:1.3,
      }}>Add more sermons</div>
    </div>
  );
}

function Avatar({ url, name, size = 28, style }) {
  const sz = { width: size, height: size, borderRadius: "50%", flexShrink: 0 };
  if (url) {
    return (
      <img
        src={url}
        alt=""
        loading="lazy"
        style={{ ...sz, objectFit: "cover", display: "block", ...style }}
      />
    );
  }
  return (
    <div style={{
      ...sz,
      background: "rgba(232,200,122,0.14)",
      border: "1px solid rgba(232,200,122,0.3)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.max(10, Math.round(size * 0.36)),
      fontWeight: 800, color: "var(--gold-ink)",
      letterSpacing: "0.02em",
      ...style,
    }}>
      {getInitials(name)}
    </div>
  );
}

// Parse "H:MM:SS", "MM:SS", or "SS" → integer seconds. Returns 0 on invalid input.
function tsToSeconds(ts) {
  if (ts == null) return 0;
  const parts = String(ts).trim().split(":").map(p => parseInt(p, 10));
  if (!parts.length || parts.some(Number.isNaN)) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0];
}

// Build a YouTube watch URL, optionally jumping to a specific second.
function youtubeAt(id, seconds) {
  if (!id) return "";
  return seconds > 0
    ? `https://www.youtube.com/watch?v=${id}&t=${seconds}s`
    : `https://www.youtube.com/watch?v=${id}`;
}

// Open a link OUTSIDE the app. Inside the Median native wrapper a plain
// window.open(url, "_blank") navigates the in-app webview (or an internal
// window) to the URL — which strands the user on YouTube with no way back to
// the app. Median.window.open(url, "external") hands the URL to the system
// browser / YouTube app instead, leaving our webview untouched. On plain web
// (Median.isNativeApp() === false) we fall back to a normal new tab.
function openExternalUrl(url) {
  if (!url) return;
  // Older Median builds inject window.median.openExternalUrl; newer ones route
  // through the median-js-bridge package. Try both before the web fallback —
  // mirrors openInSystemBrowser() in Auth.jsx / Onboarding.jsx.
  try {
    const m = typeof window !== "undefined" ? window.median : null;
    if (m && typeof m.openExternalUrl === "function") { m.openExternalUrl(url); return; }
  } catch (_e) { /* fall through */ }
  try {
    if (Median && Median.isNativeApp && Median.isNativeApp()) {
      Median.window.open(url, "external");
      return;
    }
  } catch (_e) { /* fall through to standard web behaviour */ }
  window.open(url, "_blank", "noopener,noreferrer");
}

// ─────────────────────────────────────────────────────────────────────────────
// NEW LIFE CHURCH - Tunbridge Wells
// Brand colours from logo image:
//   Navy:  #1B2A4A  (exact background from logo)
//   White: #FFFFFF  (text)
//   Door icon: white SVG recreation
// ─────────────────────────────────────────────────────────────────────────────

const YT_CHANNEL = "UCpFNgZrQ_WK1MpgH7ZnLQmw";

// Multi-colour compass used as the Elders-Only dashboard icon. The four
// quadrants of the rose pick up the app's indicator palette so the icon ties
// back to the legend.
// ── FIRST-RUN WALKTHROUGH ────────────────────────────────────
// Six-step tour shown once after a user's first sign-in (controlled
// via the 'walkthroughComplete' localStorage flag in BibleApp). The
// caller is responsible for applying the spotlight styles to the
// matching elements based on `step`; this component only renders the
// dimming overlay, the gold info card, the skip button, and a fake
// sermon sheet on step 4.
// Tiny inline glyphs used inside walkthrough body copy so the description
// can reference the actual dots/heart in line with the text.
const InlineDot   = ({ color }) => (
  <span style={{display:"inline-block", width:7, height:7, borderRadius:"50%", background:color, verticalAlign:"middle", margin:"0 3px"}}/>
);
const InlineHeart = () => (
  <span style={{display:"inline-flex", verticalAlign:"middle", margin:"0 3px"}}>
    <svg width="9" height="9" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#FF6B9D" d="M12 21s-7.5-4.6-9.6-9.2C.9 8.1 3.4 4 7.5 4c2 0 3.4 1 4.5 2.4C13.1 5 14.5 4 16.5 4c4.1 0 6.6 4.1 5.1 7.8C19.5 16.4 12 21 12 21z"/>
    </svg>
  </span>
);

const WALKTHROUGH_STEPS = [
  null,
  {
    title: "Welcome to The Bible Community!",
    body:  "Explore your bible with your church's added context, and connect with your church community.",
  },
  {
    title: "Verse indicators",
    subtitle: "Watch out for these next to a verse",
    body: (<><InlineDot color="#7C6BC4"/> means a sermon note is available and link to watch the moment online.</>),
  },
  {
    title: "Verse indicators",
    subtitle: "Watch out for these next to a verse",
    body: (<><InlineDot color="#4A90E2"/> means someone from the community has left a comment.</>),
  },
  {
    title: "Verse indicators",
    subtitle: "Watch out for these next to a verse",
    body: (<><InlineHeart/> means someone from your church community has brought a prophetic or encouraging word.</>),
  },
  { title: "Tap any verse",                body: "See sermon notes, watch the moment on YouTube, add comments and more." },
  { title: "Connect with your community",  body: "Share reflections on verses, post prayer requests, and encourage each other through scripture." },
  { title: "Your church, all in one place", body: ({ churchName }) => `View sermons, church info, events and everything happening at ${churchName || 'your church'}.` },
];
const WALKTHROUGH_LAST_STEP = WALKTHROUGH_STEPS.length - 1; // 5

const WALKTHROUGH_SPOTLIGHT = {
  position: "relative",
  zIndex: 100001,
  boxShadow: "0 0 0 9999px rgba(0,0,0,0.85)",
  border: "2px solid rgba(232,200,122,0.7)",
  borderRadius: 6,
  // Block interactions on spotlit elements — only the gold card's Next
  // button and the Skip pill drive the tour forward.
  pointerEvents: "none",
  transition: "box-shadow 0.25s ease, border-color 0.25s ease",
};

function DemoSermonSheet({ churchName }) {
  const elderLabel = churchName ? `${churchName} Elders` : 'Elders';
  return (
    <div className="walkthrough-sheet" role="presentation">
      <div style={{fontSize:10, fontWeight:700, letterSpacing:"0.18em", textTransform:"uppercase", color:"var(--gold-ink)", marginBottom:8}}>
        Sermon Notes
      </div>
      <div style={{fontSize:18, fontWeight:700, color:"var(--ink)", marginBottom:4}}>
        No Condemnation
      </div>
      <div style={{fontSize:12, color:"var(--stone)", marginBottom:12}}>
        {elderLabel} · 10 May 2026
      </div>
      <div style={{fontSize:13, color:"var(--stone)", lineHeight:1.5, marginBottom:14}}>
        The preacher opened with this verse as the foundation — no condemnation for those in Christ Jesus.
      </div>
      <div style={{
        display:"inline-flex", alignItems:"center", gap:8,
        background:"#FF0000", color:"#fff",
        borderRadius:999, padding:"8px 14px",
        fontSize:12, fontWeight:700, fontFamily:"'Plus Jakarta Sans',sans-serif",
      }}>
        <span style={{width:0,height:0,borderTop:"5px solid transparent",borderBottom:"5px solid transparent",borderLeft:"7px solid #fff"}}/>
        Watch on YouTube · Jump to 14:22
      </div>
    </div>
  );
}

// Typewriter — reveals string content character-by-character. React elements
// embedded inside (e.g. <InlineDot/>) appear instantly at their position so
// the inline glyphs don't get split across characters. Resets when
// `resetKey` changes (we pass the walkthrough step).
function Typewriter({ children, speed = 10, resetKey }) {
  const atoms = useMemo(() => {
    const out = [];
    const walk = (node) => {
      if (node == null || node === false || node === true) return;
      if (typeof node === 'string' || typeof node === 'number') {
        String(node).split('').forEach(c => out.push({ kind: 'char', value: c }));
        return;
      }
      if (Array.isArray(node)) { node.forEach(walk); return; }
      if (node.type === Fragment) { walk(node.props?.children); return; }
      // Any other React element is opaque — render as a single atom so its
      // tag / styles / nested content stay intact.
      out.push({ kind: 'el', value: node });
    };
    walk(children);
    return out;
  }, [children]);

  const [count, setCount] = useState(atoms.length === 0 ? 0 : 0);
  useEffect(() => {
    setCount(0);
    if (atoms.length === 0) return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setCount(i);
      if (i >= atoms.length) clearInterval(id);
    }, speed);
    return () => clearInterval(id);
  }, [resetKey, atoms, speed]);

  return atoms.slice(0, count).map((a, idx) => <Fragment key={idx}>{a.value}</Fragment>);
}

function WalkthroughOverlay({ step, onNext, onSkip, churchName }) {
  // Fade in on mount so the user sees the app for ~250ms first, then the
  // walkthrough smoothly takes over.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const cur = WALKTHROUGH_STEPS[step];
  if (!cur) return null;
  const isLast      = step === WALKTHROUGH_LAST_STEP;
  const isSheetStep = step === 5;
  const fadeStyle = { opacity: mounted ? 1 : 0, transition: "opacity 0.35s ease" };

  return (
    <>
      {/* Backdrop sits behind everything spotlit (z-index 99999 < 100001) */}
      <div style={{
        position:"fixed", inset:0, zIndex:99999,
        background:"rgba(0,0,0,0.85)",
        pointerEvents:"none",
        ...fadeStyle,
      }}/>

      {isSheetStep && <DemoSermonSheet churchName={churchName}/>}

      {/* Gold info card. On the sheet step it lifts up to clear the demo sheet. */}
      <div style={{
        position:"fixed",
        bottom: isSheetStep ? 280 : 86,
        left: "50%",
        transform:"translateX(-50%)",
        width:"calc(100% - 32px)", maxWidth:380,
        background:"var(--gold)",
        borderRadius:16,
        padding:20,
        zIndex:100003,
        boxSizing:"border-box",
        transition:"bottom 0.3s ease, opacity 0.35s ease",
        opacity: mounted ? 1 : 0,
      }}>
        {/* Close (X) — pinned just above the card so it reads as part of the
            walkthrough rather than a stray button in the screen corner. */}
        <button onClick={onSkip}
          aria-label="Close walkthrough"
          style={{
            position:"absolute", top:-44, right:0, zIndex:100004,
            width:32, height:32, borderRadius:"50%",
            background:"rgba(255,255,255,0.12)",
            border:"1px solid rgba(255,255,255,0.25)",
            color:"#fff", fontSize:18, lineHeight:1, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontFamily:"'Plus Jakarta Sans',sans-serif",
          }}>✕</button>
        <div style={{fontSize:14, fontWeight:800, color:"var(--ink)", marginBottom: cur.subtitle ? 4 : 8, fontFamily:"'Plus Jakarta Sans',sans-serif"}}>{cur.title}</div>
        {cur.subtitle && (
          <div style={{fontSize:11, fontWeight:600, color:"rgba(31,27,22,0.6)", marginBottom:8, fontFamily:"'Plus Jakarta Sans',sans-serif"}}>{cur.subtitle}</div>
        )}
        <div style={{fontSize:11, color:"rgba(31,27,22,0.75)", lineHeight:1.5, marginBottom:16, fontFamily:"'Plus Jakarta Sans',sans-serif", minHeight:"3em"}}>
          <Typewriter resetKey={step}>{typeof cur.body === 'function' ? cur.body({ churchName }) : cur.body}</Typewriter>
        </div>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between"}}>
          <div style={{display:"flex", gap:6}}>
            {Array.from({length: WALKTHROUGH_LAST_STEP}, (_, i) => i + 1).map(i => (
              <span key={i} style={{
                width:6, height:6, borderRadius:"50%",
                background: i === step ? "var(--ink)" : "rgba(31,27,22,0.25)",
              }}/>
            ))}
          </div>
          <button onClick={onNext} style={{
            background:"var(--ink)", color:"var(--gold)", border:"none",
            borderRadius:8, padding:"9px 16px", fontSize:13, fontWeight:800,
            cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif",
          }}>
            {isLast ? "Got it ✓" : "Next →"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── CHURCH INFO SETTINGS SHEET ───────────────────────────────
// Elders / admins / super-admins can edit their church's display info
// (name, website, vision, "when & where we meet") and manage upcoming
// events from this sheet. Events show on the Church > Info tab for all
// users with an "Add to calendar" button that downloads an .ics file.

// Pad to two digits.
function _pad2(n) { return String(n).padStart(2, "0"); }

// Build an ICS event from { name, event_date (YYYY-MM-DD), event_time (HH:MM
// or HH:MM:SS, optional), location, description }. Uses floating local time
// (no Z suffix, no TZID) so the calendar app fires at the stated wall-clock
// time in whatever zone the user is in.
function buildIcsForEvent(ev) {
  const datePart = String(ev.event_date || "").replace(/-/g, "");
  const tParts = String(ev.event_time || "").split(":").map(s => s.trim());
  const hh = _pad2(parseInt(tParts[0], 10) || 0);
  const mm = _pad2(parseInt(tParts[1], 10) || 0);
  const dtStart = `${datePart}T${hh}${mm}00`;
  // One-hour default duration.
  const endHh = _pad2((parseInt(hh, 10) + 1) % 24);
  const dtEnd = `${datePart}T${endHh}${mm}00`;
  const escape = (s) => String(s || "")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
  const uid = `${ev.id || Math.random().toString(36).slice(2)}@biblecommunityproject`;
  const dtStamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d+/, "");
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Bible Community//Church Events//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:${escape(ev.name)}`,
    ev.location ? `LOCATION:${escape(ev.location)}` : null,
    ev.description ? `DESCRIPTION:${escape(ev.description)}` : null,
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean);
  return lines.join("\r\n");
}

function downloadIcs(ev) {
  const ics = buildIcsForEvent(ev);
  const slug = String(ev.name || "event").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "event";
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${slug}.ics`;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ChurchInfoSettingsSheet({ onClose, church, onSaved }) {
  // ── church-detail draft state ────────────────────────────
  const [name,        setName]        = useState(church?.name        || "");
  const [website,     setWebsite]     = useState(church?.website     || "");
  const [vision,      setVision]      = useState(church?.vision      || "");
  const [meetingInfo, setMeetingInfo] = useState(church?.meeting_info || "");
  const [welcomeMessage, setWelcomeMessage] = useState(church?.welcome_message || "");
  const [welcomeFrom,    setWelcomeFrom]    = useState(church?.welcome_from    || "");
  // Invite-only defaults ON (true unless explicitly false).
  const [inviteOnly,  setInviteOnly]  = useState(church?.invite_only !== false);
  const [showInviteInfo, setShowInviteInfo] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [savedMsg,    setSavedMsg]    = useState("");
  const [error,       setError]       = useState("");

  // ── events state ─────────────────────────────────────────
  const [events,    setEvents]    = useState([]);
  const [loadingEv, setLoadingEv] = useState(false);
  const [evError,   setEvError]   = useState("");
  // Draft being edited (or null for "add new"). When non-null, the form
  // below the list is visible.
  const [editingEvent, setEditingEvent] = useState(null);

  // Load events.
  const loadEvents = useCallback(async () => {
    if (!church?.id) return;
    setLoadingEv(true); setEvError("");
    const { data, error: e } = await supabase
      .from("church_events")
      .select("*")
      .eq("church_id", church.id)
      .order("event_date", { ascending: true });
    setLoadingEv(false);
    if (e) {
      // Table not present yet → not a hard error, just hide the section
      // with a hint message.
      if (/relation .* does not exist/i.test(e.message || "")) {
        setEvError("Events table not set up yet. Run the SQL migration noted below.");
        setEvents([]);
        return;
      }
      setEvError(e.message || String(e));
      return;
    }
    setEvents(data || []);
  }, [church?.id]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  async function saveChurchDetails() {
    if (!church?.id) return;
    setSaving(true); setError(""); setSavedMsg("");
    const updates = {
      name:         name.trim() || church.name,
      website:      website.trim() || null,
      vision:           vision.trim() || null,
      meeting_info:     meetingInfo.trim() || null,
      welcome_message:  welcomeMessage.trim() || null,
      welcome_from:     welcomeFrom.trim() || null,
      invite_only:      inviteOnly,
    };
    // First try the full update.
    let { error: err } = await supabase
      .from("churches").update(updates).eq("id", church.id);
    // Tolerate missing columns (vision / meeting_info migration not run yet).
    if (err && /column .* does not exist/i.test(err.message || "")) {
      const missing = (err.message.match(/column "?(\w+)"?/i) || [])[1];
      if (missing && updates[missing] !== undefined) {
        const fallback = { ...updates };
        delete fallback[missing];
        ;({ error: err } = await supabase
          .from("churches").update(fallback).eq("id", church.id));
        if (!err) setSavedMsg(`Saved (without ${missing} — run the SQL migration to enable that field).`);
      }
    }
    setSaving(false);
    if (err) { setError(err.message || String(err)); return; }
    if (!savedMsg) setSavedMsg("Saved ✓");
    onSaved?.();
    setTimeout(() => setSavedMsg(""), 3500);
  }

  async function deleteEvent(id) {
    if (!window.confirm("Delete this event?")) return;
    const { error: e } = await supabase.from("church_events").delete().eq("id", id);
    if (e) { setEvError(e.message || String(e)); return; }
    loadEvents();
  }

  return (
    <>
      <div className="overlay" onClick={onClose}/>
      <div className="sheet" style={{maxHeight:"92vh", display:"flex", flexDirection:"column"}}>
        <div className="shandle"/>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding:"4px 16px 8px", flexShrink:0}}>
          <div className="shd" style={{padding:0}}>Church settings</div>
          <button onClick={onClose} style={{background:"none", border:"none", color:"var(--stone-dim)", fontSize:20, cursor:"pointer", padding:4}}>✕</button>
        </div>

        <div style={{flex:1, overflowY:"auto", padding:"6px 16px 24px"}}>
          {/* ── Church details ───────────────────────────── */}
          <div className="profile-section-title">Church details</div>

          <label style={{fontSize:11, color:"var(--stone-dim)", display:"block", marginBottom:4, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase"}}>Church name</label>
          <input className="invite-input" value={name} onChange={e=>setName(e.target.value)}/>

          <label style={{fontSize:11, color:"var(--stone-dim)", display:"block", marginTop:10, marginBottom:4, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase"}}>Website</label>
          <input className="invite-input" type="url" placeholder="https://…" value={website} onChange={e=>setWebsite(e.target.value)}/>

          <label style={{fontSize:11, color:"var(--stone-dim)", display:"block", marginTop:10, marginBottom:4, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase"}}>Vision</label>
          <textarea className="invite-input" rows={2} placeholder="What's the church's vision?" value={vision} onChange={e=>setVision(e.target.value)}/>

          <label style={{fontSize:11, color:"var(--stone-dim)", display:"block", marginTop:10, marginBottom:4, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase"}}>When and where we meet</label>
          <textarea className="invite-input" rows={3} placeholder="e.g. Sundays at 10:30am · 12 Church Lane, Bristol BS1 2AB" value={meetingInfo} onChange={e=>setMeetingInfo(e.target.value)}/>

          <label style={{fontSize:11, color:"var(--stone-dim)", display:"block", marginTop:10, marginBottom:4, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase"}}>Welcome message (shown to new invitees)</label>
          <textarea className="invite-input" rows={2} placeholder="We're so glad you're here. Read along with Sunday's sermon all week, and pray with us." value={welcomeMessage} onChange={e=>setWelcomeMessage(e.target.value)}/>

          <label style={{fontSize:11, color:"var(--stone-dim)", display:"block", marginTop:10, marginBottom:4, fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase"}}>Welcome message from (optional)</label>
          <input className="invite-input" placeholder="e.g. Pastor Dave, New Life Church" value={welcomeFrom} onChange={e=>setWelcomeFrom(e.target.value)}/>

          {/* ── Access: invite-only toggle ───────────────── */}
          <div style={{marginTop:16, padding:"13px 14px", background:"var(--white)", border:"1px solid var(--border)", borderRadius:10}}>
            <div style={{display:"flex", alignItems:"center", gap:8}}>
              <div style={{flex:1, display:"flex", alignItems:"center", gap:6, fontSize:14, fontWeight:700, color:"var(--ink)"}}>
                Invite only
                <button type="button" onClick={()=>setShowInviteInfo(v=>!v)} title="What does this mean?"
                  style={{background:"none", border:"none", color:"var(--stone-dim)", cursor:"pointer", padding:0, display:"inline-flex", alignItems:"center"}}>
                  <Info size={15}/>
                </button>
              </div>
              <button type="button" role="switch" aria-checked={inviteOnly} onClick={()=>setInviteOnly(v=>!v)}
                style={{width:46, height:26, borderRadius:13, border:"none", cursor:"pointer", flexShrink:0, padding:0,
                  background: inviteOnly ? "#E8C87A" : "var(--border-strong)", position:"relative", transition:"background 160ms"}}>
                <span style={{position:"absolute", top:3, left: inviteOnly ? 23 : 3, width:20, height:20, borderRadius:"50%", background:"#fff", transition:"left 160ms"}}/>
              </button>
            </div>
            {showInviteInfo && (
              <div style={{marginTop:10, fontSize:12.5, color:"var(--stone)", lineHeight:1.55}}>
                If this setting is turned off then anyone can join the church as a visitor to view sermon notes &amp; clips. They will still need to request member access to view community features.
              </div>
            )}
            <div style={{marginTop:8, fontSize:11.5, color:"var(--stone-dim)", lineHeight:1.5}}>
              {inviteOnly
                ? "Hidden from the public church list - people join by invitation only."
                : "Listed publicly - anyone can join as a visitor to view sermon notes. Visitors still need to request member access to view community features."}
            </div>
          </div>

          {error    && <div style={{marginTop:8, padding:"8px 12px", background:"rgba(249,112,102,0.10)", border:"1px solid rgba(249,112,102,0.30)", borderRadius:8, fontSize:13, color:"#C8372A"}}>{error}</div>}
          {savedMsg && <div style={{marginTop:8, padding:"8px 12px", background:"rgba(80,200,150,0.10)",  border:"1px solid rgba(80,200,150,0.30)",  borderRadius:8, fontSize:13, color:"#1F7A52"}}>{savedMsg}</div>}

          <button type="button" onClick={saveChurchDetails} disabled={saving}
            style={{
              marginTop:12, width:"100%",
              background:"var(--gold)", color:"var(--ink)", border:"none",
              borderRadius:10, padding:"11px", fontSize:14, fontWeight:800,
              cursor: saving ? "default" : "pointer", opacity: saving ? 0.5 : 1,
              fontFamily:"'Plus Jakarta Sans',sans-serif",
            }}>
            {saving ? "Saving…" : "Save church details"}
          </button>

          {/* ── Events ───────────────────────────────────── */}
          <div className="profile-section-title" style={{marginTop:24}}>Events</div>
          <div style={{fontSize:12, color:"var(--stone-dim)", marginBottom:10, lineHeight:1.5}}>
            Upcoming events appear on the Church > Info page for everyone in your church. Each event has an "Add to calendar" button.
          </div>

          {evError && <div style={{padding:"8px 12px", background:"rgba(232,200,122,0.10)", border:"1px solid rgba(232,200,122,0.30)", borderRadius:8, fontSize:13, color:"var(--gold-ink)", marginBottom:10}}>{evError}</div>}

          {loadingEv && <div style={{fontSize:12, color:"var(--stone-dim)"}}>Loading events…</div>}

          {!loadingEv && events.length === 0 && !editingEvent && (
            <div style={{padding:"12px 14px", background:"var(--white)", border:"1px solid var(--border)", borderRadius:10, fontSize:13, color:"var(--stone)"}}>
              No events yet.
            </div>
          )}

          {!loadingEv && events.map(ev => (
            <div key={ev.id} style={{
              padding:"10px 14px", background:"var(--white)", border:"1px solid var(--border)",
              borderRadius:10, marginBottom:8, display:"flex", alignItems:"flex-start", gap:10,
            }}>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:14, fontWeight:700, color:"var(--ink)"}}>{ev.name}</div>
                <div style={{fontSize:12, color:"var(--stone)", marginTop:2}}>
                  {ev.event_date ? new Date(ev.event_date).toLocaleDateString("en-GB",{weekday:"short", day:"numeric", month:"short", year:"numeric"}) : ""}
                  {ev.event_time ? ` · ${ev.event_time.slice(0,5)}` : ""}
                  {ev.location   ? ` · ${ev.location}` : ""}
                </div>
                {ev.description && <div style={{fontSize:12, color:"var(--stone)", marginTop:6, lineHeight:1.5, whiteSpace:"pre-wrap"}}>{ev.description}</div>}
              </div>
              <div style={{display:"flex", flexDirection:"column", gap:6, flexShrink:0}}>
                <button type="button" onClick={() => setEditingEvent(ev)}
                  style={{background:"none", border:"1px solid var(--border)", color:"var(--stone)", borderRadius:6, padding:"4px 8px", fontSize:11, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>Edit</button>
                <button type="button" onClick={() => deleteEvent(ev.id)}
                  style={{background:"none", border:"1px solid rgba(249,112,102,0.40)", color:"#C8372A", borderRadius:6, padding:"4px 8px", fontSize:11, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>Delete</button>
              </div>
            </div>
          ))}

          {!evError && (
            <button type="button" onClick={() => setEditingEvent({})}
              style={{marginTop:10, width:"100%", background:"transparent", color:"var(--gold-ink)", border:"1px dashed rgba(232,200,122,0.40)", borderRadius:8, padding:"9px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
              + Add event
            </button>
          )}
        </div>
      </div>
      {editingEvent && (
        <EventEditorSheet
          event={editingEvent.id ? editingEvent : null}
          churchId={church.id}
          onClose={() => setEditingEvent(null)}
          onSaved={loadEvents}
        />
      )}
    </>
  );
}

// ── REMOVED POSTS SHEET ──────────────────────────────────────
// Moderator-only inbox of soft-deleted posts. Each row keeps the
// original text + who removed it; tapping Restore unmarks the
// source row and drops the audit entry.
// Maps a report.content_type to the table it lives in + a display label.
const REPORT_TABLE = {
  post:              { table: 'community_posts',          label: 'Community post' },
  comment:           { table: 'verse_comments',            label: 'Verse comment' },
  prayer:            { table: 'prayers',                   label: 'Prayer request' },
  encouragement:     { table: 'encouragements',             label: 'Encouragement' },
  plan_day_comment:  { table: 'reading_plan_day_comments',  label: 'Plan day comment' },
  general:           { table: null,                         label: 'App / general' },
};

// Moderation sheet — elders/admins/super_admins, scoped to the active church.
// Tab 1: reported content (act on member reports). Tab 2: removed posts.
function ModerationSheet({ items, onClose, onRestore, onRefresh, activeChurchId, initialTab }) {
  const [tab, setTab] = useState(initialTab || "reports"); // 'reports' | 'removed'

  // ── Removed-posts tab ──
  const [busyIds, setBusyIds] = useState(new Set());
  const [error,   setError]   = useState("");
  useEffect(() => { onRefresh?.(); /* refresh removed list on mount */ }, []); // eslint-disable-line react-hooks/exhaustive-deps
  async function handleRestore(rp) {
    setBusyIds(s => new Set(s).add(rp.id));
    setError("");
    const { error: err } = await onRestore(rp);
    setBusyIds(s => { const n = new Set(s); n.delete(rp.id); return n; });
    if (err) setError(err);
  }
  const removedLabel = (t) => ({
    verse_comment:  'Verse comment', prayer: 'Prayer request', prayer_comment: 'Prayer comment',
    encouragement:  'Encouragement', community_post: 'Community Board post',
  }[t] || t || 'Post');

  // ── Reported-content tab ──
  const [reports, setReports]   = useState([]);
  const [snippets, setSnippets] = useState({});         // content_id -> { text, removed }
  const [repLoading, setRepLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [repBusy, setRepBusy]   = useState(null);
  const [viewing, setViewing]   = useState(null);       // report whose full content is shown

  const loadReports = useCallback(async () => {
    if (!activeChurchId) { setReports([]); setRepLoading(false); return; }
    setRepLoading(true);
    let q = supabase.from("reports")
      .select("id, content_type, content_id, reason, notes, resolved, created_at, reporter:profiles!reporter_id(full_name, email)")
      .eq("church_id", activeChurchId)
      .order("created_at", { ascending: false });
    if (!showResolved) q = q.eq("resolved", false);
    const { data, error: err } = await q;
    setRepLoading(false);
    if (err) { console.warn("load reports:", err.message); setReports([]); return; }
    const rows = data || [];
    setReports(rows);
    const byTable = {};
    for (const r of rows) { const t = REPORT_TABLE[r.content_type]?.table; if (t) (byTable[t] = byTable[t] || []).push(r.content_id); }
    const map = {};
    await Promise.all(Object.entries(byTable).map(async ([table, ids]) => {
      const { data: cData } = await supabase.from(table).select("id, text, removed").in("id", ids);
      for (const c of (cData || [])) map[c.id] = { text: c.text, removed: c.removed };
    }));
    setSnippets(map);
  }, [activeChurchId, showResolved]);
  useEffect(() => { if (tab === "reports") loadReports(); }, [tab, loadReports]);

  async function removeReported(r) {
    const table = REPORT_TABLE[r.content_type]?.table;
    if (!table) return;
    setRepBusy(r.id);
    const { error: upErr } = await supabase.from(table).update({ removed: true }).eq("id", r.content_id);
    if (!upErr) await supabase.from("reports").update({ resolved: true }).eq("content_id", r.content_id);
    else console.warn("remove reported:", upErr.message);
    setRepBusy(null);
    setViewing(null);
    await loadReports(); onRefresh?.();
  }
  async function dismissReport(r) {
    setRepBusy(r.id);
    await supabase.from("reports").update({ resolved: true }).eq("id", r.id);
    setRepBusy(null);
    await loadReports();
  }

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-GB",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}); } catch { return ""; } };
  const reportText = (r) => r.content_type === "general" ? (r.notes || "") : (snippets[r.content_id]?.text || "");
  const grouped = {};
  for (const r of reports) (grouped[r.content_type] = grouped[r.content_type] || []).push(r);

  const tabBtn = (id, label) => (
    <button type="button" onClick={()=>setTab(id)}
      style={{ flex:1, background:"none", border:"none", borderBottom: tab===id ? "2px solid var(--gold)" : "2px solid transparent",
        color: tab===id ? "var(--gold-ink)" : "var(--stone-dim)", fontWeight:700, fontSize:13, padding:"10px 0", cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      {label}
    </button>
  );
  const actBtn = (label, onClick, kind) => (
    <button type="button" onClick={onClick} disabled={!!repBusy}
      style={{ background: kind==="danger" ? "#F97066" : kind==="primary" ? "var(--gold)" : "transparent",
        color: kind==="danger" ? "#fff" : kind==="primary" ? "var(--ink)" : "var(--stone)",
        border: kind==="ghost" ? "1px solid var(--border-strong)" : "none", borderRadius:8, padding:"6px 12px",
        fontSize:12, fontWeight:700, cursor: repBusy ? "default" : "pointer", opacity: repBusy ? 0.6 : 1, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      {label}
    </button>
  );

  return (
    <>
      <div className="overlay" style={{zIndex:220}} onClick={onClose}/>
      <div className="sheet" style={{zIndex:221, maxHeight:"90vh", display:"flex", flexDirection:"column"}}>
        <div className="shandle"/>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 16px 4px",flexShrink:0}}>
          <div className="shd" style={{padding:0}}>Moderation</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:20,cursor:"pointer",padding:4}}>✕</button>
        </div>
        <div style={{display:"flex",gap:4,padding:"0 16px",flexShrink:0,borderBottom:"1px solid var(--border)"}}>
          {tabBtn("reports", `Reported${reports.length && !showResolved ? ` (${reports.length})` : ""}`)}
          {tabBtn("removed", "Removed posts")}
        </div>

        {tab === "reports" && (
          <div style={{flex:1, overflowY:"auto", padding:"10px 16px 16px"}}>
            <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"var(--stone)",marginBottom:10,cursor:"pointer"}}>
              <input type="checkbox" checked={showResolved} onChange={e=>setShowResolved(e.target.checked)} style={{accentColor:"#E8C87A"}}/>
              Show resolved
            </label>
            {repLoading ? (
              <div style={{color:"var(--stone-dim)",fontSize:13,padding:"20px 0"}}>Loading…</div>
            ) : reports.length === 0 ? (
              <div className="empty" style={{padding:"40px 0"}}>
                <div className="eico">🚩</div>
                <div className="etit">{showResolved ? "No reports" : "No open reports"}</div>
                <div className="esub">When a member reports a post, prayer, comment or encouragement it appears here.</div>
              </div>
            ) : Object.entries(grouped).map(([ctype, rows]) => (
              <div key={ctype} style={{marginBottom:18}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:"var(--gold-ink)",marginBottom:8}}>
                  {(REPORT_TABLE[ctype]?.label || ctype)} · {rows.length}
                </div>
                {rows.map(r => {
                  const snip = snippets[r.content_id];
                  const text = reportText(r);
                  const reporter = r.reporter?.full_name || r.reporter?.email?.split("@")[0] || "A member";
                  return (
                    <div key={r.id} className="flag-card">
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                        <span style={{background:"rgba(249,112,102,0.12)",color:"#C8372A",fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:6}}>{r.reason}</span>
                        {snip?.removed && <span style={{color:"var(--stone-dim)",fontSize:11}}>· already removed</span>}
                        {r.resolved && <span style={{color:"#1F7A52",fontSize:11}}>· resolved</span>}
                        <span style={{marginLeft:"auto",fontSize:11,color:"var(--stone-dim)"}}>{fmtDate(r.created_at)}</span>
                      </div>
                      <div style={{padding:"10px 12px",background:"var(--white)",border:"1px solid var(--border)",borderRadius:8,fontSize:13,color:"var(--stone)",lineHeight:1.5,marginBottom:8,whiteSpace:"pre-wrap"}}>
                        {text ? (text.length > 100 ? text.slice(0,100).trim() + "…" : text) : <span style={{color:"var(--stone-dim)"}}>(content not found — may be deleted)</span>}
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span style={{fontSize:11,color:"var(--stone-dim)",marginRight:"auto"}}>Reported by {reporter}</span>
                        {text && actBtn("View", ()=>setViewing(r), "ghost")}
                        {!r.resolved && REPORT_TABLE[r.content_type]?.table && !snip?.removed && actBtn("Remove", ()=>removeReported(r), "danger")}
                        {!r.resolved && actBtn("Dismiss", ()=>dismissReport(r), "primary")}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {tab === "removed" && (
          <div style={{flex:1, overflowY:"auto", padding:"10px 16px 16px"}}>
            {error && <div style={{margin:"0 0 8px",fontSize:12,color:"#C8372A"}}>{error}</div>}
            {items.length === 0 ? (
              <div className="empty" style={{padding:"40px 0"}}>
                <div className="eico">🗑</div>
                <div className="etit">Nothing removed</div>
                <div className="esub">When a moderator removes a post it appears here, with the option to restore it.</div>
              </div>
            ) : items.map(rp => {
              const fmt = rp.removed_at ? new Date(rp.removed_at).toLocaleString("en-GB",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "";
              const busy = busyIds.has(rp.id);
              return (
                <div key={rp.id} className="flag-card">
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                    <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:"var(--gold-ink)"}}>{removedLabel(rp.post_type)}</div>
                    <div style={{fontSize:11,color:"var(--stone-dim)"}}>{fmt}</div>
                  </div>
                  {rp.original_text && (
                    <div style={{padding:"10px 12px",background:"var(--white)",border:"1px solid var(--border)",borderRadius:8,fontSize:13,color:"var(--stone)",fontStyle:"italic",lineHeight:1.5,marginBottom:8,whiteSpace:"pre-wrap"}}>"{rp.original_text}"</div>
                  )}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                    <div style={{fontSize:11,color:"var(--stone-dim)"}}>Removed by {rp.profiles?.full_name || "Elder"}</div>
                    <button type="button" disabled={busy} onClick={()=>handleRestore(rp)}
                      style={{background:"var(--gold)",color:"var(--ink)",border:"none",borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:800,cursor:busy?"default":"pointer",opacity:busy?0.6:1,fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                      {busy ? "Restoring…" : "Restore"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* View full reported content */}
      {viewing && (
        <>
          <div className="overlay" style={{zIndex:240}} onClick={()=>setViewing(null)}/>
          <div className="sheet" style={{zIndex:241, maxHeight:"80vh", display:"flex", flexDirection:"column"}}>
            <div className="shandle"/>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 16px 8px"}}>
              <div className="shd" style={{padding:0}}>{REPORT_TABLE[viewing.content_type]?.label || viewing.content_type}</div>
              <button onClick={()=>setViewing(null)} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:20,cursor:"pointer",padding:4}}>✕</button>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"0 16px 16px"}}>
              <div style={{padding:"12px 14px",background:"var(--white)",border:"1px solid var(--border)",borderRadius:10,fontSize:14,color:"var(--ink)",lineHeight:1.6,whiteSpace:"pre-wrap"}}>
                {reportText(viewing) || "(content not found — it may have been deleted)"}
              </div>
              {viewing.notes && viewing.content_type !== "general" && (
                <div style={{fontSize:12,color:"var(--stone)",marginTop:10}}>Reporter note: {viewing.notes}</div>
              )}
              <div style={{display:"flex",gap:8,marginTop:16}}>
                {REPORT_TABLE[viewing.content_type]?.table && !snippets[viewing.content_id]?.removed && (
                  <button type="button" disabled={!!repBusy} onClick={()=>removeReported(viewing)}
                    style={{flex:1,background:"#F97066",color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                    Remove content
                  </button>
                )}
                <button type="button" disabled={!!repBusy} onClick={()=>{ dismissReport(viewing); setViewing(null); }}
                  style={{flex:1,background:"transparent",color:"var(--stone)",border:"1px solid var(--border-strong)",borderRadius:10,padding:"12px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ── CONFIRM DELETE SHEET ─────────────────────────────────────
// Used for cross-author deletions by moderators — shows a clear
// "this isn't yours" warning + a note about restoring from the
// Removed Posts panel. Self-deletions still use window.confirm.
function ConfirmDeleteSheet({ title, subtitle, onCancel, onConfirm, busy }) {
  return (
    <>
      <div className="overlay" style={{zIndex:300}} onClick={busy ? undefined : onCancel}/>
      <div className="sheet" style={{zIndex:301, maxHeight:"60vh"}}>
        <div className="shandle"/>
        <div style={{padding:"4px 20px 20px"}}>
          <div style={{fontSize:15, fontWeight:700, color:"var(--ink)", lineHeight:1.4, marginBottom:8}}>{title}</div>
          {subtitle && (
            <div style={{fontSize:12, color:"var(--stone)", lineHeight:1.5, marginBottom:18}}>{subtitle}</div>
          )}
          <div style={{display:"flex", gap:8}}>
            <button type="button" onClick={busy ? undefined : onCancel}
              style={{flex:1, background:"transparent", color:"var(--stone)", border:"1px solid var(--border-strong)", borderRadius:10, padding:"12px", fontSize:14, fontWeight:600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.5 : 1, fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
              Cancel
            </button>
            <button type="button" onClick={busy ? undefined : onConfirm} disabled={busy}
              style={{flex:1, background:"#F97066", color:"#fff", border:"none", borderRadius:10, padding:"12px", fontSize:14, fontWeight:800, cursor: busy ? "default" : "pointer", opacity: busy ? 0.5 : 1, fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
              {busy ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── CONTENT ACTIONS MENU (kebab) ─────────────────────────────
// 3-dot menu on every piece of user-generated content. Holds Delete (for the
// author + moderators) and Report (on others' content).
function ContentActionsMenu({ canDelete, isOwn, onDelete, onReport }) {
  const [open, setOpen] = useState(false);
  const showReport = !isOwn && !!onReport;
  if (!canDelete && !showReport) return null;
  const item = (label, color, onClick, border) => (
    <div onClick={(e)=>{ e.stopPropagation(); setOpen(false); onClick(); }}
      style={{padding:"11px 14px", fontSize:13, fontWeight:600, color, cursor:"pointer", borderTop: border ? "1px solid var(--border)" : "none"}}>
      {label}
    </div>
  );
  return (
    <div style={{position:"relative", display:"inline-flex"}}>
      <button type="button" aria-label="More options"
        onClick={(e)=>{ e.stopPropagation(); setOpen(o=>!o); }}
        style={{background:"none",border:"none",padding:"4px 6px",cursor:"pointer",color:"var(--stone-dim)",display:"inline-flex",alignItems:"center",fontSize:18,lineHeight:1}}>
        ⋮
      </button>
      {open && (
        <>
          <div onClick={(e)=>{ e.stopPropagation(); setOpen(false); }} style={{position:"fixed", inset:0, zIndex:90}}/>
          <div style={{position:"absolute", top:"100%", right:0, marginTop:6, zIndex:91, background:"var(--white)", border:"1px solid var(--border-strong)", borderRadius:10, minWidth:170, overflow:"hidden", boxShadow:"0 10px 24px rgba(0,0,0,0.2)"}}>
            {showReport && item("Report", "var(--ink)", onReport, false)}
            {canDelete  && item("Delete", "#C8372A", onDelete, showReport)}
          </div>
        </>
      )}
    </div>
  );
}

// ── REPORT SHEET ─────────────────────────────────────────────
const REPORT_REASONS = ["Inappropriate content","Spam or advertising","Offensive language","Harmful or dangerous","Other"];
function ReportSheet({ target, onClose, onSubmit, busy }) {
  const isGeneral = target?.content_type === "general";
  const [reason, setReason] = useState(isGeneral ? "Other" : "");
  const [notes,  setNotes]  = useState("");
  const [done,   setDone]   = useState(false);
  const canSubmit = isGeneral ? notes.trim().length > 0 : !!reason;
  const submit = async () => {
    if (!canSubmit || busy) return;
    const ok = await onSubmit({ reason: reason || "Other", notes: notes.trim() || null });
    if (ok) setDone(true);
  };
  return (
    <>
      <div className="overlay" style={{zIndex:320}} onClick={busy ? undefined : onClose}/>
      <div className="sheet" style={{zIndex:321, maxHeight:"82vh"}}>
        <div className="shandle"/>
        <div style={{padding:"4px 20px 24px"}}>
          {done ? (
            <div style={{textAlign:"center", padding:"12px 0"}}>
              <div style={{fontSize:34, marginBottom:10, color:"var(--gold-ink)"}}>✓</div>
              <div style={{fontSize:15, fontWeight:700, color:"var(--ink)", lineHeight:1.4, marginBottom:18}}>
                {isGeneral ? "Thank you for contacting us" : "Thank you for reporting this content"}
              </div>
              <button type="button" onClick={onClose}
                style={{background:"var(--gold)", color:"var(--ink)", border:"none", borderRadius:10, padding:"12px 28px", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                Done
              </button>
            </div>
          ) : (
            <>
              <div style={{fontSize:16, fontWeight:800, color:"var(--ink)", marginBottom:4}}>
                {isGeneral ? "Report a problem" : "Report this content"}
              </div>
              <div style={{fontSize:12, color:"var(--stone)", marginBottom:16}}>
                {isGeneral ? "Tell us about any issue with content or the app." : "Why are you reporting this?"}
              </div>
              {!isGeneral && REPORT_REASONS.map(r => (
                <label key={r} style={{display:"flex", alignItems:"center", gap:10, padding:"11px 4px", cursor:"pointer", borderBottom:"1px solid var(--border)"}}>
                  <input type="radio" name="report-reason" checked={reason===r} onChange={()=>setReason(r)} style={{accentColor:"#E8C87A", width:16, height:16}}/>
                  <span style={{fontSize:14, color:"var(--ink)"}}>{r}</span>
                </label>
              ))}
              <textarea
                value={notes} onChange={e=>setNotes(e.target.value)}
                placeholder={isGeneral ? "Describe the issue…" : "Add any details (optional)…"}
                rows={isGeneral ? 4 : 2}
                style={{width:"100%", boxSizing:"border-box", marginTop:14, background:"var(--white)", border:"1px solid var(--border-strong)", borderRadius:10, padding:"10px 12px", color:"var(--ink)", fontSize:14, fontFamily:"'Plus Jakarta Sans',sans-serif", resize:"vertical"}}/>
              <button type="button" onClick={submit} disabled={!canSubmit || busy}
                style={{width:"100%", marginTop:16, background:"var(--gold)", color:"var(--ink)", border:"none", borderRadius:10, padding:"13px", fontSize:14, fontWeight:800, cursor:(!canSubmit||busy)?"default":"pointer", opacity:(!canSubmit||busy)?0.5:1, fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                {busy ? "Submitting…" : "Submit report"}
              </button>
              <div style={{textAlign:"center", marginTop:12}}>
                <button type="button" onClick={onClose} style={{background:"none", border:"none", color:"var(--stone)", fontSize:13, textDecoration:"underline", cursor:"pointer"}}>Cancel</button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── NOTIFICATIONS SHEET ──────────────────────────────────────
// Inbox split into "My activity" (engagements on your own content)
// and "For elders" (moderator-only items). Tapping an item marks
// it read and runs onNavigate to jump to the relevant screen.
const NOTIF_ICONS = {
  prayer_reply:        "🙏",
  prayer_reaction:     "❤️",
  verse_comment_reply: "💬",
  board_post_reply:    "📣",
  community_post_comment: "💬",
  access_approved:     "✅",
  invite_received:     "✉️",
  access_request:      "🔓",
  question_flagged:    "❓",
  elder_prayer:        "🙏",
};
const ELDER_TYPES  = new Set(["question_flagged","elder_prayer"]);
const ACCESS_TYPES = new Set(["access_request"]);
const MOD_TYPES    = new Set([...ELDER_TYPES, ...ACCESS_TYPES]);

// Icon-circle colour + short category label per notification type, for the
// Notifications page (docs/designs/Notifications.dc.html).
const NOTIF_COLORS = {
  prayer_reply:           { bg: "var(--blush)", ink: "var(--blush-ink)" },
  prayer_reaction:        { bg: "var(--blush)", ink: "var(--blush-ink)" },
  elder_prayer:           { bg: "var(--blush)", ink: "var(--blush-ink)" },
  verse_comment_reply:    { bg: "var(--sky)",   ink: "var(--sky-ink)" },
  community_post_comment: { bg: "var(--sky)",   ink: "var(--sky-ink)" },
  board_post_reply:       { bg: "var(--peach)", ink: "var(--peach-ink)" },
  access_request:         { bg: "var(--peach)", ink: "var(--peach-ink)" },
  invite_received:        { bg: "var(--peach)", ink: "var(--peach-ink)" },
  access_approved:        { bg: "var(--sage)",  ink: "var(--sage-ink)" },
  question_flagged:       { bg: "#F5E4B8",      ink: "#7A5A12" },
};
const NOTIF_CATEGORY = {
  prayer_reply: "Prayer", prayer_reaction: "Prayer", elder_prayer: "Prayer",
  verse_comment_reply: "Bible", community_post_comment: "Community", board_post_reply: "Community",
  access_request: "Access", access_approved: "Access", invite_received: "Invite",
  question_flagged: "Elders",
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const sec = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (sec < 60)        return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60)        return `${min}m`;
  const hr  = Math.round(min / 60);
  if (hr  < 24)        return `${hr}h`;
  const d   = Math.round(hr / 24);
  if (d   < 7)         return `${d}d`;
  const w   = Math.round(d / 7);
  if (w   < 5)         return `${w}w`;
  return new Date(iso).toLocaleDateString("en-GB", { day:"numeric", month:"short" });
}

function NotificationsPage({ onClose, items, markAsRead, markAllRead, isModerator, onNavigate, onApproveRequest, onDeclineRequest, accessActionState, scrollToAccess }) {
  // When opened from an access-request deep link, scroll the access section in.
  const accessSectionRef = useRef(null);
  useEffect(() => {
    if (!scrollToAccess) return;
    const t = setTimeout(() => {
      accessSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(t);
  }, [scrollToAccess]);
  // Once an access request has been approved/declined we want it gone from
  // the list for the rest of this session; reopening the sheet should also
  // start fresh (the request row is now non-pending in the DB anyway). We
  // track actioned IDs in component-local state plus filter out any
  // access_request notification whose `read` flag is already true.
  const [actionedNotifIds, setActionedNotifIds] = useState(new Set());
  // Opening this page clears the This Week avatar's unread badge (docs/
  // FIX-PROFILE.md + FIX-HEADERS.md) — but calling markAllRead() on mount
  // would flip every item to "read" before the New/Earlier split below ever
  // renders, collapsing "New" instantly. Snapshot which ids were unread once,
  // at mount only (lazy initializer, never re-runs), and use that snapshot
  // for the split instead of the live `read` flag — this viewing still shows
  // what was new, even though the badge clears right away.
  const [wasUnreadSnapshot] = useState(() => new Set(items.filter(n => !n.read).map(n => n.id)));
  useEffect(() => { markAllRead?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const visibleItems = items.filter(n => {
    if (actionedNotifIds.has(n.id)) return false;
    if (n.type === 'access_request' && n.read) return false;
    return true;
  });
  const myActivity   = visibleItems.filter(n => !MOD_TYPES.has(n.type));
  const elderItems   = visibleItems.filter(n =>  ELDER_TYPES.has(n.type));
  const accessItems  = visibleItems.filter(n =>  ACCESS_TYPES.has(n.type));
  // Everything except the pinned access-request queue, split by read status
  // (New / Earlier) per docs/designs/Notifications.dc.html.
  const generalItems = [...myActivity, ...elderItems];
  const newItems      = generalItems.filter(n => wasUnreadSnapshot.has(n.id));
  const earlierItems  = generalItems.filter(n => !wasUnreadSnapshot.has(n.id));

  // Local toasts surfaced when a moderator approves a request.
  // Stored in component-local state so they vanish when the sheet closes
  // and never reappear on reopen.
  const [toasts, setToasts] = useState([]);
  // Tracks which button the moderator clicked for a given request so the
  // accessActionState → "done" effect can choose between "approved" toast
  // and silent hide on decline. Map<reqId, 'approve'|'decline'>.
  const actionTypeRef = useRef(new Map());
  // toastedRef de-duplicates the "done" → toast effect so it doesn't fire
  // twice on subsequent renders.
  const toastedRef = useRef(new Set());

  // For access/elder-prayer/question notifications we want to show the
  // actor's avatar + name. access_request notifications carry requester_id
  // directly; for prayer/question we look up the source row (prayer.author_id
  // / flagged_verse.user_id) and then batch-fetch the profile. Result is a
  // map keyed by notification id so each row can render its actor easily.
  const [actorsByNotif, setActorsByNotif] = useState({});

  // Effect: when a request transitions to "done", hide the notification
  // and (for approvals) push a toast.
  useEffect(() => {
    if (!accessActionState) return;
    for (const [reqId, raw] of Object.entries(accessActionState)) {
      const kind = typeof raw === 'string' ? raw : raw?.kind;
      if (kind !== 'done' || toastedRef.current.has(reqId)) continue;
      toastedRef.current.add(reqId);
      const notif = items.find(n => n.data?.access_request_id === reqId);
      if (!notif) continue;
      setActionedNotifIds(s => { const n2 = new Set(s); n2.add(notif.id); return n2; });
      if (actionTypeRef.current.get(reqId) !== 'decline') {
        const actor = actorsByNotif[notif.id];
        const name  = actor?.full_name || actor?.email?.split('@')[0] || 'this member';
        setToasts(t => [...t, { id: `${reqId}-${Date.now()}`, text: `Access Request for ${name} approved` }]);
      }
    }
  }, [accessActionState, items, actorsByNotif]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const accessNs  = items.filter(n => n.type === 'access_request'  && n.data?.requester_id);
      const prayerNs  = items.filter(n => n.type === 'elder_prayer'    && n.data?.prayer_id);
      const flaggedNs = items.filter(n => n.type === 'question_flagged' && n.data?.flagged_id);
      const todo = accessNs.concat(prayerNs, flaggedNs).filter(n => !(n.id in actorsByNotif));
      if (todo.length === 0) return;

      const notifToActor = {};
      accessNs.forEach(n => { notifToActor[n.id] = n.data.requester_id; });

      const prayerIds  = [...new Set(prayerNs.map(n => n.data.prayer_id))];
      if (prayerIds.length > 0) {
        const { data } = await supabase.from('prayers').select('id, author_id').in('id', prayerIds);
        if (cancelled) return;
        const m = Object.fromEntries((data || []).map(p => [p.id, p.author_id]));
        prayerNs.forEach(n => { notifToActor[n.id] = m[n.data.prayer_id]; });
      }

      const flaggedIds = [...new Set(flaggedNs.map(n => n.data.flagged_id))];
      if (flaggedIds.length > 0) {
        const { data } = await supabase.from('flagged_verses').select('id, user_id').in('id', flaggedIds);
        if (cancelled) return;
        const m = Object.fromEntries((data || []).map(f => [f.id, f.user_id]));
        flaggedNs.forEach(n => { notifToActor[n.id] = m[n.data.flagged_id]; });
      }

      const actorIds = [...new Set(Object.values(notifToActor).filter(Boolean))];
      if (actorIds.length === 0) return;
      const { data: profs } = await supabase.from('profiles')
        .select('id, full_name, avatar_url').in('id', actorIds);
      if (cancelled) return;
      const profsById = Object.fromEntries((profs || []).map(p => [p.id, p]));
      const byNotif = {};
      Object.entries(notifToActor).forEach(([nid, aid]) => {
        if (profsById[aid]) byNotif[nid] = profsById[aid];
      });
      setActorsByNotif(curr => ({ ...curr, ...byNotif }));
    })();
    return () => { cancelled = true; };
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  function NotifRow({ n }) {
    const isAccess     = n.type === "access_request";
    const showsActor   = isAccess || n.type === "elder_prayer" || n.type === "question_flagged";
    const reqId        = n.data?.access_request_id;
    const raw          = accessActionState?.[reqId];
    const kind         = typeof raw === 'string' ? raw : raw?.kind;
    const fallbackIcon = NOTIF_ICONS[n.type] || "🔔";
    const actor        = actorsByNotif[n.id] || null;
    const actorName    = actor?.full_name || (actor ? "Someone" : "");
    // Override the trigger-supplied access_request title to the cleaner copy.
    const displayTitle = isAccess ? "New member access request" : n.title;
    // For access_request the actor avatar/name carries the requester — so we
    // normally suppress the original body. But if the actor profile couldn't be
    // loaded (e.g. the requester isn't a co-member yet, so RLS hides their
    // profile), fall back to the trigger body ("<name> is requesting access")
    // so the moderator can still see WHO is asking, not a nameless request.
    const displayBody  = isAccess ? (actor ? null : n.body) : n.body;

    const colors   = NOTIF_COLORS[n.type] || { bg: "var(--border)", ink: "var(--stone)" };
    const category = NOTIF_CATEGORY[n.type] || "Activity";

    return (
      <div
        onClick={() => {
          // Access requests are only marked read via approve/decline so a
          // stray body tap doesn't dismiss a pending action.
          if (isAccess) return;
          markAsRead(n.id);
          onNavigate?.(n);
        }}
        style={{
          display:"flex", gap:12, alignItems:"flex-start",
          background: n.read ? "transparent" : "var(--white)",
          borderRadius:20, padding:12, marginBottom:8,
          cursor:"pointer",
        }}>
        <div style={{flexShrink:0, width:40, height:40, borderRadius:20, background:colors.bg, color:colors.ink, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18}}>
          {fallbackIcon}
        </div>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:14, fontWeight:700, color:"var(--ink)", lineHeight:1.35}}>{displayTitle}</div>
          {showsActor && actor && (
            <div style={{display:"flex", alignItems:"center", gap:6, marginTop:6}}>
              <Avatar url={actor.avatar_url || null} name={actorName} size={20}/>
              <span style={{fontSize:12, color:"var(--stone)", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{actorName || "Someone"}</span>
            </div>
          )}
          {displayBody && (
            <div style={{fontSize:13, color:"var(--stone)", marginTop:showsActor ? 6 : 2, lineHeight:1.4, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden"}}>{displayBody}</div>
          )}
          <div style={{fontSize:12, color:"var(--stone)", marginTop:4}}>{category} · {timeAgo(n.created_at)}</div>
          {isAccess && reqId && (
            <div style={{display:"flex", gap:6, marginTop:8}} onClick={e=>e.stopPropagation()}>
              <button type="button"
                disabled={kind === "pending" || kind === "done"}
                onClick={()=>{
                  actionTypeRef.current.set(reqId, 'approve');
                  onApproveRequest?.({ id: reqId, user_id: n.data?.requester_id, church_id: n.church_id }, n.id);
                }}
                style={{flex:1, background:"var(--gold)", color:"var(--ink)", border:"none", borderRadius:8, padding:"7px", fontSize:12, fontWeight:800, cursor: kind === "pending" ? "default" : "pointer", opacity: kind === "pending" ? 0.5 : 1, fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                {kind === "done" ? "Approved" : kind === "pending" ? "…" : "Approve"}
              </button>
              <button type="button"
                disabled={kind === "pending" || kind === "done"}
                onClick={()=>{
                  actionTypeRef.current.set(reqId, 'decline');
                  onDeclineRequest?.({ id: reqId, user_id: n.data?.requester_id, church_id: n.church_id }, n.id);
                }}
                style={{background:"transparent", color:"#C8372A", border:"1px solid rgba(249,112,102,0.35)", borderRadius:8, padding:"7px 12px", fontSize:12, fontWeight:700, cursor: kind === "pending" ? "default" : "pointer", opacity: kind === "pending" ? 0.5 : 1, fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                Decline
              </button>
            </div>
          )}
        </div>
        {!n.read && <span style={{width:8, height:8, borderRadius:4, background:"#E0533D", flexShrink:0, marginTop:6}}/>}
      </div>
    );
  }

  const sectionHeadStyle = { fontSize:12, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:"#7A5A12", padding:"14px 2px 8px" };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:500, background:"var(--cream)",
      display:"flex", flexDirection:"column",
      padding:"calc(env(safe-area-inset-top) + 12px) 22px 0",
    }}>
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0}}>
        <div style={{display:"flex", alignItems:"center", gap:12}}>
          <button type="button" aria-label="Back" onClick={onClose}
            style={{width:44, height:44, borderRadius:22, border:"none", background:"var(--white)", color:"var(--ink)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>
          </button>
          <div style={{fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:26, fontWeight:800, letterSpacing:"-0.02em", color:"var(--ink)"}}>Notifications</div>
        </div>
        {items.some(n => !n.read) && (
          <button type="button" onClick={markAllRead}
            style={{background:"none", border:"none", color:"var(--gold-ink)", fontSize:13, fontWeight:700, cursor:"pointer", padding:4, fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
            Mark all read
          </button>
        )}
      </div>

      <div style={{flex:1, overflowY:"auto", paddingBottom:"calc(env(safe-area-inset-bottom) + 24px)"}}>
        {/* Toasts for moderator actions. Cleared when the page closes. */}
        {toasts.length > 0 && (
          <div style={{paddingTop:8}}>
            {toasts.map(t => (
              <div key={t.id} style={{
                margin:"4px 0",
                padding:"10px 12px",
                background:"rgba(80,200,150,0.10)",
                border:"1px solid rgba(80,200,150,0.30)",
                borderRadius:10,
                display:"flex", alignItems:"center", gap:8,
                fontSize:13, fontWeight:600, color:"#1F7A52",
                fontFamily:"'Plus Jakarta Sans',sans-serif",
              }}>
                <span style={{fontSize:14, lineHeight:1}}>✓</span>
                <span style={{flex:1, minWidth:0}}>{t.text}</span>
              </div>
            ))}
          </div>
        )}
        {/* Access requests pinned at the top for moderators — these are
            the most time-sensitive notifications (a visitor is blocked
            until they're actioned) so they get the prime position, ahead
            of the New/Earlier split below. */}
        {isModerator && (
          <>
            <div ref={accessSectionRef} style={sectionHeadStyle}>Access requests</div>
            {accessItems.length === 0 ? (
              <div style={{padding:"8px 2px 16px", color:"var(--stone-dim)", fontSize:13}}>No pending access requests.</div>
            ) : accessItems.map(n => <NotifRow key={n.id} n={n}/>)}
          </>
        )}

        {newItems.length > 0 && (
          <>
            <div style={sectionHeadStyle}>New</div>
            {newItems.map(n => <NotifRow key={n.id} n={n}/>)}
          </>
        )}
        {earlierItems.length > 0 && (
          <>
            <div style={sectionHeadStyle}>Earlier</div>
            {earlierItems.map(n => <NotifRow key={n.id} n={n}/>)}
          </>
        )}
        {generalItems.length === 0 && (
          <div style={{padding:"8px 2px 16px", color:"var(--stone-dim)", fontSize:13}}>Nothing here yet.</div>
        )}
      </div>
    </div>
  );
}

function ColouredCompass({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10.5" fill="none" stroke="var(--border-strong)" strokeWidth="1.3"/>
      {/* N - red (sermon) */}
      <polygon points="12,3.5 13.6,12 10.4,12" fill="#7C6BC4"/>
      {/* E - pink (encouragement) */}
      <polygon points="20.5,12 12,13.6 12,10.4" fill="#FF6B9D"/>
      {/* S - blue (community comment) */}
      <polygon points="12,20.5 10.4,12 13.6,12" fill="#4A90E2"/>
      {/* W - purple (question) */}
      <polygon points="3.5,12 12,10.4 12,13.6" fill="#9B6FD4"/>
      {/* hub */}
      <circle cx="12" cy="12" r="1.4" fill="#fff"/>
    </svg>
  );
}
const NLC_LOGO_B64 = "data:image/jpeg;base64,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";


const Styles = () => (
  <style>{`
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; -webkit-tap-highlight-color: transparent; }
    html, body, #root { height: 100%; }
    body { background: var(--cream); color: var(--ink); font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif; overflow: hidden; }

    /* Dyslexia-friendly reading setting (Phase 6) — self-hosted, SIL OFL
       licensed (see public/fonts/OFL.txt). Only loaded/used when the Aa
       sheet's toggle is on; var(--scripture-font) falls back to the
       scripture serif stack otherwise. */
    @font-face { font-family: 'OpenDyslexic'; src: url('/fonts/OpenDyslexic-Regular.woff2') format('woff2'); font-weight: 400; font-style: normal; font-display: swap; }
    @font-face { font-family: 'OpenDyslexic'; src: url('/fonts/OpenDyslexic-Bold.woff2') format('woff2'); font-weight: 700; font-style: normal; font-display: swap; }

    :root {
      /* Core palette */
      --cream:     #F6F2EC;
      --white:     #FFFFFF;
      --ink:       #1F1B16;
      --stone:     #6B6258;
      --gold:      #E8C87A;
      --gold-ink:  #7A5A12;
      --highlight: #F5E4B8;

      /* Category tag colours: fill / text pairs */
      --lavender: #DCD3F5;  --lavender-ink: #2E2552;
      --sky:      #CFE0F2;  --sky-ink:      #253A50;
      --sage:     #CFE3CF;  --sage-ink:     #2F5A31;
      --blush:    #F4D3D1;  --blush-ink:    #7A3B36;
      --peach:    #F6C9A8;  --peach-ink:    #5C3A1E;
      --sermon-dot: #7C6BC4;

      /* Derived structural tokens - not given explicit hex in the redesign spec */
      --border:        rgba(31,27,22,0.10);
      --border-strong: rgba(31,27,22,0.18);
      --stone-dim:     rgba(107,98,88,0.60);
      --ink-dim:       rgba(31,27,22,0.55);
      --shadow:        rgba(31,27,22,0.12);

      --radius-card: 24px;
      --radius-pill: 999px;
    }

    .app {
      max-width: 480px; margin: 0 auto;
      height: 100vh; height: 100dvh;
      /* Clear the status bar / notch under viewport-fit=cover. border-box keeps
         the total height at 100dvh; the bottom indicator is handled by .bnav. */
      padding-top: env(safe-area-inset-top);
      display: flex; flex-direction: column;
      overflow: hidden;
      background: var(--cream);
    }
    /* On desktop, give the centred column subtle side rails so it reads as a
       phone-style app centred on the black page rather than a floating strip. */
    @media (min-width: 768px) {
      .app { border-left: 1px solid var(--border); border-right: 1px solid var(--border); }
    }

    /* ══ HEADER ══ */
    .header {
      flex-shrink: 0;
      background: var(--cream);
      padding: 0 22px;
      height: 62px;
      display: flex; align-items: center; justify-content: space-between;
    }
    .hdr-title { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
    /* Only This Week uses these now (every other tab has its own in-wrap
       title) — hdr-main is the big greeting, hdr-sub the small day/church
       line above it, per docs/FIX-HEADERS.md. */
    .hdr-main {
      font-size: 30px; font-weight: 800; color: var(--ink); line-height: 1.05;
      font-family: 'Plus Jakarta Sans', sans-serif; letter-spacing: -0.02em;
    }
    .hdr-sub {
      font-size: 13px; font-weight: 600; line-height: 1.2;
      color: var(--stone);
      font-family: 'Plus Jakarta Sans', sans-serif;
    }
    .nlc-logo-img {
      height: 36px;
      width: auto;
      object-fit: contain;
      object-position: left center;
      max-width: 210px;
    }
    .avatar {
      width: 34px; height: 34px; border-radius: 50%;
      background: var(--white);
      border: 1.5px solid var(--border-strong);
      display: flex; align-items: center; justify-content: center;
      font-size: 12px; font-weight: 700; color: var(--ink);
      cursor: pointer; letter-spacing: 0.05em;
    }

    /* ══ PASSAGE BAR ══ */
    .pbar { flex-shrink: 0; padding: 10px 16px 6px; display: flex; gap: 8px; align-items: center; }
    .ppill {
      flex: 1; background: var(--white); border: 1px solid var(--border-strong);
      border-radius: 12px; padding: 10px 16px;
      display: flex; align-items: center; justify-content: space-between;
      cursor: pointer; transition: border-color 0.15s;
    }
    .ppill:hover { border-color: var(--stone-dim); }
    .ppill-txt { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 20px; font-weight: 700; color: var(--ink); letter-spacing: 0.03em; }
    .vbadge {
      font-size: 10px; font-weight: 700; letter-spacing: 0.1em;
      color: var(--gold-ink); background: var(--gold);
      padding: 3px 9px; border-radius: 6px; border: 1px solid rgba(232,200,122,0.25);
    }
    .carrows { display: flex; gap: 4px; }
    .carrow {
      width: 38px; height: 38px; border-radius: 10px;
      background: var(--white); border: 1px solid var(--border);
      color: var(--stone); font-size: 17px; cursor: pointer; transition: all 0.15s;
      display: flex; align-items: center; justify-content: center;
    }
    .carrow:hover { border-color: var(--border-strong); color: var(--ink); }
    .carrow:disabled { opacity: 0.25; pointer-events: none; }

    .vstrip { flex-shrink: 0; padding: 0 16px 10px; display: flex; gap: 6px; }
    .vbtn {
      flex: 1; padding: 7px 4px; border-radius: 8px;
      background: var(--white); border: 1px solid var(--border);
      font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
      color: var(--stone-dim); cursor: pointer; transition: all 0.15s;
      font-family: 'Plus Jakarta Sans', sans-serif;
    }
    .vbtn.on { background: var(--white); color: var(--ink); border-color: var(--white); }

    /* ══ SCRIPTURE ══ */
    .scripture { flex: 1; overflow-y: auto; padding: 4px 22px 20px; }
    .scripture::-webkit-scrollbar { width: 2px; }
    .scripture::-webkit-scrollbar-thumb { background: var(--white); border-radius: 2px; }

    .vrow {
      display: flex; gap: 10px; padding: 8px 10px; border-radius: 10px;
      margin-bottom: 2px; cursor: pointer; transition: background 0.12s;
    }
    .vrow:hover   { background: var(--border); }
    /* Marker-style highlight that wraps line-by-line with the text. The inner
       span is inline, so each visual line gets its own colored strip. */
    .vtxt-inner {
      display: inline;
      -webkit-box-decoration-break: clone;
      box-decoration-break: clone;
      padding: 0 2px;
      border-radius: 3px;
      transition: background 0.12s;
    }
    .vrow.hl-yellow .vtxt-inner { background: var(--highlight); color: var(--ink); }
    .vrow.hl-blue   .vtxt-inner { background: var(--sky); color: var(--ink); }
    .vrow.hl-green  .vtxt-inner { background: var(--sage); color: var(--ink); }
    .vrow.hl-pink   .vtxt-inner { background: var(--blush); color: var(--ink); }
    .vrow.selected  {
      background: rgba(232,200,122,0.18);
      box-shadow: inset 3px 0 0 0 var(--gold);
    }
    .vrow.selected.hl-yellow,
    .vrow.selected.hl-blue,
    .vrow.selected.hl-green,
    .vrow.selected.hl-pink { box-shadow: inset 3px 0 0 0 var(--gold); }

    /* Verse indicators - up to 4 small markers stacked vertically per verse */
    .vind-row { display: flex; gap: 4px; align-items: center; padding-top: 9px; flex-shrink: 0; }
    .vind { font-size: 11px; line-height: 1; transition: transform 0.12s; cursor: pointer; }
    .vind:hover { transform: scale(1.25); }
    .vind-dot {
      display: inline-block; width: 8px; height: 8px; border-radius: 50%;
      cursor: pointer; transition: transform 0.12s; position: relative;
    }
    .vind-dot:hover { transform: scale(1.4); }
    .vind-dot.sermon  { background: #7C6BC4; }
    /* Widen the sermon dot's actual tap target to 32px (docs/FIX-SERMON-CLIPS.md
       §1b) without changing its visible 8px size — an invisible centred box. */
    .vind-dot.sermon::before {
      content: ''; position: absolute; top: 50%; left: 50%;
      width: 32px; height: 32px; transform: translate(-50%, -50%);
    }
    /* Only a followed church (not home) has a ref at this verse — hollow
       instead of filled, per docs/designs/BibleDots.dc.html. */
    .vind-dot.sermon.followed { background: transparent; border: 2px solid #7C6BC4; box-sizing: border-box; }
    .vind-dot.comm    { background: #4A90E2; }
    .vind-dot.sermon.hint {
      animation: sermon-hint-pulse 1.15s ease-out infinite;
      transform-origin: center;
    }
    @keyframes sermon-hint-pulse {
      0%   { transform: scale(1);    box-shadow: 0 0 0 0 rgba(220,38,38,0.75); }
      70%  { transform: scale(1.4);  box-shadow: 0 0 0 12px rgba(220,38,38,0); }
      100% { transform: scale(1);    box-shadow: 0 0 0 0 rgba(220,38,38,0); }
    }
    .vind-heart {
      font-size: 12px; line-height: 1; color: #FF6B9D; cursor: pointer;
      transition: transform 0.12s;
    }
    .vind-heart:hover { transform: scale(1.3); }
    .vind-q {
      font-size: 12px; line-height: 1; color: #9B6FD4; font-weight: 800;
      cursor: pointer; transition: transform 0.12s;
    }
    .vind-q:hover { transform: scale(1.3); }

    /* Compact verse-indicator legend on the Read tab. The default state is a
       single-line pill listing the indicators present in the current chapter.
       Tapping it opens an absolute-positioned overlay panel with the full
       descriptions; the panel sits over the bible content (it does not push
       it down). */
    .legend-wrap {
      position: relative;
      margin: 6px 14px 2px;
      flex-shrink: 0;
    }
    /* Minimal: no pill, no border — just italic muted text + an ⓘ at the end.
       Tapping anywhere on the row (or the icon) opens the descriptions overlay. */
    .legend-row {
      width: 100%;
      display: flex; align-items: center; gap: 8px;
      padding: 4px 14px 4px 4px;
      background: transparent;
      border: none;
      color: var(--stone-dim);
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 11px; font-weight: 400; font-style: italic;
      cursor: pointer; text-align: left;
    }
    .legend-row:hover { color: var(--stone); }
    /* Single inline text run so the whole list of indicators can ellipsize
       at the end as a unit. Icons are inline-block so they sit in the
       text flow; ellipsis applies once at the right edge. */
    .legend-row-items {
      flex: 1; min-width: 0;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .legend-row-icon {
      display: inline-block; vertical-align: middle;
      margin-right: 5px;
      line-height: 0;
    }
    .legend-row-gap {
      display: inline-block; width: 10px;
    }
    .legend-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; }

    .legend-overlay {
      position: absolute;
      top: calc(100% + 6px); left: 0; right: 0;
      z-index: 6;
      background: var(--white);
      border: 1px solid var(--border-strong);
      border-radius: 12px;
      padding: 12px 14px 14px;
      box-shadow: 0 12px 30px rgba(0,0,0,0.25);
      animation: legend-fadein 0.18s ease-out;
    }
    @keyframes legend-fadein {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .legend-overlay-item {
      display: flex; align-items: flex-start; gap: 10px;
    }
    .legend-overlay-item + .legend-overlay-item { margin-top: 12px; }
    .legend-overlay-icon {
      flex-shrink: 0;
      width: 16px; height: 16px;
      display: flex; align-items: center; justify-content: center;
      margin-top: 2px;
    }
    .legend-overlay-name {
      font-size: 13px; font-weight: 700; color: var(--ink);
      letter-spacing: 0.01em;
    }
    .legend-overlay-desc {
      font-size: 11px; color: var(--stone-dim); line-height: 1.45;
      margin-top: 2px;
    }

    /* Legacy single-dot legend (kept for compatibility) */
    .sermon-legend {
      display: flex; align-items: center; gap: 10px;
      padding: 6px 14px 4px;
      font-size: 11px; color: var(--stone);
      font-style: italic;
      flex-shrink: 0;
    }
    .sermon-legend .bsdot { margin: 0; }
    .sermon-legend-x {
      margin-left: auto; background: none; border: none;
      color: var(--stone-dim); font-size: 14px; line-height: 1;
      cursor: pointer; padding: 4px 6px; font-family: 'Plus Jakarta Sans', sans-serif;
    }
    .sermon-legend-x:hover { color: var(--ink); }

    /* Verse-selection sheet (docs/designs/SelBarB2.dc.html, §1b) — a short
       white sheet above the tab bar, replacing the old pill-shaped bar. */
    .sel-sheet {
      position: fixed; left: 50%; transform: translateX(-50%);
      width: min(100%, 480px);
      bottom: calc(env(safe-area-inset-bottom) + 62px);
      background: var(--white);
      border-radius: 24px 24px 0 0;
      display: flex; flex-direction: column; gap: 12px;
      padding: 10px 18px 14px; z-index: 150;
      box-shadow: 0 -8px 24px rgba(31,27,22,0.14);
      animation: sel-up 0.18s cubic-bezier(0.32,0.72,0,1);
      box-sizing: border-box;
    }
    @keyframes sel-up { from { transform: translate(-50%, 100%); } to { transform: translate(-50%, 0); } }
    /* Walkthrough demo sermon sheet — fake, slides up on step 4. */
    .walkthrough-sheet {
      position: fixed; bottom: 0; left: 50%;
      transform: translateX(-50%);
      width: 100%; max-width: 480px;
      background: var(--cream);
      border-top: 1px solid var(--border-strong);
      border-radius: 24px 24px 0 0;
      padding: 20px 22px 26px;
      z-index: 100001;
      box-sizing: border-box;
      animation: walkthrough-slide 0.32s cubic-bezier(0.2, 0.7, 0.3, 1);
    }
    @keyframes walkthrough-slide {
      from { transform: translate(-50%, 100%); }
      to   { transform: translateX(-50%); }
    }
    @keyframes walkthrough-pulse {
      0%, 100% { transform: scale(1);   filter: drop-shadow(0 0 0 transparent); }
      50%      { transform: scale(1.45); filter: drop-shadow(0 0 6px rgba(255,255,255,0.35)); }
    }
    .walkthrough-pulse {
      animation: walkthrough-pulse 1.3s ease-in-out infinite;
      transform-origin: center center;
    }

    /* Per-verse indicators - now clickable */
    .vind { cursor: pointer; }
    .vind:hover { transform: scale(1.25); }

    .vnum { font-size: 10px; font-weight: 600; color: var(--stone-dim); min-width: 18px; padding-top: 5px; text-align: right; flex-shrink: 0; }
    .vbody { flex: 1; display: flex; align-items: flex-start; gap: 8px; }
    .vtxt { font-family: var(--scripture-font); font-size: var(--scripture-size); font-weight: 400; line-height: var(--scripture-lh); color: var(--ink); flex: 1; }

    .sdot-wrap { padding-top: 9px; flex-shrink: 0; }
    .sdot {
      width: 9px; height: 9px; border-radius: 50%;
      background: var(--gold); cursor: pointer;
      box-shadow: 0 0 0 3px var(--gold);
      transition: transform 0.15s;
      animation: sglow 3s ease-in-out infinite;
    }
    .sdot:hover { transform: scale(1.7); }
    .sdot.multi { background: #FF8A80; box-shadow: 0 0 0 3px rgba(255,138,128,0.22); }
    @keyframes sglow {
      0%,100% { box-shadow: 0 0 0 3px var(--gold); }
      50%      { box-shadow: 0 0 10px var(--gold), 0 0 0 5px var(--gold); }
    }

    /* Pulsing loading caption + animated trailing ellipsis. */
    @keyframes loading-pulse { 0%,100% { opacity:0.45; } 50% { opacity:1; } }
    .loading-anim { animation: loading-pulse 1.6s ease-in-out infinite; }
    .loading-anim::after {
      content: ""; display: inline-block; width: 1.2em; text-align: left;
      animation: loading-ellipsis 1.6s steps(1,end) infinite;
    }
    @keyframes loading-ellipsis {
      0% { content:""; } 25% { content:"."; } 50% { content:".."; } 75%,100% { content:"..."; }
    }

    /* ══ BOTTOM NAV ══ */
    .bnav {
      flex-shrink: 0; background: var(--cream);
      border-top: 1px solid var(--border);
      display: flex;
      padding: 6px 0 max(10px, env(safe-area-inset-bottom));
    }
    .btab { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 5px 0; cursor: pointer; color: var(--stone); transition: color 0.15s; }
    .bico { width: 52px; height: 30px; border-radius: 15px; position: relative; display: flex; align-items: center; justify-content: center; color: inherit; transition: background-color 0.15s; }
    .blbl { font-size: 11.5px; font-weight: 600; color: inherit; transition: color 0.15s, font-weight 0.15s; letter-spacing: 0.02em; }
    .btab.on            { color: var(--ink); }
    .btab.on .bico      { background: var(--gold); }
    .btab.on .blbl      { font-weight: 700; }
    .btab.locked        { opacity: 0.4; }
    .btab.locked .bico  { opacity: 1; }
    .btab.locked .blbl  { opacity: 1; }

    .lock-badge {
      position: absolute; right: -10px; top: -6px;
      font-size: 10px; background: var(--white); border: 1px solid var(--border);
      border-radius: 999px; padding: 0 4px; line-height: 16px;
    }

    /* ══ FLOAT TOOLBAR ══ */
    .ftbar {
      position: fixed; z-index: 300;
      background: var(--white); border: 1px solid var(--border-strong);
      border-radius: 16px; padding: 10px 14px;
      display: flex; align-items: center; gap: 10px;
      box-shadow: 0 12px 40px rgba(0,0,0,0.2);
      animation: popin 0.2s cubic-bezier(0.34,1.56,0.64,1);
    }
    @keyframes popin { from { opacity:0; transform:scale(0.8) translateY(6px); } to { opacity:1; transform:none; } }
    .tdiv { width: 1px; height: 22px; background: var(--border-strong); }
    .tbtn {
      width: 34px; height: 34px; border-radius: 8px;
      background: var(--white); border: 1px solid var(--border);
      display: flex; align-items: center; justify-content: center;
      font-size: 15px; cursor: pointer; transition: all 0.15s; color: var(--stone);
    }
    .tbtn:hover { background: var(--gold); border-color: rgba(232,200,122,0.3); }

    /* ══ OVERLAY + SHEET ══ */
    .overlay { position: fixed; inset: 0; z-index: 200; background: rgba(10,16,32,0.80); backdrop-filter: blur(6px); animation: fdo 0.2s ease; }
    @keyframes fdo { from { opacity:0; } to { opacity:1; } }

    .sheet {
      position: fixed; bottom: 0; left: 50%; z-index: 201;
      transform: translateX(-50%);
      width: 100%; max-width: 480px;
      background: var(--cream);
      border-radius: 24px 24px 0 0;
      border-top: 1px solid var(--border-strong);
      max-height: 88vh; display: flex; flex-direction: column;
      animation: shu 0.3s cubic-bezier(0.32,0.72,0,1);
      padding-bottom: max(18px, env(safe-area-inset-bottom));
    }
    @keyframes shu { from { transform:translateX(-50%) translateY(100%); } to { transform:translateX(-50%) translateY(0); } }
    .shandle { width: 40px; height: 4px; border-radius: 2px; background: var(--white); margin: 12px auto 0; flex-shrink: 0; }
    .shd { padding: 14px 20px 10px; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; border-bottom: 1px solid var(--border); }
    .shtitle { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 19px; font-weight: 700; color: var(--ink); letter-spacing: 0.03em; }
    .shx { width: 30px; height: 30px; border-radius: 50%; background: var(--white); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; font-size: 14px; color: var(--stone); cursor: pointer; }
    .shtabs { display: flex; padding: 0 20px; flex-shrink: 0; border-bottom: 1px solid var(--border); }
    .shtab { flex: 1; padding: 11px 0; text-align: center; font-size: 12px; font-weight: 700; color: var(--stone-dim); border-bottom: 2px solid transparent; cursor: pointer; transition: all 0.15s; letter-spacing: 0.06em; text-transform: uppercase; }
    .shtab.on { color: var(--ink); border-bottom-color: var(--ink); }
    .shbody { flex: 1; overflow-y: auto; padding: 20px; }
    .shbody::-webkit-scrollbar { width: 2px; }
    .shbody::-webkit-scrollbar-thumb { background: var(--white); }

    /* ══ SERMON CARD - highly readable ══ */
    .scard {
      background: var(--white);
      border: 1px solid var(--border-strong);
      border-radius: 16px; margin-bottom: 16px;
    }
    .scard-hd {
      padding: 16px 18px 10px;
      background: var(--white);
      border-bottom: 1px solid var(--border);
    }
    .scard-series {
      font-size: 10px; font-weight: 700; letter-spacing: 0.2em;
      text-transform: uppercase; color: var(--gold-ink); margin-bottom: 6px;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .scard-title {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 16px; font-weight: 700; color: var(--ink);
      line-height: 1.25; margin-bottom: 4px; letter-spacing: 0.01em;
    }
    .scard-pastor { font-size: 13px; font-weight: 600; color: var(--ink); margin-bottom: 2px; }
    .scard-date   { font-size: 12px; color: var(--stone-dim); }

    .scard-bd { padding: 7px 18px 20px; }

    .scard-refs { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
    .ref-tag {
      background: rgba(232,200,122,0.12);
      border: 1px solid rgba(232,200,122,0.25);
      border-radius: 6px; padding: 4px 10px;
      font-size: 11px; font-weight: 700; color: var(--gold-ink); letter-spacing: 0.04em;
    }

    /* ── NOTES - full flowing text, easy to read ── */
    .notes-label {
      font-size: 10px; font-weight: 600; letter-spacing: 0.14em;
      text-transform: uppercase; color: var(--stone-dim); margin-bottom: 10px;
    }
    .scard-notes {
      font-family: Georgia, 'Times New Roman', serif;
      font-size: 17px; line-height: 1.8;
      color: var(--ink); margin-bottom: 14px;
    }
    .scard-notes em { color: var(--gold-ink); font-style: italic; }

    /* ── YOUTUBE BUTTON - prominent ── */
    .yt-section { margin-bottom: 4px; }
    .yt-label {
      font-size: 10px; font-weight: 700; letter-spacing: 0.18em;
      text-transform: uppercase; color: var(--stone-dim); margin-bottom: 8px;
    }
    .yt-btn {
      display: flex; align-items: center; justify-content: center; gap: 10px;
      background: #FF0000;
      color: #fff; border: none; border-radius: 12px; padding: 15px 18px;
      font-size: 15px; font-weight: 700; width: 100%; cursor: pointer;
      box-shadow: 0 6px 20px rgba(255,0,0,0.35); transition: all 0.15s;
      font-family: 'Plus Jakarta Sans', sans-serif; letter-spacing: 0.02em;
    }
    .yt-btn:hover { background: #CC0000; transform: translateY(-1px); }
    .yt-btn:active { transform: translateY(0); }
    .yt-play { font-size: 18px; }
    .yt-text { display: flex; flex-direction: column; align-items: flex-start; }
    .yt-main { font-size: 14px; font-weight: 700; }
    .yt-sub  { font-size: 11px; font-weight: 400; opacity: 0.85; margin-top: 1px; }

    /* ══ AI CHAT ══ */
    .ai-ctx {
      background: var(--white); border-radius: 12px;
      padding: 13px 15px; margin-bottom: 16px;
      border-left: 3px solid var(--gold);
    }
    .ai-ctx-lbl { font-size: 10px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: var(--gold-ink); margin-bottom: 6px; }
    .ai-ctx-v { font-family: Georgia, 'Times New Roman', serif; font-style: italic; font-size: 18px; line-height: 1.55; color: var(--ink); }
    .chatlist { flex: 1; overflow-y: auto; margin-bottom: 12px; }
    .chatlist::-webkit-scrollbar { width: 0; }
    .cmsg { margin-bottom: 14px; animation: cmin 0.2s ease; }
    @keyframes cmin { from { opacity:0; transform:translateY(4px); } to { opacity:1; } }
    .cmsg.me { display: flex; justify-content: flex-end; }
    .cbub { padding: 11px 15px; border-radius: 16px; max-width: 90%; font-size: 15px; line-height: 1.55; }
    .cmsg.me .cbub { background: var(--white); color: var(--ink); font-weight: 500; border-bottom-right-radius: 4px; }
    .cmsg.ai .cbub { background: var(--white); color: var(--ink); border: 1px solid var(--border-strong); border-bottom-left-radius: 4px; font-family: Georgia, 'Times New Roman', serif; font-size: 18px; line-height: 1.65; }
    .ai-sender { font-size: 10px; font-weight: 700; color: var(--gold-ink); margin-bottom: 5px; letter-spacing: 0.1em; text-transform: uppercase; }
    .chat-row { flex-shrink: 0; border-top: 1px solid var(--border); padding-top: 12px; display: flex; gap: 8px; align-items: flex-end; }
    .chat-ta { flex: 1; background: var(--white); border: 1px solid var(--border-strong); border-radius: 12px; padding: 11px 14px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 15px; color: var(--ink); resize: none; outline: none; line-height: 1.4; max-height: 96px; }
    .chat-ta::placeholder { color: var(--stone-dim); }
    .chat-ta:focus { border-color: var(--border-strong); }
    .send-btn { width: 42px; height: 42px; border-radius: 10px; background: var(--white); border: 1px solid var(--border-strong); cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 18px; color: var(--ink); flex-shrink: 0; transition: all 0.15s; }
    .send-btn:hover { background: var(--white); color: var(--ink); }
    .thinking::after { content:''; animation: td 1.2s infinite; }
    @keyframes td { 0%,20%{content:'.'} 40%,60%{content:'..'} 80%,100%{content:'...'} }

    /* ══ HOME ══ */
    .hscroll { flex: 1; overflow-y: auto; padding: 0 16px 16px; }
    .hscroll::-webkit-scrollbar { width: 2px; }
    .sec-lbl { font-size: 10px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; color: var(--stone-dim); margin: 20px 0 10px; }

    .vision-card {
      background: var(--white);
      border: 1px solid var(--border-strong);
      border-radius: 16px; padding: 20px 18px;
      margin-bottom: 6px; position: relative; overflow: hidden;
      display: flex; gap: 14px; align-items: flex-start;
    }
    .vision-door { flex-shrink: 0; opacity: 0.9; }
    .vision-body {}
    .vision-tag { font-size: 9px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; color: var(--gold-ink); margin-bottom: 6px; }
    .vision-txt { font-family: Georgia, 'Times New Roman', serif; font-size: 19px; font-style: italic; line-height: 1.5; color: var(--ink); }

    .daily-card {
      background: var(--white); border: 1px solid var(--border-strong);
      border-radius: 16px; padding: 20px 18px; margin-bottom: 6px;
    }
    .daily-ref { font-size: 10px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: var(--gold-ink); margin-bottom: 8px; }
    .daily-v { font-family: Georgia, 'Times New Roman', serif; font-size: 22px; font-style: italic; line-height: 1.5; color: var(--ink); margin-bottom: 14px; }
    .daily-cta {
      display: inline-flex; align-items: center; gap: 6px;
      background: var(--white); color: var(--ink); border: none;
      border-radius: 8px; padding: 9px 16px; font-size: 13px; font-weight: 700;
      cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; letter-spacing: 0.02em;
    }

    .srow { display: flex; align-items: center; gap: 12px; background: var(--white); border: 1px solid var(--border); border-radius: 12px; padding: 13px 14px; margin-bottom: 8px; cursor: pointer; transition: border-color 0.15s; }
    .srow:hover { border-color: var(--border-strong); }
    .srow-ico { width: 44px; height: 44px; border-radius: 10px; background: var(--white); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; }
    .srow-title { font-size: 14px; font-weight: 600; color: var(--ink); margin-bottom: 2px; }
    .srow-meta  { font-size: 11px; color: var(--stone-dim); }
    .srow-arr   { margin-left: auto; color: var(--stone-dim); font-size: 20px; }

    /* ══ BOOK PICKER ══ */
    .bgrid { display: grid; grid-template-columns: repeat(3,1fr); gap: 7px; padding-bottom: 16px; }
    /* Chapter step of the book/chapter picker — denser than .bgrid since
       cells are just a number (Psalms has 150 of them). */
    .chgrid { display: grid; grid-template-columns: repeat(6,1fr); gap: 7px; padding-bottom: 16px; }
    .bcard { background: var(--white); border: 1px solid var(--border); border-radius: 10px; padding: 12px 8px; text-align: center; cursor: pointer; transition: all 0.15s; }
    .bcard:hover, .bcard.on { border-color: var(--border-strong); background: var(--white); }
    .bcard-n { font-size: 12px; font-weight: 600; color: var(--ink); margin-bottom: 2px; }
    .bcard-c { font-size: 10px; color: var(--stone-dim); }
    .bsdot { width: 5px; height: 5px; border-radius: 50%; background: var(--gold); display: inline-block; margin-left: 4px; vertical-align: middle; }
    .divider-lbl { font-size: 10px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; color: var(--stone-dim); margin: 14px 0 10px; }

    /* ══ HIGHLIGHTS ══ */
    .hlscroll { flex: 1; overflow-y: auto; padding: 0 16px 16px; }
    .hlscroll::-webkit-scrollbar { width: 2px; }
    .hlitem { display: flex; gap: 12px; padding: 14px; border-radius: 12px; margin-bottom: 8px; }
    .hlitem.yellow { background: var(--highlight); }
    .hlitem.blue   { background: var(--sky); }
    .hlitem.green  { background: var(--sage); }
    .hldot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; margin-top: 7px; }
    .hlref { font-size: 10px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 5px; }
    .hltxt { font-family: var(--scripture-font); font-size: var(--scripture-size); line-height: var(--scripture-lh); color: var(--ink); }

    /* ══ SERMONS SCREEN ══ */
    .slist { flex: 1; overflow-y: auto; padding: 0 16px 40px; }
    .slist::-webkit-scrollbar { width: 2px; }
    .slist::-webkit-scrollbar-thumb { background: var(--white); border-radius: 2px; }

    .empty { text-align: center; padding: 50px 20px; color: var(--stone-dim); }
    .eico { font-size: 44px; margin-bottom: 14px; }
    .etit { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px; font-weight: 700; color: var(--stone); margin-bottom: 6px; }
    .esub { font-size: 13px; line-height: 1.6; }

    /* ══ SAVED VERSES ══ */
    .saved-scroll { flex: 1; overflow-y: auto; padding: 0 16px 40px; }
    .saved-scroll::-webkit-scrollbar { width: 2px; }
    .saved-filter-row { display: flex; gap: 6px; padding: 6px 16px 0; overflow-x: auto; flex-shrink: 0; }
    .saved-filter-row::-webkit-scrollbar { display: none; }
    .saved-chip {
      flex: 0 0 auto; background: var(--white); border: 1px solid var(--border);
      color: var(--stone); border-radius: 999px; padding: 6px 12px;
      font-size: 12px; font-weight: 700; cursor: pointer;
      font-family: 'Plus Jakarta Sans', sans-serif; white-space: nowrap;
      letter-spacing: 0.02em;
    }
    .saved-chip.on {
      background: rgba(232,200,122,0.18); border-color: rgba(232,200,122,0.5);
      color: var(--gold-ink);
    }
    .saved-card {
      background: var(--white); border: 1px solid var(--border-strong);
      border-radius: 14px; padding: 16px; margin-bottom: 12px;
      position: relative;
    }
    .saved-ref {
      font-size: 10px; font-weight: 700; letter-spacing: 0.2em;
      text-transform: uppercase; color: var(--gold-ink); margin-bottom: 8px;
      display: flex; align-items: center; justify-content: space-between;
    }
    .saved-ref-actions { display: flex; gap: 8px; align-items: center; }
    .saved-date { font-size: 10px; color: var(--stone-dim); font-weight: 400; letter-spacing: 0.05em; text-transform: none; }
    .saved-verse-txt {
      font-family: Georgia, 'Times New Roman', serif;
      font-size: 20px; line-height: 1.7; color: var(--ink);
      margin-bottom: 12px;
      padding-bottom: 12px;
      border-bottom: 1px solid var(--border);
    }
    .saved-note-label {
      font-size: 10px; font-weight: 700; letter-spacing: 0.15em;
      text-transform: uppercase; color: var(--stone-dim); margin-bottom: 6px;
    }
    .saved-note-input {
      width: 100%; background: var(--white); border: 1px solid var(--border);
      border-radius: 8px; padding: 10px 12px;
      font-family: Georgia, 'Times New Roman', serif; font-size: 17px; color: var(--ink);
      resize: none; outline: none; line-height: 1.5; min-height: 60px;
    }
    .saved-note-input::placeholder { color: var(--stone-dim); font-style: italic; }
    .saved-note-input:focus { border-color: var(--border-strong); }
    .saved-note-static {
      font-family: Georgia, 'Times New Roman', serif; font-size: 17px; font-style: italic;
      color: var(--stone); line-height: 1.55;
    }
    .unsave-btn {
      background: none; border: none; cursor: pointer;
      color: var(--stone-dim); font-size: 16px; padding: 2px;
      transition: color 0.15s; line-height: 1;
    }
    .unsave-btn:hover { color: #C8372A; }
    .goto-btn {
      background: var(--white); border: 1px solid var(--border);
      border-radius: 6px; padding: 4px 10px; font-size: 11px; font-weight: 600;
      color: var(--stone); cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif;
      letter-spacing: 0.04em; transition: all 0.15s;
    }
    .goto-btn:hover { border-color: var(--border-strong); color: var(--ink); }
    .saved-count-badge {
      position: absolute; top: -6px; right: -8px;
      background: var(--gold); color: var(--ink); border-radius: 8px;
      font-size: 9px; font-weight: 800; padding: 1px 5px;
      line-height: 14px; min-width: 14px; text-align: center;
    }
    /* ══ VERSE ACTION SHEET ══ */
    .verse-action-row {
      display: flex; align-items: center; gap: 14px;
      padding: 14px 16px; border-radius: 12px; cursor: pointer;
      transition: opacity 0.15s;
    }
    .verse-action-row:hover { opacity: 0.85; }
    .verse-action-row:active { opacity: 0.7; }

    /* ══ PROFILE SHEET ══ */
    .profile-avatar-lg { width:56px; height:56px; border-radius:50%; background:rgba(232,200,122,0.14); border:2px solid var(--gold); display:flex; align-items:center; justify-content:center; font-size:20px; font-weight:800; color:var(--gold-ink); flex-shrink:0; }
    .profile-role-badge { display:inline-block; background:rgba(232,200,122,0.12); border:1px solid rgba(232,200,122,0.3); border-radius:6px; padding:2px 8px; font-size:11px; font-weight:700; color:var(--gold-ink); text-transform:uppercase; letter-spacing:0.1em; margin-top:4px; }
    .signout-btn { width:100%; background:var(--white); border:1px solid var(--border); border-radius:10px; padding:12px; font-size:14px; font-weight:600; color:#F97066; cursor:pointer; font-family:'Plus Jakarta Sans',sans-serif; margin-top:12px; }
    .profile-section { padding:16px; border-top:1px solid var(--border); }
    .profile-section-title { font-size:10px; font-weight:700; letter-spacing:0.15em; text-transform:uppercase; color:var(--stone-dim); margin-bottom:12px; }
    .church-search-input { width:100%; background:var(--white); border:1px solid var(--border); border-radius:10px; padding:10px 12px; color:var(--ink); font-size:14px; font-family:'Plus Jakarta Sans',sans-serif; outline:none; box-sizing:border-box; margin-bottom:8px; }
    .church-search-input:focus { border-color:rgba(232,200,122,0.4); }
    .church-option { padding:10px 12px; border-radius:8px; cursor:pointer; font-size:14px; color:var(--ink); }
    .church-option.selected { background:rgba(232,200,122,0.1); color:var(--gold-ink); }
    .invite-role-row { display:flex; gap:8px; margin-bottom:12px; }
    .invite-role-btn { flex:1; background:var(--white); border:1px solid var(--border); border-radius:8px; padding:9px; font-size:13px; font-weight:600; color:var(--stone-dim); cursor:pointer; text-align:center; }
    .invite-role-btn.on { background:rgba(232,200,122,0.12); border-color:rgba(232,200,122,0.4); color:var(--gold-ink); }
    .invite-input { width:100%; background:var(--white); border:1px solid var(--border); border-radius:10px; padding:11px 14px; color:var(--ink); font-size:14px; font-family:'Plus Jakarta Sans',sans-serif; outline:none; box-sizing:border-box; margin-bottom:10px; }
    .invite-input:focus { border-color:rgba(232,200,122,0.4); }
    .invite-send-btn { width:100%; background:var(--gold); color:var(--ink); border:none; border-radius:10px; padding:12px; font-size:14px; font-weight:800; cursor:pointer; font-family:'Plus Jakarta Sans',sans-serif; }
    .invite-send-btn:disabled { opacity:0.4; cursor:default; }
    .invite-success { background:rgba(80,200,150,0.1); border:1px solid rgba(80,200,150,0.25); border-radius:10px; padding:12px 14px; font-size:13px; color:#1F7A52; margin-top:10px; line-height:1.5; }

    /* ══ SERMON LIST ROWS ══ */
    .sermon-row {
      display: flex; align-items: center; gap: 12px;
      padding: 12px 16px; border-bottom: 1px solid var(--border);
      cursor: pointer; transition: background 0.15s;
    }
    .sermon-row:active { background: var(--white); }
    .sermon-row-thumb {
      width: 80px; height: 52px; border-radius: 8px;
      overflow: hidden; flex-shrink: 0; background: var(--white);
    }
    .sermon-row-info { flex: 1; min-width: 0; }
    .sermon-row-date {
      font-size: 10px; font-weight: 700; letter-spacing: 0.12em;
      text-transform: uppercase; color: var(--gold-ink); margin-bottom: 3px;
    }
    .sermon-row-title {
      font-size: 15px; font-weight: 700; color: var(--ink);
      margin-bottom: 5px; overflow-wrap: anywhere;
    }
    .sermon-row-refs { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; }

    /* ══ SERMON DETAIL SHEET ══ */
    .sermon-detail { display: flex; flex-direction: column; max-height: 92vh; }
    .sermon-detail-scroll { flex: 1; overflow-y: auto; padding: 0 16px 30px; }
    .sermon-detail-scroll::-webkit-scrollbar { width: 2px; }
    .sermon-detail-thumb {
      width: 100%; aspect-ratio: 16/9; object-fit: cover;
      border-radius: 8px; margin-bottom: 14px; background: var(--white);
    }
    .sermon-detail-series {
      font-size: 10px; font-weight: 700; letter-spacing: 0.12em;
      text-transform: uppercase; margin-bottom: 6px;
    }
    .sermon-detail-series-label { color: var(--stone-dim); }
    .sermon-detail-series-value { color: var(--gold-ink); }
    .sermon-detail-title {
      font-size: 22px; font-weight: 700; color: var(--ink);
      margin-bottom: 8px; line-height: 1.2;
    }
    .sermon-detail-meta {
      font-size: 13px; color: var(--stone); margin-bottom: 16px;
    }
    .sermon-detail-refs { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
    .sermon-detail-notes {
      font-size: 15px; color: var(--stone); line-height: 1.7;
      margin-bottom: 10px;
    }
    .sermon-ref-section { margin-bottom: 20px; }
    .sermon-ref-label {
      font-size: 10px; font-weight: 700; letter-spacing: 0.12em;
      text-transform: uppercase; color: var(--stone-dim); margin-bottom: 8px;
    }
    /* Pinterest-style flush sub-tabs - text only, coloured underline on
       active tab, no background panel, no flex stretch. */
    .comm-tabs {
      display: flex; padding: 0 16px; gap: 22px; flex-shrink: 0;
      background: none; border-bottom: 1px solid var(--border);
      overflow-x: auto;
    }
    .comm-tabs::-webkit-scrollbar { display: none; }
    .comm-tab {
      padding: 12px 0 10px;
      font-size: 14px; font-weight: 600; letter-spacing: 0;
      color: var(--stone-dim); cursor: pointer;
      border-bottom: 2px solid transparent;
      margin-bottom: -1px;       /* sits flush over the row's bottom border */
      transition: color 0.15s, border-bottom-color 0.15s;
      white-space: nowrap;
      font-family: 'Plus Jakarta Sans', sans-serif;
    }
    .comm-tab.on {
      color: var(--ink);
      font-weight: 700;
      border-bottom-color: var(--gold);
    }

    /* ══ PRAYER CARDS ══ */
    .prayer-card {
      background: var(--white); border: 1px solid var(--border);
      border-radius: 14px; padding: 14px; margin-bottom: 10px;
    }
    .prayer-header { display:flex; justify-content:space-between; margin-bottom:8px; }
    .prayer-author { font-size:12px; font-weight:700; color:var(--gold-ink); }
    .prayer-date   { font-size:11px; color:var(--stone-dim); }
    .prayer-text   { font-size:15px; color:var(--ink); line-height:1.6; margin-bottom:12px; }
    .prayer-actions { display:flex; gap:8px; align-items:center; }
    .pray-btn {
      display:flex; align-items:center; gap:5px;
      background:var(--white); border:1px solid var(--border);
      border-radius:20px; padding:5px 12px; cursor:pointer;
      font-size:13px; font-weight:600; color:var(--stone); transition:all 0.15s;
    }
    .pray-btn.prayed { background:rgba(130,180,255,0.12); border-color:rgba(130,180,255,0.35); color:#82B4FF; }
    .pray-btn:active { transform:scale(0.95); }
    .prayer-expand-btn {
      margin-left:auto; background:none; border:none; color:var(--stone-dim);
      font-size:12px; cursor:pointer; padding:4px 8px;
    }
    .prayer-comments { margin-top:10px; border-top:1px solid var(--border); padding-top:10px; }
    .prayer-comment-input-row { display:flex; gap:6px; margin-top:8px; }
    .prayer-comment-input {
      flex:1; background:var(--white); border:1px solid var(--border);
      border-radius:8px; padding:8px 12px; color:var(--ink);
      font-size:13px; font-family:'Plus Jakarta Sans',sans-serif; outline:none; resize:none;
    }
    .prayer-comment-input:focus { border-color:rgba(232,200,122,0.4); }

    /* ══ ENCOURAGEMENT CARDS ══ */
    .enc-card {
      background: var(--white); border: 1px solid var(--border);
      border-radius: 14px; padding: 14px; margin-bottom: 10px;
    }
    .enc-link {
      display:flex; align-items:center; gap:10px;
      background:var(--white); border:1px solid var(--border);
      border-radius:10px; padding:10px 12px; margin:10px 0; cursor:pointer;
      text-decoration:none;
    }
    .enc-link-icon { font-size:20px; flex-shrink:0; }
    .enc-link-label { font-size:13px; font-weight:600; color:var(--ink); }
    .enc-link-url { font-size:11px; color:var(--stone-dim); margin-top:2px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:200px; }
    .heart-btn {
      display:inline-flex; align-items:center; gap:5px;
      background:var(--white); border:1px solid var(--border);
      border-radius:20px; padding:5px 12px; cursor:pointer;
      font-size:13px; font-weight:600; color:var(--stone); transition:all 0.15s;
    }
    .heart-btn.hearted { background:rgba(255,100,130,0.12); border-color:rgba(255,100,130,0.35); color:#FF6482; }
    .heart-btn:active { transform:scale(0.95); }

    /* ══ COMMUNITY POST INPUT ══ */
    .post-box {
      background:var(--white); border:1px solid var(--border);
      border-radius:14px; padding:14px; margin-bottom:14px;
    }
    .post-box-title { font-size:12px; font-weight:700; color:var(--stone-dim); letter-spacing:0.1em; text-transform:uppercase; margin-bottom:10px; }
    .post-textarea {
      width:100%; background:var(--white); border:1px solid var(--border);
      border-radius:10px; padding:10px 12px; color:var(--ink);
      font-size:14px; font-family:'Plus Jakarta Sans',sans-serif; resize:none; outline:none;
      box-sizing:border-box;
    }
    .post-textarea:focus { border-color:rgba(232,200,122,0.4); }
    .post-link-input {
      width:100%; background:var(--white); border:1px solid var(--border);
      border-radius:10px; padding:8px 12px; color:var(--ink);
      font-size:13px; font-family:'Plus Jakarta Sans',sans-serif; outline:none; margin-top:6px;
      box-sizing:border-box;
    }
    .post-link-input:focus { border-color:rgba(232,200,122,0.4); }
    .post-btn {
      margin-top:10px; background:var(--gold); color:var(--ink);
      border:none; border-radius:10px; padding:9px 20px;
      font-size:13px; font-weight:700; cursor:pointer; font-family:'Plus Jakarta Sans',sans-serif;
    }
    .post-btn:disabled { opacity:0.4; cursor:default; }

    /* ══ FILTER SELECTORS ══ */
    .filter-bar {
      display: flex; gap: 8px; padding: 12px 16px 4px; flex-shrink: 0;
    }
    .filter-select {
      flex: 1; background: var(--white); border: 1px solid var(--border);
      border-radius: 10px; color: var(--ink); font-size: 13px;
      font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 600;
      padding: 8px 10px; cursor: pointer; appearance: none;
      -webkit-appearance: none; outline: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B6258' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
      background-repeat: no-repeat; background-position: right 10px center;
      padding-right: 28px;
    }
    .filter-select:focus { border-color: rgba(232,200,122,0.4); }
    .filter-select option { background: var(--white); }

    /* ══ COMMENTS ══ */
    .comment-sheet { display:flex; flex-direction:column; max-height:88vh; }
    .comment-list  { flex:1; overflow-y:auto; padding:0 16px 8px; }
    .comment-list::-webkit-scrollbar { width:2px; }
    .comment-card {
      background: transparent; border: none; border-radius: 0;
      padding: 10px 12px; margin-bottom: 4px;
      border-bottom: 1px solid var(--border);
    }
    .comment-card:last-child { border-bottom: none; }
    .comment-header {
      display: flex; align-items: center; gap: 6px;
      font-size: 11px; color: var(--stone-dim); margin-bottom: 4px;
    }
    .comment-author { font-size: 11px; font-weight: 500; color: var(--stone); }
    .comment-sep    { color: var(--stone-dim); }
    .comment-date   { font-size: 11px; color: var(--stone-dim); }
    .comment-private-lock { display:inline-flex; align-items:center; margin-right:6px; opacity:0.6; font-size:12px; }
    .comment-text   { font-size: 14px; color: var(--ink); line-height: 1.5; margin-bottom: 6px; }
    .comment-like   {
      display: inline-flex; align-items: center; gap: 4px;
      background: transparent; border: none; padding: 0; cursor: pointer;
      font-size: 12px; color: var(--stone-dim);
      transition: color 0.15s, transform 0.12s;
    }
    .comment-like.liked  { color: var(--gold-ink); }
    .comment-like:active { transform: scale(0.95); }
    .comment-input-row {
      display:flex; gap:8px; padding:12px 16px;
      border-top:1px solid var(--border); background:var(--cream);
    }
    .comment-input {
      flex:1; background:var(--white); border:1px solid var(--border);
      border-radius:10px; padding:10px 14px; color:var(--ink);
      font-size:14px; font-family:'Plus Jakarta Sans',sans-serif; resize:none;
      outline:none;
    }
    .comment-input:focus { border-color:rgba(232,200,122,0.4); }
    .comment-send {
      background:var(--gold); color:var(--ink); border:none;
      border-radius:10px; padding:0 16px; font-size:13px;
      font-weight:700; cursor:pointer; font-family:'Plus Jakarta Sans',sans-serif;
      flex-shrink:0;
    }
    .comment-send:disabled { opacity:0.4; cursor:default; }
    .comment-verse-ref {
      font-size:10px; font-weight:700; letter-spacing:0.16em;
      text-transform:uppercase; color:var(--gold-ink); padding:14px 16px 4px;
    }
    .comment-verse-preview {
      font-family:Georgia,serif; font-size:14px; line-height:1.5;
      color:var(--stone); padding:0 16px 12px;
      border-bottom:1px solid var(--border); margin-bottom:4px;
    }
    .flag-scroll { flex: 1; overflow-y: auto; padding: 0 16px 40px; }
    .flag-scroll::-webkit-scrollbar { width: 2px; }
    .elder-banner {
      background: rgba(249,112,102,0.08);
      border: 1px solid rgba(249,112,102,0.25); border-radius: 14px;
      padding: 14px 16px; margin-bottom: 14px;
      display: flex; align-items: center; gap: 12px;
    }
    .elder-banner-icon { font-size: 24px; flex-shrink: 0; }
    .elder-banner-title { font-size: 14px; font-weight: 700; color: var(--ink); margin-bottom: 2px; }
    .elder-banner-sub { font-size: 11px; color: var(--stone-dim); }
    .flag-card {
      background: var(--white); border: 1px solid rgba(249,112,102,0.2);
      border-radius: 12px; padding: 14px; margin-bottom: 8px;
    }
    .flag-ref { font-size: 10px; font-weight: 700; letter-spacing: 0.15em;
      text-transform: uppercase; color: #C8372A; margin-bottom: 6px; }
    .flag-text { font-family: Georgia,serif; font-size: 17px; line-height: 1.6;
      color: var(--ink); margin-bottom: 8px; }
    .flag-date { font-size: 11px; color: var(--stone-dim); }
    .latest-sermon-card {
      background: var(--white);
      border: 1px solid var(--border-strong);
      border-radius: 16px; overflow: hidden; margin-bottom: 6px;
    }
    .ls-thumb {
      width: 100%; height: 160px; position: relative;
      background: var(--white); overflow: hidden;
    }
    .ls-thumb-img {
      width: 100%; height: 100%; object-fit: cover;
    }
    .ls-play-overlay {
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0,0,0,0.35);
      font-size: 32px; color: #fff;
      opacity: 0; transition: opacity 0.2s;
      cursor: pointer;
    }
    .ls-thumb:hover .ls-play-overlay { opacity: 1; }
    .ls-body { padding: 14px 16px 16px; }
    .ls-series {
      font-size: 10px; font-weight: 700; letter-spacing: 0.18em;
      text-transform: uppercase; color: var(--gold-ink); margin-bottom: 5px;
    }
    .ls-title {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 22px; font-weight: 800; color: var(--ink);
      line-height: 1.2; margin-bottom: 4px; letter-spacing: 0.01em;
    }
    .ls-passage {
      font-size: 13px; color: var(--stone); margin-bottom: 14px;
    }
    .ls-actions { display: flex; gap: 8px; }
    .ls-watch-btn {
      background: #FF0000; color: #fff; border: none; border-radius: 8px;
      padding: 9px 16px; font-size: 13px; font-weight: 700;
      cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif;
      box-shadow: 0 4px 12px rgba(255,0,0,0.3); transition: opacity 0.15s;
    }
    .ls-watch-btn:hover { opacity: 0.88; }
    .ls-read-btn {
      background: var(--white); color: var(--ink);
      border: 1px solid var(--border-strong); border-radius: 8px;
      padding: 9px 16px; font-size: 13px; font-weight: 600;
      cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; transition: all 0.15s;
    }
    .ls-read-btn:hover { border-color: var(--stone-dim); }
  `}</style>
);

// ── Bible versions
// KJV / WEB → bible-api.com (free, no key, CORS)
// ESV       → Crossway via claude-proxy `fetch_esv` (server-side key)
// NIV       → API.Bible via claude-proxy `fetch_niv` (server-side key)
// Menu order: ESV first (also the default for new users — see the `ver`
// useState fallback), then NIV, then the free public-domain texts.
const VERSIONS = ["ESV","NIV","KJV","WEB"];

const TRANSLATION_ATTRIBUTION = {
  ESV: "ESV® Bible, © Crossway",
  NIV: "NIV® © Biblica",
};

// Map book display names to bible-api.com URL format (lowercase, spaces to +)
function bookToApiName(bookName) {
  return bookName.toLowerCase()
    .replace(/\s+/g, '+')
    .replace(/1\+/g, '1+')
    .replace(/2\+/g, '2+')
    .replace(/3\+/g, '3+');
}

// ── localStorage chapter cache (translation-aware) ────────────────────
// Speeds up navigation + keeps ESV/NIV calls under their free quotas.
// Key shape: `bible_{translation}_{book}_{chapter}` → JSON array of {v,t}.
function bibleCacheKey(translation, book, chapter) {
  return `bible_${translation}_${book}_${chapter}`;
}
function loadCachedChapter(translation, book, chapter) {
  try {
    const raw = localStorage.getItem(bibleCacheKey(translation, book, chapter));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch { return null; }
}
function saveCachedChapter(translation, book, chapter, verses) {
  try {
    localStorage.setItem(bibleCacheKey(translation, book, chapter), JSON.stringify(verses));
  } catch (e) {
    // QuotaExceeded — silently ignore. In-memory chapterCache still works.
    // eslint-disable-next-line no-console
    console.warn("[bible cache] could not persist chapter:", e.message);
  }
}

// ── ESV / NIV fetchers (via claude-proxy edge function) ────────────────
async function fetchESVChapter(book, chapter) {
  const { data, error } = await supabase.functions.invoke("claude-proxy", {
    body: { action: "fetch_esv", book, chapter },
  });
  if (error) {
    const upstream = data?.error || data?.detail || error.message || "ESV fetch failed";
    throw new Error(upstream);
  }
  const verses = Array.isArray(data?.verses) ? data.verses : [];
  if (verses.length === 0) throw new Error("ESV returned no verses");
  return verses;
}

async function fetchNIVChapter(book, chapter) {
  const { data, error } = await supabase.functions.invoke("claude-proxy", {
    body: { action: "fetch_niv", book, chapter },
  });
  if (error) {
    const upstream = data?.error || data?.detail || error.message || "NIV fetch failed";
    throw new Error(upstream);
  }
  const verses = Array.isArray(data?.verses) ? data.verses : [];
  if (verses.length === 0) throw new Error("NIV returned no verses");
  return verses;
}

// Verified on app mount, then cached in localStorage so the answer survives
// reloads without another round-trip. Returns true if the configured
// BIBLE_API_KEY on the edge function has NIV access.
async function checkNivAvailability() {
  try {
    const { data, error } = await supabase.functions.invoke("claude-proxy", {
      body: { action: "check_niv_available" },
    });
    if (error) return false;
    return !!data?.available;
  } catch {
    return false;
  }
}

const BOOKS = [
  // Old Testament
  {name:"Genesis",ch:50,t:"OT"},{name:"Exodus",ch:40,t:"OT"},{name:"Leviticus",ch:27,t:"OT"},
  {name:"Numbers",ch:36,t:"OT"},{name:"Deuteronomy",ch:34,t:"OT"},{name:"Joshua",ch:24,t:"OT"},
  {name:"Judges",ch:21,t:"OT"},{name:"Ruth",ch:4,t:"OT"},{name:"1 Samuel",ch:31,t:"OT"},
  {name:"2 Samuel",ch:24,t:"OT"},{name:"1 Kings",ch:22,t:"OT"},{name:"2 Kings",ch:25,t:"OT"},
  {name:"1 Chronicles",ch:29,t:"OT"},{name:"2 Chronicles",ch:36,t:"OT"},{name:"Ezra",ch:10,t:"OT"},
  {name:"Nehemiah",ch:13,t:"OT"},{name:"Esther",ch:10,t:"OT"},{name:"Job",ch:42,t:"OT"},
  {name:"Psalms",ch:150,t:"OT"},{name:"Proverbs",ch:31,t:"OT"},{name:"Ecclesiastes",ch:12,t:"OT"},
  {name:"Song of Solomon",ch:8,t:"OT"},{name:"Isaiah",ch:66,t:"OT"},{name:"Jeremiah",ch:52,t:"OT"},
  {name:"Lamentations",ch:5,t:"OT"},{name:"Ezekiel",ch:48,t:"OT"},{name:"Daniel",ch:12,t:"OT"},
  {name:"Hosea",ch:14,t:"OT"},{name:"Joel",ch:3,t:"OT"},{name:"Amos",ch:9,t:"OT"},
  {name:"Obadiah",ch:1,t:"OT"},{name:"Jonah",ch:4,t:"OT"},{name:"Micah",ch:7,t:"OT"},
  {name:"Nahum",ch:3,t:"OT"},{name:"Habakkuk",ch:3,t:"OT"},{name:"Zephaniah",ch:3,t:"OT"},
  {name:"Haggai",ch:2,t:"OT"},{name:"Zechariah",ch:14,t:"OT"},{name:"Malachi",ch:4,t:"OT"},
  // New Testament
  {name:"Matthew",ch:28,t:"NT"},{name:"Mark",ch:16,t:"NT"},{name:"Luke",ch:24,t:"NT"},
  {name:"John",ch:21,t:"NT"},{name:"Acts",ch:28,t:"NT"},{name:"Romans",ch:16,t:"NT"},
  {name:"1 Corinthians",ch:16,t:"NT"},{name:"2 Corinthians",ch:13,t:"NT"},{name:"Galatians",ch:6,t:"NT"},
  {name:"Ephesians",ch:6,t:"NT"},{name:"Philippians",ch:4,t:"NT"},{name:"Colossians",ch:4,t:"NT"},
  {name:"1 Thessalonians",ch:5,t:"NT"},{name:"2 Thessalonians",ch:3,t:"NT"},{name:"1 Timothy",ch:6,t:"NT"},
  {name:"2 Timothy",ch:4,t:"NT"},{name:"Titus",ch:3,t:"NT"},{name:"Philemon",ch:1,t:"NT"},
  {name:"Hebrews",ch:13,t:"NT"},{name:"James",ch:5,t:"NT"},{name:"1 Peter",ch:5,t:"NT"},
  {name:"2 Peter",ch:3,t:"NT"},{name:"1 John",ch:5,t:"NT"},{name:"2 John",ch:1,t:"NT"},
  {name:"3 John",ch:1,t:"NT"},{name:"Jude",ch:1,t:"NT"},{name:"Revelation",ch:22,t:"NT"},
];

// ── Real NLC video ID: Sunday 10th May 2026
// youtube.com/live/faujJRpl15A
// Title: "Welcome to our Morning Service || Sunday 10th May"
// Duration: 39:16  |  Channel: New Life Church – Tunbridge Wells

// Verses are now fetched live from bible.helloao.org - see useBibleChapter() hook below

// ── Seed data: Romans 8 KJV - displayed immediately, no API call needed
const SEED_VERSES = [
  {v:1,t:"There is therefore now no condemnation to them which are in Christ Jesus, who walk not after the flesh, but after the Spirit."},
  {v:2,t:"For the law of the Spirit of life in Christ Jesus hath made me free from the law of sin and death."},
  {v:3,t:"For what the law could not do, in that it was weak through the flesh, God sending his own Son in the likeness of sinful flesh, and for sin, condemned sin in the flesh:"},
  {v:4,t:"That the righteousness of the law might be fulfilled in us, who walk not after the flesh, but after the Spirit."},
  {v:5,t:"For they that are after the flesh do mind the things of the flesh; but they that are after the Spirit the things of the Spirit."},
  {v:6,t:"For to be carnally minded is death; but to be spiritually minded is life and peace."},
  {v:7,t:"Because the carnal mind is enmity against God: for it is not subject to the law of God, neither indeed can be."},
  {v:8,t:"So then they that are in the flesh cannot please God."},
  {v:9,t:"But ye are not in the flesh, but in the Spirit, if so be that the Spirit of God dwell in you. Now if any man have not the Spirit of Christ, he is none of his."},
  {v:10,t:"And if Christ be in you, the body is dead because of sin; but the Spirit is life because of righteousness."},
  {v:11,t:"But if the Spirit of him that raised up Jesus from the dead dwell in you, he that raised up Christ from the dead shall also quicken your mortal bodies by his Spirit that dwelleth in you."},
  {v:12,t:"Therefore, brethren, we are debtors, not to the flesh, to live after the flesh."},
  {v:13,t:"For if ye live after the flesh, ye shall die: but if ye through the Spirit do mortify the deeds of the body, ye shall live."},
  {v:14,t:"For as many as are led by the Spirit of God, they are the sons of God."},
  {v:15,t:"For ye have not received the spirit of bondage again to fear; but ye have received the Spirit of adoption, whereby we cry, Abba, Father."},
  {v:16,t:"The Spirit itself beareth witness with our spirit, that we are the children of God:"},
  {v:17,t:"And if children, then heirs; heirs of God, and joint-heirs with Christ; if so be that we suffer with him, that we may be also glorified together."},
  {v:18,t:"For I reckon that the sufferings of this present time are not worthy to be compared with the glory which shall be revealed in us."},
  {v:19,t:"For the earnest expectation of the creature waiteth for the manifestation of the sons of God."},
  {v:20,t:"For the creature was made subject to vanity, not willingly, but by reason of him who hath subjected the same in hope,"},
  {v:21,t:"Because the creature itself also shall be delivered from the bondage of corruption into the glorious liberty of the children of God."},
  {v:22,t:"For we know that the whole creation groaneth and travaileth in pain together until now."},
  {v:23,t:"And not only they, but ourselves also, which have the firstfruits of the Spirit, even we ourselves groan within ourselves, waiting for the adoption, to wit, the redemption of our body."},
  {v:24,t:"For we are saved by hope: but hope that is seen is not hope: for what a man seeth, why doth he yet hope for?"},
  {v:25,t:"But if we hope for that we see not, then do we with patience wait for it."},
  {v:26,t:"Likewise the Spirit also helpeth our infirmities: for we know not what we should pray for as we ought: but the Spirit itself maketh intercession for us with groanings which cannot be uttered."},
  {v:27,t:"And he that searcheth the hearts knoweth what is the mind of the Spirit, because he maketh intercession for the saints according to the will of God."},
  {v:28,t:"And we know that all things work together for good to them that love God, to them who are the called according to his purpose."},
  {v:29,t:"For whom he did foreknow, he also did predestinate to be conformed to the image of his Son, that he might be the firstborn among many brethren."},
  {v:30,t:"Moreover whom he did predestinate, them he also called: and whom he called, them he also justified: and whom he justified, them he also glorified."},
  {v:31,t:"What shall we then say to these things? If God be for us, who can be against us?"},
  {v:32,t:"He that spared not his own Son, but delivered him up for us all, how shall he not with him also freely give us all things?"},
  {v:33,t:"Who shall lay any thing to the charge of God's elect? It is God that justifieth."},
  {v:34,t:"Who is he that condemneth? It is Christ that died, yea rather, that is risen again, who is even at the right hand of God, who also maketh intercession for us."},
  {v:35,t:"Who shall separate us from the love of Christ? shall tribulation, or distress, or persecution, or famine, or nakedness, or peril, or sword?"},
  {v:36,t:"As it is written, For thy sake we are killed all the day long; we are accounted as sheep for the slaughter."},
  {v:37,t:"Nay, in all these things we are more than conquerors through him that loved us."},
  {v:38,t:"For I am persuaded, that neither death, nor life, nor angels, nor principalities, nor powers, nor things present, nor things to come,"},
  {v:39,t:"Nor height, nor depth, nor any other creature, shall be able to separate us from the love of God, which is in Christ Jesus our Lord."},
];

// "Churches I also follow" picker. The user's primary (active) church is
// NEVER listed here — it's always implicitly followed, and this section is
// only for the additional churches they want to pull sermon content from.
// Additional churches widen sermon listings + sermon-ref dots; they do
// NOT widen community, comments, or encouragements (those stay with the
// active church only — that's what the scope note below makes explicit).
// ── Entity-type helpers (Parts 5B/5G/3G) ────────────────────────────────────
// Colour-coded metadata for the five entity types the churches table can hold.
const ENTITY_META = {
  church:       { label: 'Church',       color: '#7DA3E8', bg: 'rgba(125,163,232,0.14)', border: 'rgba(125,163,232,0.4)' },
  event:        { label: 'Event',        color: '#f59e0b', bg: 'rgba(245,158,11,0.14)',  border: 'rgba(245,158,11,0.4)' },
  event_series: { label: 'Event Series', color: '#f59e0b', bg: 'rgba(245,158,11,0.14)',  border: 'rgba(245,158,11,0.4)' },
  network:      { label: 'Network',      color: '#9B6FD4', bg: 'rgba(155,111,212,0.14)', border: 'rgba(155,111,212,0.4)' },
  ministry:     { label: 'Ministry',     color: '#22c55e', bg: 'rgba(34,197,94,0.14)',   border: 'rgba(34,197,94,0.4)' },
};
function EntityBadge({ type }) {
  const m = ENTITY_META[type] || ENTITY_META.church;
  return (
    <span style={{
      flexShrink:0, fontSize:9, fontWeight:800, letterSpacing:"0.06em", textTransform:"uppercase",
      padding:"2px 7px", borderRadius:6, color:m.color, background:m.bg, border:`1px solid ${m.border}`,
    }}>{m.label}</span>
  );
}

// Shared full-screen overlay wrapper for the entity detail sheets below.
function EntityDetailOverlay({ children, onClose }) {
  return (
    <>
      <div className="overlay" style={{zIndex:360}} onClick={onClose}/>
      <div className="sheet" style={{zIndex:361}}>
        <div className="shandle"/>
        <div className="shd">
          <div onClick={onClose} style={{display:"flex",alignItems:"center",gap:4,padding:"6px 10px",fontSize:13,fontWeight:600,background:"var(--white)",border:"1px solid var(--border)",borderRadius:8,color:"var(--stone)",cursor:"pointer"}}>← Back</div>
          <div style={{width:60}}/>
        </div>
        <div className="shbody">{children}</div>
      </div>
    </>
  );
}

function FollowBtn({ following, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      flexShrink:0, fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:12, fontWeight:800, cursor:"pointer",
      borderRadius:8, padding:"7px 14px",
      background: following ? "transparent" : "var(--gold)",
      color: following ? "var(--stone)" : "var(--ink)",
      border: following ? "1px solid var(--border-strong)" : "none",
    }}>{following ? "Following" : "Follow"}</button>
  );
}

// Part 5D — Event Series detail. Lists child event years, combined sermons
// (with a year filter), and unique speakers across all years.
function EventSeriesDetail({ series, churches, isFollowing, onToggleFollow, onClose }) {
  const children = (churches || []).filter(c => c.parent_id === series.id);
  const allIds = [series.id, ...children.map(c => c.id)];
  const [sermons, setSermons] = useState(null);
  const [yearFilter, setYearFilter] = useState('all');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('sermons')
        .select('id, title, date, preacher, church_id, youtube_id')
        .in('church_id', allIds)
        .order('date', { ascending: false });
      if (!cancelled) setSermons(data || []);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series.id]);
  const yearOf = (c) => { const d = c?.event_start_date; return d ? new Date(d).getFullYear() : null; };
  const countFor = (id) => (sermons || []).filter(s => s.church_id === id).length;
  const total = sermons?.length || 0;
  const speakers = [...new Set((sermons || []).map(s => (s.preacher || '').trim()).filter(Boolean))];
  const years = children.map(c => ({ c, year: yearOf(c) })).sort((a,b) => (b.year||0) - (a.year||0));
  const shown = (sermons || []).filter(s => yearFilter === 'all' || s.church_id === yearFilter);
  return (
    <EntityDetailOverlay onClose={onClose}>
      <div style={{display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, marginBottom:6}}>
        <div style={{minWidth:0}}>
          <EntityBadge type="event_series"/>
          <div style={{fontSize:22, fontWeight:800, color:"var(--ink)", marginTop:6, fontFamily:"Georgia, serif"}}>{series.name}</div>
          <div style={{fontSize:13, color:"var(--stone)", marginTop:4}}>{children.length} {children.length===1?"year":"years"} · {total} sermons</div>
        </div>
        <FollowBtn following={isFollowing(series.id)} onClick={() => onToggleFollow(series.id)}/>
      </div>
      {isFollowing(series.id) && (
        <div style={{fontSize:11, fontWeight:700, color:"#22c55e", marginBottom:10}}>✓ Following all years</div>
      )}

      {years.length > 0 && (
        <>
          <div className="profile-section-title" style={{margin:"14px 0 8px"}}>Years</div>
          <div style={{border:"1px solid var(--border)", borderRadius:10, overflow:"hidden", marginBottom:16}}>
            {years.map(({ c, year }, i) => (
              <div key={c.id} style={{display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderBottom: i===years.length-1?"none":"1px solid var(--border)"}}>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:13.5, fontWeight:700, color:"var(--ink)"}}>{c.name}{year ? ` · ${year}` : ""}</div>
                  <div style={{fontSize:11, color:"var(--stone-dim)", marginTop:1}}>{countFor(c.id)} sermons</div>
                </div>
                <FollowBtn following={isFollowing(c.id)} onClick={() => onToggleFollow(c.id)}/>
              </div>
            ))}
          </div>
        </>
      )}

      {speakers.length > 0 && (
        <>
          <div className="profile-section-title" style={{margin:"14px 0 8px"}}>Speakers</div>
          <div style={{display:"flex", flexWrap:"wrap", gap:6, marginBottom:16}}>
            {speakers.map(sp => (
              <span key={sp} style={{fontSize:12, color:"var(--ink)", background:"var(--white)", border:"1px solid var(--border)", borderRadius:20, padding:"4px 10px"}}>{sp}</span>
            ))}
          </div>
        </>
      )}

      <div className="profile-section-title" style={{margin:"14px 0 8px", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap"}}>
        <span>Sermons</span>
        {years.length > 0 && (
          <span style={{display:"flex", gap:6, flexWrap:"wrap"}}>
            <FilterPill active={yearFilter==='all'} onClick={()=>setYearFilter('all')} label="All"/>
            {years.map(({ c, year }) => (
              <FilterPill key={c.id} active={yearFilter===c.id} onClick={()=>setYearFilter(c.id)} label={year || c.name}/>
            ))}
          </span>
        )}
      </div>
      {sermons === null ? (
        <div style={{fontSize:13, color:"var(--stone-dim)", padding:"8px 0"}}>Loading…</div>
      ) : shown.length === 0 ? (
        <div style={{fontSize:13, color:"var(--stone-dim)", padding:"8px 0"}}>No sermons yet.</div>
      ) : shown.map(s => (
        <div key={s.id} style={{display:"flex", gap:10, padding:"8px 0", borderBottom:"1px solid var(--border)"}}>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:13.5, color:"var(--ink)", fontWeight:600}}>{s.title || "Untitled"}</div>
            <div style={{fontSize:11, color:"var(--stone-dim)", marginTop:1}}>{[s.preacher, s.date ? new Date(s.date).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : null].filter(Boolean).join("  ·  ")}</div>
          </div>
        </div>
      ))}
    </EntityDetailOverlay>
  );
}

function FilterPill({ active, onClick, label }) {
  return (
    <button type="button" onClick={onClick} style={{
      fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11, fontWeight:700, cursor:"pointer",
      borderRadius:20, padding:"3px 10px",
      background: active ? "var(--gold)" : "transparent",
      color: active ? "var(--ink)" : "var(--stone)",
      border: active ? "none" : "1px solid var(--border)",
    }}>{label}</button>
  );
}

// Part 5E — Network detail. Member churches with individual follow buttons and
// combined coverage stats.
function NetworkDetail({ network, churches, isFollowing, onToggleFollow, onFollowMany, onClose }) {
  const members = (churches || []).filter(c => c.network_id === network.id || c.parent_id === network.id);
  const memberIds = members.map(c => c.id);
  const [stats, setStats] = useState(null);   // { sermons, refs }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (memberIds.length === 0) { setStats({ sermons: 0, refs: 0 }); return; }
      const [{ count: sCount }, { count: rCount }] = await Promise.all([
        supabase.from('sermons').select('id', { count: 'exact', head: true }).in('church_id', memberIds),
        supabase.from('sermon_refs').select('id', { count: 'exact', head: true }).in('church_id', memberIds),
      ]);
      if (!cancelled) setStats({ sermons: sCount || 0, refs: rCount || 0 });
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [network.id]);
  const allFollowed = members.length > 0 && members.every(c => isFollowing(c.id));
  return (
    <EntityDetailOverlay onClose={onClose}>
      <div style={{display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, marginBottom:6}}>
        <div style={{minWidth:0}}>
          <EntityBadge type="network"/>
          <div style={{fontSize:22, fontWeight:800, color:"var(--ink)", marginTop:6, fontFamily:"Georgia, serif"}}>{network.name}</div>
          {network.description && <div style={{fontSize:13, color:"var(--stone)", marginTop:4, lineHeight:1.5}}>{network.description}</div>}
        </div>
        <FollowBtn following={allFollowed} onClick={() => allFollowed ? members.forEach(c => isFollowing(c.id) && onToggleFollow(c.id)) : onFollowMany(memberIds)}/>
      </div>
      {stats && (
        <div style={{fontSize:13, color:"var(--stone)", marginBottom:14}}>{members.length} churches · {stats.sermons} sermons · {stats.refs} references</div>
      )}
      <div className="profile-section-title" style={{margin:"6px 0 8px"}}>Member churches</div>
      {members.length === 0 ? (
        <div style={{fontSize:13, color:"var(--stone-dim)", padding:"8px 0"}}>No member churches yet.</div>
      ) : (
        <div style={{border:"1px solid var(--border)", borderRadius:10, overflow:"hidden"}}>
          {members.map((c, i) => (
            <div key={c.id} style={{display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderBottom: i===members.length-1?"none":"1px solid var(--border)"}}>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:13.5, fontWeight:700, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{c.name}</div>
                {c.location && <div style={{fontSize:11, color:"var(--stone-dim)", marginTop:1}}>{c.location}</div>}
              </div>
              <FollowBtn following={isFollowing(c.id)} onClick={() => onToggleFollow(c.id)}/>
            </div>
          ))}
        </div>
      )}
    </EntityDetailOverlay>
  );
}

function SermonNoteChurchesPicker({ churches, activeChurchId, sermonNoteChurchIds, setSermonNoteChurchIds, onRequestChurch }) {
  const [search, setSearch] = useState("");
  // An event_series / network row opens its detail sheet (Parts 5D/5E).
  const [detail, setDetail] = useState(null);   // church row | null
  // church_stats gives each church its network (for grouping search results)
  // plus counts. Falls back to the plain churches prop if the RPC is absent.
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    supabase.rpc("church_stats").then(({ data, error }) => {
      if (cancelled || error || !Array.isArray(data)) return;
      setStats(data.map(r => ({ id:r.church_id, name:r.name, location:r.location, network_id:r.network_id, network_name:r.network_name })));
    });
    return () => { cancelled = true; };
  }, []);
  const source = stats || churches || [];

  const list = Array.isArray(sermonNoteChurchIds) ? sermonNoteChurchIds : [];
  // The persisted list may still contain activeChurchId (legacy behaviour);
  // filter it out for display + writes so the active church never appears.
  const extras = list.filter(id => id && id !== activeChurchId);
  const selectedSet = new Set(extras);
  const q = search.trim().toLowerCase();
  const candidates = source.filter(c => c.id !== activeChurchId && !selectedSet.has(c.id));
  const available = candidates.filter(c => !q
    || (c.name || "").toLowerCase().includes(q)
    || (c.location || "").toLowerCase().includes(q)
    || (c.network_name || "").toLowerCase().includes(q));

  // When the query matches a network name, surface that network as a group
  // (with "Follow all churches"), then any remaining individual matches.
  const matchedNetworks = [];
  let flatAvailable = available;
  if (q) {
    const netMap = new Map();
    for (const c of candidates) {
      if (c.network_id && (c.network_name || "").toLowerCase().includes(q)) {
        if (!netMap.has(c.network_id)) netMap.set(c.network_id, { id:c.network_id, name:c.network_name, churches:[] });
        netMap.get(c.network_id).churches.push(c);
      }
    }
    for (const g of netMap.values()) matchedNetworks.push(g);
    const inGroup = new Set(matchedNetworks.flatMap(g => g.churches.map(c => c.id)));
    flatAvailable = available.filter(c => !inGroup.has(c.id));
  }

  function addChurch(c) {
    if (!c?.id || c.id === activeChurchId || selectedSet.has(c.id)) return;
    // Persist with the active church re-prepended so multi-church scoping
    // (effectiveSermonChurchIds) keeps working.
    setSermonNoteChurchIds([activeChurchId, ...extras, c.id].filter(Boolean));
    setSearch("");
  }
  function followMany(ids) {
    const add = ids.filter(id => id && id !== activeChurchId && !selectedSet.has(id));
    if (!add.length) return;
    setSermonNoteChurchIds([activeChurchId, ...extras, ...add].filter(Boolean));
  }
  function removeChurch(id) {
    if (!id || id === activeChurchId) return;
    setSermonNoteChurchIds([activeChurchId, ...extras.filter(x => x !== id)].filter(Boolean));
  }
  const availRow = (c) => (
    <div key={c.id} onClick={() => addChurch(c)}
      style={{
        padding:"10px 12px", cursor:"pointer",
        borderBottom:"1px solid var(--border)",
        display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
      }}>
      <div style={{minWidth:0, flex:1}}>
        <div style={{fontSize:13, fontWeight:600, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{c.name}</div>
        {c.location && <div style={{fontSize:11, color:"var(--stone-dim)", marginTop:1}}>{c.location}</div>}
      </div>
      <div style={{fontSize:11, fontWeight:700, color:"var(--gold-ink)", letterSpacing:"0.08em", textTransform:"uppercase"}}>Follow</div>
    </div>
  );

  const initialOf = (s) => {
    const trimmed = String(s || "").trim();
    return trimmed ? trimmed[0].toUpperCase() : "?";
  };

  return (
    <div className="profile-section">
      {/* Scope explainer */}
      <div style={{fontSize:13, color:"var(--stone)", marginBottom:14, lineHeight:1.5}}>
        You'll see sermon notes for all churches you are following. You'll only be able to access community features for your home church.
      </div>

      {/* Churches you follow — bounded scroll box so it never reads as the
          whole directory. Each row has a remove (×) control. */}
      {extras.length > 0 ? (
        <div style={{
          maxHeight:240, overflowY:"auto",
          border:"1px solid var(--border)", borderRadius:10,
          padding:"0 10px", marginBottom:18,
        }}>
          {extras.map((id, i) => {
            const c = churches?.find(x => x.id === id);
            const name = c?.name || "Unknown church";
            const etype = c?.entity_type;
            const openable = etype === 'event_series' || etype === 'network';
            return (
              <div key={id} style={{
                display:"flex", alignItems:"center", gap:12,
                padding:"10px 2px",
                borderBottom: i === extras.length - 1 ? "none" : "1px solid var(--border)",
              }}>
                <div
                  onClick={() => openable && setDetail(c)}
                  style={{
                  flexShrink:0, width:32, height:32, borderRadius:8,
                  background:"rgba(232,200,122,0.12)",
                  border:"1px solid rgba(232,200,122,0.25)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:13, fontWeight:700, color:"var(--gold-ink)",
                  letterSpacing:"0.02em", cursor: openable ? "pointer" : "default",
                }}>{initialOf(name)}</div>
                <div
                  onClick={() => openable && setDetail(c)}
                  style={{flex:1, minWidth:0, cursor: openable ? "pointer" : "default"}}>
                  <div style={{display:"flex", alignItems:"center", gap:6}}>
                    <div style={{fontSize:13, fontWeight:600, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{name}</div>
                    {c && <EntityBadge type={etype}/>}
                  </div>
                  {c?.location && (
                    <div style={{fontSize:11, color:"var(--stone-dim)", marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{c.location}</div>
                  )}
                </div>
                <button onClick={() => removeChurch(id)}
                  aria-label={`Unfollow ${name}`}
                  style={{
                    flexShrink:0,
                    background:"none", border:"none",
                    color:"var(--stone-dim)",
                    padding:6, fontSize:18, lineHeight:1, cursor:"pointer",
                    fontFamily:"'Plus Jakarta Sans',sans-serif",
                  }}>×</button>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{fontSize:13, color:"var(--stone-dim)", padding:"4px 0 16px"}}>
          You're not following any other churches yet.
        </div>
      )}

      {/* Search for more churches to follow */}
      <div className="profile-section-title" style={{marginBottom:8}}>Search for more churches to follow</div>
      <div style={{
        position:"relative",
        display:"flex", alignItems:"center", gap:8,
        padding:"10px 12px",
        border:"1px dashed var(--border-strong)",
        borderRadius:10,
        background:"transparent",
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{flexShrink:0, color:"var(--stone-dim)"}}>
          <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2"/>
          <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        <input
          placeholder="Search for a church, network, or location"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex:1, minWidth:0,
            background:"transparent", border:"none", outline:"none",
            color:"var(--ink)", fontSize:13,
            fontFamily:"'Plus Jakarta Sans',sans-serif",
          }}/>
      </div>

      {search.trim() && (
        <div style={{
          maxHeight:260, overflowY:"auto",
          border:"1px solid var(--border)", borderRadius:8, marginTop:6,
        }}>
          {matchedNetworks.length === 0 && flatAvailable.length === 0 ? (
            <div style={{padding:"10px 12px", fontSize:13, color:"var(--stone-dim)"}}>
              No matches.
            </div>
          ) : (
            <>
              {matchedNetworks.map(g => {
                const followable = g.churches.filter(c => c.id !== activeChurchId);
                const allFollowed = followable.length > 0 && followable.every(c => selectedSet.has(c.id));
                return (
                  <div key={"net-"+g.id} style={{borderBottom:"1px solid var(--border)", background:"rgba(232,200,122,0.05)"}}>
                    <div style={{display:"flex", alignItems:"center", gap:8, padding:"10px 12px"}}>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{fontSize:10, color:"var(--stone-dim)", textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:800}}>Network</div>
                        <div style={{fontSize:13.5, fontWeight:800, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{g.name}</div>
                      </div>
                      <button type="button" onClick={(e)=>{ e.stopPropagation(); followMany(g.churches.map(c=>c.id)); }} disabled={allFollowed}
                        style={{flexShrink:0, fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:11.5, fontWeight:800, cursor:allFollowed?"default":"pointer", borderRadius:8, padding:"6px 10px", background:allFollowed?"transparent":"var(--gold)", color:allFollowed?"var(--stone-dim)":"var(--ink)", border:allFollowed?"1px solid var(--border-strong)":"none"}}>
                        {allFollowed ? "Following all" : "Follow all churches"}
                      </button>
                    </div>
                    <div style={{paddingLeft:12}}>
                      {g.churches.map(availRow)}
                    </div>
                  </div>
                );
              })}
              {flatAvailable.map(availRow)}
            </>
          )}
        </div>
      )}

      {/* Request a church we don't have yet. */}
      <div style={{marginTop:14, fontSize:13, color:"var(--stone)", lineHeight:1.5}}>
        Can't see a church you want to follow?{" "}
        <span onClick={() => onRequestChurch?.()}
          style={{color:"var(--gold-ink)", textDecoration:"underline", cursor:"pointer"}}>
          Ask us to add it here
        </span>
      </div>

      {/* Entity detail sheets (event series / network) */}
      {detail?.entity_type === 'event_series' && (
        <EventSeriesDetail
          series={detail}
          churches={churches}
          isFollowing={(cid) => cid === activeChurchId || selectedSet.has(cid)}
          onToggleFollow={(cid) => { selectedSet.has(cid) ? removeChurch(cid) : addChurch({ id: cid }); }}
          onClose={() => setDetail(null)}/>
      )}
      {detail?.entity_type === 'network' && (
        <NetworkDetail
          network={detail}
          churches={churches}
          isFollowing={(cid) => cid === activeChurchId || selectedSet.has(cid)}
          onToggleFollow={(cid) => { selectedSet.has(cid) ? removeChurch(cid) : addChurch({ id: cid }); }}
          onFollowMany={followMany}
          onClose={() => setDetail(null)}/>
      )}
    </div>
  );
}

// Profile sheet building blocks (docs/designs/ProfileElder.dc.html /
// ProfileSuper.dc.html): every section is ONE white 20px-radius card holding
// its rows, each row separated by a 1px inset-free divider rather than its
// own box. Icons are gold-ink 18px line SVGs (the design's actual colour —
// not the stone grey originally asked for; matching the file, flagged to
// Leanne). Row chevrons/badges are shared so every row looks identical.
const PROFILE_GOLD = "#7A5A12";
const PROFILE_DIVIDER = "#EFE8DD";
function ProfileCard({ children }) {
  return <div style={{ background: "var(--white)", borderRadius: 20, padding: "2px 14px" }}>{children}</div>;
}
function ProfileSectionLabel({ children }) {
  return <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: PROFILE_GOLD, paddingLeft: 4 }}>{children}</div>;
}
function ProfileChevron() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9A9086" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>;
}
function ProfileRow({ icon, title, titleWeight = 700, subtitle, badge, right, first, onClick }) {
  return (
    <div onClick={onClick}
      style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", borderTop: first ? "none" : `1px solid ${PROFILE_DIVIDER}`, cursor: onClick ? "pointer" : "default" }}>
      {icon}
      <div style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: titleWeight, color: "var(--ink)" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: "var(--stone)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</div>}
      </div>
      {badge > 0 && (
        <div style={{ minWidth: 22, height: 22, borderRadius: 11, background: "#E0533D", color: "#fff", fontSize: 11.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 6px", boxSizing: "border-box", flexShrink: 0 }}>{badge}</div>
      )}
      {right}
      {onClick && <ProfileChevron/>}
    </div>
  );
}

function ProfileSheet({ onClose, activeChurchId, setActiveChurchId, sermonNoteChurchIds = [], setSermonNoteChurchIds = () => {}, onOpenSaved, savedCount, onOpenLeaders, onStartWalkthrough, onReportProblem, notifications = [], notifUnread = 0, onOpenNotifications, onOpenReadingSettings }) {
  const { profile, user, signOut, deleteAccount, sendInvite, updateProfile, createChurch, churches: authChurches } = useAuth();
  // Two-step destructive-action UX: tap "Delete account" → confirm
  // dialog → "Delete account (red)". Once the request is in flight we
  // disable both buttons. On success the user is signed out and the
  // page redirects to landing; on error we surface the message.
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // "Change home church" asks for confirmation first (docs/FIX-PROFILE.md
  // §2: "it changes their This Week and devotional") — a pending church pick
  // only opens this dialog; saveAndReloadToChurch() itself only runs once
  // it's confirmed.
  const [changeChurchConfirmOpen, setChangeChurchConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  async function handleDeleteAccount() {
    if (deleting) return;
    setDeleting(true); setDeleteError('');
    const { ok, error } = await deleteAccount();
    if (ok) {
      setDeleteConfirmOpen(false);
      // Replace history so back-button doesn't return to a half-rendered
      // profile sheet, then navigate to the public landing.
      try { window.history.replaceState({}, '', '/'); } catch (_e) {}
      window.location.assign('/');
      return;
    }
    setDeleting(false);
    setDeleteError(error?.message || 'Could not delete your account. Please try again or contact support.');
  }
  const role        = profile?.role || "member";
  const isSuperAdmin = role === "super_admin";
  const isElder      = ["elder","admin","super_admin"].includes(role);

  // Leaders-dashboard badge count — same four counts LeadersPage.jsx's own
  // "Needs attention" card computes (docs/FIX-PROFILE.md §3: "a red count
  // badge, the total of the Needs attention items").
  const [leaderBadgeCount, setLeaderBadgeCount] = useState(0);
  useEffect(() => {
    const cid = activeChurchId || profile?.church_id;
    if (!isElder || !cid) { setLeaderBadgeCount(0); return; }
    let cancelled = false;
    (async () => {
      const [reportsRes, plansRes, accessRes, prayersRes] = await Promise.all([
        supabase.from("reports").select("id", { count: "exact", head: true }).eq("church_id", cid).eq("resolved", false),
        supabase.from("reading_plans").select("id", { count: "exact", head: true }).eq("church_id", cid).in("status", ["draft", "approved"]),
        supabase.from("access_requests").select("id", { count: "exact", head: true }).eq("church_id", cid).eq("status", "pending"),
        supabase.from("prayers").select("id", { count: "exact", head: true }).eq("church_id", cid).eq("visibility", "elders"),
      ]);
      if (cancelled) return;
      setLeaderBadgeCount((reportsRes.count || 0) + (plansRes.count || 0) + (accessRes.count || 0) + (prayersRes.count || 0));
    })();
    return () => { cancelled = true; };
  }, [isElder, activeChurchId, profile?.church_id]);

  // Super admin's "Church requests" badge — pending requests awaiting review
  // (docs/designs/ProfileSuper.dc.html shows a count; today's row has none).
  const [churchRequestsBadgeCount, setChurchRequestsBadgeCount] = useState(0);
  useEffect(() => {
    if (!isSuperAdmin) { setChurchRequestsBadgeCount(0); return; }
    let cancelled = false;
    supabase.from("church_requests").select("id", { count: "exact", head: true }).eq("status", "pending")
      .then(({ count }) => { if (!cancelled) setChurchRequestsBadgeCount(count || 0); });
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  // Seed with churches already loaded by the auth context (avoids an empty-list flash),
  // then refresh from Supabase so newly created churches show up immediately.
  const [churches,        setChurches]        = useState(authChurches || []);
  const [churchesLoading, setChurchesLoading] = useState(true);
  const [churchesError,   setChurchesError]   = useState("");
  useEffect(() => {
    setChurches(prev => prev.length ? prev : (authChurches || []));
  }, [authChurches]);

  const loadChurches = useCallback(async () => {
    setChurchesLoading(true);
    setChurchesError("");
    const { data, error } = await supabase.from('churches').select('*').order('name');
    setChurchesLoading(false);
    if (error) {
      console.error('Failed to load churches:', error);
      setChurchesError(error.message || 'Could not load churches.');
      return;
    }
    // Only overwrite if we actually got rows back - otherwise keep the auth-context
    // fallback in place so an RLS or transient miss doesn't wipe the list.
    if (Array.isArray(data) && data.length > 0) setChurches(data);
  }, []);
  useEffect(() => { loadChurches(); }, [loadChurches]);

  const [pickerOpen,    setPickerOpen]    = useState(false);
  // No-church users get the search inline in the row; results only appear once
  // they focus the field (so we don't dump the whole directory on them).
  const [churchFieldFocused, setChurchFieldFocused] = useState(false);
  const [pendingChurchId, setPendingChurchId] = useState("");
  const [switchedMsg,   setSwitchedMsg]   = useState("");
  const [switchError,   setSwitchError]   = useState("");
  const [switchSaving,  setSwitchSaving]  = useState(false);
  // Fall back to profile.church_id when activeChurchId hasn't hydrated
  // yet — stops the "blank Home church" flash on first paint after a
  // login or a localStorage clear.
  const activeChurch = churches?.find(c => c.id === (activeChurchId || profile?.church_id));

  // Multi-church elders/admins are tracked in church_admins. If they have
  // more than one row, they get the same switcher as super admins. The
  // table may not exist yet on older deployments — treat that as "no
  // multi-church access" rather than failing.
  const [adminChurches, setAdminChurches] = useState([]);
  useEffect(() => {
    if (!user?.id || !["elder","admin"].includes(role)) { setAdminChurches([]); return; }
    let cancelled = false;
    supabase.from('church_admins').select('church_id, role').eq('user_id', user.id)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          if (!/relation .* does not exist/i.test(error.message || '')) {
            console.warn('[profile] church_admins load:', error.message);
          }
          setAdminChurches([]);
          return;
        }
        setAdminChurches(Array.isArray(data) ? data : []);
      });
    return () => { cancelled = true; };
  }, [user?.id, role]);

  const isMultiChurchAdmin = ["elder","admin"].includes(role) && adminChurches.length > 1;
  const canSwitchChurch    = isSuperAdmin || isMultiChurchAdmin;
  // A user with no home church at all — show the picker so they can join one,
  // plus the invite-a-leader / set-up-your-church options.
  const noHomeChurch       = !activeChurchId && !profile?.church_id;

  // Picking a church row in the dropdown only marks it as pending — nothing
  // is persisted until the user hits "Save & reload" below. Two-step so a
  // mistaken tap doesn't yank the whole app's state to a different church.
  function selectPendingChurch(c) {
    if (!c?.id) return;
    setPendingChurchId(c.id);
    setSwitchError("");
  }

  // Commits the pending selection: writes profile.church_id, persists
  // activeChurchId via localStorage, then HARD-RELOADS the page. Reloading
  // is the simplest way to guarantee every cached hook (useSermons,
  // useEncouragements, useCommunityBoard, the events fetcher, etc.) starts
  // fresh against the new church — no stale state, no race conditions.
  async function saveAndReloadToChurch() {
    setSwitchError("");
    const target = churches.find(c => c.id === pendingChurchId);
    if (!target) { setSwitchError("Pick a church first."); return; }
    setSwitchSaving(true);
    // join_church() decides server-side (see
    // supabase-schema-security-church-id.sql) — it bypasses the invite-only
    // access-request detour for a super_admin or an existing church_admins
    // row, which this switcher is only ever shown to in the first place.
    if (profile?.church_id !== target.id) {
      const { data: result, error } = await supabase.rpc('join_church', { p_church_id: target.id });
      if (error) {
        setSwitchSaving(false);
        setSwitchError(error.message || "Could not switch church.");
        return;
      }
      if (result !== 'joined') {
        setSwitchSaving(false);
        setSwitchError("This church needs approval before you can switch to it.");
        return;
      }
    }
    // Mirror the choice into the activeChurchId localStorage key so the
    // App component reads it back as the post-reload active church.
    setActiveChurchId(target.id);
    writeUserScoped("nlc.activeChurchId", user?.id, target.id);
    setSwitchedMsg(`Switching to ${target.name} — reloading…`);
    // Small delay so the user sees the confirmation before the reload.
    setTimeout(() => { window.location.reload(); }, 600);
  }

  const fullName       = profile?.full_name || user?.email || "Unnamed";
  const initials       = getInitials(profile?.full_name || user?.email);

  // Profile photo upload (Supabase Storage `avatars` bucket; one object per
  // user, always named <uid>.jpg, public bucket). Picked file goes through
  // an in-app crop sheet before being uploaded so users can frame their face
  // inside the circular avatar.
  const avatarInputRef = useRef(null);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [avatarBusy,     setAvatarBusy]     = useState(false);
  const [avatarError,    setAvatarError]    = useState("");
  const [cropFile,       setCropFile]       = useState(null);

  function onAvatarClick() {
    if (avatarBusy) return;
    if (profile?.avatar_url) setAvatarMenuOpen(true);   // existing photo → show menu
    else avatarInputRef.current?.click();               // no photo → straight to picker
  }

  function handleAvatarFile(file) {
    if (!file || !user?.id) return;
    setAvatarMenuOpen(false);
    setAvatarError("");
    setCropFile(file);
    if (avatarInputRef.current) avatarInputRef.current.value = "";
  }

  async function uploadCroppedBlob(blob) {
    if (!blob || !user?.id) return;
    setAvatarBusy(true); setAvatarError("");
    try {
      const path = `${user.id}.jpg`;
      const { error: upErr } = await supabase.storage
        .from('avatars')
        .upload(path, blob, { upsert: true, contentType: 'image/jpeg' });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path);
      // Cache-bust so the new image actually replaces the old one in <img>.
      const publicUrl = `${pub.publicUrl}?v=${Date.now()}`;
      const { error: profErr } = await updateProfile({ avatar_url: publicUrl });
      if (profErr) throw profErr;
      setCropFile(null);
    } catch (e) {
      setAvatarError(e?.message || String(e));
    } finally {
      setAvatarBusy(false);
    }
  }

  async function handleAvatarRemove() {
    if (!user?.id) return;
    setAvatarBusy(true); setAvatarError(""); setAvatarMenuOpen(false);
    try {
      const path = `${user.id}.jpg`;
      // Best-effort delete; ignore "not found" so an externally-hosted Google
      // avatar (which isn't in our bucket) can still be unset on the profile.
      const { error: rmErr } = await supabase.storage.from('avatars').remove([path]);
      if (rmErr && !/not.*found/i.test(rmErr.message || "")) throw rmErr;
      const { error: profErr } = await updateProfile({ avatar_url: null });
      if (profErr) throw profErr;
    } catch (e) {
      setAvatarError(e?.message || String(e));
    } finally {
      setAvatarBusy(false);
    }
  }

  const [view,          setView]          = useState("profile"); // "profile" | "invite" | "church" | "settings" | "contact"
  // Settings view - editable name, read-only email + role.
  const [nameDraft,     setNameDraft]     = useState("");
  const [nameSaving,    setNameSaving]    = useState(false);
  const [nameSaved,     setNameSaved]     = useState(false);
  const [nameError,     setNameError]     = useState("");
  // Contact us view.
  const [contactDraft,  setContactDraft]  = useState("");
  const [contactSending, setContactSending] = useState(false);
  const [contactSent,   setContactSent]   = useState(false);
  const [contactError,  setContactError]  = useState("");
  // Request-a-church view (same form/endpoint as onboarding).
  const [reqName,      setReqName]      = useState("");
  const [reqLocation,  setReqLocation]  = useState("");
  const [reqBusy,      setReqBusy]      = useState(false);
  const [reqSent,      setReqSent]      = useState(false);
  const [reqError,     setReqError]     = useState("");

  // ── Super admin: All churches / Church requests / Platform stats ──
  const [allChurchesSearch, setAllChurchesSearch] = useState("");
  const [churchRequests, setChurchRequests] = useState(null);   // null = not loaded
  const [platformStats, setPlatformStats] = useState(null);     // null = not loaded
  useEffect(() => {
    if (view === "churchRequests" && churchRequests === null) {
      supabase.from("church_requests").select("*").order("created_at", { ascending: false })
        .then(({ data }) => setChurchRequests(data || []));
    }
    if (view === "platformStats" && platformStats === null) {
      Promise.all([
        supabase.from("profiles").select("id", { count: "exact", head: true }),
        supabase.from("churches").select("id", { count: "exact", head: true }),
        supabase.from("sermons").select("id", { count: "exact", head: true }),
      ]).then(([m, c, s]) => setPlatformStats({
        members: m.count || 0, churches: c.count || 0, sermons: s.count || 0,
      }));
    }
  }, [view, churchRequests, platformStats]);

  // ── Super admin: approve a church request into a real church ──
  // There was no "turn this request into a real church" action before this
  // (the list was read-only) — FIX-CHURCH-LOGOS.md assumed one existed to
  // attach a logo preview to, so this adds the approval action itself too.
  // Direct insert (not the onboard_church RPC, which makes the CALLER that
  // church's admin — wrong here, since the super admin approving isn't
  // going to run the church). churches INSERT is already super_admin-only,
  // so this needs no new RLS.
  const [approvingReqId, setApprovingReqId] = useState(null);
  const [approvedChurchId, setApprovedChurchId] = useState(null);
  const [reqLogoPreview, setReqLogoPreview] = useState(null); // { dataUrl, channelTitle } | null
  const [reqLogoLoading, setReqLogoLoading] = useState(false);
  const [reqLogoBusy, setReqLogoBusy] = useState(false);

  async function approveChurchRequest(r) {
    setApprovingReqId(r.id);
    setApprovedChurchId(null);
    setReqLogoPreview(null);
    const slug = (r.church_name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "church";
    const { data: newChurch, error } = await supabase.from("churches").insert({
      name: r.church_name, slug, location: r.location || null,
      youtube_channel_url: r.youtube_channel_url || null,
      invite_only: false, setup_complete: true,
    }).select().single();
    if (error) {
      alert(`Could not create church: ${error.message}`);
      setApprovingReqId(null);
      return;
    }
    setApprovedChurchId(newChurch.id);
    if (r.youtube_channel_url) {
      setReqLogoLoading(true);
      try {
        const { data } = await supabase.functions.invoke("fetch-church-logo", { body: { channel_url: r.youtube_channel_url } });
        if (data?.image_data_url) setReqLogoPreview({ dataUrl: data.image_data_url, channelTitle: data.channel_title || "" });
      } catch {}
      setReqLogoLoading(false);
    }
  }

  async function applyRequestLogo(useYoutube, r, churchId) {
    setReqLogoBusy(true);
    try {
      if (useYoutube && r.youtube_channel_url) {
        await supabase.functions.invoke("fetch-church-logo", { body: { channel_url: r.youtube_channel_url, church_id: churchId } });
      }
    } catch {}
    setReqLogoBusy(false);
    await finishChurchRequest(r);
  }

  async function finishChurchRequest(r) {
    await supabase.from("church_requests").update({ status: "complete" }).eq("id", r.id);
    setChurchRequests(prev => (prev || []).map(x => (x.id === r.id ? { ...x, status: "complete" } : x)));
    setApprovingReqId(null);
    setApprovedChurchId(null);
    setReqLogoPreview(null);
  }

  // Reset settings/contact state when leaving those views so the next
  // visit starts fresh (no stale 'Saved' or 'Thank you' message).
  useEffect(() => {
    if (view !== "settings") { setNameSaved(false); setNameError(""); }
    if (view !== "contact")  { setContactSent(false); setContactError(""); }
    if (view !== "request-church") { setReqError(""); setReqSent(false); }
  }, [view]);
  // Prime the name draft from the current profile when entering settings.
  useEffect(() => {
    if (view === "settings") setNameDraft(profile?.full_name || "");
  }, [view, profile?.full_name]);
  const [inviteEmail,   setInviteEmail]   = useState("");
  const [inviteRole,    setInviteRole]    = useState("member");
  const [churchSearch,  setChurchSearch]  = useState("");
  const [selectedChurch,setSelectedChurch]= useState("");
  const [churchPickerOpen, setChurchPickerOpen] = useState(false);
  const [inviteSent,    setInviteSent]    = useState(false);

  useEffect(() => {
    if (!isSuperAdmin && profile?.church_id) setSelectedChurch(profile.church_id);
  }, [isSuperAdmin, profile?.church_id]);

  // Create church form
  const [churchName,    setChurchName]    = useState("");
  const [churchLoc,     setChurchLoc]     = useState("");
  const [churchWeb,     setChurchWeb]     = useState("");
  const [adminEmail,    setAdminEmail]    = useState("");
  // null when idle, an object { name, inviteEmail, inviteError } on success.
  const [churchCreated, setChurchCreated] = useState(null);
  const [churchSaving,  setChurchSaving]  = useState(false);
  const [churchError,   setChurchError]   = useState("");

  // When the box is empty or still shows the chosen church's name, list every
  // church so reopening the dropdown browses the full directory; once they type
  // something different we filter by name/location.
  const selectedChurchObj = churches.find(c => c.id === selectedChurch);
  const churchQuery = churchSearch.trim().toLowerCase();
  const filteredChurches = (!churchQuery || churchSearch === selectedChurchObj?.name)
    ? churches
    : churches.filter(c =>
        (c.name || "").toLowerCase().includes(churchQuery) ||
        (c.location || "").toLowerCase().includes(churchQuery)
      );


  const [inviteData,    setInviteData]    = useState(null);  // server row, contains .token (only set for a lone invite)
  const [inviteError,   setInviteError]   = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteCopied,  setInviteCopied]  = useState(false);
  // Bulk-invite outcome: { invited:[email], granted:[email], failed:[{email,reason}] }
  const [bulkSummary,   setBulkSummary]   = useState(null);

  // Pull a clean, de-duplicated list of email addresses out of whatever the
  // user pasted — newlines, commas, semicolons or spaces all work as
  // separators. Anything that doesn't look like an email is dropped.
  function parseEmailList(raw) {
    const seen = new Set();
    const out = [];
    for (const tok of String(raw || "").split(/[\s,;]+/)) {
      const e = tok.trim().toLowerCase();
      if (!e) continue;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) continue;
      if (seen.has(e)) continue;
      seen.add(e); out.push(e);
    }
    return out;
  }

  // Fire the "your access has been granted" email (best-effort; the in-app
  // notification + push are already triggered DB-side by the status change).
  async function sendAccessGrantedEmail(email, churchName) {
    try {
      const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
      await fetch('https://icsdpckobituqivafuqo.supabase.co/functions/v1/send-invite-email', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', 'Authorization':`Bearer ${SUPABASE_ANON_KEY}`, 'apikey':SUPABASE_ANON_KEY },
        body: JSON.stringify({
          email, role: inviteRole, churchName,
          inviteUrl: 'https://thebiblecommunity.co.uk/app',
          inviterName: profile?.full_name || profile?.email || '',
          accessGranted: true,
        }),
      });
    } catch (e) { console.warn('[invite] access-granted email failed:', e); }
  }

  async function handleInvite() {
    const emails = parseEmailList(inviteEmail);
    if (emails.length === 0) { setInviteError("Enter at least one valid email address."); return; }
    setInviteLoading(true); setInviteError(""); setInviteData(null); setInviteSent(false); setInviteCopied(false); setBulkSummary(null);

    const targetChurchId = isSuperAdmin ? selectedChurch : profile?.church_id;
    if (!targetChurchId) { setInviteLoading(false); setInviteError("Please choose a church."); return; }
    const churchName = churches.find(c => c.id === targetChurchId)?.name || "your church";

    // Map of email → pending access request for this church, so an invite to
    // someone who already asked to join becomes an instant grant instead.
    const pendingByEmail = new Map();
    try {
      const { data: reqs } = await supabase
        .from('access_requests')
        .select('id, user_id, status, profiles!user_id(email)')
        .eq('church_id', targetChurchId)
        .eq('status', 'pending');
      for (const r of (reqs || [])) {
        const e = (r.profiles?.email || "").toLowerCase();
        if (e && !pendingByEmail.has(e)) pendingByEmail.set(e, r);
      }
    } catch (e) { console.warn('[invite] could not load pending access requests:', e); }

    const invited = [], granted = [], failed = [];
    let lastInvite = null;

    for (const email of emails) {
      const pending = pendingByEmail.get(email);
      if (pending) {
        // Already requested access → grant it. Promoting the profile + flipping
        // the request to 'approved' fires the DB trigger that creates the
        // in-app notification AND sends the push. We add the email on top.
        try {
          const profUp = await supabase.from('profiles')
            .update({ role: inviteRole, church_id: targetChurchId })
            .eq('id', pending.user_id).select();
          if (profUp.error) throw profUp.error;
          const reqUp = await supabase.from('access_requests')
            .update({ status: 'approved', reviewed_by: user.id, reviewed_at: new Date().toISOString() })
            .eq('user_id', pending.user_id).eq('church_id', targetChurchId).eq('status', 'pending')
            .select();
          if (reqUp.error) throw reqUp.error;
          await sendAccessGrantedEmail(email, churchName);
          granted.push(email);
        } catch (e) {
          failed.push({ email, reason: e?.message || "Could not grant access." });
        }
        continue;
      }
      // Otherwise create a normal invitation (+ invite email via the hook).
      const result = await sendInvite(email, inviteRole, isSuperAdmin ? selectedChurch : undefined);
      const { data, error, emailSent, emailError, inviteUrl } = result || {};
      if (error) { failed.push({ email, reason: error.message || "Invite failed." }); continue; }
      invited.push(email);
      lastInvite = { ...data, _emailSent: !!emailSent, _emailError: emailError || null, _inviteUrl: inviteUrl };
    }

    setInviteLoading(false);
    setBulkSummary({ invited, granted, failed });
    setInviteSent(invited.length + granted.length > 0);
    // Keep the copy-link affordance only when exactly one invitation was made
    // and nobody was auto-granted (the common single-invite case).
    if (invited.length === 1 && granted.length === 0 && lastInvite?.token) setInviteData(lastInvite);
    if (failed.length === 0) setInviteEmail("");
  }

  async function copyInviteLink() {
    if (!inviteData?.token) return;
    // Prefer the canonical prod URL the hook built, fall back to window origin.
    const link = inviteData._inviteUrl || `${window.location.origin}/?invite=${inviteData.token}`;
    try {
      await navigator.clipboard.writeText(link);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    } catch {
      window.prompt("Copy this invite link:", link);
    }
  }

  async function handleCreateChurch({ withInvite = false } = {}) {
    const name = churchName.trim();
    if (!name) return;
    if (withInvite && !adminEmail.trim()) return;
    setChurchSaving(true); setChurchError("");
    const { church, error, inviteError } = await createChurch(
      name,
      churchLoc.trim(),
      churchWeb.trim(),
      withInvite ? adminEmail.trim() : "",
    );
    setChurchSaving(false);
    if (error) {
      setChurchError(error.message || "Could not create church.");
      return;
    }
    setChurchCreated({
      name: church?.name || name,
      inviteEmail: withInvite ? adminEmail.trim() : null,
      inviteError: inviteError || null,
    });
    // Reload churches so the new one is selectable everywhere.
    loadChurches();
    // Auto-clear form + close back to profile after a short pause.
    setTimeout(() => {
      setChurchCreated(null);
      setChurchName(""); setChurchLoc(""); setChurchWeb(""); setAdminEmail("");
      setView("profile");
    }, 3500);
  }

  async function handleSaveName() {
    const next = nameDraft.trim();
    if (!next || next === (profile?.full_name || "")) return;
    setNameSaving(true); setNameError(""); setNameSaved(false);
    const { error } = await updateProfile({ full_name: next });
    setNameSaving(false);
    if (error) { setNameError(error.message || String(error)); return; }
    setNameSaved(true);
  }

  async function handleSendContact() {
    const text = contactDraft.trim();
    if (!text || contactSending) return;
    setContactSending(true); setContactError("");
    const { error } = await supabase.from('contact_messages').insert({
      user_id:   user?.id || null,
      email:     user?.email || profile?.email || null,
      full_name: profile?.full_name || null,
      message:   text,
    });
    setContactSending(false);
    if (error) { setContactError(error.message || String(error)); return; }
    setContactDraft("");
    setContactSent(true);
  }

  async function submitChurchRequest() {
    if (!reqName.trim() || !reqLocation.trim()) { setReqError("Church name and location are required."); return; }
    if (reqBusy) return;
    setReqBusy(true); setReqError("");
    const requesterName  = (profile?.full_name || user?.email?.split("@")[0] || "").trim();
    const requesterEmail = (user?.email || profile?.email || "").trim();
    try {
      const { error: insErr } = await supabase.from('church_requests').insert({
        requested_by:    user?.id || null,
        church_name:     reqName.trim(),
        location:        reqLocation.trim(),
        requester_name:  requesterName || null,
        requester_email: requesterEmail || null,
      });
      if (insErr) console.warn('church_requests insert failed:', insErr.message);
      await supabase.functions.invoke('submit-church-enquiry', {
        body: { type: 'church_request', name: requesterName || 'A user', email: requesterEmail,
                church: reqName.trim(), location: reqLocation.trim() },
      });
      setReqSent(true);
    } catch (e) {
      setReqError(e?.message || String(e));
    } finally {
      setReqBusy(false);
    }
  }

  return (
    <>
      <div className="overlay" onClick={onClose}/>
      <div className="sheet" style={{maxHeight:"90vh",display:"flex",flexDirection:"column"}}>
        <div className="shandle"/>

        {/* Header (docs/designs/ProfileElder.dc.html): 20px/800, no
            underline — the old look came from .shd's own border-bottom,
            not an actual text-decoration. */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 18px 0",flexShrink:0}}>
          <div style={{fontSize:20,fontWeight:800,color:"var(--ink)",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
            {view==="profile"   ? "Profile"
             : view==="invite"    ? "Invite Someone"
             : view==="church"    ? "Add New Church"
             : view==="settings"  ? "Account"
             : view==="reminder"  ? "Daily reminder"
             : view==="notifPrefs" ? "Notifications"
             : view==="contact"   ? "Contact us"
             : view==="following" ? "Churches you follow"
             : view==="request-church" ? "Request a church"
             : view==="allChurches" ? "All churches"
             : view==="churchRequests" ? "Church requests"
             : view==="platformStats" ? "Platform stats"
             : "Profile"}
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{width:36,height:36,borderRadius:18,border:"none",background:"var(--white)",color:"var(--ink)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
        </div>

        <div style={{flex:1,overflowY:"auto"}}>

          {/* ── PROFILE VIEW ── */}
          {view==="profile" && (
            <div style={{display:"flex", flexDirection:"column", gap:18, padding:"16px 18px 20px"}}>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:14}}>
                  {/* Tappable avatar w/ camera overlay. Opens a small
                      Change / Remove menu if an avatar is already set,
                      otherwise jumps straight to the file picker. */}
                  <div style={{position:"relative"}}>
                    <button type="button" onClick={onAvatarClick}
                      disabled={avatarBusy}
                      aria-label="Change profile photo"
                      style={{background:"none",border:"none",padding:0,cursor:avatarBusy?"default":"pointer",display:"block",borderRadius:"50%"}}>
                      <Avatar
                        url={profile?.avatar_url || null}
                        name={profile?.full_name || user?.email}
                        size={64}
                        style={{border:"2px solid var(--gold)"}}
                      />
                    </button>
                    {/* Camera overlay badge (top-right) — white circle, gold-ink camera icon */}
                    <div style={{
                      position:"absolute", right:-2, bottom:-2,
                      width:22, height:22, borderRadius:11,
                      background:"var(--white)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      pointerEvents:"none",
                    }} aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 8h3l2-3h6l2 3h3v11H4z"/><circle cx="12" cy="13" r="3.5"/></svg>
                    </div>

                    {/* Change / Remove popup (shown only when a photo already exists) */}
                    {avatarMenuOpen && (
                      <>
                        <div onClick={()=>setAvatarMenuOpen(false)}
                          style={{position:"fixed",inset:0,zIndex:60}}/>
                        <div style={{
                          position:"absolute", top:"100%", left:0, marginTop:8, zIndex:61,
                          background:"var(--white)", border:"1px solid var(--border-strong)",
                          borderRadius:10, minWidth:170, overflow:"hidden",
                          boxShadow:"0 10px 24px rgba(0,0,0,0.2)",
                        }}>
                          <div onClick={()=>{ setAvatarMenuOpen(false); avatarInputRef.current?.click(); }}
                            style={{padding:"10px 14px",cursor:"pointer",fontSize:13,color:"var(--ink)",borderBottom:"1px solid var(--border)"}}>
                            Change image
                          </div>
                          <div onClick={handleAvatarRemove}
                            style={{padding:"10px 14px",cursor:"pointer",fontSize:13,color:"#C8372A"}}>
                            Remove image
                          </div>
                        </div>
                      </>
                    )}

                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      style={{display:"none"}}
                      onChange={e => handleAvatarFile(e.target.files?.[0])}
                    />
                  </div>
                  <div style={{minWidth:0, flex:1}}>
                    <div style={{fontSize:20,fontWeight:800,color:"var(--ink)"}}>{fullName}</div>
                    <div style={{fontSize:12.5,color:"var(--stone)",marginTop:2}}>{user?.email || profile?.email || ""}</div>
                  </div>
                  <button type="button" onClick={()=>setView("settings")}
                    style={{flexShrink:0, background:"none", border:"none", color:"var(--gold-ink)", fontSize:13, fontWeight:700, cursor:"pointer", padding:0, fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                    Edit
                  </button>
                </div>
                {avatarError && <div style={{fontSize:12,color:"#C8372A",marginBottom:8}}>{avatarError}</div>}
                {avatarBusy   && <div style={{fontSize:12,color:"var(--stone)",marginBottom:8}}>Uploading…</div>}
              </div>

              {/* ── Notifications (docs/FIX-PROFILE.md + FIX-HEADERS.md,
                  reconciled: no bell — the This Week avatar's red badge is
                  the only unread indicator, and it opens here). First card,
                  right under the header. Opens the existing NotificationsPage. ── */}
              <ProfileCard>
                <ProfileRow first
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9a6 6 0 0 1 12 0c0 6 2.5 7.5 2.5 7.5h-17S6 15 6 9z"/></svg>}
                  title="Notifications"
                  subtitle={notifications[0]?.title || "No notifications yet"}
                  badge={notifUnread}
                  onClick={() => onOpenNotifications?.()}/>
              </ProfileCard>

              {/* Home church + Following (docs/designs/ProfileElder.dc.html).
                  "Change" is now offered to EVERYONE with a home church, not
                  just super admins / multi-church admins (who get it via the
                  same picker, already scoped to their own admin churches by
                  the existing `scoped` filter below) — the gear icon is gone. */}
              {(activeChurch || canSwitchChurch || noHomeChurch) && (() => {
                const homeName = activeChurch?.name || (activeChurchId ? "Unknown church" : "Choose a church");
                let hash = 0;
                for (const ch of String(activeChurchId || homeName)) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
                const homePastel = CIRCLE_PASTELS[hash % CIRCLE_PASTELS.length];
                return (
                  <div style={{display:"flex", flexDirection:"column", gap:6}}>
                    <ProfileSectionLabel>Your churches</ProfileSectionLabel>
                    {switchedMsg && (
                      <div style={{
                        marginBottom:8, padding:"8px 12px",
                        background:"rgba(80,200,150,0.10)", border:"1px solid rgba(80,200,150,0.30)",
                        borderRadius:8, color:"#1F7A52", fontSize:13,
                      }}>✓ {switchedMsg}</div>
                    )}
                    {switchError && (
                      <div style={{
                        marginBottom:8, padding:"8px 12px",
                        background:"rgba(249,112,102,0.10)", border:"1px solid rgba(249,112,102,0.30)",
                        borderRadius:8, color:"#C8372A", fontSize:13,
                      }}>{switchError}</div>
                    )}
                    <ProfileCard>
                    {/* Home church row: logo/initials circle, house icon +
                        "HOME CHURCH" label above the name, gold Change —
                        available to everyone with a home church, not just
                        admins. No gear anywhere. */}
                    {!noHomeChurch && (
                    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 0" }}>
                      <span style={{ flexShrink:0, width:40, height:40, borderRadius:20, overflow:"hidden", background:homePastel.bg, color:homePastel.ink, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800 }}>
                        <CircleLogoOrInitials url={activeChurch?.logo_url} name={homeName}/>
                      </span>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{display:"flex", alignItems:"center", gap:5, fontSize:11, fontWeight:800, color:PROFILE_GOLD, textTransform:"uppercase", letterSpacing:"0.05em"}}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 11l9-7 9 7v9H3z"/></svg>
                          Home church
                        </div>
                        <div style={{fontSize:15, fontWeight:800, color:"var(--ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{homeName}</div>
                        {activeChurch?.location && (
                          <div style={{fontSize:12, color:"var(--stone)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{activeChurch.location}</div>
                        )}
                      </div>
                      <button
                        onClick={()=>{setPickerOpen(o => !o); setChurchSearch("");}}
                        aria-label="Change home church"
                        aria-expanded={pickerOpen}
                        style={{
                          flexShrink:0,
                          background:"none", border:"none",
                          color: PROFILE_GOLD,
                          padding:0, cursor:"pointer", fontSize:12.5, fontWeight:700,
                          fontFamily:"'Plus Jakarta Sans',sans-serif",
                        }}>
                        Change
                      </button>
                    </div>
                    )}

                    {/* No home church: full-width search field, styled like the
                        "Search to follow another church" box (search icon left). */}
                    {noHomeChurch && (
                      <div style={{
                        display:"flex", alignItems:"center", gap:8,
                        padding:"10px 12px",
                        border:"1px dashed var(--border-strong)",
                        borderRadius:10, background:"transparent",
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{flexShrink:0, color:"var(--stone-dim)"}}>
                          <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2"/>
                          <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <input
                          placeholder="Search for your church"
                          value={churchSearch}
                          onChange={e=>setChurchSearch(e.target.value)}
                          onFocus={()=>setChurchFieldFocused(true)}
                          style={{
                            flex:1, minWidth:0,
                            background:"transparent", border:"none", outline:"none",
                            color:"var(--ink)", fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif",
                          }}/>
                      </div>
                    )}

                    {/* Inline picker — when "Change" is tapped (everyone with
                        a home church now, not just admins) or always for a
                        user with no home church (so they can join one). Same
                        search + list + confirm + Save & reload commit step. */}
                    {(pickerOpen || noHomeChurch) && (
                      <div style={{marginTop:12}}>
                        {!noHomeChurch && (
                          <input className="church-search-input" placeholder="Choose a church" autoFocus
                            value={churchSearch} onChange={e=>setChurchSearch(e.target.value)}/>
                        )}
                        {(!noHomeChurch || churchFieldFocused || churchSearch.trim()) && (
                        <div style={{maxHeight:240,overflowY:"auto",border:"1px solid var(--border)",borderRadius:10,marginTop:8}}>
                          {churchesLoading && churches.length===0 ? (
                            <div style={{padding:"12px",fontSize:13,color:"var(--stone-dim)"}}>Loading churches…</div>
                          ) : churchesError && churches.length===0 ? (
                            <div style={{padding:"12px",fontSize:13,color:"#C8372A"}}>Couldn't load churches: {churchesError}</div>
                          ) : churches.length===0 ? (
                            <div style={{padding:"12px",fontSize:13,color:"var(--stone-dim)",lineHeight:1.5}}>
                              No churches returned. If you can see rows in the Supabase table editor, RLS is blocking the API read.
                              <div style={{marginTop:8,color:"var(--stone)"}}>
                                Open the Supabase SQL editor and run:
                                <pre style={{marginTop:6,padding:"10px 12px",background:"#000",border:"1px solid var(--border)",borderRadius:8,fontSize:11,color:"var(--gold-ink)",whiteSpace:"pre-wrap",fontFamily:"ui-monospace,SFMono-Regular,Menlo,monospace"}}>{`alter table public.churches enable row level security;
drop policy if exists "Anyone can read churches" on public.churches;
create policy "Anyone can read churches"
  on public.churches for select using (true);`}</pre>
                                <div style={{marginTop:6,fontSize:11,color:"var(--stone-dim)"}}>
                                  (Or apply <code style={{color:"var(--gold-ink)"}}>supabase-schema-churches-read-fix.sql</code> from the repo - same SQL, packaged.)
                                </div>
                              </div>
                            </div>
                          ) : (() => {
                            // Super admins see every church. Genuine multi-church
                            // admins (adminChurches tracks the churches they
                            // administer) are scoped to just those. Everyone else —
                            // a user with no home church yet, OR a regular member
                            // now using "Change" — sees the same open list,
                            // minus invite-only churches (those need an invite).
                            const scoped = isSuperAdmin
                              ? filteredChurches
                              : (noHomeChurch || adminChurches.length === 0)
                                ? filteredChurches.filter(c => !c.invite_only)
                                : filteredChurches.filter(c => adminChurches.some(ac => ac.church_id === c.id));
                            if (scoped.length === 0) {
                              return <div style={{padding:"12px",fontSize:13,color:"var(--stone-dim)"}}>No churches match "{churchSearch}".</div>;
                            }
                            return scoped.map(c => {
                              const isPending = pendingChurchId === c.id;
                              const isActive  = activeChurchId === c.id;
                              return (
                                <div key={c.id}
                                  className={`church-option ${isActive ? "selected" : ""}`}
                                  onClick={() => selectPendingChurch(c)}
                                  style={isPending ? {
                                    outline:"2px solid var(--gold)",
                                    outlineOffset:"-2px",
                                    background:"rgba(232,200,122,0.08)",
                                  } : undefined}>
                                  {c.name}
                                  {c.location && <div className="church-option-sub">{c.location}</div>}
                                  {isActive && (
                                    <div style={{fontSize:10,color:"var(--gold-ink)",marginTop:2,letterSpacing:"0.08em",textTransform:"uppercase",fontWeight:700}}>Current</div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                        )}
                        {pendingChurchId && pendingChurchId !== activeChurchId && (() => {
                          const pending = churches.find(c => c.id === pendingChurchId);
                          if (!pending) return null;
                          return (
                            <div style={{
                              marginTop:10, padding:"10px 12px",
                              background:"rgba(232,200,122,0.08)",
                              border:"1px solid rgba(232,200,122,0.30)",
                              borderRadius:8, display:"flex", alignItems:"center",
                              gap:10, flexWrap:"wrap",
                            }}>
                              <div style={{flex:1, minWidth:160, fontSize:13, color:"var(--ink)"}}>
                                {noHomeChurch ? "Join" : "Switch to"} <strong>{pending.name}</strong>?
                                <div style={{fontSize:11, color:"var(--stone-dim)", marginTop:2}}>
                                  The page will reload so every section refreshes against the new church.
                                </div>
                              </div>
                              <button
                                onClick={() => setChangeChurchConfirmOpen(true)}
                                disabled={switchSaving}
                                style={{
                                  background:"var(--gold)", color:"var(--ink)", border:"none",
                                  borderRadius:8, padding:"8px 14px", fontSize:13, fontWeight:800,
                                  cursor: switchSaving ? "default" : "pointer",
                                  opacity: switchSaving ? 0.5 : 1,
                                  fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:"nowrap",
                                }}>
                                {switchSaving ? "Saving…" : (noHomeChurch ? "Join" : "Change")}
                              </button>
                            </div>
                          );
                        })()}
                        {!noHomeChurch && (
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
                            <button
                              onClick={()=>{ setPickerOpen(false); setPendingChurchId(""); }}
                              style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:12,cursor:"pointer",padding:0}}>
                              Cancel
                            </button>
                            <button
                              onClick={loadChurches}
                              disabled={churchesLoading}
                              style={{background:"none",border:"none",color:"var(--gold-ink)",fontSize:12,cursor:churchesLoading?"default":"pointer",padding:0,opacity:churchesLoading?0.4:1}}>
                              {churchesLoading ? "Refreshing…" : "Refresh list"}
                            </button>
                          </div>
                        )}

                        {/* Can't find a church → request we add it (by request
                            only — self-service church setup has been removed). */}
                        {noHomeChurch && (
                          <div style={{marginTop:12, fontSize:13, color:"var(--stone)", lineHeight:1.5}}>
                            Can't find a church —{" "}
                            <span onClick={()=>setView("request-church")}
                              style={{color:"var(--gold-ink)", textDecoration:"underline", cursor:"pointer"}}>
                              request we add it
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Following row — same for all 3 "Your churches" states.
                        Overlapping avatars when there's at least one followed
                        church beyond home; a plain "Follow churches" CTA when
                        there's none yet (docs/designs/ProfileStates.dc.html). */}
                    {(() => {
                      const followedIds = (Array.isArray(sermonNoteChurchIds) ? sermonNoteChurchIds : [])
                        .filter(id => id && id !== (activeChurchId || profile?.church_id));
                      const n = followedIds.length;
                      return (
                        <div style={{display:"flex", alignItems:"center", gap:10, padding:"11px 0", borderTop:`1px solid ${PROFILE_DIVIDER}`}}>
                          {n > 0 ? (
                            <>
                              <div style={{display:"flex", flexShrink:0}}>
                                {followedIds.slice(0,4).map((id, j) => {
                                  const c = churches.find(ch => ch.id === id);
                                  const pastel = CIRCLE_PASTELS[j % CIRCLE_PASTELS.length];
                                  return (
                                    <span key={id} style={{marginLeft:j===0?0:-7, width:26, height:26, borderRadius:13, overflow:"hidden", border:"2px solid var(--white)", background:pastel.bg, color:pastel.ink, fontSize:9, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center"}}>
                                      <CircleLogoOrInitials url={c?.logo_url} name={c?.name}/>
                                    </span>
                                  );
                                })}
                              </div>
                              <div style={{flex:1, minWidth:0, fontSize:14.5, fontWeight:700, color:"var(--ink)"}}>Following {n} church{n===1?"":"es"}</div>
                              <button type="button" onClick={()=>setView("following")}
                                style={{flexShrink:0, background:"none", border:"none", color:PROFILE_GOLD, fontSize:12.5, fontWeight:700, cursor:"pointer", padding:0, fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                                Manage
                              </button>
                              <ProfileChevron/>
                            </>
                          ) : (
                            <button type="button" onClick={()=>setView("following")}
                              style={{flex:1, display:"flex", alignItems:"center", gap:10, background:"none", border:"none", cursor:"pointer", padding:0, textAlign:"left", fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                              <div style={{flex:1, minWidth:0}}>
                                <div style={{fontSize:14.5, fontWeight:700, color:"var(--ink)"}}>Follow churches</div>
                                <div style={{fontSize:12, color:"var(--stone)", marginTop:1}}>See their teaching alongside your own</div>
                              </div>
                              <ProfileChevron/>
                            </button>
                          )}
                        </div>
                      );
                    })()}
                    </ProfileCard>
                  </div>
                );
              })()}

              {/* ── [Church] leaders — elders/admins of the HOME church only
                  (docs/FIX-PROFILE.md §3). The long Church Management list is
                  gone; everything it linked to now lives in the Leaders
                  dashboard, reached from the one row below. ── */}
              {isElder && (
                <div style={{display:"flex", flexDirection:"column", gap:6}}>
                  <ProfileSectionLabel>{activeChurch?.name ? `${activeChurch.name} leaders` : "Leaders"}</ProfileSectionLabel>
                  <ProfileCard>
                    <ProfileRow first titleWeight={800}
                      icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z"/></svg>}
                      title="Leaders dashboard" subtitle="Join requests, sermons, moderation, settings"
                      badge={leaderBadgeCount} onClick={()=>onOpenLeaders?.()}/>
                    <ProfileRow
                      icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 6h16v12H4z"/><path d="M4 7l8 6 8-6"/></svg>}
                      title="Invite someone" subtitle="Share the join link or QR code"
                      onClick={()=>setView("invite")}/>
                  </ProfileCard>
                </div>
              )}

              {/* ── Super admin — platform-level, not tied to the home church ── */}
              {isSuperAdmin && (
                <div style={{display:"flex", flexDirection:"column", gap:6}}>
                  <ProfileSectionLabel>Super admin</ProfileSectionLabel>
                  <ProfileCard>
                    <ProfileRow first
                      icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2v5M10 4h4M5 21V11l7-4 7 4v10M9 21v-5h6v5"/></svg>}
                      title="All churches" subtitle={`${churches.length} churches · search, edit, verify`}
                      onClick={()=>setView("allChurches")}/>
                    <ProfileRow
                      icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16v16H4z"/><path d="M8 9h8M8 13h5"/></svg>}
                      title="Church requests" subtitle="Leaders asking to set up a church"
                      badge={churchRequestsBadgeCount} onClick={()=>setView("churchRequests")}/>
                    <ProfileRow
                      icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>}
                      title="Add a church" subtitle="Create a church and invite its leaders"
                      onClick={()=>setView("church")}/>
                    <ProfileRow
                      icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/></svg>}
                      title="Platform stats" subtitle="Members, churches, sermons processed"
                      onClick={()=>setView("platformStats")}/>
                  </ProfileCard>
                </div>
              )}

              {/* ── Settings ── */}
              <div style={{display:"flex", flexDirection:"column", gap:6}}>
                <ProfileSectionLabel>Settings</ProfileSectionLabel>
                <ProfileCard>
                  <ProfileRow first
                    icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7V5h16v2M9 19h6M12 5v14"/></svg>}
                    title="Reading" subtitle="Text size, dyslexia-friendly font"
                    onClick={()=>onOpenReadingSettings?.()}/>
                  <ProfileRow
                    icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>}
                    title="Daily reminder" subtitle={profile?.reminder_time ? `Reminds you at ${profile.reminder_time}` : "Off"}
                    onClick={()=>setView("reminder")}/>
                  <ProfileRow
                    icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9a6 6 0 0 1 12 0c0 6 2.5 7.5 2.5 7.5h-17S6 15 6 9z"/></svg>}
                    title="Notifications" subtitle="Choose what you hear about"
                    onClick={()=>setView("notifPrefs")}/>
                  <ProfileRow
                    icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/></svg>}
                    title="Account" subtitle="Name, email, delete account"
                    onClick={()=>setView("settings")}/>
                </ProfileCard>
              </div>

              {/* ── Help ── */}
              <div style={{display:"flex", flexDirection:"column", gap:6}}>
                <ProfileSectionLabel>Help</ProfileSectionLabel>
                <ProfileCard>
                  <ProfileRow first
                    icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .9-1 1.7M12 17h.01"/></svg>}
                    title="App tour" subtitle="See how it works again"
                    onClick={()=>onStartWalkthrough?.()}/>
                  <ProfileRow
                    icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 21V4h11l-2 4 2 4H5"/></svg>}
                    title="Report a problem"
                    onClick={()=>onReportProblem?.()}/>
                  <ProfileRow
                    icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PROFILE_GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 6h16v12H4z"/><path d="M4 7l8 6 8-6"/></svg>}
                    title="Contact us"
                    onClick={()=>setView("contact")}/>
                </ProfileCard>
              </div>

              {/* ── Sign out — its own card, centred red text, no icon/chevron. ── */}
              <button type="button" onClick={signOut}
                style={{ background:"var(--white)", border:"none", borderRadius:20, padding:13, textAlign:"center", fontSize:14.5, fontWeight:700, color:"#C2412D", cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                Sign out
              </button>

              {/* Small footer link to the privacy policy. Opens in a new
                  tab so the user doesn't lose their place inside the app. */}
              <div style={{textAlign:"center", paddingBottom:4}}>
                <a href="/privacy" target="_blank" rel="noreferrer"
                  style={{
                    fontSize:11, color:"var(--stone-dim)",
                    textDecoration:"underline", letterSpacing:"0.06em",
                    fontFamily:"'Plus Jakarta Sans',sans-serif",
                  }}>
                  Privacy policy
                </a>
              </div>
            </div>
          )}

          {/* ── DELETE ACCOUNT CONFIRMATION DIALOG ── */}
          {deleteConfirmOpen && (
            <>
              <div
                onClick={() => { if (!deleting) setDeleteConfirmOpen(false); }}
                style={{
                  position:"fixed", inset:0, zIndex:200,
                  background:"rgba(0,0,0,0.65)", backdropFilter:"blur(2px)",
                }}/>
              <div
                role="dialog" aria-modal="true" aria-labelledby="del-acct-title"
                style={{
                  position:"fixed", zIndex:201,
                  top:"50%", left:"50%", transform:"translate(-50%, -50%)",
                  width:"min(380px, calc(100vw - 32px))",
                  background:"var(--white)",
                  border:"1px solid var(--border-strong)",
                  borderRadius:16,
                  padding:"22px 22px 18px",
                  fontFamily:"'Plus Jakarta Sans',sans-serif",
                  boxShadow:"0 24px 60px rgba(0,0,0,0.25)",
                }}>
                <div id="del-acct-title" style={{fontSize:18, fontWeight:700, color:"#C8372A", marginBottom:10}}>
                  Delete your account?
                </div>
                <div style={{fontSize:14, color:"var(--stone)", lineHeight:1.55, marginBottom:18}}>
                  This will permanently delete your profile, posts, comments and saved verses. This cannot be undone.
                </div>
                {deleteError && (
                  <div style={{
                    fontSize:12, color:"#C8372A",
                    background:"rgba(249,112,102,0.10)",
                    border:"1px solid rgba(249,112,102,0.30)",
                    borderRadius:8, padding:"8px 10px", marginBottom:14,
                  }}>
                    {deleteError}
                  </div>
                )}
                <div style={{display:"flex", gap:10}}>
                  <button
                    type="button"
                    disabled={deleting}
                    onClick={() => setDeleteConfirmOpen(false)}
                    style={{
                      flex:1,
                      background:"transparent",
                      border:"1px solid var(--border-strong)",
                      borderRadius:10, padding:"11px",
                      fontSize:14, fontWeight:600,
                      color:"var(--ink)",
                      cursor: deleting ? "default" : "pointer",
                      opacity: deleting ? 0.5 : 1,
                      fontFamily:"'Plus Jakarta Sans',sans-serif",
                    }}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={deleting}
                    onClick={handleDeleteAccount}
                    style={{
                      flex:1,
                      background:"#F97066",
                      border:"none",
                      borderRadius:10, padding:"11px",
                      fontSize:14, fontWeight:700,
                      color:"#fff",
                      cursor: deleting ? "default" : "pointer",
                      opacity: deleting ? 0.7 : 1,
                      fontFamily:"'Plus Jakarta Sans',sans-serif",
                    }}>
                    {deleting ? "Deleting…" : "Delete account"}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ── CHANGE HOME CHURCH CONFIRMATION DIALOG (docs/FIX-PROFILE.md
              §2: "it changes their This Week and devotional") ── */}
          {changeChurchConfirmOpen && (() => {
            const pending = churches.find(c => c.id === pendingChurchId);
            if (!pending) return null;
            return (
              <>
                <div
                  onClick={() => { if (!switchSaving) setChangeChurchConfirmOpen(false); }}
                  style={{ position:"fixed", inset:0, zIndex:200, background:"rgba(0,0,0,0.65)", backdropFilter:"blur(2px)" }}/>
                <div
                  role="dialog" aria-modal="true" aria-labelledby="change-church-title"
                  style={{
                    position:"fixed", zIndex:201,
                    top:"50%", left:"50%", transform:"translate(-50%, -50%)",
                    width:"min(380px, calc(100vw - 32px))",
                    background:"var(--white)", border:"1px solid var(--border-strong)",
                    borderRadius:16, padding:"22px 22px 18px",
                    fontFamily:"'Plus Jakarta Sans',sans-serif",
                    boxShadow:"0 24px 60px rgba(0,0,0,0.25)",
                  }}>
                  <div id="change-church-title" style={{fontSize:18, fontWeight:700, color:"var(--ink)", marginBottom:10}}>
                    {noHomeChurch ? `Join ${pending.name}?` : `Change home church to ${pending.name}?`}
                  </div>
                  <div style={{fontSize:14, color:"var(--stone)", lineHeight:1.55, marginBottom:18}}>
                    This changes your This Week and devotional to {pending.name}.{pending.invite_only ? " This church needs approval, so you'll see \"Requested\" until it's accepted." : ""} The page will reload.
                  </div>
                  {switchError && (
                    <div style={{ fontSize:12, color:"#C8372A", background:"rgba(249,112,102,0.10)", border:"1px solid rgba(249,112,102,0.30)", borderRadius:8, padding:"8px 10px", marginBottom:14 }}>
                      {switchError}
                    </div>
                  )}
                  <div style={{display:"flex", gap:10}}>
                    <button type="button" disabled={switchSaving}
                      onClick={() => setChangeChurchConfirmOpen(false)}
                      style={{ flex:1, background:"transparent", border:"1px solid var(--border-strong)", borderRadius:10, padding:"11px", fontSize:14, fontWeight:600, color:"var(--ink)", cursor: switchSaving ? "default" : "pointer", opacity: switchSaving ? 0.5 : 1, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                      Cancel
                    </button>
                    <button type="button" disabled={switchSaving}
                      onClick={async () => { await saveAndReloadToChurch(); }}
                      style={{ flex:1, background:"var(--gold)", border:"none", borderRadius:10, padding:"11px", fontSize:14, fontWeight:800, color:"var(--ink)", cursor: switchSaving ? "default" : "pointer", opacity: switchSaving ? 0.7 : 1, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                      {switchSaving ? "Saving…" : (noHomeChurch ? "Join" : "Change")}
                    </button>
                  </div>
                </div>
              </>
            );
          })()}

          {/* ── SETTINGS VIEW ── */}
          {view==="settings" && (
            <div className="profile-section" style={{borderTop:"none"}}>
              <button onClick={()=>setView("profile")} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:13,cursor:"pointer",padding:"0 0 16px 0",display:"block"}}>← Back</button>

              <div className="profile-section-title">Your name</div>
              <input
                type="text"
                value={nameDraft}
                onChange={e=>{ setNameDraft(e.target.value); setNameSaved(false); setNameError(""); }}
                placeholder="e.g. Sarah Mitchell"
                style={{width:"100%",background:"var(--white)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 12px",color:"var(--ink)",fontSize:14,fontFamily:"'Plus Jakarta Sans',sans-serif",outline:"none",boxSizing:"border-box"}}/>
              {nameError && <div style={{marginTop:8,fontSize:12,color:"#C8372A"}}>{nameError}</div>}
              <button type="button"
                onClick={handleSaveName}
                disabled={nameSaving || !nameDraft.trim() || nameDraft.trim() === (profile?.full_name || "")}
                style={{
                  marginTop:10, width:"100%",
                  background:"var(--gold)", color:"var(--ink)", border:"none",
                  borderRadius:10, padding:"11px", fontSize:14, fontWeight:800,
                  cursor:(nameSaving || !nameDraft.trim() || nameDraft.trim() === (profile?.full_name || "")) ? "default" : "pointer",
                  opacity:(nameSaving || !nameDraft.trim() || nameDraft.trim() === (profile?.full_name || "")) ? 0.4 : 1,
                  fontFamily:"'Plus Jakarta Sans',sans-serif",
                }}>
                {nameSaving ? "Saving…" : nameSaved ? "Saved ✓" : "Save name"}
              </button>

              <div className="profile-section-title" style={{marginTop:20}}>Email</div>
              <div style={{padding:"10px 12px",background:"var(--white)",border:"1px solid var(--border)",borderRadius:10,color:"var(--stone)",fontSize:14,wordBreak:"break-all"}}>
                {user?.email || profile?.email || "—"}
              </div>
              <div style={{fontSize:11,color:"var(--stone-dim)",marginTop:4}}>Your sign-in email — can't be changed here.</div>

              <div className="profile-section-title" style={{marginTop:20}}>Role</div>
              <div style={{padding:"10px 12px",background:"var(--white)",border:"1px solid var(--border)",borderRadius:10,color:"var(--stone)",fontSize:14,textTransform:"capitalize"}}>
                {(profile?.role || "visitor").replace(/_/g, " ")}
              </div>
              <div style={{fontSize:11,color:"var(--stone-dim)",marginTop:4}}>Set by your church — request a role change via Contact us.</div>

              <button
                type="button"
                onClick={() => { setDeleteError(''); setDeleteConfirmOpen(true); }}
                style={{
                  marginTop:20, width:"100%",
                  background:"transparent",
                  border:"1px solid rgba(249,112,102,0.35)",
                  borderRadius:10,
                  padding:"12px",
                  fontSize:14, fontWeight:600,
                  color:"#C8372A",
                  cursor:"pointer",
                  fontFamily:"'Plus Jakarta Sans',sans-serif",
                }}>
                Delete account
              </button>
            </div>
          )}

          {/* ── DAILY REMINDER VIEW — reads/writes profiles.reminder_time
              directly (the same column ReminderPromptSheet's first-run
              nudge writes to; this is the "come back and change it anytime"
              counterpart, not a duplicate of that flow). ── */}
          {view==="reminder" && (
            <div className="profile-section" style={{borderTop:"none"}}>
              <button onClick={()=>setView("profile")} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:13,cursor:"pointer",padding:"0 0 16px 0",display:"block"}}>← Back</button>
              <div style={{fontSize:14,color:"var(--stone)",lineHeight:1.5,marginBottom:16}}>
                A daily nudge to keep up with your reading and devotional.
              </div>
              <div className="profile-section-title">Time</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:8,marginBottom:16}}>
                {["6:30am","7:00am","12:30pm","9:00pm"].map(t => (
                  <button key={t} type="button"
                    onClick={async()=>{ await updateProfile({ reminder_time: t }); }}
                    style={{
                      border:"none",borderRadius:999,padding:"9px 16px",fontSize:13,fontWeight:700,
                      cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif",
                      background: profile?.reminder_time===t ? "var(--gold)" : "var(--white)",
                      color: "var(--ink)",
                    }}>
                    {t}
                  </button>
                ))}
              </div>
              <button type="button"
                onClick={async()=>{ await updateProfile({ reminder_time: null }); }}
                disabled={!profile?.reminder_time}
                style={{
                  width:"100%", background:"transparent", border:"1px solid var(--border-strong)",
                  borderRadius:10, padding:"11px", fontSize:14, fontWeight:600, color:"var(--ink)",
                  cursor: profile?.reminder_time ? "pointer" : "default", opacity: profile?.reminder_time ? 1 : 0.4,
                  fontFamily:"'Plus Jakarta Sans',sans-serif",
                }}>
                Turn off
              </button>
            </div>
          )}

          {/* ── NOTIFICATION PREFERENCES VIEW — saves which categories you
              want, but nothing reads this yet: no notification trigger
              checks it, so turning a category off doesn't suppress it. Said
              plainly in-sheet below rather than pretending it's enforced. ── */}
          {view==="notifPrefs" && (() => {
            const prefs = profile?.notification_prefs || {};
            const categories = [
              ["prayer", "Prayer requests"],
              ["replies", "Replies to your comments"],
              ["events", "Church events"],
              ["notices", "Church notices"],
              ["devotionals", "New devotionals"],
            ];
            return (
              <div className="profile-section" style={{borderTop:"none"}}>
                <button onClick={()=>setView("profile")} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:13,cursor:"pointer",padding:"0 0 16px 0",display:"block"}}>← Back</button>
                <div style={{fontSize:13,color:"var(--stone)",lineHeight:1.5,marginBottom:16,background:"rgba(232,200,122,0.10)",border:"1px solid rgba(232,200,122,0.3)",borderRadius:10,padding:"10px 12px"}}>
                  Your choice is saved, but switching a category off doesn't stop those notifications yet — that part isn't built.
                </div>
                {categories.map(([key, label]) => {
                  const on = prefs[key] !== false;
                  return (
                    <button key={key} type="button"
                      onClick={async()=>{ await updateProfile({ notification_prefs: { ...prefs, [key]: !on } }); }}
                      style={{display:"flex",alignItems:"center",gap:12,width:"100%",background:"none",border:"none",borderTop:"1px solid var(--border)",padding:"12px 0",cursor:"pointer",textAlign:"left",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                      <span style={{flex:1,fontSize:14,fontWeight:600,color:"var(--ink)"}}>{label}</span>
                      <span style={{width:40,height:24,borderRadius:12,padding:2,boxSizing:"border-box",display:"flex",flexShrink:0,justifyContent:on?"flex-end":"flex-start",background:on?"var(--ink)":"var(--border-strong)"}}>
                        <span style={{width:20,height:20,borderRadius:10,background:on?"var(--gold)":"var(--white)"}}/>
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {/* ── CONTACT US VIEW ── */}
          {view==="contact" && (
            <div className="profile-section" style={{borderTop:"none"}}>
              <button onClick={()=>setView("profile")} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:13,cursor:"pointer",padding:"0 0 16px 0",display:"block"}}>← Back</button>

              <div style={{fontSize:20,fontWeight:700,color:"var(--ink)",marginBottom:4}}>Got questions or feedback?</div>
              <div style={{fontSize:14,color:"var(--stone)",lineHeight:1.5,marginBottom:16}}>We'd love to hear from you.</div>

              {contactSent && (
                <div style={{
                  background:"rgba(80,200,150,0.10)", border:"1px solid rgba(80,200,150,0.30)",
                  borderRadius:10, padding:"12px 14px", fontSize:14, color:"#1F7A52",
                  lineHeight:1.5, marginBottom:14,
                }}>
                  Thank you for sending us a message!
                </div>
              )}

              <textarea className="post-textarea" rows={5}
                placeholder="Type your message here…"
                value={contactDraft}
                onChange={e=>setContactDraft(e.target.value)}/>
              {contactError && <div style={{marginTop:8,fontSize:12,color:"#C8372A"}}>{contactError}</div>}
              <button type="button"
                onClick={handleSendContact}
                disabled={contactSending || !contactDraft.trim()}
                style={{
                  marginTop:10, width:"100%",
                  background:"var(--gold)", color:"var(--ink)", border:"none",
                  borderRadius:10, padding:"12px", fontSize:14, fontWeight:800,
                  cursor:(contactSending || !contactDraft.trim()) ? "default" : "pointer",
                  opacity:(contactSending || !contactDraft.trim()) ? 0.4 : 1,
                  fontFamily:"'Plus Jakarta Sans',sans-serif",
                }}>
                {contactSending ? "Sending…" : "Send message"}
              </button>
            </div>
          )}

          {/* ── FOLLOWING VIEW ── */}
          {view==="following" && (
            <div className="profile-section" style={{borderTop:"none"}}>
              <button onClick={()=>setView("profile")} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:13,cursor:"pointer",padding:"0 0 8px 0",display:"block"}}>← Back</button>
              <SermonNoteChurchesPicker
                churches={churches}
                activeChurchId={activeChurchId || profile?.church_id || ''}
                sermonNoteChurchIds={sermonNoteChurchIds}
                setSermonNoteChurchIds={setSermonNoteChurchIds}
                onRequestChurch={()=>setView("request-church")}/>
            </div>
          )}

          {/* ── REQUEST A CHURCH VIEW ── (same form/endpoint as onboarding) */}
          {view==="request-church" && (
            <div className="profile-section" style={{borderTop:"none"}}>
              <button onClick={()=>setView("profile")} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:13,cursor:"pointer",padding:"0 0 16px 0",display:"block"}}>← Back</button>
              {reqSent ? (
                <div>
                  <div style={{fontSize:20,fontWeight:700,color:"var(--ink)",marginBottom:6}}>Request sent!</div>
                  <div style={{fontSize:14,color:"var(--stone)",lineHeight:1.5,marginBottom:18}}>
                    We'll add {reqName.trim() || "the church"} and let you know when it's ready — usually within 24 hours.
                  </div>
                  <button type="button" onClick={()=>{ setReqSent(false); setReqName(""); setReqLocation(""); setView("profile"); }}
                    style={{width:"100%",background:"var(--gold)",color:"var(--ink)",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                    Done
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{fontSize:20,fontWeight:700,color:"var(--ink)",marginBottom:4}}>Request we add a church</div>
                  <div style={{fontSize:14,color:"var(--stone)",lineHeight:1.5,marginBottom:16}}>
                    We'll add all of this church's recorded sermons — usually within 24 hours.
                  </div>
                  <div className="profile-section-title">Church name *</div>
                  <input className="church-search-input" placeholder="e.g. Hope Church"
                    value={reqName} onChange={e=>setReqName(e.target.value)} style={{marginBottom:12}}/>
                  <div className="profile-section-title">Location *</div>
                  <input className="church-search-input" placeholder="Town / city"
                    value={reqLocation} onChange={e=>setReqLocation(e.target.value)} style={{marginBottom:12}}/>
                  {reqError && <div style={{fontSize:12,color:"#C8372A",marginBottom:8}}>{reqError}</div>}
                  <button type="button" onClick={submitChurchRequest} disabled={reqBusy}
                    style={{
                      marginTop:4, width:"100%", background:"var(--gold)", color:"var(--ink)", border:"none",
                      borderRadius:10, padding:"12px", fontSize:14, fontWeight:800,
                      cursor:reqBusy?"default":"pointer", opacity:reqBusy?0.5:1, fontFamily:"'Plus Jakarta Sans',sans-serif",
                    }}>
                    {reqBusy ? "Sending…" : "Send request →"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── ALL CHURCHES (super admin) ── */}
          {view==="allChurches" && (
            <div className="profile-section" style={{borderTop:"none"}}>
              <button onClick={()=>setView("profile")} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:13,cursor:"pointer",padding:"0 0 12px 0",display:"block"}}>← Back</button>
              <input className="church-search-input" placeholder="Search churches by name or location"
                value={allChurchesSearch} onChange={e=>setAllChurchesSearch(e.target.value)} style={{marginBottom:12}}/>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {churches
                  .filter(c => !allChurchesSearch.trim() || (c.name||"").toLowerCase().includes(allChurchesSearch.trim().toLowerCase()) || (c.location||"").toLowerCase().includes(allChurchesSearch.trim().toLowerCase()))
                  .map(c => (
                    <div key={c.id} style={{background:"var(--white)",border:"1px solid var(--border)",borderRadius:12,padding:"12px 14px"}}>
                      <div style={{fontSize:14,fontWeight:700,color:"var(--ink)"}}>{c.name}</div>
                      {c.location && <div style={{fontSize:12,color:"var(--stone-dim)",marginTop:1}}>{c.location}</div>}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* ── CHURCH REQUESTS (super admin) ── */}
          {view==="churchRequests" && (
            <div className="profile-section" style={{borderTop:"none"}}>
              <button onClick={()=>setView("profile")} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:13,cursor:"pointer",padding:"0 0 12px 0",display:"block"}}>← Back</button>
              {churchRequests === null ? (
                <div style={{fontSize:13,color:"var(--stone-dim)"}}>Loading…</div>
              ) : churchRequests.length === 0 ? (
                <div style={{fontSize:13,color:"var(--stone-dim)"}}>No church requests right now.</div>
              ) : churchRequests.map(r => (
                <div key={r.id} style={{background:"var(--white)",border:"1px solid var(--border)",borderRadius:12,padding:"12px 14px",marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10}}>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:700,color:"var(--ink)"}}>{r.church_name}</div>
                      <div style={{fontSize:12,color:"var(--stone-dim)",marginTop:2}}>{r.location}</div>
                      {r.requester_name && <div style={{fontSize:11.5,color:"var(--stone-dim)",marginTop:4}}>Requested by {r.requester_name}{r.requester_email ? ` · ${r.requester_email}` : ""}</div>}
                    </div>
                    {r.status === "complete" ? (
                      <span style={{flexShrink:0,fontSize:11,fontWeight:800,color:"var(--gold-ink)",background:"rgba(232,200,122,0.15)",borderRadius:999,padding:"4px 10px"}}>Added</span>
                    ) : approvingReqId !== r.id && (
                      <button type="button" onClick={() => approveChurchRequest(r)}
                        style={{flexShrink:0,fontSize:12,fontWeight:800,color:"var(--ink)",background:"var(--gold)",border:"none",borderRadius:999,padding:"7px 14px",cursor:"pointer"}}>
                        Approve
                      </button>
                    )}
                  </div>

                  {approvingReqId === r.id && (
                    <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid var(--border)"}}>
                      {reqLogoLoading ? (
                        <div style={{fontSize:12.5,color:"var(--stone-dim)"}}>Looking up the channel logo…</div>
                      ) : reqLogoPreview ? (
                        <div style={{display:"flex",alignItems:"center",gap:12}}>
                          <img src={reqLogoPreview.dataUrl} alt="" style={{width:44,height:44,borderRadius:10,objectFit:"cover",flexShrink:0}}/>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12.5,fontWeight:700,color:"var(--ink)",marginBottom:6}}>Is this {r.church_name}'s logo?</div>
                            <div style={{display:"flex",gap:8}}>
                              <button type="button" disabled={reqLogoBusy} onClick={() => applyRequestLogo(true, r, approvedChurchId)}
                                style={{fontSize:11.5,fontWeight:700,padding:"6px 10px",borderRadius:999,border:"none",cursor:"pointer",background:"var(--gold)",color:"var(--ink)"}}>
                                Use this
                              </button>
                              <button type="button" disabled={reqLogoBusy} onClick={() => applyRequestLogo(false, r, approvedChurchId)}
                                style={{fontSize:11.5,fontWeight:700,padding:"6px 10px",borderRadius:999,border:"none",cursor:"pointer",background:"var(--border)",color:"var(--stone-dim)"}}>
                                Use initials
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button type="button" onClick={() => finishChurchRequest(r)}
                          style={{fontSize:12,fontWeight:700,color:"var(--ink)",background:"var(--border)",border:"none",borderRadius:999,padding:"7px 14px",cursor:"pointer"}}>
                          {r.church_name} created — mark as added
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── PLATFORM STATS (super admin) ── */}
          {view==="platformStats" && (
            <div className="profile-section" style={{borderTop:"none"}}>
              <button onClick={()=>setView("profile")} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:13,cursor:"pointer",padding:"0 0 12px 0",display:"block"}}>← Back</button>
              {platformStats === null ? (
                <div style={{fontSize:13,color:"var(--stone-dim)"}}>Loading…</div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {[["Members", platformStats.members], ["Churches", platformStats.churches], ["Sermons processed", platformStats.sermons]].map(([label, value]) => (
                    <div key={label} style={{background:"var(--white)",border:"1px solid var(--border)",borderRadius:12,padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                      <div style={{fontSize:13.5,color:"var(--stone)"}}>{label}</div>
                      <div style={{fontSize:18,fontWeight:800,color:"var(--ink)"}}>{value.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── INVITE VIEW ── */}
          {view==="invite" && (
            <div className="profile-section" style={{borderTop:"none"}}>
              <button onClick={()=>setView("profile")} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:13,cursor:"pointer",padding:"0 0 16px 0",display:"block"}}>← Back</button>

              {/* Role selector */}
              <div className="profile-section-title">Role</div>
              <div className="invite-role-row">
                {["member","elder","admin"].map(r=>(
                  <div key={r} className={`invite-role-btn ${inviteRole===r?"on":""}`}
                    onClick={()=>setInviteRole(r)}>
                    {r.charAt(0).toUpperCase()+r.slice(1)}
                  </div>
                ))}
              </div>

              {/* Church selector - super admin only */}
              {isSuperAdmin && (
                <>
                  <div className="profile-section-title" style={{marginTop:16}}>Church</div>
                  <input className="church-search-input" placeholder="Search churches…"
                    value={churchSearch}
                    onFocus={e=>{ setChurchPickerOpen(true); e.target.select(); }}
                    onChange={e=>{
                      const v = e.target.value;
                      setChurchSearch(v);
                      setChurchPickerOpen(true);
                      // typing away from the chosen name invalidates the pick
                      // until they select again (keeps text + selection in sync)
                      if (selectedChurch && v !== selectedChurchObj?.name) setSelectedChurch("");
                    }}
                    onKeyDown={e=>{ if(e.key==="Escape"){ setChurchPickerOpen(false); e.target.blur(); } }}
                    onBlur={()=>setTimeout(()=>setChurchPickerOpen(false),120)}/>
                  {churchPickerOpen && (
                    <div style={{maxHeight:160,overflowY:"auto",border:"1px solid var(--border)",borderRadius:10,marginBottom:12}}>
                      {filteredChurches.map(c=>(
                        <div key={c.id}
                          className={`church-option ${selectedChurch===c.id?"selected":""}`}
                          onClick={()=>{ setSelectedChurch(c.id); setChurchSearch(c.name); setChurchPickerOpen(false); }}>
                          {c.name}
                          <div className="church-option-sub">{c.location}</div>
                        </div>
                      ))}
                      {filteredChurches.length===0 && (
                        <div style={{padding:"12px",fontSize:13,color:"var(--stone-dim)"}}>No churches found</div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Email input — accepts one address or a pasted list (commas,
                  spaces or new lines all work) for bulk invites. */}
              <div className="profile-section-title" style={{marginTop:8}}>Email</div>
              <textarea className="invite-input" rows={3}
                placeholder={"To invite in bulk paste one email per line\nor comma separated"}
                style={{resize:"vertical", lineHeight:1.5}}
                value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)}/>
              <div style={{fontSize:12, color:"var(--stone-dim)", marginTop:-4, marginBottom:10, lineHeight:1.5}}>
                Anyone who's already requested access is granted instantly.
              </div>

              <button className="invite-send-btn"
                disabled={!inviteEmail.trim() || inviteLoading || (isSuperAdmin && !selectedChurch)}
                onClick={handleInvite}>
                {inviteLoading ? "Sending…" : (() => {
                  const n = parseEmailList(inviteEmail).length;
                  return n > 1 ? `Invite ${n} people` : "Send invite";
                })()}
              </button>

              {bulkSummary && (bulkSummary.invited.length + bulkSummary.granted.length + bulkSummary.failed.length) > 0 && (
                <div className="invite-success" style={{lineHeight:1.6}}>
                  {bulkSummary.granted.length > 0 && (
                    <div style={{fontWeight:700, marginBottom:4}}>
                      ✓ Granted access to {bulkSummary.granted.length} {bulkSummary.granted.length === 1 ? "member who had requested it" : "members who had requested it"} (notified by email + push).
                    </div>
                  )}
                  {bulkSummary.invited.length > 0 && (
                    <div style={{fontWeight:700, marginBottom:4}}>
                      ✉️ Sent {bulkSummary.invited.length} {bulkSummary.invited.length === 1 ? "invite" : "invites"}.
                    </div>
                  )}
                  {bulkSummary.failed.length > 0 && (
                    <div style={{color:"#C8372A", marginTop:4}}>
                      {bulkSummary.failed.length} failed:
                      {bulkSummary.failed.map(f => (
                        <div key={f.email} style={{fontSize:12, opacity:0.9}}>· {f.email} — {f.reason}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {inviteError && (
                <div className="invite-success" style={{background:"rgba(249,112,102,0.10)",border:"1px solid rgba(249,112,102,0.30)",color:"#C8372A"}}>
                  {inviteError}
                </div>
              )}

              {inviteSent && inviteData?.token && (
                <div className="invite-success">
                  <div style={{fontWeight:700, marginBottom:6}}>
                    {inviteData._emailSent
                      ? "Email invite sent!"
                      : `✓ Invite created as ${inviteRole}${isSuperAdmin && selectedChurch ? ` at ${churches.find(c=>c.id===selectedChurch)?.name}` : ""}`}
                  </div>
                  <div style={{fontSize:12, opacity:0.85, marginBottom:8}}>
                    {inviteData._emailSent
                      ? "You can also share their invite link here:"
                      : (inviteData._emailError
                          ? `Couldn't send the email automatically (${inviteData._emailError}). Share the link below instead.`
                          : "Share this link with them (expires in 7 days):")}
                  </div>
                  <div style={{
                    fontSize:12, fontFamily:'monospace',
                    background:'var(--cream)',
                    border:'1px solid var(--border-strong)',
                    borderRadius:8, padding:'8px 10px',
                    wordBreak:'break-all', userSelect:'all',
                    marginBottom:8,
                  }}>
                    {inviteData._inviteUrl || `${window.location.origin}/?invite=${inviteData.token}`}
                  </div>
                  <button type="button" onClick={copyInviteLink}
                    style={{background:"var(--gold)",color:"var(--ink)",border:"none",borderRadius:8,padding:"8px 14px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                    {inviteCopied ? "✓ Copied" : "📋 Copy link"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── CREATE CHURCH VIEW - super admin only ── */}
          {view==="church" && (
            <div className="profile-section" style={{borderTop:"none"}}>
              <button onClick={()=>setView("profile")} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:13,cursor:"pointer",padding:"0 0 16px 0",display:"block"}}>← Back</button>

              <div className="profile-section-title">Church details</div>
              <input className="invite-input" placeholder="Church name *" value={churchName} onChange={e=>setChurchName(e.target.value)}/>
              <input className="invite-input" placeholder="Location (e.g. Bristol, UK)" value={churchLoc} onChange={e=>setChurchLoc(e.target.value)}/>
              <input className="invite-input" type="url" placeholder="Website (optional)" value={churchWeb} onChange={e=>setChurchWeb(e.target.value)}/>

              <div className="profile-section-title" style={{marginTop:8}}>
                Invite first admin <span style={{color:"var(--stone-dim)",fontWeight:400,textTransform:"none",letterSpacing:0}}>(optional)</span>
              </div>
              <div style={{fontSize:12,color:"var(--stone-dim)",marginBottom:8,lineHeight:1.5}}>
                You can invite the first admin now, or skip this and invite them later from the invite panel in your profile.
              </div>
              <input className="invite-input" type="email" placeholder="pastor@church.org (optional)" value={adminEmail} onChange={e=>setAdminEmail(e.target.value)}/>

              {churchError && (
                <div style={{marginTop:8,marginBottom:8,fontSize:13,color:"#C8372A",background:"rgba(249,112,102,0.10)",border:"1px solid rgba(249,112,102,0.30)",borderRadius:8,padding:"10px 12px"}}>
                  {churchError}
                </div>
              )}

              {adminEmail.trim() && (
                <button className="invite-send-btn"
                  disabled={churchSaving || !churchName.trim()}
                  onClick={()=>handleCreateChurch({ withInvite: true })}>
                  {churchSaving ? "Creating…" : "Create church & invite admin"}
                </button>
              )}
              <button className="invite-send-btn"
                style={{
                  marginTop: adminEmail.trim() ? 8 : 0,
                  background: adminEmail.trim() ? "transparent" : undefined,
                  color: adminEmail.trim() ? "var(--ink)" : undefined,
                  border: adminEmail.trim() ? "1px solid var(--border)" : undefined,
                }}
                disabled={churchSaving || !churchName.trim()}
                onClick={()=>handleCreateChurch({ withInvite: false })}>
                {churchSaving ? "Creating…" : adminEmail.trim() ? "Create church (skip invite)" : "Create church"}
              </button>

              {churchCreated && (
                <div className="invite-success">
                  ✓ Church created successfully{churchCreated.name ? `: ${churchCreated.name}` : ""}.
                  {churchCreated.inviteEmail && !churchCreated.inviteError && (
                    <div style={{marginTop:4,fontSize:12,opacity:0.85}}>Admin invite sent to {churchCreated.inviteEmail}.</div>
                  )}
                  {churchCreated.inviteError && (
                    <div style={{marginTop:4,fontSize:12,color:"#C8372A"}}>Church created, but invite failed: {churchCreated.inviteError}</div>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Avatar crop sheet — opened by the file picker */}
      {cropFile && (
        <AvatarCropSheet
          file={cropFile}
          saving={avatarBusy}
          error={avatarError}
          onCancel={()=>{ if (!avatarBusy) setCropFile(null); }}
          onSave={uploadCroppedBlob}
        />
      )}
    </>
  );
}

function SermonSettingsSheet({ onClose, churchId, onSermonsChanged, onScanUpload }) {
  // Scope to the church being managed (admins don't need every church's sermons).
  const { sermons, loading, refresh } = useSermons(churchId ? [churchId] : undefined);
  const { deleteSermon } = useSermonAdmin();
  const { profile, user, churches } = useAuth();

  const churchSermons = churchId ? sermons.filter(s => s.church_id === churchId) : sermons;
  const myChurch = (churches || []).find(c => c.id === churchId) || null;

  // Free-scan usage + self-serve-upload flag for this church (re-read whenever
  // the sermon count changes).
  const [usage, setUsage] = useState(null); // { free_scans_used, is_paid, self_serve_upload }
  useEffect(() => {
    let cancelled = false;
    if (!churchId) { setUsage(null); return; }
    (async () => {
      let { data, error } = await supabase.from("churches")
        .select("free_scans_used, is_paid, self_serve_upload, scan_limit").eq("id", churchId).single();
      // Tolerate the self_serve_upload / scan_limit columns not existing yet.
      if (error && /self_serve_upload|scan_limit/i.test(error.message || "")) {
        ({ data } = await supabase.from("churches")
          .select("free_scans_used, is_paid").eq("id", churchId).single());
      }
      if (!cancelled) setUsage(data || null);
    })();
    return () => { cancelled = true; };
  }, [churchId, sermons.length]);

  const used      = usage?.free_scans_used || 0;
  const isPaid    = !!usage?.is_paid;
  // Per-church scan allowance, floored at the global minimum.
  const scanLimit = Math.max(FREE_SCAN_LIMIT, usage?.scan_limit || FREE_SCAN_LIMIT);
  const remaining = Math.max(0, scanLimit - used);
  // Self-serve upload defaults ON unless a super admin has switched it off for
  // this church. When off, in-app users can't upload — only super admins can,
  // from the admin tool.
  const selfServe = usage?.self_serve_upload !== false;
  const canUpload = selfServe && (isPaid || remaining > 0);

  // One-click "upload all my videos for me" enquiry — logs a row and emails the
  // team at hello@thebiblecommunity.co.uk via submit-church-enquiry.
  const [notifyState, setNotifyState] = useState("idle"); // idle|sending|done|error
  async function notifyBulkUpload() {
    if (notifyState === "sending" || notifyState === "done") return;
    setNotifyState("sending");
    const name       = profile?.full_name || "Church admin";
    const email      = profile?.email || user?.email || "";
    const churchName = myChurch?.name || "";
    try {
      const { error: insErr } = await supabase.from("bulk_upload_requests").insert({
        church_id: churchId || null,
        user_id:   user?.id || null,
        name, email, church_name: churchName,
      });
      if (insErr) console.warn("bulk_upload_requests insert:", insErr.message);

      const ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;
      const base = process.env.REACT_APP_SUPABASE_URL;
      const res = await fetch(`${base}/functions/v1/submit-church-enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${ANON}`, "apikey": ANON },
        body: JSON.stringify({
          type: "bulk_upload_request",
          name, email, church: churchName,
          message: "Wants their full sermon archive uploaded for them (skip the self-serve tool).",
          scans_used: used, church_id: churchId || "",
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNotifyState("done");
    } catch (e) {
      console.warn("notifyBulkUpload failed:", e);
      setNotifyState("error");
    }
  }

  async function handleDelete(id) {
    await deleteSermon(id);
    refresh();
    onSermonsChanged?.();
  }

  return (
    <>
      <div className="overlay" onClick={onClose}/>
      <div className="sheet" style={{maxHeight:"90vh",display:"flex",flexDirection:"column"}}>
        <div className="shandle"/>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 16px 0",flexShrink:0}}>
          <div className="shd">Add sermons</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:20,cursor:"pointer",padding:4}}>✕</button>
        </div>

        <div style={{flex:1,overflowY:"auto"}}>
              <div style={{padding:"8px 16px 0"}}>
                {!churchId && (
                  <div style={{background:"rgba(249,112,102,0.1)",border:"1px solid rgba(249,112,102,0.25)",borderRadius:10,padding:"12px 14px",fontSize:13,color:"#C8372A",marginBottom:12}}>
                    Pick an active church in your profile before uploading.
                  </div>
                )}

                {/* Self-serve upload disabled by a super admin */}
                {churchId && usage && !selfServe && (
                  <div style={{background:"rgba(249,112,102,0.1)",border:"1px solid rgba(249,112,102,0.25)",borderRadius:10,padding:"12px 14px",fontSize:13,color:"#C8372A",marginBottom:12,lineHeight:1.5}}>
                    Sermon uploads for your church are managed by your administrator. Use the option below to request an import.
                  </div>
                )}

                {/* Free-scan usage */}
                {churchId && usage && selfServe && (
                  isPaid ? (
                    <div style={{background:"rgba(232,200,122,0.08)",border:"1px solid rgba(232,200,122,0.3)",borderRadius:10,padding:"12px 14px",marginBottom:12,fontSize:14,fontWeight:700,color:"var(--gold-ink)"}}>
                      ✓ Unlimited sermon uploads
                    </div>
                  ) : (
                    <div style={{background:"var(--white)",border:"1px solid var(--border)",borderRadius:10,padding:"12px 14px",marginBottom:12}}>
                      <div style={{fontSize:13,fontWeight:700,color:"var(--ink)",marginBottom:8}}>{used} / {scanLimit} sermons added</div>
                      <div style={{height:6,borderRadius:3,background:"var(--border)",overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${Math.min(100,(used/scanLimit)*100)}%`,background: remaining>0?"var(--gold)":"#C8372A",borderRadius:3}}/>
                      </div>
                      {remaining<=0 && (
                        <div style={{fontSize:12,color:"var(--stone)",marginTop:8,lineHeight:1.5}}>
                          You've used all {scanLimit} uploads — use the option below and we'll add the rest for you.
                        </div>
                      )}
                    </div>
                  )
                )}

                <button
                  onClick={()=>onScanUpload?.()}
                  disabled={!churchId || !canUpload}
                  style={{width:"100%",background:(churchId && canUpload)?"var(--gold)":"var(--white)",color:(churchId && canUpload)?"var(--ink)":"var(--stone-dim)",border:"none",borderRadius:10,padding:"13px",fontSize:14,fontWeight:800,cursor:(churchId && canUpload)?"pointer":"default",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                  + Upload a sermon{churchId && usage && !isPaid && canUpload ? ` · ${remaining} left` : ""}
                </button>

                {/* One-click bulk upload enquiry */}
                {churchId && (
                  <div style={{marginTop:14,background:"rgba(232,200,122,0.06)",border:"1px solid rgba(232,200,122,0.28)",borderRadius:12,padding:"14px 16px"}}>
                    <div style={{fontSize:14,fontWeight:700,color:"var(--ink)",lineHeight:1.45,marginBottom:4}}>
                      Want to upload more sermons? You can do this with our automated bulk tool.
                    </div>
                    <div style={{fontSize:13,color:"var(--stone)",lineHeight:1.5,marginBottom:12}}>
                      Notify us and we'll get back to you today.
                    </div>
                    <button
                      onClick={notifyBulkUpload}
                      disabled={notifyState==="sending"||notifyState==="done"}
                      style={{width:"100%",background: notifyState==="done"?"rgba(80,200,150,0.15)":"var(--gold)",color: notifyState==="done"?"#1F7A52":"var(--ink)",border: notifyState==="done"?"1px solid rgba(80,200,150,0.4)":"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:800,cursor: (notifyState==="sending"||notifyState==="done")?"default":"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                      {notifyState==="done" ? "✓ We'll be in touch today" : notifyState==="sending" ? "Sending…" : notifyState==="error" ? "Try again →" : "Notify us →"}
                    </button>
                  </div>
                )}
              </div>
              {loading ? (
                <div style={{padding:"16px",textAlign:"center",color:"var(--stone-dim)",fontSize:13}}>Loading sermons…</div>
              ) : (
                <SermonList sermons={churchSermons} onDelete={handleDelete}/>
              )}
        </div>
      </div>
    </>
  );
}

function EncouragementSettingsSheet({ onClose, churchId, onChanged, editing }) {
  const [mode, setMode] = useState(editing ? "edit" : "list"); // "list" | "add" | "edit"
  // Local copy of the encouragement being edited so toggling the sheet
  // doesn't reset to "list" mid-edit.
  const [editTarget] = useState(editing || null);

  return (
    <>
      <div className="overlay" onClick={onClose}/>
      <div className="sheet" style={{maxHeight:"90vh",display:"flex",flexDirection:"column"}}>
        <div className="shandle"/>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 16px 0",flexShrink:0}}>
          <div className="shd">{mode==="add" ? "Add an encouragement" : mode==="edit" ? "Edit encouragement" : "Encouragement settings"}</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:20,cursor:"pointer",padding:4}}>✕</button>
        </div>

        <div style={{flex:1,overflowY:"auto"}}>
          {mode==="list" && (
            <div style={{padding:"8px 16px 16px"}}>
              {!churchId && (
                <div style={{background:"rgba(249,112,102,0.1)",border:"1px solid rgba(249,112,102,0.25)",borderRadius:10,padding:"12px 14px",fontSize:13,color:"#C8372A",marginBottom:12}}>
                  Pick an active church in your profile before adding an encouragement.
                </div>
              )}
              <button
                onClick={()=>setMode("add")}
                disabled={!churchId}
                style={{width:"100%",background:churchId?"var(--gold)":"var(--white)",color:churchId?"var(--ink)":"var(--stone-dim)",border:"none",borderRadius:10,padding:"13px",fontSize:14,fontWeight:800,cursor:churchId?"pointer":"default",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                + Add encouragement
              </button>
            </div>
          )}

          {mode==="add" && (
            <>
              <div style={{padding:"8px 16px 0"}}>
                <button
                  onClick={()=>setMode("list")}
                  style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:13,cursor:"pointer",padding:"0 0 8px 0",display:"block"}}>
                  ← Back
                </button>
              </div>
              <EncouragementUpload churchId={churchId} onSaved={()=>{ onChanged?.(); setMode("list"); onClose?.(); }}/>
            </>
          )}

          {mode==="edit" && editTarget && (
            <EncouragementUpload
              churchId={churchId}
              existing={editTarget}
              onSaved={()=>{ onChanged?.(); onClose?.(); }}
            />
          )}
        </div>
      </div>
    </>
  );
}

// ── Visitor "Request access" modal ───────────────────────────────────────────
// Shown when a visitor taps a locked feature. Lets them either pick a church
// (if they signed up without one) or submit a request to the church they're
// already attached to. Persists to the access_requests table.
const FEATURE_LABELS = {
  community:     "the Community tab",
  comment:       "posting community comments",
  question:      "asking questions of your Elders",
  encouragement: "reading the church's words of encouragement",
};
function AccessRequestModal({ feature, churchId, churches, requestAccess, onClose }) {
  const { user, profile, updateProfile } = useAuth();
  const [chosenChurchId, setChosenChurchId] = useState(churchId || "");
  const [query,          setQuery]          = useState("");
  const [status,         setStatus]         = useState("idle"); // 'idle' | 'sending' | 'sent' | 'error'
  const [errorMsg,       setErrorMsg]       = useState("");
  const [alreadyRequested, setAlreadyRequested] = useState(false);
  const churchName = churches?.find(c => c.id === chosenChurchId)?.name || "your church";
  // No-church visitors get a different intro + the church picker is the
  // primary affordance ("fill in a church first").
  const noChurch = !profile?.church_id;

  // Look up any existing pending request from this user for the selected
  // church — if they've already asked, swap the action button for a greyed
  // "already requested" pill so they don't spam the moderators' inbox.
  useEffect(() => {
    if (!user?.id || !chosenChurchId) { setAlreadyRequested(false); return; }
    let cancelled = false;
    supabase.from('access_requests')
      .select('id, status')
      .eq('user_id', user.id)
      .eq('church_id', chosenChurchId)
      .eq('status', 'pending')
      .limit(1)
      .then(({ data }) => {
        if (cancelled) return;
        setAlreadyRequested((data || []).length > 0);
      });
    return () => { cancelled = true; };
  }, [user?.id, chosenChurchId]);
  const filtered = (() => {
    const q = query.trim().toLowerCase();
    if (!q) return churches || [];
    return (churches || []).filter(c =>
      (c.name || "").toLowerCase().includes(q) || (c.location || "").toLowerCase().includes(q)
    );
  })();

  async function attachAsVisitor() {
    if (!chosenChurchId) { setErrorMsg("Pick a church first."); return; }
    setStatus("sending"); setErrorMsg("");
    const { error: profErr } = await updateProfile({ church_id: chosenChurchId });
    if (profErr) {
      setStatus("error");
      setErrorMsg(profErr.message || "Could not save your church.");
      return;
    }
    // Close the modal — the user's now a visitor of this church and the
    // rest of the app will start scoping content to it on next render.
    setStatus("idle");
    onClose?.();
  }

  async function submit() {
    if (!chosenChurchId) { setErrorMsg("Pick a church first."); return; }
    setStatus("sending"); setErrorMsg("");
    // If the user has no church on their profile yet, attach them to the
    // one they just picked as a visitor — so future content (sermons,
    // encouragements, the bell badge) starts scoping to this church and
    // they stop seeing nothing.
    if (noChurch) {
      const { error: profErr } = await updateProfile({ church_id: chosenChurchId });
      if (profErr) {
        setStatus("error");
        setErrorMsg(profErr.message || "Could not save your church.");
        return;
      }
    }
    const { error } = await requestAccess(feature, chosenChurchId);
    if (error) {
      setStatus("error");
      setErrorMsg(typeof error === "string" ? error : (error.message || "Could not send request."));
      return;
    }
    setStatus("sent");
  }

  return (
    <>
      {/* Sit above any other open sheet (action menu, comment thread, etc.)
          - those use z-index 201 by default. Without this the access-request
          modal can render *under* the sheet that triggered it. */}
      <div className="overlay" style={{zIndex:500}} onClick={onClose}/>
      <div className="sheet" style={{zIndex:501,maxHeight:"90vh",display:"flex",flexDirection:"column"}}>
        <div className="shandle"/>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 16px 0"}}>
          <div className="shd" style={{padding:0}}>Members only 🔒</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:20,cursor:"pointer",padding:4}}>✕</button>
        </div>
        <div style={{padding:"6px 18px 14px",overflowY:"auto"}}>
          {status === "sent" ? (
            <div style={{background:"rgba(80,200,150,0.10)",border:"1px solid rgba(80,200,150,0.30)",borderRadius:10,padding:"14px",fontSize:14,color:"#1F7A52",lineHeight:1.5}}>
              ✓ Request sent to <strong>{churchName}</strong>. An elder will review it and approve your access soon.
            </div>
          ) : (
            <>
              <div style={{fontSize:14,color:"var(--ink)",lineHeight:1.55,marginBottom:14}}>
                {noChurch
                  ? "You need to be a church member to view community features. Pick your church below to get started."
                  : <>The Community features are for church members only. Request access from <strong>{churchName}</strong> here.</>}
              </div>

              {(!churchId || noChurch) && (
                <>
                  <div style={{fontSize:11,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase",color:"var(--stone-dim)",marginBottom:6}}>Choose a church</div>
                  <input
                    type="text" placeholder="Search by name or location"
                    value={query} onChange={e=>{ setQuery(e.target.value); setChosenChurchId(""); }}
                    style={{width:"100%",background:"var(--white)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 12px",color:"var(--ink)",fontSize:14,fontFamily:"'Plus Jakarta Sans',sans-serif",outline:"none",boxSizing:"border-box",marginBottom:8}}
                  />
                  <div style={{maxHeight:200,overflowY:"auto",background:"var(--white)",border:"1px solid var(--border)",borderRadius:10,padding:4,marginBottom:12}}>
                    {filtered.length === 0 ? (
                      <div style={{padding:"10px 12px",fontSize:13,color:"var(--stone-dim)"}}>No churches found.</div>
                    ) : filtered.map(c => (
                      <div key={c.id} onClick={()=>setChosenChurchId(c.id)}
                        style={{padding:"8px 10px",borderRadius:8,cursor:"pointer",marginBottom:2,
                          background: chosenChurchId===c.id ? "rgba(232,200,122,0.16)" : "transparent",
                          border: chosenChurchId===c.id ? "1px solid rgba(232,200,122,0.4)" : "1px solid transparent"}}>
                        <div style={{fontSize:14,fontWeight:600,color: chosenChurchId===c.id ? "var(--gold-ink)" : "var(--ink)"}}>{c.name}</div>
                        {c.location && <div style={{fontSize:11,color:"var(--stone-dim)"}}>{c.location}</div>}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {errorMsg && (
                <div style={{background:"rgba(249,112,102,0.1)",border:"1px solid rgba(249,112,102,0.25)",borderRadius:10,padding:"10px 12px",fontSize:13,color:"#C8372A",marginBottom:10}}>{errorMsg}</div>
              )}

              {alreadyRequested ? (
                <button disabled
                  style={{width:"100%",background:"var(--white)",color:"var(--stone)",border:"1px solid var(--border)",borderRadius:10,padding:"13px",fontSize:13,fontWeight:600,cursor:"default",fontFamily:"'Plus Jakarta Sans',sans-serif",lineHeight:1.35}}>
                  You have requested access to become a member of {churchName}
                </button>
              ) : noChurch ? (
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  <button onClick={attachAsVisitor}
                    disabled={status === "sending" || !chosenChurchId}
                    style={{width:"100%",background:"var(--white)",color:"var(--ink)",border:"1px solid var(--border-strong)",borderRadius:10,padding:"12px",fontSize:14,fontWeight:700,cursor:(status === "sending" || !chosenChurchId)?"default":"pointer",opacity:(status === "sending" || !chosenChurchId)?0.5:1,fontFamily:"'Plus Jakarta Sans',sans-serif",textAlign:"left"}}>
                    <div>Join as visitor</div>
                    <div style={{fontSize:11,fontWeight:500,color:"var(--stone-dim)",marginTop:2,letterSpacing:0}}>You will see all church content</div>
                  </button>
                  <button onClick={submit}
                    disabled={status === "sending" || !chosenChurchId}
                    style={{width:"100%",background:"var(--gold)",color:"var(--ink)",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:800,cursor:(status === "sending" || !chosenChurchId)?"default":"pointer",opacity:(status === "sending" || !chosenChurchId)?0.5:1,fontFamily:"'Plus Jakarta Sans',sans-serif",textAlign:"left"}}>
                    <div>{status === "sending" ? "Sending…" : "Request member access"}</div>
                    <div style={{fontSize:11,fontWeight:600,color:"rgba(31,27,22,0.7)",marginTop:2,letterSpacing:0}}>You can access community features</div>
                  </button>
                </div>
              ) : (
                <button onClick={submit}
                  disabled={status === "sending" || !chosenChurchId}
                  style={{width:"100%",background:"var(--gold)",color:"var(--ink)",border:"none",borderRadius:10,padding:"13px",fontSize:14,fontWeight:800,cursor:(status === "sending" || !chosenChurchId)?"default":"pointer",opacity:(status === "sending" || !chosenChurchId)?0.5:1,fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                  {status === "sending" ? "Sending…" : "Request access"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

// Cache to avoid re-fetching the same chapter
const chapterCache = { "KJV/Romans/8": SEED_VERSES };

// ── User-scoped localStorage helpers ──────────────────────────────────
// Per-account preferences (active church, followed churches, etc.) are
// stored under "{base}:{userId}" so a different account signing in on
// the same browser doesn't inherit the previous account's data. On
// first read we transparently migrate from the legacy unscoped key.
function userScopedKey(base, userId) {
  return userId ? `${base}:${userId}` : base;
}
function readUserScoped(base, userId) {
  if (typeof window === "undefined") return null;
  if (!userId) return null;
  const scoped = userScopedKey(base, userId);
  try {
    let val = localStorage.getItem(scoped);
    if (val == null) {
      // First time this user is loading after the per-user-scope rollout —
      // adopt the legacy unscoped value (if any) and remove the original.
      const legacy = localStorage.getItem(base);
      if (legacy != null) {
        localStorage.setItem(scoped, legacy);
        localStorage.removeItem(base);
        val = legacy;
      }
    }
    return val;
  } catch (_e) { return null; }
}
function writeUserScoped(base, userId, value) {
  if (typeof window === "undefined") return;
  if (!userId) return;
  try { localStorage.setItem(userScopedKey(base, userId), value); }
  catch (_e) { /* ignore quota */ }
}

// Expand a sermon_ref / encouragement_ref row to the verse keys it
// covers, preferring the explicit `end_verse` column when present (cheap
// and exact) and falling back to verse_ref string parsing for legacy
// rows that haven't been migrated yet. Returns the same { verseKeys,
// chapterKey } shape as expandVerseRef so callers can swap freely.
function expandRefRange(r) {
  if (!r) return { verseKeys: [], chapterKey: null };
  const startV = typeof r.verse === 'number' ? r.verse : parseInt(r.verse, 10);
  const endV   = r.end_verse != null ? (typeof r.end_verse === 'number' ? r.end_verse : parseInt(r.end_verse, 10))
              : r.endVerse  != null ? (typeof r.endVerse  === 'number' ? r.endVerse  : parseInt(r.endVerse,  10))
              : startV;
  const chap = typeof r.chapter === 'number' ? r.chapter : parseInt(r.chapter, 10);
  const canonical = r.book ? (canonicalizeBook(r.book) || r.book) : null;
  if (canonical && Number.isFinite(chap) && Number.isFinite(startV)) {
    const verseKeys = [];
    const lo = Math.min(startV, Number.isFinite(endV) ? endV : startV);
    const hi = Math.max(startV, Number.isFinite(endV) ? endV : startV);
    for (let v = lo; v <= hi; v++) verseKeys.push(`${canonical} ${chap}:${v}`);
    return { verseKeys, chapterKey: null };
  }
  // Legacy fallback — string parse via the existing helper. Catches
  // chapter-only refs ("Romans 8") and rows missing structured cols.
  return expandVerseRef(r.verse_ref || r.verseRef);
}

function fmtShortSermonDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

// Playing card for a verse clip — docs/designs/VerseClip.dc.html. A real
// (module-level) component, not an inline closure, since it needs its own
// playback-time/ended/error state. Falls back to a link-out thumbnail if
// the video returns error 101/150 (embedding disabled) — per-video, per
// docs/FIX-SERMON-CLIPS.md §1/§2, independent of whether other clips embed fine.
function VerseClipPlayingCard({ clip, onOpenFullSermon, onCollapse }) {
  const bounds = clipBoundsFor(clip);
  const [currentTime, setCurrentTime] = useState(bounds.start);
  const [ended, setEnded] = useState(false);
  const [embedFailed, setEmbedFailed] = useState(false);
  const playerRef = useRef(null);

  if (!clip.youtubeId || embedFailed) {
    const t = clip.timestampS != null ? Math.floor(clip.timestampS) : 0;
    const url = clip.youtubeId ? `https://www.youtube.com/watch?v=${clip.youtubeId}${t ? `&t=${t}s` : ''}` : null;
    return (
      <div style={{ border: '2px solid var(--lavender)', borderRadius: 18, padding: 12, marginTop: 8, marginBottom: 8 }}>
        <a href={url || undefined} target="_blank" rel="noreferrer"
          style={{ display: 'block', position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: 14, overflow: 'hidden', background: 'var(--border)' }}>
          {clip.youtubeId && <img src={`https://i.ytimg.com/vi/${clip.youtubeId}/mqdefault.jpg`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>}
          <span style={{ position: 'absolute', left: '50%', top: '50%', width: 46, height: 46, marginLeft: -23, marginTop: -23, borderRadius: 23, background: 'rgba(255,255,255,0.94)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>▶</span>
        </a>
        <div style={{ fontSize: 12.5, color: 'var(--stone)', marginTop: 8 }}>This clip can't play here — tap to watch on YouTube.</div>
        <button type="button" onClick={onCollapse} style={{ marginTop: 8, background: 'none', border: 'none', color: 'var(--gold-ink)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Close</button>
      </div>
    );
  }

  const total = (bounds.end || currentTime || 1) - bounds.start;
  const progressPct = total > 0 ? Math.min(100, Math.max(0, ((currentTime - bounds.start) / total) * 100)) : 0;

  return (
    <div style={{ border: '2px solid var(--lavender)', borderRadius: 18, padding: 12, marginTop: 8, marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: 14, overflow: 'hidden', background: '#000' }}>
        <YouTubeEmbed ref={playerRef} videoId={clip.youtubeId} start={bounds.start} end={bounds.end}
          onTimeUpdate={setCurrentTime}
          onEnded={() => setEnded(true)}
          onError={(code) => { if (code === 101 || code === 150) setEmbedFailed(true); }}
        />
        <span style={{ position: 'absolute', left: 8, top: 8, background: 'rgba(31,27,22,0.78)', color: '#fff', borderRadius: 999, padding: '5px 10px', fontSize: 11.5, fontWeight: 700 }}>Clip · {clip.verseLabel}</span>
        {ended && (
          <button type="button" onClick={() => { setEnded(false); playerRef.current?.seekTo(bounds.start); playerRef.current?.play(); }}
            style={{ position: 'absolute', inset: 0, background: 'rgba(31,27,22,0.55)', border: 'none', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            ↻ Replay clip
          </button>
        )}
      </div>
      {bounds.end && (
        <div>
          <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progressPct}%`, background: '#7C6BC4' }}/>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--stone)', marginTop: 4 }}>
            <span>{fmtClipClock(bounds.start)}</span><span>{fmtClipClock(bounds.end)}</span>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: clip.isHome ? 'var(--lavender-ink)' : 'var(--sage-ink)' }}>{clip.churchName} · {fmtShortSermonDate(clip.date)}</span>
        <span style={{ background: clip.isHome ? 'var(--lavender)' : 'var(--border)', color: clip.isHome ? 'var(--lavender-ink)' : 'var(--stone)', borderRadius: 999, padding: '3px 9px', fontSize: 11, fontWeight: 700 }}>
          {clip.kind === 'main_passage' ? 'Main passage' : clip.kind === 'moment' ? 'Key moment' : 'Mentioned'}
        </span>
      </div>
      <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--ink)' }}>{clip.displayTitle}</div>
      {clip.notes && <div style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--ink)' }}>{clip.notes}</div>}
      <button type="button" onClick={() => onOpenFullSermon(currentTime)}
        style={{ width: '100%', background: 'none', border: '1.5px solid var(--gold)', borderRadius: 999, color: 'var(--gold-ink)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", padding: 12 }}>
        Open full sermon →
      </button>
    </div>
  );
}

// Strip common YouTube title metadata patterns for *display only*. Church
// uploaders pack preacher / church / date into the title separated by
// " // " or " || " — we keep only the first segment when displaying.
// The database title is untouched so admins still see the raw value.
function cleanSermonTitle(title) {
  if (!title) return "";
  const parts = String(title).split(/\s+(?:\/\/|\|\|)\s+/);
  const first = (parts[0] || "").trim().replace(/\s{2,}/g, " ");
  return first || String(title);
}

// ── In-app Back-button handling ──────────────────────────────────────────
// The whole app lives at one URL (/app): the bottom tabs, the bible/connect/
// church sub-sections, the sermon series, the open book/chapter and every
// sheet/modal are all useState with no URL change. So the SPA had a single
// history entry — the Android hardware Back button (and a browser Back, and the
// iOS edge-swipe) had nothing in-app to return to and left the app entirely
// (white-screen-then-exit / "back to the store").
//
// Fix: mirror in-app navigation into the browser history as throwaway entries.
// Two producers share ONE back-stack + popstate listener so ordering is
// correct when they interleave:
//   • useViewHistory — pushes a frame whenever the main "view" changes
//     (tab / sub-section / series / book / chapter); Back restores the previous
//     view.
//   • useBackClose   — pushes a frame when an overlay opens; Back closes it.
// A Back press pops the top frame and runs its undo. The URL never changes, so
// App.js's pathname router (its own popstate handler) just re-reads /app and
// no-ops.
const _backStack = [];        // [{ undo }], most-recent last
let _backListening = false;

function _ensureBackListener() {
  if (_backListening || typeof window === "undefined") return;
  _backListening = true;
  window.addEventListener("popstate", () => {
    // SPA route changes (App.js navigateTo, deep-link nav, etc.) dispatch a
    // SYNTHETIC popstate to drive the pathname router. Those are programmatic
    // forward navigations, not Back presses — running an undo here (a setState
    // on this component, sometimes mid-render of the router) would violate
    // React's update rules and blank the screen (e.g. on sign-out → /login).
    // The route-nav sites set this flag so we ignore their synthetic events.
    if (window.__nlcProgrammaticNav) return;
    const frame = _backStack.pop();
    if (frame) frame.undo();   // genuine Back → undo the most recent navigation
  });
}

// Push a throwaway history entry + a frame whose undo() runs on Back. Returns
// the frame so a UI-driven close can drop it from the stack again.
function _pushBackFrame(undo) {
  _ensureBackListener();
  const frame = { undo };
  _backStack.push(frame);
  try { window.history.pushState({ _back: true }, ""); } catch (_e) { /* ignore */ }
  return frame;
}

// Drop a frame that was closed via the UI (✕ / tap-out) rather than by Back.
// We deliberately DON'T history.back() to remove its sentinel: doing so during
// a route change or component unmount — e.g. sign-out unmounting the app while
// the Profile sheet is open — thrashes history backwards against the router's
// forward navigation and blanked the screen. Leaving the sentinel is harmless:
// at worst one later Back press is silently absorbed (the JS stack is empty so
// nothing runs), never a crash, and in-app Back order is preserved.
function _removeBackFrame(frame) {
  const idx = _backStack.indexOf(frame);
  if (idx !== -1) _backStack.splice(idx, 1);
}

// Close `onClose` when the user presses Back while `isOpen`. Safe to call for
// many overlays in one component. Idempotent under React StrictMode's dev
// double-invoke (cleanup guards on stack membership + the sentinel still being
// the current history entry).
function useBackClose(isOpen, onClose) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!isOpen || typeof window === "undefined") return;
    const frame = _pushBackFrame(() => { closeRef.current && closeRef.current(); });
    return () => _removeBackFrame(frame);
  }, [isOpen]);
}

// Mirror the main navigable view into history so Back restores the previous
// view. `view` is a small plain object (tab / sub-section / series / book /
// chapter); `restore(view)` applies a captured snapshot back onto state. View
// frames are only ever consumed by Back (never removed by UI), so they form a
// natural navigation stack.
function useViewHistory(view, restore) {
  const restoreRef = useRef(restore); restoreRef.current = restore;
  const prevRef = useRef(view);                 // the view BEFORE the latest change
  const sigRef  = useRef(JSON.stringify(view)); // signature of the current view
  const restoringRef = useRef(false);           // true while applying a Back-restore
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sig = JSON.stringify(view);
    if (sig === sigRef.current) return;          // no navigable change
    if (restoringRef.current) {                  // change came FROM a Back-restore
      restoringRef.current = false;
      sigRef.current = sig; prevRef.current = view;
      return;
    }
    const snapshot = prevRef.current;            // push the PREVIOUS view as the target
    _pushBackFrame(() => {
      restoringRef.current = true;
      restoreRef.current && restoreRef.current(snapshot);
    });
    sigRef.current = sig; prevRef.current = view;
  });
}

export default function App() {
  const { profile, user, churches, isVisitor, requestAccess, refreshChurches, refreshProfile, updateProfile } = useAuth();
  const headerInitials = getInitials(profile?.full_name || user?.email);
  const isSuperAdmin = profile?.role === "super_admin";
  const isModerator  = ["elder","admin","super_admin"].includes(profile?.role);

  // Visitor → which feature triggered the "Request access" modal. Null = closed.
  const [accessFeature, setAccessFeature] = useState(null);
  function openAccessRequest(feature) { setAccessFeature(feature); }

  // Super admin picks which church they're acting as; everyone else is locked to their own.
  // Persisted to user-scoped localStorage so the choice survives reloads
  // without contaminating other accounts on the same browser.
  const [activeChurchId, setActiveChurchId] = useState(
    () => readUserScoped("nlc.activeChurchId", user?.id) || ""
  );
  useEffect(() => {
    if (!isSuperAdmin && profile?.church_id) setActiveChurchId(profile.church_id);
  }, [isSuperAdmin, profile?.church_id]);
  useEffect(() => {
    if (activeChurchId) writeUserScoped("nlc.activeChurchId", user?.id, activeChurchId);
  }, [activeChurchId, user?.id]);

  // Multi-church sermon-notes selection. The active church is always
  // included and locked; the user can add additional churches via the
  // profile sheet ("Show sermon notes for"). Persisted to localStorage as
  // a JSON array of church_ids. Note: this only widens sermon refs /
  // sermon listings — community posts, comments, encouragements and
  // questions stay scoped to activeChurchId.
  const [sermonNoteChurchIds, setSermonNoteChurchIds] = useState(() => {
    const raw = readUserScoped("nlc.sermonNoteChurchIds", user?.id);
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter(x => typeof x === "string") : [];
    } catch (_e) { return []; }
  });
  // Persist any change to user-scoped localStorage. The DB sync effect
  // below is the durable source of truth across devices and Safari ITP
  // evictions; localStorage is just a fast cache for first paint.
  useEffect(() => {
    if (!user?.id) return;
    writeUserScoped("nlc.sermonNoteChurchIds", user?.id, JSON.stringify(sermonNoteChurchIds));
  }, [sermonNoteChurchIds, user?.id]);
  // Make sure the active church is always present in the list — never let
  // it be missing, even on first load before any persisted value exists.
  useEffect(() => {
    if (!activeChurchId) return;
    setSermonNoteChurchIds(prev => prev.includes(activeChurchId) ? prev : [activeChurchId, ...prev]);
  }, [activeChurchId]);

  // ── DB-backed followed-church persistence ──────────────────────────
  // On first mount: hydrate from user_church_follows so the list survives
  // signouts, new devices, and Safari ITP eviction of localStorage.
  // Local additions/removals are then mirrored back to the table via the
  // sync effect below. The two refs guard against double-fetch and
  // detect what we last successfully wrote so we only push diffs.
  const followsDbSyncedRef = useRef(null);
  const followsHydratedRef = useRef(false);
  useEffect(() => {
    if (!user?.id) return;
    if (followsHydratedRef.current) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('user_church_follows')
        .select('church_id')
        .eq('user_id', user.id);
      if (cancelled) return;
      if (error) {
        // Likely the migration hasn't been run yet — fall back to
        // localStorage-only behaviour silently.
        // eslint-disable-next-line no-console
        console.warn('user_church_follows fetch failed:', error.message);
        followsHydratedRef.current = true;
        return;
      }
      const dbIds = new Set((data || []).map(r => r.church_id).filter(Boolean));
      // Merge with whatever's in local state. DB wins for the set of
      // ids (it's the cross-device source of truth), but we keep any
      // local ids the DB doesn't know about yet so a pending offline add
      // isn't lost — they'll be pushed back to the DB by the sync effect.
      setSermonNoteChurchIds(prev => {
        const merged = new Set([...dbIds]);
        for (const id of prev) {
          if (id && id !== activeChurchId) merged.add(id);
        }
        const ordered = activeChurchId
          ? [activeChurchId, ...[...merged].filter(id => id !== activeChurchId)]
          : [...merged];
        return ordered;
      });
      followsDbSyncedRef.current = dbIds;
      followsHydratedRef.current = true;
    })();
    return () => { cancelled = true; };
  // activeChurchId is read inside but doesn't need to retrigger this — the
  // followsHydratedRef guard makes it idempotent.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Sync user-driven changes back to user_church_follows. Skipped while
  // hydration is still in flight so we don't re-write what the DB just
  // returned. The "extras" (everything except the home church) is what
  // the table tracks.
  useEffect(() => {
    if (!user?.id) return;
    if (!followsHydratedRef.current) return;
    const current = new Set(
      sermonNoteChurchIds.filter(id => id && id !== activeChurchId)
    );
    const synced = followsDbSyncedRef.current || new Set();
    const toAdd = [...current].filter(id => !synced.has(id));
    const toRemove = [...synced].filter(id => !current.has(id));
    if (toAdd.length === 0 && toRemove.length === 0) return;
    (async () => {
      if (toAdd.length) {
        const { error } = await supabase
          .from('user_church_follows')
          .upsert(
            toAdd.map(church_id => ({ user_id: user.id, church_id })),
            { onConflict: 'user_id,church_id' }
          );
        if (error) {
          // eslint-disable-next-line no-console
          console.warn('user_church_follows upsert failed:', error.message);
        }
      }
      if (toRemove.length) {
        const { error } = await supabase
          .from('user_church_follows')
          .delete()
          .eq('user_id', user.id)
          .in('church_id', toRemove);
        if (error) {
          // eslint-disable-next-line no-console
          console.warn('user_church_follows delete failed:', error.message);
        }
      }
      followsDbSyncedRef.current = current;
    })();
  }, [sermonNoteChurchIds, activeChurchId, user?.id]);
  // Effective list used for all sermon scoping. Guarantees activeChurchId
  // is at index 0 even on the very first render before the effect above
  // has had a chance to run.
  const effectiveSermonChurchIds = useMemo(() => {
    // Fall back to profile.church_id before activeChurchId has hydrated from
    // localStorage / the profile-effect, otherwise a brief render with an
    // empty list will spill into the "no filter" branch below and show
    // sermons from every church on the platform.
    const home = activeChurchId || profile?.church_id;
    const base = !home
      ? sermonNoteChurchIds
      : (sermonNoteChurchIds.includes(home)
          ? sermonNoteChurchIds
          : [home, ...sermonNoteChurchIds]);

    // Hierarchical inclusion (Part 5C): following an entity implicitly follows
    // its descendants via parent_id. This is TRANSITIVE so the full chain works:
    // Network → Event Series → Year, and Network → Church. A child is any church
    // whose parent_id points at something already in the followed set; we loop
    // until no new descendants are found so grandchildren (a series' years under
    // a followed network) are included too. The explicit follow set
    // (sermonNoteChurchIds) is left untouched so the follow-toggle UI still
    // reflects what the user chose.
    if (base.length === 0 || !Array.isArray(churches) || churches.length === 0) return base;
    const followed = new Set(base);
    const result = [...base];
    let changed = true;
    while (changed) {
      changed = false;
      for (const c of churches) {
        if (!c || !c.id || followed.has(c.id)) continue;
        if (c.parent_id && followed.has(c.parent_id)) {
          followed.add(c.id);
          result.push(c.id);
          changed = true;
        }
      }
    }
    return result.length === base.length ? base : result;
  }, [activeChurchId, profile?.church_id, sermonNoteChurchIds, churches]);

  const [sermonSettingsOpen, setSermonSettingsOpen] = useState(false);
  const [encourSettingsOpen, setEncourSettingsOpen] = useState(false);
  const [editingEncouragement, setEditingEncouragement] = useState(null);

  // Live sermons data (replaces the SERMONS mock)
  // Scope the sermon load to the user's followed + home churches so the reader
  // never downloads the whole platform's sermons (key for scale). New follows
  // change effectiveSermonChurchIds → the hook refetches automatically.
  const { sermons: allSermons, loading: sermonsLoading, refresh: refreshSermons } = useSermons(effectiveSermonChurchIds);
  // Scope to the user's selected churches. The active church is always
  // in the list; the profile sheet "Show sermon notes for" picker lets
  // the user widen the set. Single-church users still rely on RLS as a
  // second line of defence — this filter is the client-side gate for
  // super admins (who can see everything) and for users who explicitly
  // opted into additional churches.
  // When no church is in scope (e.g. brand-new visitor with no home church
  // and no follows), return [] rather than allSermons — otherwise the user
  // accidentally sees sermon notes from every church on the platform, even
  // ones they never followed.
  const churchSermons = effectiveSermonChurchIds.length > 0
    ? allSermons.filter(s => effectiveSermonChurchIds.includes(s.church_id))
    : [];

  // True while sermon data is still arriving OR the user's church scope hasn't
  // hydrated yet. The second case is the subtle one: the RLS query can return
  // sermons before activeChurchId/profile.church_id have loaded, so
  // effectiveSermonChurchIds is briefly empty and churchSermons collapses to
  // []. Without this flag the Sermons tab would flash "No sermons yet" during
  // that window. We require allSermons to be non-empty so a genuine no-church
  // visitor (who legitimately has nothing) still falls through to the empty
  // state instead of spinning forever.
  const sermonsHydrating =
    sermonsLoading || (effectiveSermonChurchIds.length === 0 && allSermons.length > 0);

  // Whether the Bible read view should hold behind a spinner until the
  // sermon-note dots can be computed. The dots come from churchSermons, which
  // needs both the sermon fetch to finish AND the user's church to be known —
  // otherwise a reader sees scripture momentarily dot-less and assumes their
  // church has no sermon notes. Gated only on the fetch + profile hydration so
  // it always resolves: a logged-in user whose profile hasn't loaded, or any
  // in-flight fetch, waits; everyone else (incl. a church with zero sermons or
  // a no-church visitor) falls straight through.
  const sermonDotsPending = sermonsLoading || (!!user && !profile);

  // Active church record — drives the header copy + walkthrough interpolation.
  // The Church > Info page now uses `infoChurchId` (below) so the user can
  // browse a followed church's info without changing their home church.
  const activeChurch = churches?.find(c => c.id === (activeChurchId || profile?.church_id)) || null;
  const activeChurchName     = activeChurch?.name         || "";
  const activeChurchLocation = activeChurch?.location     || "";

  // Which church's content is shown on Church > Info. Defaults to the
  // home church but can be switched via the dropdown at the top of the
  // Info page so users see the right church's latest sermons, vision,
  // events, etc. without affecting the rest of the app's scoping.
  const [infoChurchId, setInfoChurchId] = useState(null);
  // When the home church hydrates (or changes), set the Info-page target
  // to it — but only if the user hasn't already picked something else.
  useEffect(() => {
    const home = activeChurchId || profile?.church_id || null;
    if (!home) return;
    setInfoChurchId(prev => prev || home);
  }, [activeChurchId, profile?.church_id]);
  const resolvedInfoChurchId = infoChurchId || activeChurchId || profile?.church_id || null;
  const infoChurch = churches?.find(c => c.id === resolvedInfoChurchId) || activeChurch || null;
  const infoChurchWebsite  = infoChurch?.website      || "";
  const infoChurchVision   = infoChurch?.vision       || "";
  const infoChurchMeetInfo = infoChurch?.meeting_info || "";

  // ── Church set-up onboarding (resume) ─────────────────────────────
  // A church admin whose church hasn't completed first-run setup gets dropped
  // into the ChurchOnboarding flow to scan their first sermon. Normal
  // self-serve onboarding sets setup_complete at creation time, so this only
  // fires for churches created another way (e.g. by a super admin) or for an
  // interrupted flow. Dismissable so an admin is never trapped.
  const [showChurchOnboarding, setShowChurchOnboarding] = useState(false);
  // A no-church user (or any leader) creating a brand-new church from the
  // profile — runs the full ChurchOnboarding flow with no church to resume.
  const [createChurchOpen, setCreateChurchOpen] = useState(false);
  useEffect(() => {
    if (!profile || profile.role !== "admin") return;
    if (!activeChurch || activeChurch.setup_complete !== false) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(`nlc.churchSetupDismissed:${activeChurch.id}`) === "1"; } catch {}
    if (!dismissed) setShowChurchOnboarding(true);
  }, [profile, activeChurch]);
  function dismissChurchOnboarding() {
    try { if (activeChurch?.id) localStorage.setItem(`nlc.churchSetupDismissed:${activeChurch.id}`, "1"); } catch {}
    setShowChurchOnboarding(false);
  }

  // Manual "add a sermon" — reuses the onboarding URL→scan→refs flow (resume
  // mode) against the current church instead of the old upload form.
  const [scanSermonOpen, setScanSermonOpen] = useState(false);

  // Pending "open the sermon sheet" request from a deep link (open=sermon).
  // Applied by an effect once the target chapter's verses have loaded, so we
  // call the latest openSermonForSelection (not a stale memoised closure).
  const [pendingSermonVk, setPendingSermonVk] = useState(null);

  // Pending "scroll the reader to this verse" request from an in-app navigation
  // (e.g. "Open in Bible" on a sermon sheet). The first-mount restore only
  // handles the saved place; this scrolls when we navigate within the session.
  // Shape: { book, chap, verse } | null. Applied once the chapter loads.
  const [pendingScrollVerse, setPendingScrollVerse] = useState(null);

  // Settings sheet — only elders / admins / super-admins can open it.
  const [churchSettingsOpen, setChurchSettingsOpen] = useState(false);

  // Upcoming events for the church currently shown on the Info page,
  // refetched when the user switches via the dropdown. Failures (e.g.
  // table not yet created) silently collapse the events section.
  const [churchEvents, setChurchEvents] = useState([]);
  const loadChurchEvents = useCallback(async () => {
    if (!resolvedInfoChurchId) { setChurchEvents([]); return; }
    const todayIso = new Date().toISOString().slice(0, 10);
    const { data, error } = await supabase
      .from("church_events")
      .select("*")
      .eq("church_id", resolvedInfoChurchId)
      .gte("event_date", todayIso)
      .order("event_date", { ascending: true })
      .limit(12);
    if (error) { setChurchEvents([]); return; }
    setChurchEvents(data || []);
  }, [resolvedInfoChurchId]);
  useEffect(() => { loadChurchEvents(); }, [loadChurchEvents]);
  // Build verseKey → [sermon refs] and chapterKey → [sermon refs] maps,
  // plus the books / chapters that have any sermon coverage. Storing the
  // full ref array per key (rather than just a Set of keys) means callers
  // can show accurate counts — "Romans 8:1 has 7 sermon clips" — instead
  // of the old "this verse has at least one ref" boolean.
  // Home church (vs followed) for the dot fill distinction — filled lavender
  // when the home church has a ref at a verse, hollow when only a followed
  // church does. See docs/designs/BibleDots.dc.html.
  const homeChurchIdForDots = activeChurchId || profile?.church_id;
  const { sermonRefMap, sermonChapterMap, sermonRefIndex, sermonChapterIndex, booksWithSermons, chaptersWithSermons, sermonDotFill, chapterSermonStats } = (() => {
    const verseMap    = new Map();   // vk → ref[]
    const chapMap     = new Map();   // chapterKey → ref[]
    const books       = new Set();
    const chapters    = new Map();   // book name → Set<number>
    const dotFill     = new Map();   // vk -> 'home' | 'followed'
    const chapStats   = new Map();   // "{book} {chapter}" -> { sermonIds: Set, churchIds: Set }
    const addBookChap = (b, c) => {
      books.add(b);
      if (!chapters.has(b)) chapters.set(b, new Set());
      chapters.get(b).add(c);
    };
    const push = (map, key, ref) => {
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ref);
    };
    const noteDotFill = (vk, ref) => {
      if (ref.church_id && ref.church_id === homeChurchIdForDots) { dotFill.set(vk, 'home'); return; }
      if (!dotFill.has(vk)) dotFill.set(vk, 'followed');
    };
    const noteChapterStats = (chapKey, ref) => {
      if (!chapStats.has(chapKey)) chapStats.set(chapKey, { sermonIds: new Set(), churchIds: new Set() });
      const s = chapStats.get(chapKey);
      if (ref.sermon_id) s.sermonIds.add(ref.sermon_id);
      if (ref.church_id) s.churchIds.add(ref.church_id);
    };
    for (const sermon of churchSermons) {
      for (const r of (sermon.sermon_refs || [])) {
        if (!r?.verse_ref) continue;
        // Range refs like "Matthew 20:1-16" and chapter-only refs like
        // "Matthew 20" expand into every covered verse so the per-verse
        // indicator lights up across the whole range. Prefers the
        // explicit `end_verse` column from the DB when present.
        const { verseKeys: vks, chapterKey } = expandRefRange(r);
        for (const vk of vks) {
          push(verseMap, vk, r);
          noteDotFill(vk, r);
          const m = vk.match(/^(.+)\s(\d+):\d+$/);
          if (m) { addBookChap(m[1], parseInt(m[2])); noteChapterStats(`${m[1]} ${m[2]}`, r); }
        }
        if (chapterKey) {
          push(chapMap, chapterKey, r);
          const m = chapterKey.match(/^(.+)\s(\d+)$/);
          if (m) { addBookChap(m[1], parseInt(m[2])); noteChapterStats(chapterKey, r); }
        }
      }
    }
    const chapterStatsOut = new Map();
    for (const [key, s] of chapStats) chapterStatsOut.set(key, { sermonCount: s.sermonIds.size, churchCount: s.churchIds.size });
    return {
      sermonRefMap:        verseMap,
      sermonChapterMap:    chapMap,
      // Back-compat: existing call sites use .has(key) on these.
      sermonRefIndex:      new Set(verseMap.keys()),
      sermonChapterIndex:  new Set(chapMap.keys()),
      booksWithSermons:    books,
      chaptersWithSermons: chapters,
      sermonDotFill:       dotFill,
      chapterSermonStats:  chapterStatsOut,
    };
  })();

  // sermon_id -> sermon, for turning a selection's touching refs into real
  // sermon objects (thumbnails, display_title, church) — the ref rows
  // themselves only carry ids.
  const sermonById = (() => {
    const m = new Map();
    for (const s of churchSermons) m.set(s.id, s);
    return m;
  })();

  // Sermons touching a selection of verse keys (per-verse refs + chapter
  // refs, deduped by sermon id). Used by the verse-selection bar's "n sermon
  // clips" row (docs/designs/SelBarB2.dc.html §1b) — returns sermon objects,
  // not just a count, so the row can show real thumbnails and church names.
  function sermonsTouchingSelection(vks) {
    const seen = new Map();
    for (const vk of vks || []) {
      const refs = sermonRefMap.get(vk);
      if (refs) for (const r of refs) {
        if (r.sermon_id && !seen.has(r.sermon_id)) {
          const s = sermonById.get(r.sermon_id);
          if (s) seen.set(r.sermon_id, s);
        }
      }
      // Whole-chapter refs in the same chapter cover every selected verse.
      const m = vk.match(/^(.+)\s(\d+):/);
      if (m) {
        const cRefs = sermonChapterMap.get(`${m[1]} ${m[2]}`);
        if (cRefs) for (const r of cRefs) {
          if (r.sermon_id && !seen.has(r.sermon_id)) {
            const s = sermonById.get(r.sermon_id);
            if (s) seen.set(r.sermon_id, s);
          }
        }
      }
    }
    return [...seen.values()];
  }

  // Helper: returns the gold church-name pill for a sermon card. Only
  // shown when the user has subscribed to more than one church — in
  // single-church mode there's no ambiguity to surface so cards stay
  // clean. When multi-church is active, EVERY card carries the pill
  // (including refs from the active church) so the user can always tell
  // which church each ref came from.
  function renderChurchPill(churchId) {
    if (!churchId) return null;
    if (effectiveSermonChurchIds.length <= 1) return null;
    const c = churches?.find(x => x.id === churchId);
    const name = c?.name;
    if (!name) return null;
    // Part 5G — entity-type indicators. Events (and event series) get an amber
    // pill with a 📅 and, where known, the year. Everything else (churches,
    // ministries, networks-as-entity, or when entity_type is absent) keeps the
    // existing gold pill showing the specific entity's own name.
    const etype = c?.entity_type;
    const isEvent = etype === 'event' || etype === 'event_series';
    if (isEvent) {
      let label = name;
      const d = c?.event_start_date || c?.event_end_date;
      if (d) {
        const yr = new Date(d).getFullYear();
        if (Number.isFinite(yr) && !new RegExp(`\\b${yr}\\b`).test(name)) label = `${name} ${yr}`;
      }
      return (
        <div style={{
          display:"inline-block",
          background:"rgba(245,158,11,0.15)",
          border:"1px solid rgba(245,158,11,0.45)",
          color:"#f59e0b",
          fontSize:11, fontWeight:700, letterSpacing:"0.06em",
          padding:"4px 10px", borderRadius:20,
          marginBottom:10, textTransform:"uppercase",
        }}>📅 {label}</div>
      );
    }
    return (
      <div style={{
        display:"inline-block",
        background:"rgba(232,200,122,0.15)",
        border:"1px solid rgba(232,200,122,0.4)",
        color:"var(--gold-ink)",
        fontSize:11, fontWeight:700, letterSpacing:"0.08em",
        padding:"4px 10px", borderRadius:20,
        marginBottom:10, textTransform:"uppercase",
      }}>{name}</div>
    );
  }

  // New 3-section navigation: bible / connect / church. Each section drives
  // its own sub-tab state below.
  const [tab,        setTab]        = useState("bible");
  const [bibleSub,   setBibleSub]   = useState("read");   // "read" | "saved"
  const [churchSub,  setChurchSub]  = useState("info");   // "info" | "sermons"

  // Default landing tab is This Week once we know the member has a home
  // church (profile loads asynchronously, so this can't be the useState
  // initializer above — it needs to fire once profile actually arrives).
  // Guarded to run only once so it never yanks the user back to This Week
  // after they've deliberately navigated elsewhere.
  const defaultedTabRef = useRef(false);
  useEffect(() => {
    if (defaultedTabRef.current) return;
    if (profile?.church_id) {
      defaultedTabRef.current = true;
      setTab("week");
    }
  }, [profile?.church_id]);
  // Reading settings (Aa) - text size + translation + dyslexia-friendly font.
  const readingSettings = useReadingSettings();
  const [readingSettingsOpen, setReadingSettingsOpen] = useState(false);

  // NIV availability — depends on the API.Bible key tier on the edge function.
  // Checked once on mount, cached in localStorage so subsequent loads don't
  // hit the network. `null` = "not yet known".
  const [nivAvailable, setNivAvailable] = useState(() => {
    try {
      const raw = localStorage.getItem("nlc.nivAvailable");
      return raw === null ? null : raw === "true";
    } catch { return null; }
  });
  useEffect(() => {
    if (nivAvailable !== null) return;
    let cancelled = false;
    checkNivAvailability().then(avail => {
      if (cancelled) return;
      setNivAvailable(!!avail);
      try { localStorage.setItem("nlc.nivAvailable", String(!!avail)); } catch {}
    });
    return () => { cancelled = true };
  }, [nivAvailable]);

  // Bible-reader toast — non-blocking message for KJV fallback etc.
  const [bibleToast, setBibleToast] = useState("");
  function showBibleToast(msg) {
    setBibleToast(msg);
    setTimeout(() => setBibleToast(""), 3500);
  }
  // App-wide accent is a single purple - removed the per-section variations.
  const sectionAccent = "#E8C87A";
  // Default to where the user last was; falls back to KJV / Romans 8 for
  // first-time visits (cleared storage, fresh device, etc.).
  const lastPlace = (() => {
    if (typeof window === "undefined") return {};
    try { return JSON.parse(localStorage.getItem("nlc.lastPlace") || "{}"); }
    catch { return {}; }
  })();
  const [ver,        setVer]        = useState(lastPlace.ver  || "ESV");
  const [book,       setBook]       = useState(lastPlace.book || "Romans");
  const [chap,       setChap]       = useState(lastPlace.chap || 8);
  // Topmost visible verse — persisted so we can restore scroll position
  // (not just the chapter) when the user re-enters the app.
  const [topVerse,   setTopVerse]   = useState(() => {
    const v = Number(lastPlace.verse);
    return Number.isFinite(v) && v >= 1 ? v : 1;
  });
  useEffect(() => {
    localStorage.setItem("nlc.lastPlace", JSON.stringify({ ver, book, chap, verse: topVerse }));
  }, [ver, book, chap, topVerse]);

  // One-shot scroll restoration on first mount: snapshot the saved verse
  // and book/chap/ver, and after verses load, scroll to that verse. After
  // that, normal scroll tracking takes over.
  const initialRestoreRef = useRef({
    done: false,
    verse: (() => {
      const v = Number(lastPlace.verse);
      return Number.isFinite(v) && v >= 1 ? v : 1;
    })(),
    key: `${lastPlace.ver || "ESV"}/${lastPlace.book || "Romans"}/${lastPlace.chap || 8}`,
  });
  // Ref for the scripture scroll container so we can read scroll position
  // and scroll-to-verse without querying the DOM by class.
  const scriptureRef = useRef(null);
  // Track the previous nav key so a true book/chap/ver change resets the
  // topmost-visible verse back to 1 (otherwise a scroll-event might fire
  // before the new chapter renders and persist the wrong verse).
  const prevNavKeyRef = useRef(`${ver}/${book}/${chap}`);
  useEffect(() => {
    const key = `${ver}/${book}/${chap}`;
    if (key !== prevNavKeyRef.current) {
      prevNavKeyRef.current = key;
      setTopVerse(1);
      // Reset scroll for the new chapter so we don't carry over the previous
      // chapter's scroll position.
      if (scriptureRef.current) scriptureRef.current.scrollTop = 0;
    }
  }, [ver, book, chap]);
  const [highlights, setHighlights] = useState({"Romans 8:1":"yellow"});
  const [saved,      setSaved]      = useState({});
  const [flagged,    setFlagged]    = useState({});
  // Question sheet - opens when the user taps "I've got questions" in the
  // action menu. Shape: { vk, book, chap, verseNum, text, draft, isEditing }.
  const [questionSheet, setQuestionSheet] = useState(null);
  // Saved tab filter - "all" | "highlighted" | "questions" | "notes".
  const [savedView, setSavedView] = useState("all");
  // Church-wide anonymous questions, loaded for moderators only via RLS.
  const [churchQuestions, setChurchQuestions] = useState([]);
  // Pending access requests + removed-posts log for the Elders Only dashboard.
  const [pendingAccess, setPendingAccess] = useState([]);
  const [removedPostsList, setRemovedPostsList] = useState([]);
  const [accessActionState, setAccessActionState] = useState({}); // request id -> 'pending' | 'done' | 'error'
  // Elders dashboard sheet opened from the profile. The Access sub-tab
  // is reused for the "Invite requests" entry, which routes here too.
  const [eldersSheetOpen, setEldersSheetOpen] = useState(false);
  // AI-generated pattern summary for the Elders dashboard. Cached by question
  // count so we don't re-call Claude on every render.
  const [eldersAiSummary, setEldersAiSummary] = useState({ count: -1, text: "", loading: false, error: "" });
  // Elders sub-tab + collapsible-section state.
  const [eldersSubTab,   setEldersSubTab]   = useState("questions"); // "questions" | "prayer"
  const [questionThemesOpen, setQuestionThemesOpen] = useState(true);

  // Hydrate the local `flagged` map from Supabase so questions persist across
  // sessions and devices. The Supabase row is the source of truth; the local
  // map is just a render-friendly view of it.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('flagged_verses').select('*').eq('user_id', user.id);
      if (error || cancelled) return;
      const next = {};
      const today = new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
      for (const row of data || []) {
        next[row.verse_ref] = {
          text: row.verse_text || '',
          ref: row.verse_ref,
          flaggedAt: row.created_at ? new Date(row.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : today,
          book: row.book,
          chap: row.chapter,
          verse: row.verse,
          question: row.question || '',
        };
      }
      setFlagged(next);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Hydrate the local `saved` map from Supabase. Mirrors the `flagged` pattern
  // above - the saved_verses row is the source of truth and the local map is
  // a render-friendly keyed view of it.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('saved_verses').select('*').eq('user_id', user.id);
      if (error || cancelled) return;
      const next = {};
      for (const row of data || []) {
        next[row.verse_ref] = {
          text: row.verse_text || '',
          ref: row.verse_ref,
          note: row.note || '',
          savedAt: row.created_at
            ? new Date(row.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})
            : '',
        };
      }
      setSaved(next);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Moderator-only: load every flagged_verses row in the active church so the
  // elder dashboard can show all anonymous questions. RLS guarantees regular
  // members only see their own rows, so for them this select returns the same
  // data as the per-user load above - the dashboard still renders correctly.
  useEffect(() => {
    if (!isModerator || !activeChurchId) { setChurchQuestions([]); return; }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('flagged_verses')
        .select('id, verse_ref, book, chapter, verse, verse_text, question, created_at, user_id, author:profiles!user_id(full_name, email)')
        .eq('church_id', activeChurchId)
        .not('question', 'is', null)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (error) { console.warn('Load church questions failed:', error.message); return; }
      setChurchQuestions(data || []);
    })();
    return () => { cancelled = true; };
  }, [isModerator, activeChurchId, flagged]);

  // Pending access requests for the active church (moderator-only via RLS).
  // Includes the requester's profile so we can render name/email.
  async function loadPendingAccess() {
    if (!isModerator || !activeChurchId) { setPendingAccess([]); return; }
    const { data, error } = await supabase
      .from('access_requests')
      .select('id, user_id, church_id, feature, message, status, created_at, profiles!user_id(full_name, role)')
      .eq('church_id', activeChurchId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    if (error) { console.warn('Load access requests failed:', error.message); return; }
    setPendingAccess(data || []);
  }
  useEffect(() => { loadPendingAccess(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [isModerator, activeChurchId]);

  // Removed-posts log for the active church (lives behind the
  // profile → Church Management → Removed Posts entry).
  const loadRemovedPosts = useCallback(async () => {
    if (!isModerator || !activeChurchId) { setRemovedPostsList([]); return; }
    const { data, error } = await supabase
      .from('removed_posts')
      .select('id, post_type, post_id, original_text, removed_by, removed_at, profiles!removed_by(full_name)')
      .order('removed_at', { ascending: false })
      .limit(100);
    if (error) { console.warn('Load removed posts failed:', error.message); return; }
    setRemovedPostsList(data || []);
  }, [isModerator, activeChurchId]);
  useEffect(() => { loadRemovedPosts(); }, [loadRemovedPosts]);

  async function restoreRemovedPost(rp) {
    const tableMap = {
      verse_comment:  'verse_comments',
      prayer:         'prayers',
      prayer_comment: 'prayer_comments',
      encouragement:  'encouragements',
      community_post: 'community_posts',
    };
    const table = tableMap[rp.post_type];
    if (!table) return { error: `Unknown post type: ${rp.post_type}` };
    const { error: upErr } = await supabase.from(table)
      .update({ removed: false }).eq('id', rp.post_id);
    if (upErr) return { error: upErr.message };
    const { error: delErr } = await supabase.from('removed_posts')
      .delete().eq('id', rp.id);
    if (delErr) console.warn('Restore: failed to delete log row:', delErr.message);
    setRemovedPostsList(curr => curr.filter(x => x.id !== rp.id));
    return { error: null };
  }

  async function approveAccessRequest(req) {
    setAccessActionState(s => ({ ...s, [req.id]: "pending" }));
    try {
      // Promote the requester to member of the church they asked to join.
      // .select() so RLS-silenced empty updates surface as an explicit
      // "no rows updated" error instead of looking like a success.
      const profileUpdate = await supabase.from('profiles')
        .update({ role: 'member', church_id: req.church_id })
        .eq('id', req.user_id)
        .select();
      if (profileUpdate.error) throw profileUpdate.error;
      if (!profileUpdate.data || profileUpdate.data.length === 0) {
        throw new Error("Couldn't update the requester's profile (RLS denied or no matching row).");
      }
      const reqUpdate = await supabase.from('access_requests')
        .update({ status: 'approved', reviewed_by: user.id, reviewed_at: new Date().toISOString() })
        .eq('id', req.id)
        .select();
      if (reqUpdate.error) throw reqUpdate.error;
      if (!reqUpdate.data || reqUpdate.data.length === 0) {
        throw new Error("Couldn't update the access request row (RLS denied).");
      }
      setAccessActionState(s => ({ ...s, [req.id]: "done" }));
      loadPendingAccess();
    } catch (e) {
      const msg = e?.message || String(e);
      console.warn('approveAccessRequest:', msg);
      setAccessActionState(s => ({ ...s, [req.id]: { kind: "error", msg } }));
    }
  }
  async function declineAccessRequest(req) {
    setAccessActionState(s => ({ ...s, [req.id]: "pending" }));
    try {
      const { data, error } = await supabase.from('access_requests')
        .update({ status: 'denied', reviewed_by: user.id, reviewed_at: new Date().toISOString() })
        .eq('id', req.id)
        .select();
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error("Couldn't update the access request row (RLS denied).");
      }
      setAccessActionState(s => ({ ...s, [req.id]: "done" }));
      loadPendingAccess();
    } catch (e) {
      const msg = e?.message || String(e);
      console.warn('declineAccessRequest:', msg);
      setAccessActionState(s => ({ ...s, [req.id]: { kind: "error", msg } }));
    }
  }

  // Generate the Elders-dashboard AI summary the first time we're on the
  // elders tab and we have questions to summarise. Cached by question count
  // so we don't re-call Claude until new ones appear.
  useEffect(() => {
    if (tab !== "elders" || !isModerator) return;
    if (!churchQuestions || churchQuestions.length === 0) return;
    if (eldersAiSummary.count === churchQuestions.length && eldersAiSummary.text) return;

    let cancelled = false;
    setEldersAiSummary({ count: churchQuestions.length, text: "", loading: true, error: "" });
    (async () => {
      try {
        const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
        const lines = churchQuestions.map(q => `- ${q.verse_ref}: "${(q.question || '').replace(/\s+/g, ' ').slice(0, 240)}"`).join("\n");
        const r = await fetch('https://icsdpckobituqivafuqo.supabase.co/functions/v1/claude-proxy', {
          method: 'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'apikey':        SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            model: 'claude-sonnet-4-6',
            max_tokens: 800,
            system: "You help church elders spot patterns in the anonymous questions their members raise about Bible verses. Identify 3-5 themes, name the books or passages that recur, and end with one short teaching suggestion. 2-3 short paragraphs, plain language, no preamble. Markdown allowed.",
            messages: [{ role: 'user', content: `Anonymous member questions from our church:\n\n${lines}` }],
          }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (data.error) throw new Error(data.error.message || 'Claude error');
        const text = data.content?.[0]?.text || '';
        if (!cancelled) setEldersAiSummary({ count: churchQuestions.length, text, loading: false, error: "" });
      } catch (e) {
        if (!cancelled) setEldersAiSummary({ count: churchQuestions.length, text: "", loading: false, error: e.message || String(e) });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isModerator, churchQuestions.length]);
  // Verse comments - loaded from Supabase, scoped to the current user's
  // church_id (set in useData via useAuth().profile.church_id). Returned as
  // an object keyed by verse_ref so the UI can read `comments[vk]` directly.
  const {
    commentsByVerse: comments,
    addComment: addVerseCommentSupabase,
    toggleLike: toggleVerseCommentLikeSupabase,
    removeComment: removeVerseCommentSupabase,
  } = useVerseComments(effectiveSermonChurchIds);
  const [commentVk,      setCommentVk]      = useState(null);
  const [commentIn,      setCommentIn]      = useState("");
  // Default audience is "Only you" - the private path is the gentler default
  // and the explicit dropdown above the composer lets the user choose to
  // share with the church when they want to.
  const [commentPrivate, setCommentPrivate] = useState(true);
  const [audienceOpen,   setAudienceOpen]   = useState(false);
  // Reset to the private default whenever a fresh sheet opens.
  useEffect(() => {
    if (commentVk) { setCommentPrivate(true); setAudienceOpen(false); }
  }, [commentVk]);
  // Bumped whenever a community-feed lazy chapter fetch completes, so verseText()
  // recomputes against the updated chapterCache.
  const [chapterTick, setChapterTick] = useState(0);
  const loadingChaptersRef = useRef(new Set());
  async function ensureChapterLoaded(bk, ch, vr = ver) {
    const cacheKey = `${vr}/${bk}/${ch}`;
    if (chapterCache[cacheKey]) return;
    if (loadingChaptersRef.current.has(cacheKey)) return;
    loadingChaptersRef.current.add(cacheKey);
    try {
      // Dispatch to the same backend the main reader uses. bible-api.com only
      // serves public-domain translations (KJV/WEB/ASV/YLT); ESV/NIV must go
      // through claude-proxy. Previously this always hit bible-api.com, so on
      // an ESV/NIV church opening a verse's comments/saved/flagged text fetched
      // bible-api.com?translation=esv, got nothing back, and the verse sat on
      // "Loading verse…" forever even though the reader rendered it fine.
      const bibleApiTranslations = new Set(["KJV", "WEB", "ASV", "YLT"]);
      let parsed;
      if (vr === "ESV") {
        parsed = await fetchESVChapter(bk, ch);
      } else if (vr === "NIV") {
        parsed = await fetchNIVChapter(bk, ch);
      } else if (bibleApiTranslations.has(vr)) {
        const r = await fetch(`https://bible-api.com/${bookToApiName(bk)}+${ch}?translation=${vr.toLowerCase()}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        parsed = (data?.verses || []).map(v => ({ v: v.verse, t: v.text.replace(/\n/g, " ").trim() }));
      } else {
        throw new Error(`Unsupported translation: ${vr}`);
      }
      if (parsed && parsed.length) {
        chapterCache[cacheKey] = parsed;
        saveCachedChapter(vr, bk, ch, parsed);
        setChapterTick(t => t + 1);
      }
    } catch (e) {
      console.error('ensureChapterLoaded:', e);
    } finally {
      loadingChaptersRef.current.delete(cacheKey);
    }
  }
  const [commentError,   setCommentError]   = useState("");
  const [filterBook, setFilterBook] = useState("all");
  const [filterChap, setFilterChap] = useState("all");
  const [chip, setChip] = useState("all");
  // Bible › Comments tab (docs/FIX-BIBLE-COMMENTS.md) — separate from the
  // Saved tab's filterBook/filterChap above, which stay as they are.
  const [commentsFilterTab, setCommentsFilterTab] = useState("all"); // all | mine | replies
  const [commentsScope, setCommentsScope] = useState("chapter"); // chapter | book | bible
  const [commentsScopeMenuOpen, setCommentsScopeMenuOpen] = useState(false);
  const [selectedSermon, setSelectedSermon] = useState(null);
  // Full Sermon Sheet (sermon-sheet feature): the sermon being shown full-screen
  // and the verse the user came from (null when opened from a sermon list).
  const [fullSermon, setFullSermon] = useState(null);
  const [fullSermonOrigin, setFullSermonOrigin] = useState(null);
  // Seconds to resume at when the sermon sheet is opened from a playing
  // clip's "Open full sermon →" (docs/FIX-SERMON-CLIPS.md §2) — the user
  // carries on from exactly where the clip was, not from 0.
  const [fullSermonStartAt, setFullSermonStartAt] = useState(null);
  // Resume point for the sermon sheet, applied once on the next mount (set
  // when restoring from the "Back to sermon" bar below; cleared by
  // openFullSermon()'s normal path).
  const [fullSermonInitialTab, setFullSermonInitialTab] = useState(null);
  const [fullSermonInitialScrollTop, setFullSermonInitialScrollTop] = useState(null);
  // §3b: the verse-clip sheet to restore when the sermon sheet now open was
  // itself opened from one (via VerseClipPlayingCard's "Open full sermon").
  const [fullSermonReturnToVerseSheet, setFullSermonReturnToVerseSheet] = useState(null);
  // §3b: snapshot of a sermon sheet "paused" by tapping an Open-in-Bible pill
  // from inside it — drives the "Back to sermon" bar. null when not paused.
  const [pausedSermon, setPausedSermon] = useState(null);
  // Reading plan day sheet, opened from PlansTab or a push deep link:
  // { plan, follow, initialDay } | null.
  const [openPlan, setOpenPlan] = useState(null);
  // Sermons tab: search-input toggle (button lives in the Sermons header,
  // state read by SermonsTab) and a one-shot signal bumped by the "See in
  // Bible Map" deep link to auto-open SermonsTab's Bible Map overlay.
  const [sermonsSearchOpen, setSermonsSearchOpen] = useState(false);
  const [bibleMapSignal, setBibleMapSignal] = useState(null);
  // Church/event/network profile page (full-screen overlay). Replaces the old
  // Church tab; opens when any entity card is tapped (Discover, search, etc.).
  const [profileEntity, setProfileEntity] = useState(null);
  // Inline edit of the selected sermon's title / date (elders+ only).
  // null, or { field: 'title' | 'date', value: string }.
  const [sermonEdit, setSermonEdit] = useState(null);
  const [sermonEditSaving, setSermonEditSaving] = useState(false);
  // Drop any in-progress edit when the sheet opens a different sermon or closes.
  const [sermonDeleteConfirm, setSermonDeleteConfirm] = useState(false);
  const [sermonDeleting, setSermonDeleting] = useState(false);
  useEffect(() => { setSermonEdit(null); setSermonDeleteConfirm(false); }, [selectedSermon?.id]);
  function startSermonEdit(field) {
    if (!selectedSermon) return;
    const value = field === "title"
      ? (selectedSermon.title || "")
      : (selectedSermon.rawDate || "").slice(0, 10);
    setSermonEdit({ field, value });
  }
  async function saveSermonEdit() {
    if (!sermonEdit || !selectedSermon?.id) return;
    const { field, value } = sermonEdit;
    setSermonEditSaving(true);
    const update = field === "title" ? { title: value.trim() } : { date: value || null };
    const { error } = await supabase.from("sermons").update(update).eq("id", selectedSermon.id);
    setSermonEditSaving(false);
    if (error) { console.error("saveSermonEdit:", error); return; }
    setSelectedSermon(prev => {
      if (!prev) return prev;
      if (field === "title") return { ...prev, title: value.trim() };
      const lbl = value ? new Date(value).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" }) : "";
      return { ...prev, rawDate: value, date: lbl };
    });
    setSermonEdit(null);
    refreshSermons?.();
  }
  // Delete the open sermon (elders/admins/super-admins only — RLS enforces the
  // same). sermon_refs cascade-delete with the parent row.
  async function deleteSelectedSermon() {
    if (!selectedSermon?.id) return;
    setSermonDeleting(true);
    const { error } = await supabase.from("sermons").delete().eq("id", selectedSermon.id);
    setSermonDeleting(false);
    if (error) { console.error("deleteSelectedSermon:", error); setSermonDeleteConfirm(false); return; }
    setSelectedSermon(null);
    setSermonDeleteConfirm(false);
    refreshSermons?.();
  }
  // Sermons tab: null = series grid, "__all__" = every sermon, or a series name
  // Sermons view: "__all__" = the default flat all-sermons list, null = the
  // playlist grid ("View by playlist"), or a playlist name = that playlist's
  // sermons (with a "back to playlists" button).
  const [selectedSeries, setSelectedSeries] = useState("__all__");
  // Sermons tab church filter: church_id, or 'all' to show every followed
  // church. Defaults to the user's primary church so the tab opens clean.
  // Has no effect on the Bible reader verse-dot indices — those still
  // span every followed church.
  const [sermonTabChurchFilter, setSermonTabChurchFilter] = useState('all');
  const [sermonChurchMenuOpen, setSermonChurchMenuOpen] = useState(false);
  // First-time default: when profile.church_id loads, set the filter to it
  // (unless the user has already chosen something else).
  const _sermonTabFilterInitRef = useRef(false);
  useEffect(() => {
    if (_sermonTabFilterInitRef.current) return;
    if (profile?.church_id) {
      setSermonTabChurchFilter(profile.church_id);
      _sermonTabFilterInitRef.current = true;
    }
  }, [profile?.church_id]);

  // An in-app sermon upload should target the church the user has SELECTED in
  // the Sermons tab — not their home/active church. (These are separate: the
  // Sermons filter is a view selection, activeChurchId is the app context.)
  // Falls back to the active/home church when the filter is "All churches".
  const sermonUploadChurchId = (sermonTabChurchFilter && sermonTabChurchFilter !== 'all')
    ? sermonTabChurchFilter
    : (activeChurchId || profile?.church_id || '');
  const sermonUploadChurch = (churches?.find(c => c.id === sermonUploadChurchId)) || activeChurch;
  const [profileOpen, setProfileOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [removedPostsOpen,  setRemovedPostsOpen]  = useState(false);
  const [moderationInitialTab, setModerationInitialTab] = useState(null);
  const [leadersPageOpen, setLeadersPageOpen] = useState(false);
  const [eventEditorTarget, setEventEditorTarget] = useState(null); // { event, occurrenceDate } | null
  const eventsRefreshRef = useRef(null);

  // ── First step done + reminder prompt (redesign Phase 8a) ───────────
  const [reminderPromptOpen, setReminderPromptOpen] = useState(false);
  const [reminderOtherCount, setReminderOtherCount] = useState(0);
  const [notifPermission, setNotifPermission] = useState(null); // 'granted' | 'denied' | 'undetermined' | null
  useEffect(() => {
    if (!profileOpen) return;
    Median.onReady(() => {
      try {
        if (!(Median.isNativeApp && Median.isNativeApp())) return;
        Median.permissions.status(['Notifications']).then(res => {
          setNotifPermission(res?.Notifications || null);
        }).catch(() => {});
      } catch (_e) { /* bridge not ready */ }
    });
  }, [profileOpen]);
  async function openReminderPromptFor(dayNumber, planId) {
    let total = 0;
    if (planId && dayNumber) {
      const { data } = await supabase.rpc('reading_plan_day_activity', { p_plan_id: planId, p_day_number: dayNumber, p_limit: 1 });
      total = (data && data[0]?.total_count) || 0;
    }
    setReminderOtherCount(total);
    setReminderPromptOpen(true);
  }
  async function handleReminderAccept(time) {
    await updateProfile({ reminder_time: time, reminder_prompted_at: new Date().toISOString() });
  }
  async function handleReminderNotNow() {
    await updateProfile({ reminder_prompted_at: new Date().toISOString(), reminder_prompt_count: (profile?.reminder_prompt_count || 0) + 1 });
    setReminderPromptOpen(false);
  }

  // ── First-run walkthrough ──────────────────────────────────
  // Step 0 = not running, 1-4 = step number. No longer auto-starts
  // (redesign Phase 8a) — new members land on This Week with no tour
  // overlay; it's only reachable manually via Profile → "Show me around".
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  const walkthroughActive = walkthroughStep > 0;
  function finishWalkthrough() {
    try { localStorage.setItem('walkthroughComplete', 'true'); } catch {}
    setWalkthroughStep(0);
  }
  function nextWalkthroughStep() {
    if (walkthroughStep >= WALKTHROUGH_LAST_STEP) { finishWalkthrough(); return; }
    setWalkthroughStep(s => s + 1);
  }
  const {
    items:       notifications,
    unreadCount: notifUnread,
    markAsRead:  markNotificationRead,
    markAllRead: markAllNotificationsRead,
  } = useNotifications();

  // Prayers - scoped to current user's church via useData.
  const {
    prayers,
    addPrayer:          addPrayerSupabase,
    togglePray:         togglePrayedSupabase,
    addComment:         addPrayerCommentSupabase,
    likePrayerComment:  togglePrayerCommentLikeSupabase,
    removePrayer:       removePrayerSupabase,
  } = usePrayers();

  // Encouragements - scoped to the active church (defaults to profile.church_id
  // for members/elders; super_admins pick a church via activeChurchId).
  // Posting is restricted to moderators via the Settings sheet; members only read.
  const {
    encouragements: rawEncouragements,
    toggleLike: toggleEncouragementLikeSupabase,
    removeEncouragement: removeEncouragementSupabase,
    addComment: addEncComment,
    likeComment: likeEncComment,
    removeComment: removeEncComment,
    refresh: refreshEncouragements,
  } = useEncouragements(activeChurchId);

  // Augment each encouragement with refs parsed directly from its text AND
  // pre-compute expanded verse/chapter keys (canonical book name, range
  // expansion, chapter-only routed separately). This makes verse linking
  // robust to: (a) the encouragement_refs migration not being applied,
  // (b) Claude's scan missing or being unavailable, (c) old encouragements
  // saved before the scanner, and (d) refs stored under non-canonical names
  // (e.g. "Psalm" vs "Psalms") or as ranges / chapter-only.
  const encouragements = rawEncouragements.map(en => {
    const dbRefs = en.refs || [];
    const seen = new Set(dbRefs.map(r => r.verseRef));
    const parsed = parseBibleRefs(en.text).filter(r => !seen.has(r.verseRef));
    const refs = parsed.length === 0 ? dbRefs : [
      ...dbRefs,
      ...parsed.map(r => ({ ...r, id: `text-${en.id}-${r.verseRef}`, fromText: true })),
    ];
    const verseKeys   = new Set();
    const chapterKeys = new Set();
    for (const r of refs) {
      // Prefer the explicit `end_verse` column for range expansion; falls
      // back to verse_ref string parsing for legacy rows and for refs
      // that came in via parseBibleRefs (no DB columns set).
      const { verseKeys: vks, chapterKey } = expandRefRange(r);
      for (const vk of vks) verseKeys.add(vk);
      if (chapterKey) chapterKeys.add(chapterKey);
    }
    return { ...en, refs, verseKeys, chapterKeys };
  });

  // verseRef → Set<encouragementId> (specific verses).
  // chapterKey → Set<encouragementId> (whole-chapter refs).
  const { encouragementRefIndex, encouragementChapterIndex } = (() => {
    const verseIdx   = new Map();
    const chapterIdx = new Map();
    for (const en of encouragements) {
      for (const vk of en.verseKeys) {
        if (!verseIdx.has(vk)) verseIdx.set(vk, new Set());
        verseIdx.get(vk).add(en.id);
      }
      for (const ck of en.chapterKeys) {
        if (!chapterIdx.has(ck)) chapterIdx.set(ck, new Set());
        chapterIdx.get(ck).add(en.id);
      }
    }
    return { encouragementRefIndex: verseIdx, encouragementChapterIndex: chapterIdx };
  })();

  // Books and chapters that have at least one encouragement linked. Used by
  // the book/chapter dropdowns so the gold dot appears for either kind of
  // reference (sermon OR encouragement). Uses both the verse index and the
  // chapter index so whole-chapter refs are reflected too.
  const { booksWithEncouragements, chaptersWithEncouragements } = (() => {
    const books = new Set();
    const chapters = new Map();
    const addBookChap = (b, c) => {
      books.add(b);
      if (!chapters.has(b)) chapters.set(b, new Set());
      chapters.get(b).add(c);
    };
    for (const vk of encouragementRefIndex.keys()) {
      const m = vk.match(/^(.+)\s(\d+):\d+$/);
      if (m) addBookChap(m[1], parseInt(m[2]));
    }
    for (const ck of encouragementChapterIndex.keys()) {
      const m = ck.match(/^(.+)\s(\d+)$/);
      if (m) addBookChap(m[1], parseInt(m[2]));
    }
    return { booksWithEncouragements: books, chaptersWithEncouragements: chapters };
  })();

  // Per-type book + chapter sets derived from comments and the user's own
  // flagged questions. Used to render the 4-marker indicator strip on the
  // book and chapter <option> entries.
  const { booksWithComments, chaptersWithComments } = (() => {
    const books = new Set(); const chapters = new Map();
    const addBookChap = (b, c) => {
      books.add(b);
      if (!chapters.has(b)) chapters.set(b, new Set());
      chapters.get(b).add(c);
    };
    for (const vk of Object.keys(comments || {})) {
      if ((comments[vk] || []).every(c => c.private)) continue;
      const m = vk.match(/^(.+)\s(\d+):\d+$/);
      if (m) addBookChap(m[1], parseInt(m[2]));
    }
    return { booksWithComments: books, chaptersWithComments: chapters };
  })();

  const { booksWithQuestions, chaptersWithQuestions } = (() => {
    const books = new Set(); const chapters = new Map();
    const addBookChap = (b, c) => {
      books.add(b);
      if (!chapters.has(b)) chapters.set(b, new Set());
      chapters.get(b).add(c);
    };
    for (const [vk, f] of Object.entries(flagged || {})) {
      if (!f?.question) continue;
      const m = vk.match(/^(.+)\s(\d+):\d+$/);
      if (m) addBookChap(m[1], parseInt(m[2]));
    }
    return { booksWithQuestions: books, chaptersWithQuestions: chapters };
  })();


  // Verse-refs whose encouragements we're currently viewing (sub-sheet).
  const [encourageSheetVks, setEncourageSheetVks] = useState(null);
  // Single-encouragement "Read more" sheet — shows the full verbatim
  // transcript text plus speaker/date/church/YouTube-link metadata.
  const [expandedEncouragement, setExpandedEncouragement] = useState(null);

  const [sheet,      setSheet]      = useState(null);
  const [shTab,      setShTab]      = useState("notes");
  // Which clip is expanded/playing in the verse sheet (docs/designs/VerseClip.dc.html)
  // — only one at a time; null = all rows collapsed. Reset whenever the sheet
  // reopens for a different verse.
  const [playingClipId, setPlayingClipId] = useState(null);
  const [verseSheetVisibleCount, setVerseSheetVisibleCount] = useState(5);
  // Verse selection: a sorted, contiguous run of verse numbers in the current
  // book+chapter. The action menu opens from the floating Actions bar.
  const [selection, setSelection] = useState([]); // numbers, ascending, contiguous
  // Verse-selection bar (docs/designs/SelBarB2.dc.html): the ⋯ menu's open
  // state, and a small shared "Copied" toast for the Copy action and the
  // Share fallback.
  const [selMoreOpen, setSelMoreOpen] = useState(false);
  const [selCopiedToast, setSelCopiedToast] = useState(false);
  function flashCopiedToast() {
    setSelCopiedToast(true);
    setTimeout(() => setSelCopiedToast(false), 1800);
  }
  useEffect(() => { setSelMoreOpen(false); }, [selection]);

  // ── Deep links ────────────────────────────────────────────────
  // Push notifications open URLs like /app?screen=connect&tab=prayer&prayer_id=…
  // App.js captured the params at cold start; we apply them once auth + church
  // have loaded, then clear them so a refresh doesn't re-navigate. A foreground
  // tap (Median reopens the URL) re-fires via popstate / a custom event.
  const [deepLink, setDeepLink] = useState(() => takeDeepLink());
  // { type: 'prayer' | 'post', id } — scroll-to/flash target inside Connect.
  const [deepLinkHighlight, setDeepLinkHighlight] = useState(null);
  // When true, the notifications sheet scrolls to the access-request section.
  const [notifAccessScroll, setNotifAccessScroll] = useState(false);

  const applyDeepLink = useCallback((dl, uid) => {
    if (!dl || !dl.screen) return;
    if (dl.screen === "bible") {
      let bk = null;
      if (dl.book) { try { bk = decodeURIComponent(dl.book); } catch { bk = dl.book; } setBook(bk); }
      const ch = parseInt(dl.chapter, 10); if (!Number.isNaN(ch)) setChap(ch);
      const vs = parseInt(dl.verse, 10);
      setTab("bible");
      setBibleSub(dl.open === "comments" ? "comments" : "read");
      // open=sermonhint → land on the verse and pulse its red sermon dot
      // (used by onboarding "Explore your Bible"). No selection / no sheet.
      if (dl.open === "sermonhint" && bk && !Number.isNaN(ch) && !Number.isNaN(vs)) {
        setSermonHint({ book: bk, chapter: ch, verse: vs });
        sermonHintFiredRef.current = true; // don't also auto-fire the first-time hint
      } else {
        if (!Number.isNaN(vs)) setSelection([vs]);
        // Scroll the reader to the target verse (not the chapter top) once the
        // chapter's verses have loaded.
        if (bk && !Number.isNaN(ch) && !Number.isNaN(vs)) {
          setPendingScrollVerse({ book: bk, chap: ch, verse: vs });
        }
        // open=sermon → auto-open the sermon-notes sheet for the first verse,
        // applied once the chapter's verses have loaded (see effect below).
        if (dl.open === "sermon" && bk && !Number.isNaN(ch) && !Number.isNaN(vs)) {
          setPendingSermonVk({ book: bk, chap: ch, verse: vs });
        }
      }
    } else if (dl.screen === "connect") {
      if (dl.tab === "elders") {
        setEldersSubTab("prayer");
        setEldersSheetOpen(true);
      } else {
        setTab("community");
        setChip(dl.tab === "board" ? "board" : "prayer");
      }
      if (dl.prayer_id) setDeepLinkHighlight({ type: "prayer", id: dl.prayer_id });
      if (dl.post_id)   setDeepLinkHighlight({ type: "post",   id: dl.post_id });
    } else if (dl.screen === "notifications") {
      setNotificationsOpen(true);
      setNotifAccessScroll(dl.filter === "access_requests");
    } else if (dl.screen === "plan") {
      setTab("week");
      if (dl.plan_id) {
        const dayNum = parseInt(dl.day, 10) || 1;
        (async () => {
          const { data: planRow } = await supabase
            .from("reading_plans")
            .select("*, sermons(id, title, preacher, date, youtube_id, youtube_url, church_id), reading_plan_days(*)")
            .eq("id", dl.plan_id)
            .maybeSingle();
          if (!planRow) return;
          const plan = { ...planRow, reading_plan_days: (planRow.reading_plan_days || []).slice().sort((a, b) => a.day_number - b.day_number) };
          let follow = null;
          if (uid) {
            const { data } = await supabase
              .from("reading_plan_follows")
              .upsert({ plan_id: plan.id, user_id: uid, last_opened_day: dayNum }, { onConflict: "plan_id,user_id" })
              .select().single();
            follow = data;
          }
          setOpenPlan({ plan, follow, initialDay: dayNum });
        })();
      }
    }
  }, []);

  // Apply once the session and active church are ready (so lists exist to
  // navigate into). Clears the stash + URL params afterwards.
  useEffect(() => {
    if (!deepLink || !user?.id || !activeChurchId) return;
    applyDeepLink(deepLink, user.id);
    clearDeepLink();
    setDeepLink(null);
  }, [deepLink, user?.id, activeChurchId, applyDeepLink]);

  // Foreground re-entry: the OS/Median can reopen the deep-link URL while the
  // app is already running. popstate covers history changes; nlc:deeplink is a
  // hook a native wrapper can dispatch after navigating window.location.
  useEffect(() => {
    const reread = () => {
      const dl = parseDeepLinkFromSearch(window.location.search);
      if (dl) setDeepLink(dl);
    };
    window.addEventListener("popstate", reread);
    window.addEventListener("nlc:deeplink", reread);
    return () => {
      window.removeEventListener("popstate", reread);
      window.removeEventListener("nlc:deeplink", reread);
    };
  }, []);

  // Scroll the highlighted prayer/post into view (retry while its list loads),
  // then clear the highlight after the flash.
  useEffect(() => {
    if (!deepLinkHighlight?.id) return;
    let tries = 0;
    const elId = `dl-${deepLinkHighlight.type}-${deepLinkHighlight.id}`;
    const iv = setInterval(() => {
      const el = document.getElementById(elId);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); clearInterval(iv); }
      else if (++tries > 24) clearInterval(iv);
    }, 250);
    const clear = setTimeout(() => setDeepLinkHighlight(null), 5000);
    return () => { clearInterval(iv); clearTimeout(clear); };
  }, [deepLinkHighlight]);

  // (Auto-expanding a deep-linked prayer's reply thread on load now lives
  // inside CommunityTab.jsx, which owns `expandedPrayer` since Phase 5.)

  // ── OneSignal: link this device to the signed-in user (Median app) ──
  // Modern Median exposes NO injected globals (window.median/OneSignal are
  // undefined). Instead we talk to the native OneSignal SDK through the
  // `median-js-bridge` package, which routes over the median:// scheme. On the
  // plain web Median.isNativeApp() is false and the call below no-ops.
  //
  // We set the OneSignal *External User ID* to the Supabase user id. The push
  // edge function then targets recipients by that same id (external_id alias),
  // so we never have to capture/store a player id — OneSignal maps the external
  // id to whatever the current device subscription is.
  useEffect(() => {
    if (!user?.id) return;
    const externalId = String(user.id);
    Median.onReady(() => {
      try {
        if (Median.isNativeApp && Median.isNativeApp()) {
          Median.onesignal.externalUserId.set({ externalId });
        }
      } catch (_e) { /* web / bridge not ready */ }
    });
  }, [user?.id]);

  // ── Clear the OneSignal badge on launch and on foreground (Median) ──
  useEffect(() => {
    const clearBadge = () => {
      try {
        if (Median.isNativeApp && Median.isNativeApp()) Median.onesignal.badge.set(0);
      } catch (_e) { /* web / bridge not ready */ }
    };
    Median.onReady(clearBadge);                      // on mount / cold start
    document.addEventListener("resume", clearBadge); // Median fires 'resume' on foreground
    return () => document.removeEventListener("resume", clearBadge);
  }, []);

  // ── Reporting + blocking ─────────────────────────────────────
  // reportTarget: { content_type, content_id } | { content_type:'general' }.
  const [reportTarget, setReportTarget] = useState(null);
  const [reportBusy,   setReportBusy]   = useState(false);
  const GENERAL_CONTENT_ID = "00000000-0000-0000-0000-000000000000"; // sentinel for app-level reports
  const openReport = useCallback((content_type, content_id) => {
    setReportTarget({ content_type, content_id });
  }, []);
  async function submitReport({ reason, notes }) {
    if (!user?.id) return false;
    setReportBusy(true);
    const { error } = await supabase.from("reports").insert({
      reporter_id:  user.id,
      content_type: reportTarget.content_type,
      content_id:   reportTarget.content_id || GENERAL_CONTENT_ID,
      reason,
      church_id:    activeChurchId || profile?.church_id || null,
      notes,
    });
    setReportBusy(false);
    if (error) { console.warn("submit report:", error.message); return false; }
    return true;
  }

  // Dismissible legend above the book/chapter picker explaining the gold dot.
  const [sermonLegendDismissed, setSermonLegendDismissed] = useState(
    () => (typeof window !== "undefined" && !!localStorage.getItem("nlc.sermonLegendDismissed"))
  );
  function dismissSermonLegend() {
    setSermonLegendDismissed(true);
    try { localStorage.setItem("nlc.sermonLegendDismissed", "1"); } catch {}
  }

  // Verse-indicator legend. No always-visible trigger row anymore (per
  // docs/FIX-HEADERS.md) — opens once automatically the first time a user
  // visits Read, then only reachable again by... nothing, by design (the
  // mockup has no persistent ⓘ affordance). Dialog itself is unchanged.
  const [legendOverlayOpen, setLegendOverlayOpen] = useState(false);
  const legendOverlayRef = useRef(null);
  useEffect(() => {
    if (tab !== "bible" || bibleSub !== "read" || !user?.id) return;
    const key = `nlc.seenVerseLegend:${user.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch { return; }
    setLegendOverlayOpen(true);
  }, [tab, bibleSub, user?.id]);
  useEffect(() => {
    if (!legendOverlayOpen) return;
    const onDoc = (e) => {
      if (legendOverlayRef.current?.contains(e.target)) return;
      setLegendOverlayOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [legendOverlayOpen]);

  // Swipe-down-to-dismiss support for the action menu sheet.
  const menuSheetRef = useRef(null);
  const menuDragRef  = useRef({ startY: 0, dy: 0, active: false });
  const handleMenuTouchStart = (e) => {
    const y = e.touches?.[0]?.clientY ?? e.clientY;
    if (y == null) return;
    menuDragRef.current = { startY: y, dy: 0, active: true };
    if (menuSheetRef.current) menuSheetRef.current.style.transition = "none";
  };
  const handleMenuTouchMove = (e) => {
    if (!menuDragRef.current.active) return;
    const y = e.touches?.[0]?.clientY ?? e.clientY;
    if (y == null) return;
    const dy = Math.max(0, y - menuDragRef.current.startY);
    menuDragRef.current.dy = dy;
    if (menuSheetRef.current) {
      menuSheetRef.current.style.transform = `translateX(-50%) translateY(${dy}px)`;
    }
  };
  const handleMenuTouchEnd = () => {
    if (!menuDragRef.current.active) return;
    const { dy } = menuDragRef.current;
    menuDragRef.current.active = false;
    if (menuSheetRef.current) {
      menuSheetRef.current.style.transition = "transform 0.2s cubic-bezier(0.32,0.72,0,1)";
      menuSheetRef.current.style.transform  = "";
    }
    // > 100 px or > 25 % of sheet height → dismiss (clears the selection,
    // same as the ✕ button — the sheet's only purpose is acting on a
    // selection, so there's nothing to "close" separately from it).
    const threshold = Math.min(100, (menuSheetRef.current?.offsetHeight || 400) * 0.25);
    if (dy > threshold) setTimeout(() => setSelection([]), 60);
  };
  const [aiMsgs,     setAiMsgs]     = useState([]);
  const [aiIn,       setAiIn]       = useState("");
  const [aiLoad,     setAiLoad]     = useState(false);
  // null | "book" | "chapter" — the top pill opens "book", picking a book
  // moves to "chapter" (pickerBook holds the in-progress pick) before
  // actually committing to book/chap and closing.
  const [picker,     setPicker]     = useState(null);
  const [pickerBook, setPickerBook] = useState(null);

  // ── Pre-seed Romans 8 so reader works instantly on first load
  const [verses,     setVerses]     = useState(SEED_VERSES);
  const [bibleLoad,  setBibleLoad]  = useState(false);
  const [bibleErr,   setBibleErr]   = useState(null);

  // ── Sermon-note hint ──────────────────────────────────────────
  // Pulse the red sermon dot on a specific verse so first-time readers (and
  // users arriving from onboarding) immediately see the sermon-notes value.
  const [sermonHint, setSermonHint] = useState(null); // { book, chapter, verse } | null
  const sermonHintFiredRef = useRef(false);
  const [isFirstTimeReader] = useState(() => {
    try { return !localStorage.getItem("nlc.lastPlace"); } catch { return false; }
  });

  // Earliest (canonical) verse that has a sermon note, or null.
  function firstSermonVerse() {
    let best = null;
    for (const vk of sermonRefMap.keys()) {
      const m = vk.match(/^(.+)\s(\d+):(\d+)$/);
      if (!m) continue;
      const bi = BOOKS.findIndex(b => b.name === m[1]);
      if (bi < 0) continue;
      const c = parseInt(m[2], 10), v = parseInt(m[3], 10);
      const key = bi * 1e6 + c * 1e3 + v;
      if (!best || key < best.key) best = { key, book: m[1], chapter: c, verse: v };
    }
    return best ? { book: best.book, chapter: best.chapter, verse: best.verse } : null;
  }

  // Navigate the reader to a verse and pulse its sermon dot.
  const triggerSermonHint = useCallback((ref) => {
    if (!ref?.book || !ref?.chapter || !ref?.verse) return;
    setBook(ref.book); setChap(ref.chapter);
    setSelection([]); setTab("bible"); setBibleSub("read");
    setSermonHint({ book: ref.book, chapter: ref.chapter, verse: ref.verse });
    try { if (user?.id) localStorage.setItem(`nlc.sermonHintSeen:${user.id}`, "1"); } catch {}
  }, [user?.id]);

  // First-time readers: once sermons load, jump to the first sermon verse.
  useEffect(() => {
    if (sermonHintFiredRef.current) return;
    if (!user?.id || !isFirstTimeReader || tab !== "bible") return;
    if (sermonRefMap.size === 0) return;
    let seen = false;
    try { seen = localStorage.getItem(`nlc.sermonHintSeen:${user.id}`) === "1"; } catch {}
    if (seen) { sermonHintFiredRef.current = true; return; }
    const first = firstSermonVerse();
    if (!first) return; // no sermon verses yet — wait for them to load
    sermonHintFiredRef.current = true;
    triggerSermonHint(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sermonRefMap.size, user?.id, isFirstTimeReader, tab]);

  // Stop pulsing after a few seconds.
  useEffect(() => {
    if (!sermonHint) return;
    const id = setTimeout(() => setSermonHint(null), 7000);
    return () => clearTimeout(id);
  }, [sermonHint]);

  // Scroll the hinted verse into view once its chapter's verses have loaded.
  useEffect(() => {
    if (!sermonHint) return;
    if (book !== sermonHint.book || chap !== sermonHint.chapter) return;
    let tries = 0;
    const id = setInterval(() => {
      const el = document.getElementById(`v-${sermonHint.verse}`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); clearInterval(id); }
      if (++tries > 24) clearInterval(id);
    }, 150);
    return () => clearInterval(id);
  }, [sermonHint, book, chap, verses]);

  // Scroll to a verse navigated to in-session (e.g. "Open in Bible"), once the
  // target chapter's verses have rendered. Cleared after it lands so a later
  // manual scroll isn't yanked back.
  useEffect(() => {
    if (!pendingScrollVerse) return;
    if (book !== pendingScrollVerse.book || chap !== pendingScrollVerse.chap) return;
    if (!Array.isArray(verses) || verses.length === 0) return;
    let tries = 0;
    const id = setInterval(() => {
      const el = document.getElementById(`v-${pendingScrollVerse.verse}`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); setPendingScrollVerse(null); clearInterval(id); }
      if (++tries > 24) { setPendingScrollVerse(null); clearInterval(id); }
    }, 120);
    return () => clearInterval(id);
  }, [pendingScrollVerse, book, chap, verses]);

  const msgEnd = useRef(null);

  // ── Fetch Bible chapter (KJV/WEB → bible-api.com; ESV/NIV → claude-proxy)
  useEffect(() => {
    const cacheKey = `${ver}/${book}/${chap}`;

    // Use seed data for Romans 8 KJV instantly
    if (cacheKey === "KJV/Romans/8") {
      chapterCache[cacheKey] = SEED_VERSES;
      setVerses(SEED_VERSES);
      return;
    }
    // In-memory cache for this session.
    if (chapterCache[cacheKey]) {
      setVerses(chapterCache[cacheKey]);
      return;
    }
    // localStorage cache from a previous session.
    const persisted = loadCachedChapter(ver, book, chap);
    if (persisted) {
      chapterCache[cacheKey] = persisted;
      setVerses(persisted);
      return;
    }

    setBibleLoad(true);
    setBibleErr(null);
    setVerses([]);

    // Dispatch to the right backend based on translation.
    const bibleApiTranslations = new Set(["KJV", "WEB", "ASV", "YLT"]);
    const loader = ver === "ESV"  ? () => fetchESVChapter(book, chap)
                 : ver === "NIV"  ? () => fetchNIVChapter(book, chap)
                 : bibleApiTranslations.has(ver)
                   ? () => fetch(`https://bible-api.com/${bookToApiName(book)}+${chap}?translation=${ver.toLowerCase()}`)
                       .then(r => { if (!r.ok) throw new Error(`bible-api.com ${r.status}`); return r.json(); })
                       .then(data => {
                         if (!data?.verses?.length) throw new Error("No verses returned");
                         return data.verses.map(v => ({ v: v.verse, t: v.text.replace(/\n/g, " ").trim() }));
                       })
                   : () => Promise.reject(new Error(`Unsupported translation: ${ver}`));

    loader()
      .then(parsed => {
        if (!Array.isArray(parsed) || parsed.length === 0) throw new Error("No verses returned");
        chapterCache[cacheKey] = parsed;
        saveCachedChapter(ver, book, chap, parsed);
        setVerses(parsed);
        setBibleLoad(false);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error("Bible load error:", ver, book, chap, err);
        if (ver !== "KJV") {
          // Soft-fail to KJV — keep the reader usable.
          showBibleToast(`Could not load ${ver} — switching to KJV`);
          setBibleLoad(false);
          setVer("KJV");
          // The useEffect will re-fire with ver="KJV" and load the chapter.
        } else {
          setBibleErr(`Could not load ${book} ${chap}. Please check your connection.`);
          setBibleLoad(false);
        }
      });
  }, [book, chap, ver]);

  useEffect(() => { msgEnd.current?.scrollIntoView({behavior:"smooth"}); }, [aiMsgs]);

  // Restore scroll position on the very first time the saved chapter
  // finishes loading. Only fires if the user hasn't navigated away from
  // the saved book/chap/ver before the verses come in. Gated on
  // bibleLoad=false so we don't scroll the briefly-rendered seed verses
  // before the real translation loads.
  useEffect(() => {
    const snap = initialRestoreRef.current;
    if (snap.done) return;
    if (bibleLoad) return;
    if (!verses.length) return;
    if (`${ver}/${book}/${chap}` !== snap.key) {
      snap.done = true;
      return;
    }
    if (snap.verse > 1 && scriptureRef.current) {
      const el = scriptureRef.current.querySelector(`#v-${snap.verse}`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "start" });
      }
    }
    snap.done = true;
  }, [verses, ver, book, chap, bibleLoad]);

  // Track the topmost-visible verse as the user scrolls so re-entering
  // the app lands them where they left off, not at the chapter top.
  // Debounced to avoid thrashing localStorage on every scroll frame.
  useEffect(() => {
    const container = scriptureRef.current;
    if (!container) return;
    if (!verses.length) return;
    let timer = null;
    function compute() {
      timer = null;
      const containerTop = container.getBoundingClientRect().top;
      const rows = container.querySelectorAll("[data-vrow]");
      for (const row of rows) {
        const r = row.getBoundingClientRect();
        // First row whose bottom is below the container top is the topmost
        // visible one; use a small slack so a row that's almost scrolled
        // off still counts.
        if (r.bottom >= containerTop + 8) {
          const num = Number(row.getAttribute("data-vrow"));
          if (Number.isFinite(num) && num >= 1) {
            setTopVerse(prev => prev === num ? prev : num);
          }
          return;
        }
      }
    }
    function onScroll() {
      if (timer) return;
      timer = setTimeout(compute, 150);
    }
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (timer) clearTimeout(timer);
    };
  }, [verses]);

  const bookMeta = BOOKS.find(b => b.name === book);
  const maxCh    = bookMeta?.ch || 1;

  // ── Chapter navigation (used by swipe gestures + could be reused for
  // buttons later). Crosses book boundaries: end of Revelation → no-op;
  // start of Genesis → no-op. Selection is cleared on every move so the
  // floating verse-action bar doesn't carry over to a chapter where the
  // referenced verse number doesn't exist.
  const goNextChapter = useCallback(() => {
    if (chap < maxCh) { setChap(chap + 1); setSelection([]); return; }
    const i = BOOKS.findIndex(b => b.name === book);
    if (i >= 0 && i < BOOKS.length - 1) {
      setBook(BOOKS[i + 1].name); setChap(1); setSelection([]);
    }
  }, [book, chap, maxCh]);

  const goPrevChapter = useCallback(() => {
    if (chap > 1) { setChap(chap - 1); setSelection([]); return; }
    const i = BOOKS.findIndex(b => b.name === book);
    if (i > 0) {
      const prev = BOOKS[i - 1];
      setBook(prev.name); setChap(prev.ch || 1); setSelection([]);
    }
  }, [book, chap]);

  // Touch state for swipe navigation. Refs (not state) so the gesture
  // doesn't trigger re-renders on every move event.
  const swipeRef = useRef({ startX: 0, startY: 0, startT: 0, active: false });
  function onScriptureTouchStart(e) {
    const t = e.touches?.[0]; if (!t) return;
    swipeRef.current = { startX: t.clientX, startY: t.clientY, startT: Date.now(), active: true };
  }
  function onScriptureTouchEnd(e) {
    const s = swipeRef.current;
    if (!s.active) return;
    swipeRef.current.active = false;
    const t = e.changedTouches?.[0]; if (!t) return;
    const dx = t.clientX - s.startX;
    const dy = t.clientY - s.startY;
    const dt = Date.now() - s.startT;
    // Require a clearly-horizontal gesture so vertical scrolling never
    // navigates accidentally. Threshold tuned for one-handed thumb use.
    const HORIZ_MIN = 60;
    if (dt > 700) return;
    if (Math.abs(dx) < HORIZ_MIN) return;
    if (Math.abs(dx) < Math.abs(dy) * 1.5) return;
    // Right-to-left swipe (dx < 0, like turning a page forward) → next.
    // Left-to-right swipe (dx > 0) → previous.
    if (dx < 0) goNextChapter(); else goPrevChapter();
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_e) {}
  }

  // Cheap "does this verse have any sermon refs in the active church?" check.
  // Uses the index so we don't pay for an N+1 fetch while rendering the reader.
  function sv(b,c,v) {
    return sermonRefIndex.has(`${b} ${c}:${v}`) ? [{ _placeholder: true }] : [];
  }

  function isContiguousAsc(arr) {
    for (let i = 1; i < arr.length; i++) {
      if (arr[i] !== arr[i-1] + 1) return false;
    }
    return true;
  }

  function onPress(e, verse) {
    e.stopPropagation();
    e.preventDefault();
    setSelection(curr => {
      if (curr.includes(verse.v)) {
        // Toggle off. If the remaining selection becomes non-contiguous, clear all.
        const next = curr.filter(v => v !== verse.v);
        if (next.length === 0) return [];
        return isContiguousAsc(next) ? next : [];
      }
      if (curr.length === 0) return [verse.v];
      const min = curr[0], max = curr[curr.length - 1];
      if (verse.v === max + 1) return [...curr, verse.v];
      if (verse.v === min - 1) return [verse.v, ...curr];
      return [verse.v]; // non-contiguous click → replace selection
    });
  }

  // Apply a highlight color (or clear) across every selected verse. Passing
  // null clears the highlight and removes the verses from Saved; passing a
  // color sets it and adds the verses to Saved. Callers (the selection bar's
  // swatches) implement tap-same-color-again-to-remove by passing null when
  // that color is already active on the whole selection — this function
  // itself is just "set to X or clear", not the toggle.
  function applyHL(color) {
    if (selection.length === 0) return;
    const vks = selection.map(v => `${book} ${chap}:${v}`);

    setHighlights(h => {
      const n = { ...h };
      if (color === null) {
        vks.forEach(vk => { delete n[vk]; });
      } else {
        vks.forEach(vk => { n[vk] = color; });
      }
      return n;
    });

    setSaved(s => {
      const n = { ...s };
      if (color === null) {
        vks.forEach(vk => { delete n[vk]; });
      } else {
        const today = new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
        selection.forEach(v => {
          const vk = `${book} ${chap}:${v}`;
          if (!n[vk]) {
            const text = verses.find(vs => vs.v === v)?.t || "";
            n[vk] = { text, ref: vk, note: "", savedAt: today };
          }
        });
      }
      return n;
    });
  }

  // Explicit Save toggle (Phase 6) — independent of highlighting, which
  // auto-saves as a side effect but offers no standalone save/unsave action.
  function toggleSavedForSelection() {
    if (selection.length === 0) return;
    const vks = selection.map(v => `${book} ${chap}:${v}`);
    const allSaved = vks.every(vk => saved[vk]);
    setSaved(s => {
      const n = { ...s };
      if (allSaved) {
        vks.forEach(vk => { delete n[vk]; });
      } else {
        const today = new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
        selection.forEach(v => {
          const vk = `${book} ${chap}:${v}`;
          if (!n[vk]) {
            const text = verses.find(vs => vs.v === v)?.t || "";
            n[vk] = { text, ref: vk, note: "", savedAt: today };
          }
        });
      }
      return n;
    });
  }

  // Share the current selection's text + reference via the Web Share API,
  // falling back to a clipboard copy on unsupported browsers.
  async function shareSelection() {
    if (selection.length === 0) return;
    const ref = formatSelectionLabel();
    const text = selection.map(v => verses.find(vs => vs.v === v)?.t || "").filter(Boolean).join(" ");
    const homeChurchName = churches?.find(c => c.id === activeChurchId)?.name;
    const link = `https://thebiblecommunity.co.uk/app?screen=bible&book=${encodeURIComponent(book)}&chapter=${chap}&verse=${selection[0]}`;
    const readAlong = homeChurchName
      ? `Read along with ${homeChurchName} on The Bible Community: ${link}`
      : `Read along on The Bible Community: ${link}`;
    const shareText = `"${text}" — ${ref} (${ver})\n\n${readAlong}`;
    await shareTextViaBestChannel(shareText, flashCopiedToast);
  }

  // Open the question modal for the first selected verse (or `vOverride`,
  // for callers like the "?" indicator that set selection and open the
  // sheet in the same handler — selection wouldn't have updated yet since
  // setState is async). If the verse already has a question we'll show it
  // (editable); otherwise the textarea is empty with the "I find this
  // confusing" placeholder.
  function openQuestionForSelection(vOverride) {
    const v = vOverride != null ? vOverride : selection[0];
    if (v == null) return;
    const vk = `${book} ${chap}:${v}`;
    const existing = flagged[vk];
    const verseText = verses.find(vs => vs.v === v)?.t || "";
    setQuestionSheet({
      vk, book, chap, verseNum: v,
      text: verseText,
      // Pre-fill with the default question so the user can either delete it,
      // edit it, or accept it as-is. Existing questions show their saved text.
      draft: existing?.question || "I find this confusing",
      isEditing: !!existing,
    });
  }

  async function saveQuestionFromSheet() {
    if (!questionSheet) return;
    const { vk, book: bk, chap: ch, verseNum, text, draft } = questionSheet;
    const question = (draft || "").trim() || "I find this confusing";
    const today = new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
    setFlagged(f => ({ ...f, [vk]: { text, ref: vk, flaggedAt: today, book: bk, chap: ch, verse: verseNum, question } }));
    // Anchor the verse in the user's Saved section so they can find their
    // question later - without overwriting any existing saved note.
    setSaved(s => s[vk] ? s : ({ ...s, [vk]: { text, ref: vk, note: "", savedAt: today } }));
    try {
      if (user?.id) {
        await supabase.from('flagged_verses').upsert({
          user_id:    user.id,
          verse_ref:  vk,
          book:       bk,
          chapter:    ch,
          verse:      verseNum,
          verse_text: text,
          question,
        }, { onConflict: 'user_id,verse_ref' });
      }
    } catch (e) {
      console.warn('saveQuestion upsert failed:', e?.message);
    }
    setQuestionSheet(null);
  }

  async function removeQuestionFromSheet() {
    if (!questionSheet) return;
    const { vk } = questionSheet;
    setFlagged(f => { const n = { ...f }; delete n[vk]; return n; });
    try {
      if (user?.id) {
        await supabase.from('flagged_verses').delete().eq('user_id', user.id).eq('verse_ref', vk);
      }
    } catch (e) {
      console.warn('removeQuestion delete failed:', e?.message);
    }
    setQuestionSheet(null);
  }


  // "Note" (private) in the selection bar's ⋯ menu — opens the composer for
  // the first selected verse, defaulting to private (the comment sheet's own
  // effect resets commentPrivate to true whenever commentVk changes).
  function openCommentsForFirst() {
    if (selection.length === 0) return;
    const vk = `${book} ${chap}:${selection[0]}`;
    setCommentVk(vk);
    setCommentIn("");
  }

  // "Comment" in the selection bar — opens the comment thread for the first
  // selected verse that actually has community comments, falling back to the
  // first verse (straight to the composer) when there are none.
  function openCommunityCommentsForSelection() {
    if (selection.length === 0) return;
    const firstWithComments = selection.find(v => {
      const vk = `${book} ${chap}:${v}`;
      return (comments[vk] || []).some(c => !c.private);
    });
    const target = firstWithComments != null ? firstWithComments : selection[0];
    setCommentVk(`${book} ${chap}:${target}`);
    setCommentIn("");
  }

  async function addComment(isPrivateOverride) {
    if (!commentIn.trim() || !commentVk) return;
    const verseText = verses.find(vs => `${book} ${chap}:${vs.v}` === commentVk)?.t || "";
    setCommentError("");
    const wasPrivate = typeof isPrivateOverride === "boolean" ? isPrivateOverride : commentPrivate;
    const { error } = await addVerseCommentSupabase(commentVk, commentIn.trim(), wasPrivate);
    if (error) { setCommentError(error); return; }
    // A private note implicitly saves the verse so the user can find it again
    // in the Saved tab alongside their notes.
    if (wasPrivate && !saved[commentVk]) {
      const today = new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
      setSaved(s => ({ ...s, [commentVk]: { text: verseText, ref: commentVk, note: "", savedAt: today } }));
    }
    setCommentIn("");
    // Reset audience back to the private default for the next composition.
    setCommentPrivate(true);
    setAudienceOpen(false);
  }

  function likeComment(vk, id) {
    const list = comments[vk] || [];
    let cm = list.find(c => c.id === id);
    if (!cm) { for (const c of list) { const r = (c.replies || []).find(x => x.id === id); if (r) { cm = r; break; } } }
    if (cm) toggleVerseCommentLikeSupabase(id, cm.liked);
  }

  // One-level replies on verse comments (shared by the Comments tab + the
  // verse sheet). verseReplyTo holds the comment id being replied to.
  const [verseReplyTo, setVerseReplyTo] = useState(null);
  const [verseReplyDraft, setVerseReplyDraft] = useState("");
  async function submitVerseReply(vk, parentId) {
    const text = (verseReplyDraft || "").trim();
    if (!text) return;
    const { error } = await addVerseCommentSupabase(vk, text, false, parentId);
    if (error) { setCommentError && setCommentError(error); return; }
    setVerseReplyDraft(""); setVerseReplyTo(null);
  }
  function renderVerseCommentCard(cm, vk) {
    const isOwnComment = cm.authorId === user?.id;
    const replyCount = (cm.replies || []).length;
    const hasUnreadReply = isOwnComment && notifications.some(n => n.type === 'verse_comment_reply' && !n.read && n.data?.parent_id === cm.id);
    return (
      <div key={cm.id} className="comment-card">
        <div className="comment-header">
          <Avatar url={cm.authorAvatar} name={cm.author} size={22}/>
          <span className="comment-author">{cm.author}</span>
          {isOwnComment && (
            <span style={{background:"var(--highlight)",color:"var(--gold-ink)",borderRadius:999,padding:"2px 8px",fontSize:11,fontWeight:700}}>You</span>
          )}
          <span className="comment-sep">·</span>
          <span className="comment-date">{cm.date}</span>
          <span style={{marginLeft:"auto"}} onClick={e=>e.stopPropagation()}>
            <ContentActionsMenu canDelete={canDelete(cm.authorId)} isOwn={cm.authorId === user?.id}
              authorName={cm.author} onDelete={()=>handleDeleteVerseComment(cm)} onReport={()=>openReport("comment", cm.id)}/>
          </span>
        </div>
        <div className="comment-text">
          {cm.private && <span className="comment-private-lock" title="Only you can see this">🔒</span>}
          {cm.text}
        </div>
        {!cm.private && (
          <div style={{display:"flex",alignItems:"center",gap:14,marginTop:2}}>
            <LikeButton liked={cm.liked} count={cm.likes} onClick={()=>likeComment(vk, cm.id)} size={15}/>
            {replyCount > 0 && (
              <span style={{display:"flex",alignItems:"center",gap:6,fontSize:12.5,fontWeight:700,color:"var(--stone)"}}>
                {hasUnreadReply && <span style={{width:7,height:7,borderRadius:4,background:"#E0533D"}}/>}
                💬 {replyCount} {replyCount===1?"reply":"replies"}
              </span>
            )}
            <button type="button" onClick={()=>{ setVerseReplyTo(verseReplyTo===cm.id?null:cm.id); setVerseReplyDraft(""); }}
              style={{background:"none",border:"none",color:"var(--stone)",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif",padding:"4px 6px"}}>Reply</button>
          </div>
        )}
        {cm.replies && cm.replies.length > 0 && (
          <div style={{marginTop:6,marginLeft:16,paddingLeft:10,borderLeft:"1px solid var(--border)",display:"flex",flexDirection:"column",gap:8}}>
            {cm.replies.map(r => (
              <div key={r.id}>
                <div className="comment-header">
                  <Avatar url={r.authorAvatar} name={r.author} size={20}/>
                  <span className="comment-author">{r.author}</span>
                  <span className="comment-sep">·</span>
                  <span className="comment-date">{r.date}</span>
                  <span style={{marginLeft:"auto"}} onClick={e=>e.stopPropagation()}>
                    <ContentActionsMenu canDelete={canDelete(r.authorId)} isOwn={r.authorId === user?.id}
                      authorName={r.author} onDelete={()=>handleDeleteVerseComment(r)} onReport={()=>openReport("comment", r.id)}/>
                  </span>
                </div>
                <div className="comment-text">{r.text}</div>
                <LikeButton liked={r.liked} count={r.likes} onClick={()=>likeComment(vk, r.id)} size={14}/>
              </div>
            ))}
          </div>
        )}
        {verseReplyTo === cm.id && (
          <div style={{display:"flex",gap:8,marginTop:8,marginLeft:16}}>
            <input autoFocus type="text" placeholder={`Reply to ${cm.author}…`} value={verseReplyDraft}
              onChange={e=>setVerseReplyDraft(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Enter") submitVerseReply(vk, cm.id); }}
              style={{flex:1,background:"var(--white)",border:"1px solid var(--border)",borderRadius:8,padding:"8px 10px",color:"var(--ink)",fontSize:13,fontFamily:"'Plus Jakarta Sans',sans-serif",outline:"none"}}/>
            <button type="button" disabled={!verseReplyDraft.trim()} onClick={()=>submitVerseReply(vk, cm.id)}
              style={{background:"var(--gold)",color:"var(--ink)",border:"none",borderRadius:8,padding:"8px 12px",fontSize:13,fontWeight:700,cursor:verseReplyDraft.trim()?"pointer":"default",opacity:verseReplyDraft.trim()?1:0.4,fontFamily:"'Plus Jakarta Sans',sans-serif"}}>Reply</button>
          </div>
        )}
      </div>
    );
  }

  // Delete helpers. Author-or-moderator policy is enforced again at the DB
  // layer; this guard is just a UI nicety so the button only renders where
  // the action is permitted.
  function canDelete(authorId) {
    return isModerator || (!!user?.id && authorId === user.id);
  }
  // Cross-author moderator deletes pop a custom confirm sheet that calls
  // out the action explicitly + points to the Removed Posts undo path.
  const [pendingDelete, setPendingDelete] = useState(null);
  const CROSS_DELETE_SUBTITLE = "All removed posts can be viewed in Removed Posts under profile.";
  function confirmCrossDelete(title, runDelete) {
    setPendingDelete({
      title,
      subtitle: CROSS_DELETE_SUBTITLE,
      onConfirm: () => { runDelete(); setPendingDelete(null); },
    });
  }
  // Still used by the Elders sheet's own private-prayers view (eldersSubTab
  // === "prayer") — the Community tab's own prayer composer/list now lives
  // entirely inside CommunityTab.jsx and no longer calls these.
  function togglePray(id) {
    const pr = prayers.find(p => p.id === id);
    if (pr) togglePrayedSupabase(id, pr.prayed);
  }
  function handleDeletePrayer(pr) {
    if (!canDelete(pr.authorId)) return;
    const isOther = !!user?.id && pr.authorId !== user.id;
    if (isOther) {
      confirmCrossDelete(
        "This is a post from another church member, are you sure you want to delete?",
        () => removePrayerSupabase(pr.id, pr.text),
      );
    } else {
      if (!window.confirm('Delete this prayer request?')) return;
      removePrayerSupabase(pr.id, pr.text);
    }
  }
  function handleDeleteVerseComment(cm) {
    if (!canDelete(cm.authorId)) return;
    const isOther = !!user?.id && cm.authorId !== user.id;
    if (isOther) {
      confirmCrossDelete(
        "This is a post from another church member, are you sure you want to delete?",
        () => removeVerseCommentSupabase(cm.id, cm.text),
      );
    } else {
      if (!window.confirm('Delete this comment?')) return;
      removeVerseCommentSupabase(cm.id, cm.text);
    }
  }
  function handleDeleteEncouragement(en) {
    if (!canDelete(en.authorId)) return;
    const isOther = !!user?.id && en.authorId !== user.id;
    if (isOther) {
      confirmCrossDelete(
        "This is a post from another church member, are you sure you want to delete?",
        () => removeEncouragementSupabase(en.id, en.text),
      );
    } else {
      if (!window.confirm('Delete this encouragement?')) return;
      removeEncouragementSupabase(en.id, en.text);
    }
  }

  function likeEncouragement(id) {
    const en = encouragements.find(e => e.id === id);
    if (en) toggleEncouragementLikeSupabase(id, en.liked);
  }
  // Open the sermon-notes sheet for the current selection. Aggregates sermon
  // refs for every selected verse (a sermon shown here doesn't have to cover
  // the whole selection - just touch any verse in it). Callers can pass an
  // explicit override so we don't depend on a stale `selection` closure when
  // the trigger comes from a click that just called setSelection.
  // Resolve which churches' sermon clips to show in the verse sheet. Only two
  // scopes are offered (no "all churches" — too noisy): the user's home church,
  // or every church they follow.
  function sheetScopeIds(scope) {
    const home = activeChurchId || profile?.church_id || null;
    if (scope === 'home') return home ? [home] : [];
    return effectiveSermonChurchIds.length > 0 ? effectiveSermonChurchIds : (home ? [home] : []);
  }
  // Load the sermons referencing the given verse key(s), scoped to `scope` —
  // docs/FIX-SERMON-CLIPS.md §0.2/§4.3. Range-aware and NOT dependent on the
  // client-side churchSermons/useSermons() cache (which silently truncates at
  // Supabase's 1000-row default on any scope with more than 1000 sermons —
  // the actual cause of the "home church sermon is missing" bug, not a
  // verse-range matching bug: expandRefRange() already handled ranges
  // correctly). This queries sermon_refs fresh, scoped to just the tapped
  // chapter(s), so it's always complete regardless of total sermon count.
  async function fetchVerseClips(vks, scope) {
    if (!vks || vks.length === 0) return [];
    const ids = sheetScopeIds(scope);
    if (!ids || ids.length === 0) return [];

    // Group the tapped verse keys by (book, chapter) — usually one chapter,
    // but the chapter-chip path passes every verse in a chapter at once.
    const byChapter = new Map(); // "book|chapter" -> { book, chapter, verses:Set<number> }
    for (const vk of vks) {
      const m = vk.match(/^(.+)\s(\d+):(\d+)$/);
      if (!m) continue;
      const key = `${m[1]}|${m[2]}`;
      if (!byChapter.has(key)) byChapter.set(key, { book: m[1], chapter: parseInt(m[2], 10), verses: new Set() });
      byChapter.get(key).verses.add(parseInt(m[3], 10));
    }
    if (byChapter.size === 0) return [];

    const allRows = [];
    for (const { book: qBook, chapter: qChap, verses: vSet } of byChapter.values()) {
      const maxV = Math.max(...vSet);
      // verse <= v and coalesce(end_verse, verse) >= v, per the doc — PostgREST
      // can't express coalesce() in a filter, so fetch every ref starting at or
      // before the highest tapped verse in this one chapter (small, bounded by
      // the chapter, never the whole sermons table) and finish the exact
      // range check client-side.
      const { data, error } = await supabase
        .from('sermon_refs')
        .select('id, sermon_id, book, chapter, verse, end_verse, kind, timestamp_s, notes, sermons:sermon_id!inner(id, title, display_title, date, youtube_id, duration, church_id)')
        .eq('book', qBook).eq('chapter', qChap).lte('verse', maxV)
        .in('sermons.church_id', ids);
      if (error) { console.error('fetchVerseClips:', error); continue; }
      for (const r of (data || [])) {
        const endV = r.end_verse != null ? r.end_verse : r.verse;
        if ([...vSet].some(v => r.verse <= v && endV >= v)) allRows.push(r);
      }
    }

    // One row per sermon — when a sermon has several refs touching the tapped
    // verse(s), keep the best by priority: main_passage first, then a moment
    // directly on the tapped verse, then any other moment, then cross_reference;
    // ties broken by the earliest timestamp.
    const tappedVerses = new Set([...byChapter.values()].flatMap(c => [...c.verses]));
    const priorityOf = (r) => {
      if (r.kind === 'main_passage') return 0;
      if (r.kind === 'moment' && tappedVerses.has(r.verse)) return 1;
      if (r.kind === 'moment') return 2;
      return 3;
    };
    const bySermon = new Map();
    for (const r of allRows) {
      const existing = bySermon.get(r.sermon_id);
      if (!existing) { bySermon.set(r.sermon_id, r); continue; }
      const pr = priorityOf(r), pe = priorityOf(existing);
      if (pr < pe || (pr === pe && (r.timestamp_s ?? Infinity) < (existing.timestamp_s ?? Infinity))) {
        bySermon.set(r.sermon_id, r);
      }
    }

    const homeId = activeChurchId || profile?.church_id || null;
    const out = [...bySermon.values()].map(r => {
      const s = r.sermons || {};
      const church = churches?.find(c => c.id === s.church_id);
      const rangeLabel = r.kind === 'main_passage'
        ? `${r.book} ${r.chapter}:${r.verse}${r.end_verse && r.end_verse !== r.verse ? `–${r.end_verse}` : ''}`
        : null;
      return {
        id: s.id, refId: r.id,
        churchId: s.church_id, churchName: church?.name || '', isHome: s.church_id === homeId,
        // Doc §0.3 fallback order for this sheet specifically: display_title,
        // then the main passage range, then "Sermon · [date]" — never the raw
        // YouTube title, never a random single reference.
        displayTitle: s.display_title || rangeLabel || `Sermon · ${fmtShortSermonDate(s.date)}`,
        date: s.date, youtubeId: s.youtube_id, duration: s.duration,
        kind: r.kind, timestampS: r.timestamp_s, notes: r.notes,
        verseLabel: `${r.book} ${r.chapter}:${r.verse}${r.end_verse && r.end_verse !== r.verse ? `–${r.end_verse}` : ''}`,
        rangeLabel,
      };
    });

    out.sort((a, b) => {
      if (a.isHome !== b.isHome) return a.isHome ? -1 : 1;
      const pa = a.kind === 'main_passage' ? 0 : a.kind === 'moment' ? 1 : 2;
      const pb = b.kind === 'main_passage' ? 0 : b.kind === 'moment' ? 1 : 2;
      if (pa !== pb) return pa - pb;
      return (b.date || '').localeCompare(a.date || '');
    });
    return out;
  }

  async function openSermonForSelection(opts) {
    const vks = opts?.vks && opts.vks.length
      ? opts.vks
      : selection.map(v => `${book} ${chap}:${v}`);
    if (vks.length === 0) return;
    const label      = opts?.label || formatSelectionLabel();
    const firstVerse = opts?.firstVerse || verses.find(vs => vs.v === (opts?.vks ? parseInt(opts.vks[0].match(/:(\d+)$/)?.[1] || 0) : selection[0]));
    const scope      = opts?.scope || 'followed';   // BIBLE defaults to all followed
    setSheet({type:"sermon", verse: firstVerse, vk: label, vks, sermons: null, origin: opts?.origin || null, scope});
    setShTab("notes");
    const ordered = await fetchVerseClips(vks, scope);
    setSheet(s => (s && s.vk === label) ? { ...s, sermons: ordered } : s);
  }

  // ── Full Sermon Sheet handlers (sermon-sheet feature) ──────────────────────
  // Open the full-screen sermon sheet. `origin` = { book, chapter, verse } when
  // launched from the verse note sheet (enables "back to verse"); null from a
  // sermon list. Hides the verse sheet behind it.
  function openFullSermon(sermon, origin = null, startAtSeconds = null) {
    if (!sermon) return;
    // Opened the normal way (sermon list, latest card, etc.) — any pending
    // "Back to sermon" / "resume at" state is now stale.
    setPausedSermon(null);
    setFullSermonInitialTab(null);
    setFullSermonInitialScrollTop(null);
    // Carries whatever verse-clip sheet is currently open "for free": this is
    // non-null exactly when launched via VerseClipPlayingCard's "Open full
    // sermon", the only flow where a verse sheet sits behind a fresh sermon
    // sheet.
    setFullSermonReturnToVerseSheet(sheet);
    setFullSermonOrigin(origin || null);
    setFullSermonStartAt(startAtSeconds != null ? startAtSeconds : null);
    setFullSermon(sermon);
    setSheet(null);
  }
  function closeFullSermon() {
    // §3b: closing (✕) a sheet that was opened over a verse-clip sheet
    // restores that sheet instead of just closing.
    if (fullSermonReturnToVerseSheet) {
      setSheet(fullSermonReturnToVerseSheet);
      setFullSermonReturnToVerseSheet(null);
    }
    setFullSermon(null);
    setFullSermonStartAt(null);
    setFullSermonInitialTab(null);
    setFullSermonInitialScrollTop(null);
  }
  // §3b: tapping "Open in Bible" (chip or a reference row) from inside an
  // open sermon sheet — snapshot where it was, then navigate the reader
  // there with the verse highlighted. The sheet itself unmounts (its player
  // goes with it), so "pausing" is this snapshot, not a live pause.
  function onOpenRefAndPause(b, c, v, snapshot) {
    if (!fullSermon) return;
    const fsChurch = churches?.find(ch => ch.id === fullSermon.church_id);
    setPausedSermon({
      sermon: fullSermon,
      churchName: fsChurch?.name || activeChurchName || "Church",
      churchLogo: fsChurch?.logo_url || null,
      originVerse: fullSermonOrigin,
      returnToVerseSheet: fullSermonReturnToVerseSheet,
      activeTab: snapshot?.activeTab || 'refs',
      scrollTop: snapshot?.scrollTop || 0,
      pausedAtSeconds: snapshot?.pausedAtSeconds || 0,
    });
    setFullSermon(null);
    setFullSermonReturnToVerseSheet(null);
    setBook(b); setChap(c); setSelection([v]); setTab("bible"); setBibleSub("read");
  }
  // §3b: tapping the "Back to sermon" bar — restores the sheet directly from
  // the snapshot (bypassing openFullSermon()'s normal reset).
  function resumePausedSermon() {
    if (!pausedSermon) return;
    setFullSermonOrigin(pausedSermon.originVerse);
    setFullSermonStartAt(pausedSermon.pausedAtSeconds || null);
    setFullSermonReturnToVerseSheet(pausedSermon.returnToVerseSheet || null);
    setFullSermonInitialTab(pausedSermon.activeTab || null);
    setFullSermonInitialScrollTop(pausedSermon.scrollTop || null);
    setFullSermon(pausedSermon.sermon);
    setPausedSermon(null);
  }
  // §3b: leaving the Bible tab abandons the "Back to sermon" bar — it only
  // makes sense while still looking at the verse it pointed you to.
  useEffect(() => {
    if (tab !== "bible") setPausedSermon(null);
  }, [tab]);
  // bibleMapSignal is meant as a one-shot "open the overlay" nudge, but
  // SermonsTab unmounts/remounts on every tab switch ({tab==="sermons" && ...}),
  // so its effect re-reads whatever value is already here on mount — once
  // bumped, it stayed truthy forever, reopening the Bible Map on every later
  // visit to Sermons, including a plain tab tap. Spend it once the user
  // leaves Sermons again, so a fresh mount starts from null.
  useEffect(() => {
    if (tab !== "sermons") setBibleMapSignal(null);
  }, [tab]);

  // Open the verse sermon sheet IN PLACE (over the current tab, e.g. Explore)
  // without navigating into the Bible reader. Fetches the verse text for the
  // header and inherits the originating view's scope (home if they were viewing
  // home-church stats, otherwise followed).
  async function fetchSingleVerseText(bk, ch, vn) {
    try {
      const r = await fetch(`https://bible-api.com/${bookToApiName(bk)}+${ch}:${vn}?translation=web`);
      if (!r.ok) return "";
      const d = await r.json();
      return String(d.text || d.verses?.[0]?.text || "").replace(/\s+/g, " ").trim();
    } catch { return ""; }
  }
  async function openVerseSheetInPlace(bk, ch, vn, scope) {
    const vk = `${bk} ${ch}:${vn}`;
    let t = verseText(vk);                       // user's translation if already cached
    if (!t) t = await fetchSingleVerseText(bk, ch, vn);  // public-domain fallback
    openSermonForSelection({ vks: [vk], label: vk, firstVerse: { v: vn, t: t || "" }, origin: "explore", scope: scope === 'home' ? 'home' : 'followed' });
  }

  // Apply a pending open=sermon deep link once the reader has navigated to the
  // target chapter and its verses are loaded — then open the sermon sheet for
  // the first verse the scan found.
  useEffect(() => {
    if (!pendingSermonVk) return;
    const { book: pBook, chap: pChap, verse: pVerse } = pendingSermonVk;
    if (book !== pBook || chap !== pChap) return;
    if (!Array.isArray(verses) || verses.length === 0) return;
    const vk = `${pBook} ${pChap}:${pVerse}`;
    const firstVerse = verses.find(v => v.v === pVerse) || null;
    setSelection([pVerse]);
    openSermonForSelection({ vks: [vk], label: vk, firstVerse });
    setPendingSermonVk(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSermonVk, book, chap, verses]);

  // Format the selected verses as a single label, e.g. "Romans 8:1-3" or
  // "Romans 8:1" or "Romans 8:1, 4-6" (the last case can't currently occur
  // because selection is contiguous, but the helper is generic).
  function formatSelectionLabel() {
    if (selection.length === 0) return "";
    const first = selection[0], last = selection[selection.length - 1];
    return first === last
      ? `${book} ${chap}:${first}`
      : `${book} ${chap}:${first}-${last}`;
  }

  async function sendAI() {
    if (!aiIn.trim() || aiLoad || !sheet) return;
    const msg = aiIn.trim(); setAiIn("");
    setAiMsgs(m => [...m, {role:"user", text:msg}]);
    setAiLoad(true);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          model:"claude-sonnet-4-20250514", max_tokens:1000,
          system:`You are a warm, pastoral biblical study assistant for ${activeChurchName || 'this church'}${activeChurchLocation ? `, ${activeChurchLocation}` : ''}. Hold to the historic Christian faith: the Trinity (Father, Son, Holy Spirit), the full authority of Scripture, salvation by grace through faith in Jesus Christ, the bodily resurrection of Christ, and the work of the Holy Spirit. Draw on broadly evangelical scholarship. Be concise and pastoral - 3-5 sentences. The user is reading ${sheet.vk}: "${sheet.verse?.t}".`,
          messages:[
            ...aiMsgs.filter((_,i)=>i>0).map(m=>({role:m.role==="ai"?"assistant":"user",content:m.text})),
            {role:"user",content:msg},
          ],
        }),
      });
      const data = await res.json();
      const reply = data.content?.map(b=>b.text||"").join("") || "Sorry, I couldn't respond right now.";
      setAiMsgs(m => [...m, {role:"ai", text:reply}]);
    } catch {
      setAiMsgs(m => [...m, {role:"ai", text:"Something went wrong. Please try again."}]);
    }
    setAiLoad(false);
  }

  function verseText(ref) {
    const m = ref.match(/^(.+)\s(\d+):(\d+)$/);
    if (!m) return "";
    const bk = m[1], ch = parseInt(m[2]), vn = parseInt(m[3]);
    const cached = chapterCache[`${ver}/${bk}/${ch}`];
    return cached?.find(v => v.v === vn)?.t || "";
  }

  const colMap = {yellow:"var(--gold-ink)", blue:"var(--sky-ink)", green:"var(--sage-ink)"};

  // ── Back walks the main app navigation (bottom tabs, bible/connect/church
  // sub-sections, sermon series, open book/chapter) instead of exiting the app.
  // Each change pushes a history frame; Back restores the previous view.
  useViewHistory(
    { tab, bibleSub, churchSub, chip, selectedSeries, book, chap },
    (v) => {
      setTab(v.tab === "church" ? "sermons" : v.tab);   // church tab removed
      setBibleSub(v.bibleSub);
      setChurchSub(v.churchSub);
      setChip(v.chip);
      setSelectedSeries(v.selectedSeries);
      setBook(v.book);
      setChap(v.chap);
    }
  );

  // ── Hardware/browser Back closes an open overlay instead of exiting the app.
  // One call per sheet/modal; the closer mirrors that overlay's own ✕ handler
  // (including any cleanup it does) so Back and the ✕ button behave identically.
  // Must run before the early returns below so the hook order stays stable.
  useBackClose(profileOpen,        () => setProfileOpen(false));
  useBackClose(notificationsOpen,  () => { setNotificationsOpen(false); setNotifAccessScroll(false); });
  useBackClose(removedPostsOpen,   () => setRemovedPostsOpen(false));
  useBackClose(leadersPageOpen,    () => setLeadersPageOpen(false));
  useBackClose(!!eventEditorTarget, () => setEventEditorTarget(null));
  useBackClose(eldersSheetOpen,    () => setEldersSheetOpen(false));
  useBackClose(!!selectedSermon,   () => setSelectedSermon(null));
  useBackClose(!!profileEntity,    () => setProfileEntity(null));
  useBackClose(churchSettingsOpen, () => { setChurchSettingsOpen(false); loadChurchEvents(); });
  useBackClose(sermonSettingsOpen, () => setSermonSettingsOpen(false));
  useBackClose(encourSettingsOpen, () => { setEncourSettingsOpen(false); setEditingEncouragement(null); });
  useBackClose(legendOverlayOpen,  () => setLegendOverlayOpen(false));
  useBackClose(!!picker,           () => setPicker(picker === "chapter" ? "book" : null));
  useBackClose(!!reportTarget,     () => setReportTarget(null));
  useBackClose(!!accessFeature,    () => setAccessFeature(null));

  // Church admin still needs to complete first-run setup — take over the
  // screen with the onboarding flow until they scan a sermon or defer.
  // Brand-new church creation (no church to resume) — launched from the
  // profile's "Set up your church" CTA. ChurchOnboarding's ensureChurch()
  // creates the church via the onboard_church RPC and promotes the user.
  if (createChurchOpen) {
    const closeCreate = async () => {
      setCreateChurchOpen(false);
      try { await refreshProfile?.(); } catch {}
      try { await refreshChurches?.(); } catch {}
    };
    return (
      <div className="nlc-frame nlc-wide"><ChurchOnboarding
        onExplore={closeCreate}
        onClose={closeCreate}
        onImportMore={() => {
          setCreateChurchOpen(false);
          try {
            window.history.pushState({}, "", "/admin");
            // Flag so the in-app back-stack listener ignores this programmatic
            // route change (see _ensureBackListener).
            window.__nlcProgrammaticNav = true;
            try { window.dispatchEvent(new PopStateEvent("popstate")); }
            finally { window.__nlcProgrammaticNav = false; }
          } catch (_e) { window.location.assign("/admin"); }
        }}
      /></div>
    );
  }

  if (showChurchOnboarding && activeChurch) {
    return (
      <div className="nlc-frame nlc-wide"><ChurchOnboarding
        resumeChurch={activeChurch}
        onExplore={(ref) => {
          setShowChurchOnboarding(false);
          if (ref?.book && ref?.chapter && ref?.verse) {
            triggerSermonHint({ book: ref.book, chapter: ref.chapter, verse: ref.verse });
          }
        }}
        onImportMore={() => {
          setShowChurchOnboarding(false);
          try {
            window.history.pushState({}, "", "/admin");
            // Flag so the in-app back-stack listener ignores this programmatic
            // route change (see _ensureBackListener).
            window.__nlcProgrammaticNav = true;
            try { window.dispatchEvent(new PopStateEvent("popstate")); }
            finally { window.__nlcProgrammaticNav = false; }
          } catch (_e) { window.location.assign("/admin"); }
        }}
        onClose={dismissChurchOnboarding}
      /></div>
    );
  }

  // Manual "add a sermon" via the onboarding scan flow (URL → scan → refs).
  // Targets the church selected in the Sermons tab (sermonUploadChurch), not
  // the home church. Refresh the sermon list on the way out.
  if (scanSermonOpen && sermonUploadChurch) {
    const finishAddSermon = async () => {
      // Land on the church's profile page (Sermons tab) so the freshly-saved
      // sermon shows immediately. Refresh the list first so it's present.
      try { await refreshSermons?.(); } catch {}
      setScanSermonOpen(false);
      if (sermonUploadChurch) setProfileEntity(sermonUploadChurch);
    };
    return (
      <div className="nlc-frame nlc-wide"><ChurchOnboarding
        resumeChurch={sermonUploadChurch}
        addMode
        onDone={finishAddSermon}
        onClose={finishAddSermon}
      /></div>
    );
  }

  return (
    <>
      <Styles/>
      <InstallBanner/>
      <div className="app" style={{"--accent": sectionAccent, ...readingSettings.cssVars}}>

        {/* ── HEADER ── */}
        {tab === "week" ? (
          <div className="header">
            <div className="hdr-title">
              <div className="hdr-sub">{new Date().toLocaleDateString("en-GB",{weekday:"long"})} · {activeChurchName || "The Bible Community"}</div>
              <div className="hdr-main">{getHourGreeting()}{profile?.full_name ? `, ${profile.full_name.split(/\s+/)[0]}` : ""}</div>
            </div>
            {/* Avatar → profile sheet. No bell here (or anywhere else) per
                docs/FIX-HEADERS.md — notifications open from the profile
                sheet instead; a small red dot on the avatar is the only
                unread-count affordance left. */}
            <div style={{position:"relative",cursor:"pointer"}} onClick={()=>setProfileOpen(true)}>
              <Avatar
                url={profile?.avatar_url || null}
                name={profile?.full_name || user?.email}
                size={44}
                style={{border:"2px solid var(--gold)"}}
              />
              {notifUnread > 0 && (
                <span style={{
                  position:"absolute", top:0, right:0,
                  minWidth:13, height:13, padding:"0 3px",
                  borderRadius:999, background:"#E0533D", color:"#fff",
                  fontSize:9, fontWeight:800, fontFamily:"'Plus Jakarta Sans',sans-serif",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  border:"2px solid var(--white)",
                  lineHeight:1, boxSizing:"content-box",
                }}>{notifUnread > 9 ? "9+" : notifUnread}</span>
              )}
            </div>
          </div>
        ) : (
          // Every other tab renders its own page title inside its own wrap —
          // a shared header here would just be dead space, or (Bible) a
          // church-name/bell/avatar header the design no longer wants at all.
          null
        )}

        {/* ══ BIBLE SECTION (Read / Community Comments / Saved sub-tabs) ══ */}
        {tab==="bible" && (
          <>
            {/* ── BACK TO SERMON (docs/designs/BibleFromSermon.dc.html, §3b) ── */}
            {pausedSermon && (() => {
              const ps = pausedSermon;
              const psTitle = ps.sermon?.display_title || ps.sermon?.title || "Sermon";
              return (
                <button type="button" onClick={resumePausedSermon}
                  style={{
                    display:"flex", alignItems:"center", gap:10, margin:"10px 22px 0", textAlign:"left",
                    background:"var(--ink)", color:"#fff", border:"none", borderRadius:18, padding:"10px 12px",
                    boxShadow:"0 8px 20px rgba(31,27,22,0.25)", cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif",
                  }}>
                  <span style={{width:32,height:32,borderRadius:16,background:"rgba(255,255,255,0.14)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>
                  </span>
                  <div style={{position:"relative", width:56, height:32, borderRadius:10, overflow:"hidden", flexShrink:0, background:"var(--border)"}}>
                    {ps.sermon?.youtube_id && (
                      <img src={`https://i.ytimg.com/vi/${ps.sermon.youtube_id}/mqdefault.jpg`} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} onError={e=>{e.currentTarget.style.display="none";}}/>
                    )}
                    <span style={{position:"absolute",left:"50%",top:"50%",width:22,height:22,marginLeft:-11,marginTop:-11,borderRadius:11,background:"rgba(255,255,255,0.94)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="#1F1B16" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
                    </span>
                    <span style={{position:"absolute",right:4,bottom:4,background:"rgba(31,27,22,0.78)",color:"#fff",borderRadius:5,padding:"1px 5px",fontSize:9.5,fontWeight:700}}>
                      {fmtClipClock(ps.pausedAtSeconds || 0)}
                    </span>
                  </div>
                  <span style={{display:"flex",flexDirection:"column",flex:1,minWidth:0,gap:1}}>
                    <span style={{fontSize:13.5,fontWeight:800}}>Back to sermon</span>
                    <span style={{fontSize:11.5,color:"#D9D1C4",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                      {psTitle} · paused at {fmtClipClock(ps.pausedAtSeconds || 0)}
                    </span>
                  </span>
                </button>
              );
            })()}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,padding:"10px 22px 0"}}>
              {/* Book/chapter picker + Aa are Read-only (docs/designs/PlansTab.dc.html):
                  Plans/Comments/Saved show a plain "Bible" title and just map+search. */}
              {bibleSub==="read" ? (
                <button type="button" aria-label="Choose book and chapter" onClick={()=>setPicker("book")}
                  style={{
                    display:"inline-flex",alignItems:"center",gap:8,border:"none",cursor:"pointer",
                    background:"var(--white)",borderRadius:999,padding:"10px 16px",
                    fontFamily:"'Plus Jakarta Sans',sans-serif",fontSize:17,fontWeight:800,color:"var(--ink)",
                  }}>
                  {book} {chap}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
                </button>
              ) : (
                <div style={{fontSize:24,fontWeight:800,color:"var(--ink)",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>Bible</div>
              )}
              <div style={{display:"flex",gap:8,flexShrink:0}}>
                {bibleSub==="read" && (
                  <button type="button" aria-label="Reading settings" onClick={()=>setReadingSettingsOpen(o=>!o)}
                    style={{
                      width:44,height:44,borderRadius:22,border:"none",cursor:"pointer",
                      fontFamily:"'Source Serif 4',Georgia,serif",fontSize:17,fontWeight:600,
                      color:"var(--ink)", background: readingSettingsOpen ? "var(--gold)" : "var(--white)",
                    }}>Aa</button>
                )}
                <button type="button" aria-label="Bible map" onClick={()=>{ setTab("sermons"); setBibleMapSignal(n=>n+1); }}
                  style={{width:44,height:44,borderRadius:22,border:"none",background:"var(--white)",color:"var(--ink)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3z"/><path d="M9 3v15M15 6v15"/></svg>
                </button>
                {/* No dedicated Bible search feature exists yet (out of this
                    phase's scope beyond the icon) — jumps to Read, where the
                    book/chapter picker lives, as the minimal useful action
                    for this button. */}
                <button type="button" aria-label="Search" onClick={()=>setBibleSub("read")}
                  style={{width:44,height:44,borderRadius:22,border:"none",background:"var(--white)",color:"var(--ink)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
                  <Search size={20} strokeWidth={1.8}/>
                </button>
              </div>
            </div>
            <div className="comm-tabs" style={{padding:"0 22px"}}>
              <div className={`comm-tab ${bibleSub==="read"?"on":""}`} onClick={()=>setBibleSub("read")}>Read</div>
              <div className={`comm-tab ${bibleSub==="plans"?"on":""}`} onClick={()=>setBibleSub("plans")}>Plans</div>
              <div className={`comm-tab ${bibleSub==="comments"?"on":""}`}
                onClick={()=>{
                  if (isVisitor) { openAccessRequest("community"); return; }
                  setBibleSub("comments");
                }}
                style={isVisitor ? {display:"inline-flex",alignItems:"center",gap:4} : undefined}>
                Comments{isVisitor && <span style={{fontSize:10,opacity:0.7}}>🔒</span>}
              </div>
              <div className={`comm-tab ${bibleSub==="saved"?"on":""}`} onClick={()=>setBibleSub("saved")}>Saved</div>
            </div>

            {readingSettingsOpen && (
              <div style={{
                position:"fixed", left:12, right:12, bottom:96, zIndex:200, maxWidth:536, margin:"0 auto",
                background:"var(--white)", borderRadius:26, padding:18,
                boxShadow:"0 8px 30px rgba(31,27,22,0.14)", display:"flex", flexDirection:"column", gap:16,
                fontFamily:"'Plus Jakarta Sans',sans-serif",
              }}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div style={{fontSize:16,fontWeight:800,color:"var(--ink)"}}>Reading settings</div>
                  <button aria-label="Close reading settings" onClick={()=>setReadingSettingsOpen(false)}
                    style={{width:44,height:44,borderRadius:22,border:"none",background:"var(--cream)",color:"var(--ink)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
                  </button>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <div style={{fontSize:12,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",color:"var(--stone)"}}>Text size</div>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <button aria-label="Smaller text" onClick={()=>readingSettings.setSizeIndex(readingSettings.sizeIndex-1)}
                      style={{width:48,height:48,borderRadius:16,border:"none",background:"var(--cream)",fontFamily:"inherit",fontSize:15,fontWeight:800,color:"var(--ink)",cursor:"pointer"}}>A</button>
                    <div style={{flex:1,display:"grid",gridTemplateColumns:"repeat(5, minmax(0, 1fr))",gap:6}}>
                      {readingSettings.sizes.map((_, i) => (
                        <div key={i} style={{height:8,borderRadius:4,background: i <= readingSettings.sizeIndex ? "var(--gold)" : "var(--border-strong)"}}/>
                      ))}
                    </div>
                    <button aria-label="Larger text" onClick={()=>readingSettings.setSizeIndex(readingSettings.sizeIndex+1)}
                      style={{width:48,height:48,borderRadius:16,border:"none",background:"var(--cream)",fontFamily:"inherit",fontSize:22,fontWeight:800,color:"var(--ink)",cursor:"pointer"}}>A</button>
                  </div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <div style={{fontSize:12,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",color:"var(--stone)"}}>Translation</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {VERSIONS.map(v => {
                      const disabled = v === "NIV" && nivAvailable === false;
                      return (
                        <button key={v} type="button" disabled={disabled}
                          onClick={()=>!disabled && setVer(v)}
                          title={disabled ? "NIV not available on current plan" : undefined}
                          style={{
                            border:"none", borderRadius:999, padding:"9px 14px",
                            fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13, fontWeight:700,
                            cursor: disabled ? "not-allowed" : "pointer",
                            background: ver === v ? "var(--gold)" : "var(--cream)",
                            color: disabled ? "var(--stone-dim)" : "var(--ink)",
                            opacity: disabled ? 0.55 : 1,
                          }}>{v}</button>
                      );
                    })}
                  </div>
                </div>
                <button role="switch" aria-checked={readingSettings.dyslexic} onClick={()=>readingSettings.setDyslexic(!readingSettings.dyslexic)}
                  style={{display:"flex",alignItems:"center",gap:12,border:"none",background:"var(--cream)",borderRadius:18,padding:"12px 14px",fontFamily:"inherit",textAlign:"left",cursor:"pointer",color:"var(--ink)"}}>
                  <span style={{display:"flex",flexDirection:"column",gap:2,flex:1}}>
                    <span style={{fontSize:14,fontWeight:700}}>Dyslexia-friendly font</span>
                    <span style={{fontSize:12,color:"var(--stone)"}}>Uses OpenDyslexic for Bible text</span>
                  </span>
                  <span style={{width:48,height:28,borderRadius:14,padding:3,boxSizing:"border-box",display:"flex",flexShrink:0,justifyContent: readingSettings.dyslexic ? "flex-end" : "flex-start",background: readingSettings.dyslexic ? "var(--ink)" : "var(--border-strong)"}}>
                    <span style={{width:22,height:22,borderRadius:11,background: readingSettings.dyslexic ? "var(--gold)" : "var(--white)"}}/>
                  </span>
                </button>
              </div>
            )}
          </>
        )}

        {/* ══ READ sub-tab ══ */}
        {tab==="bible" && bibleSub==="read" && <>
          {/* Chapter-level "preached in N sermons · M churches" chip, per
              docs/designs/BibleDots.dc.html. The "i" button is the only
              persistent way to reopen the dot-meaning popup — the popup
              itself still also auto-opens once per user (see the effect
              near legendOverlayOpen's declaration). */}
          {(() => {
            const chapStats = chapterSermonStats.get(`${book} ${chap}`);
            if (!chapStats || chapStats.sermonCount === 0) return null;
            return (
              <div style={{display:"flex",alignItems:"center",gap:10,padding:"0 16px 2px"}}>
                <button type="button" onClick={() => {
                  const vks = verses.map(vs => `${book} ${chap}:${vs.v}`);
                  openSermonForSelection({ vks, label: `${book} ${chap}`, firstVerse: verses[0] || null, scope: 'followed' });
                }}
                  style={{display:"inline-flex",alignItems:"center",gap:8,background:"var(--lavender)",color:"var(--lavender-ink)",border:"none",borderRadius:999,padding:"8px 12px 8px 10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM19 10v1a7 7 0 0 1-14 0v-1M12 18v4"/></svg>
                  Preached in {chapStats.sermonCount} sermon{chapStats.sermonCount===1?"":"s"} · {chapStats.churchCount} church{chapStats.churchCount===1?"":"es"}
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
                </button>
                <button type="button" aria-label="What do the dots mean?" onClick={() => setLegendOverlayOpen(true)}
                  style={{width:28,height:28,borderRadius:14,border:"1.5px solid var(--border-strong)",color:"var(--stone)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,fontFamily:"Georgia,serif",fontStyle:"italic",background:"var(--white)",cursor:"pointer",flexShrink:0}}>i</button>
              </div>
            );
          })()}
          {/* ⓘ "What the dots mean" key — docs/designs/BibleKey.dc.html.
              Auto-opens once per user (see the effect near legendOverlayOpen's
              declaration); the chapter chip's ⓘ button above is the
              persistent way to reopen it afterwards. No close/Got it button —
              dismissed by tapping outside (the overlay backdrop below, plus
              the existing outside-tap effect) or the handle's visual cue. */}
          {legendOverlayOpen && (() => {
            const churchNameForLegend = churches?.find(c => c.id === activeChurchId)?.name || "your church";
            const hasHomeChurch = !!(activeChurchId || profile?.church_id);
            return (
              <>
                <div className="overlay" onClick={() => setLegendOverlayOpen(false)} style={{ zIndex: 500 }}/>
                <div ref={legendOverlayRef} className="sheet" role="dialog" aria-label="What the dots mean" style={{ zIndex: 501 }}>
                  <div className="shandle"/>
                  <div style={{ padding: "10px 22px 28px", display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ fontSize: 19, fontWeight: 800, color: "var(--ink)", padding: "6px 0 2px" }}>What the dots mean</div>
                    <div style={{ fontSize: 14, color: "var(--stone)", lineHeight: 1.45, marginBottom: 4 }}>Tap any verse to watch the moment it was preached.</div>
                    {hasHomeChurch && (
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 0", borderTop: "1px solid var(--border)" }}>
                        <span style={{ width: 22, display: "flex", justifyContent: "center", paddingTop: 4, flexShrink: 0 }}><span style={{ width: 12, height: 12, borderRadius: 6, background: "#7C6BC4" }}/></span>
                        <div>
                          <div style={{ fontSize: 14.5, fontWeight: 800, color: "var(--ink)" }}>Preached at {churchNameForLegend}</div>
                          <div style={{ fontSize: 13, color: "var(--stone)", lineHeight: 1.4 }}>Your church has preached on this verse.</div>
                        </div>
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 0", borderTop: "1px solid var(--border)" }}>
                      <span style={{ width: 22, display: "flex", justifyContent: "center", paddingTop: 4, flexShrink: 0 }}><span style={{ width: 12, height: 12, borderRadius: 6, border: "2px solid #7C6BC4", boxSizing: "border-box" }}/></span>
                      <div>
                        <div style={{ fontSize: 14.5, fontWeight: 800, color: "var(--ink)" }}>Preached at churches you follow</div>
                        <div style={{ fontSize: 13, color: "var(--stone)", lineHeight: 1.4 }}>Only churches you follow have preached on it.</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 0", borderTop: "1px solid var(--border)" }}>
                      <span style={{ width: 22, display: "flex", justifyContent: "center", paddingTop: 4, flexShrink: 0 }}><span style={{ width: 12, height: 12, borderRadius: 6, background: "#4A90E2" }}/></span>
                      <div>
                        <div style={{ fontSize: 14.5, fontWeight: 800, color: "var(--ink)" }}>Comments</div>
                        <div style={{ fontSize: 13, color: "var(--stone)", lineHeight: 1.4 }}>People at your church have commented on this verse.</div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
          <div className="scripture"
            ref={scriptureRef}
            onTouchStart={onScriptureTouchStart}
            onTouchEnd={onScriptureTouchEnd}>
            {(bibleLoad || (sermonDotsPending && !bibleErr)) && (
              <div className="empty" style={{paddingTop:60}}>
                <div style={{fontSize:28,marginBottom:14,opacity:0.4,animation:"sglow 1.5s ease-in-out infinite"}}>✦</div>
                <div className="etit loading-anim" style={{fontSize:16}}>Loading all your church's bible references</div>
              </div>
            )}
            {!bibleLoad && bibleErr && (
              <div className="empty">
                <div className="eico">⚠️</div>
                <div className="etit">Could not load</div>
                <div className="esub">{bibleErr}</div>
              </div>
            )}
            {!bibleLoad && !sermonDotsPending && !bibleErr && verses.length===0 && (
              <div className="empty"><div className="eico">📖</div><div className="etit">No text found</div><div className="esub">Try a different version.</div></div>
            )}
            {!bibleLoad && !sermonDotsPending && verses.map(verse => {
              const vk=`${book} ${chap}:${verse.v}`;
              const hl=highlights[vk];
              const hasSermon=sermonRefIndex.has(vk) || sermonChapterIndex.has(`${book} ${chap}`);
              // 'home' (filled) unless every ref at this verse is from a
              // followed church (hollow) — home always wins when both exist.
              const sermonDotFillKind = sermonDotFill.get(vk) || 'home';
              // Hinted verse always shows its dot (we only hint known sermon
              // verses) so it appears instantly, even before sermon data loads.
              const isSermonHinted = !!sermonHint && book===sermonHint.book && chap===sermonHint.chapter && verse.v===sermonHint.verse;
              const hasComment=(comments[vk]||[]).some(c => !c.private);
              const hasEncouragement=encouragementRefIndex.has(vk) || encouragementChapterIndex.has(`${book} ${chap}`);
              const isFlagged=!!flagged[vk];
              const isSelected=selection.includes(verse.v);
              // Walkthrough overlays: demo indicators on verse 2 + row spotlight
              // on all four indicator-focused steps (2-5).
              const isDemoVerse = walkthroughActive && verse.v === 2;
              const rowSpot     = (walkthroughStep >= 2 && walkthroughStep <= 5) && verse.v === 2
                ? WALKTHROUGH_SPOTLIGHT
                : null;
              // Per-step visibility for the demo indicators. Steps 2/3/4 each
              // isolate one indicator (and pulse it); every other step shows
              // all three statically so the user sees the full row context.
              const showRedDemo   = isDemoVerse && walkthroughStep !== 3 && walkthroughStep !== 4;
              const showBlueDemo  = isDemoVerse && walkthroughStep !== 2 && walkthroughStep !== 4;
              const showHeartDemo = isDemoVerse && walkthroughStep !== 2 && walkthroughStep !== 3;
              return (
                <div key={verse.v}
                  id={`v-${verse.v}`}
                  data-vrow={verse.v}
                  className={`vrow ${hl?`hl-${hl}`:""} ${isSelected?"selected":""}`}
                  style={rowSpot || undefined}
                  onClick={e=>{ if (walkthroughActive) return; onPress(e,verse); }}>
                  <div className="vnum">{verse.v}</div>
                  <div className="vbody">
                    <div className="vtxt"><span className="vtxt-inner">{verse.t}</span></div>
                    <div style={{display:"flex",flexDirection:"column",gap:6,paddingTop:8,paddingBottom:2,flexShrink:0,alignItems:"center",minWidth:14}}>
                      {/* 1. Sermon notes - lavender dot */}
                      {(hasSermon || isSermonHinted) && (
                        <span className={`vind-dot sermon${sermonDotFillKind === 'followed' ? " followed" : ""}${isSermonHinted ? " hint" : ""}`} title="Sermon notes for this verse"
                          onClick={e=>{
                            e.stopPropagation();
                            if (sermonHint) setSermonHint(null);
                            setSelection([verse.v]);
                            openSermonForSelection({ vks:[vk], label:vk, firstVerse:verse });
                          }}/>
                      )}
                      {/* 2. Community comments - blue dot. Visitors see the dot
                          (so they know there's activity) but tapping prompts the
                          access-request flow instead of opening the thread. */}
                      {hasComment && (
                        <span className="vind-dot comm" title={isVisitor ? "Members can view community comments" : "Community comments on this verse"}
                          onClick={e=>{
                            e.stopPropagation();
                            setSelection([verse.v]);
                            if (isVisitor) { openAccessRequest("comment"); return; }
                            setCommentVk(vk);
                            setCommentIn("");
                          }}/>
                      )}
                      {/* 3. Encouragement - pink heart (inline SVG so iOS /
                          Android can't override it with the red emoji glyph). */}
                      {hasEncouragement && (
                        <span className="vind-heart"
                          title={isVisitor ? "Tap to view options" : "Word of encouragement on this verse"}
                          onClick={e=>{
                            e.stopPropagation();
                            setSelection([verse.v]);
                            if (isVisitor) { openAccessRequest("encouragement"); return; }
                            setEncourageSheetVks([vk]);
                          }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="#FF6B9D" d="M12 21s-7.5-4.6-9.6-9.2C.9 8.1 3.4 4 7.5 4c2 0 3.4 1 4.5 2.4C13.1 5 14.5 4 16.5 4c4.1 0 6.6 4.1 5.1 7.8C19.5 16.4 12 21 12 21z"/>
                          </svg>
                        </span>
                      )}
                      {/* 4. Question - purple ? - only for the current user's
                          own flag; other members' questions stay private. */}
                      {isFlagged && (
                        <span className="vind-q" title="You have a question on this verse"
                          onClick={e=>{
                            e.stopPropagation();
                            setSelection([verse.v]);
                            openQuestionForSelection(verse.v);
                          }}>?</span>
                      )}
                      {/* Demo indicators rendered only during the walkthrough so the
                          spotlighted dots are guaranteed to exist regardless of
                          whether the chapter has real data. */}
                      {showRedDemo && (
                        <span className={`legend-dot ${walkthroughStep === 2 ? "walkthrough-pulse" : ""}`} style={{background:"#7C6BC4"}}/>
                      )}
                      {showBlueDemo && (
                        <span className={`legend-dot ${walkthroughStep === 3 ? "walkthrough-pulse" : ""}`} style={{background:"#4A90E2"}}/>
                      )}
                      {showHeartDemo && (
                        <span className={walkthroughStep === 4 ? "walkthrough-pulse" : ""} style={{display:"inline-flex"}}>
                          <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="#FF6B9D" d="M12 21s-7.5-4.6-9.6-9.2C.9 8.1 3.4 4 7.5 4c2 0 3.4 1 4.5 2.4C13.1 5 14.5 4 16.5 4c4.1 0 6.6 4.1 5.1 7.8C19.5 16.4 12 21 12 21z"/>
                          </svg>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Copyright attribution for translations that require it. */}
            {!bibleLoad && verses.length > 0 && TRANSLATION_ATTRIBUTION[ver] && (
              <div style={{
                marginTop:18, paddingTop:12,
                borderTop:"1px solid var(--border)",
                fontSize:11, color:"var(--stone-dim)",
                textAlign:"center", letterSpacing:"0.02em",
              }}>
                {TRANSLATION_ATTRIBUTION[ver]}
              </div>
            )}
            <div style={{height:80}}/> {/* leave space for the floating selection bar */}
          </div>

          {/* Verse-selection bar (docs/designs/SelBarB2.dc.html, §1b) —
              visible whenever the user has selected one or more verses. */}
          {selection.length > 0 && (() => {
            const vks = selection.map(v => `${book} ${chap}:${v}`);
            const commCount = vks.reduce(
              (n, vk) => n + (comments[vk] || []).filter(c => !c.private).length, 0
            );
            const touchingSermons = sermonsTouchingSelection(vks);
            const touchingChurchNames = [...new Set(
              touchingSermons.map(s => churches?.find(c => c.id === s.church_id)?.name).filter(Boolean)
            )].join(" · ");
            const swatch = (color, hex) => {
              const active = vks.length > 0 && vks.every(vk => highlights[vk] === color);
              return (
                <span key={color} role="button" aria-label={`Highlight ${color}`}
                  onClick={()=>applyHL(active ? null : color)}
                  style={{
                    width:26, height:26, borderRadius:13, background:hex, cursor:"pointer", flexShrink:0,
                    boxShadow: active ? "0 0 0 2px var(--white), 0 0 0 4px var(--ink)" : "none",
                  }}/>
              );
            };
            const roundBtn = (label, onClick, icon, badge) => (
              <button type="button" aria-label={label} onClick={onClick}
                style={{position:"relative", width:34, height:34, borderRadius:17, background:"var(--cream)", border:"none", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0}}>
                {icon}
                {badge != null && (
                  <span style={{position:"absolute", right:-4, top:-4, minWidth:18, height:18, padding:"0 5px", boxSizing:"border-box", borderRadius:9, background:"#4A90E2", color:"#fff", fontSize:10.5, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", border:"2px solid var(--white)"}}>{badge}</span>
                )}
              </button>
            );
            return (
              <div className="sel-sheet" ref={menuSheetRef}>
                <div
                  onTouchStart={handleMenuTouchStart} onTouchMove={handleMenuTouchMove} onTouchEnd={handleMenuTouchEnd}
                  onPointerDown={handleMenuTouchStart} onPointerMove={handleMenuTouchMove} onPointerUp={handleMenuTouchEnd}
                  style={{alignSelf:"center", width:36, height:4, borderRadius:2, background:"var(--border-strong)", cursor:"grab", touchAction:"none"}}/>

                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                  <div style={{fontSize:15, fontWeight:800, color:"var(--ink)"}}>{formatSelectionLabel()}</div>
                  <button type="button" aria-label="Clear selection" onClick={()=>setSelection([])}
                    style={{width:30, height:30, borderRadius:15, background:"var(--cream)", border:"none", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer"}}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
                  </button>
                </div>

                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", position:"relative"}}>
                  <div style={{display:"flex", alignItems:"center", gap:8}}>
                    {swatch("yellow", "#F2D98A")}
                    {swatch("green", "#BFE0C2")}
                    {swatch("blue", "#C9DDF3")}
                    {swatch("pink", "#F3C6C6")}
                  </div>
                  <div style={{display:"flex", gap:8}}>
                    {roundBtn("Comment",
                      ()=>{ if (isVisitor) { openAccessRequest("comment"); return; } openCommunityCommentsForSelection(); },
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1.1-4.6A8 8 0 1 1 21 12z"/></svg>,
                      commCount > 0 ? commCount : null)}
                    {roundBtn("Share", shareSelection,
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v14"/></svg>)}
                    {roundBtn("More", ()=>setSelMoreOpen(o=>!o),
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h.01M12 12h.01M19 12h.01"/></svg>)}
                  </div>
                  {selMoreOpen && (
                    <>
                      <div onClick={()=>setSelMoreOpen(false)} style={{position:"fixed", inset:0, zIndex:160}}/>
                      <div style={{position:"absolute", right:0, top:"calc(100% + 6px)", zIndex:161, background:"var(--white)", borderRadius:14, boxShadow:"0 8px 24px rgba(31,27,22,0.18)", overflow:"hidden", minWidth:140}}>
                        <button type="button" onClick={()=>{ setSelMoreOpen(false); openCommentsForFirst(); }}
                          style={{display:"block", width:"100%", textAlign:"left", padding:"11px 16px", background:"none", border:"none", cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13.5, fontWeight:600, color:"var(--ink)"}}>
                          Note
                        </button>
                        <button type="button"
                          onClick={async ()=>{
                            setSelMoreOpen(false);
                            const text = selection.map(v => verses.find(vs => vs.v === v)?.t || "").filter(Boolean).join(" ");
                            const copyText = `"${text}" — ${formatSelectionLabel()} (${ver})`;
                            try { await navigator.clipboard.writeText(copyText); flashCopiedToast(); } catch (_e) { /* ignore */ }
                          }}
                          style={{display:"block", width:"100%", textAlign:"left", padding:"11px 16px", background:"none", border:"none", borderTop:"1px solid var(--border)", cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:13.5, fontWeight:600, color:"var(--ink)"}}>
                          Copy
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {touchingSermons.length > 0 && (
                  <div onClick={openSermonForSelection}
                    style={{display:"flex", alignItems:"center", gap:12, background:"var(--lavender)", borderRadius:18, padding:"10px 12px 10px 10px", cursor:"pointer"}}>
                    <span style={{position:"relative", width: 18 + Math.min(touchingSermons.length,3)*18, height:46, flexShrink:0}}>
                      {touchingSermons.slice(0,3).map((s,i) => (
                        <span key={s.id} style={{position:"absolute", left:i*18, top:0, width:46, height:46, borderRadius:12, overflow:"hidden", border:"2px solid var(--white)", boxSizing:"border-box", background:"var(--border)"}}>
                          {s.youtube_id && <img src={`https://i.ytimg.com/vi/${s.youtube_id}/mqdefault.jpg`} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>}
                        </span>
                      ))}
                    </span>
                    <span style={{display:"flex", flexDirection:"column", gap:2, flex:1, minWidth:0}}>
                      <span style={{display:"flex", alignItems:"center", gap:6, fontSize:15, fontWeight:800, color:"var(--lavender-ink)"}}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="var(--lavender-ink)" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
                        {touchingSermons.length} sermon clip{touchingSermons.length === 1 ? "" : "s"}
                      </span>
                      <span style={{fontSize:12, fontWeight:700, color:"var(--lavender-ink)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>{touchingChurchNames}</span>
                    </span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--lavender-ink)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>
                  </div>
                )}
              </div>
            );
          })()}

          {selCopiedToast && (
            <div style={{position:"fixed", left:"50%", bottom:"calc(env(safe-area-inset-bottom) + 100px)", transform:"translateX(-50%)", zIndex:300, background:"var(--ink)", color:"#fff", borderRadius:999, padding:"9px 18px", fontSize:13, fontWeight:700, fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:"0 8px 20px rgba(31,27,22,0.25)"}}>
              Copied
            </div>
          )}
        </>}

        {/* ══ HOME ══ */}
        {/* ══ CHURCH SECTION (Info / Sermons sub-tabs) ══ */}
        {tab==="church" && (
          <div className="comm-tabs">
            <div className={`comm-tab ${churchSub==="info"?"on":""}`} onClick={()=>setChurchSub("info")}>Info</div>
            <div className={`comm-tab ${churchSub==="sermons"?"on":""}`} onClick={()=>setChurchSub("sermons")}>Sermons</div>
          </div>
        )}

        {tab==="church" && churchSub==="info" && (() => {
          // Scope every sermon-related thing on the Info page to the
          // single church the user is viewing (selected via the dropdown
          // below). `churchSermons` is the multi-church followed list,
          // so without this filter sermons from followed churches would
          // surface here as "latest from your church" — they aren't.
          const infoSermons = resolvedInfoChurchId
            ? churchSermons.filter(s => s.church_id === resolvedInfoChurchId)
            : [];
          // Latest sermon = newest item from the church-scoped, date-sorted list.
          const latest = infoSermons[0] || null;
          const latestRef = latest?.sermon_refs?.[0]?.verse_ref || "";
          const latestDateLbl = latest?.date
            ? new Date(latest.date).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})
            : "";
          const latestSeries = latest?.series || (latestDateLbl ? `Sunday Service · ${latestDateLbl}` : "");
          const latestWatchUrl = latest?.youtube_url
            || (latest?.youtube_id ? `https://www.youtube.com/watch?v=${latest.youtube_id}` : null);
          const latestThumb = latest?.youtube_id
            ? `https://i.ytimg.com/vi/${latest.youtube_id}/maxresdefault.jpg`
            : null;
          // Parse "Book Chapter:Verse" → routable target for "Read passage →".
          const passageMatch = latestRef.match(/^(.+?)\s+(\d+):(\d+)/);
          // Same legacy shape the Recent Sermons rows build, so tapping the
          // Latest Sermon card opens the identical sermon-detail sheet.
          const latestLegacy = latest ? {
            id:        latest.id,
            church_id: latest.church_id || null,
            series:    latest.series || "",
            title:     latest.title,
            pastor:    latest.preacher || "",
            date:      latestDateLbl,
            rawDate:   latest.date || "",
            ytId:      latest.youtube_id || "",
            ts:        "",
            notes:     latest.transcript || "",
            refs:      (latest.sermon_refs || []).map(r => r.verse_ref),
            _allRefs:  latest.sermon_refs || [],
          } : null;
          // Which churches the user can switch between on the Info
          // page — their home church plus any they follow. Only render
          // the dropdown if there's more than one option.
          const infoChoices = (() => {
            const seen = new Set();
            const out  = [];
            for (const id of effectiveSermonChurchIds) {
              if (!id || seen.has(id)) continue;
              seen.add(id);
              const c = churches?.find(x => x.id === id);
              if (c) out.push(c);
            }
            return out;
          })();
          const homeChurchId = activeChurchId || profile?.church_id || null;
          const isViewingHome = resolvedInfoChurchId === homeChurchId;
          return (
            <div className="hscroll">
              {/* Top bar — church-selector on the left (only if >1 option),
                  Settings on the right (only when viewing home AND user is
                  a moderator of that church). Wraps gracefully on mobile. */}
              <div style={{
                display:"flex", alignItems:"center", justifyContent:"space-between",
                gap:8, flexWrap:"wrap", margin:"16px 0 16px",
              }}>
                {infoChoices.length > 1 ? (
                  <label style={{
                    display:"inline-flex", alignItems:"center", gap:8,
                    background:"var(--white)", border:"1px solid var(--border)",
                    borderRadius:10, padding:"6px 10px 6px 12px",
                    fontFamily:"'Plus Jakarta Sans',sans-serif",
                  }}>
                    <span style={{
                      fontSize:10, fontWeight:700, letterSpacing:"0.14em",
                      textTransform:"uppercase", color:"var(--stone-dim)",
                    }}>Church</span>
                    <select
                      value={resolvedInfoChurchId || ''}
                      onChange={e => setInfoChurchId(e.target.value)}
                      aria-label="Choose which church's info to view"
                      style={{
                        background:"transparent", border:"none",
                        color:"var(--ink)", padding:"4px 4px 4px 0",
                        fontSize:13, fontWeight:700, fontFamily:"'Plus Jakarta Sans',sans-serif",
                        cursor:"pointer", outline:"none",
                        // Dark popup so options aren't white-on-white on desktop.
                        colorScheme:"dark",
                      }}>
                    {infoChoices.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.id === homeChurchId ? ' (home)' : ''}
                      </option>
                    ))}
                    </select>
                  </label>
                ) : <span/>}
                {isModerator && isViewingHome && (
                  <button type="button" onClick={() => setChurchSettingsOpen(true)}
                    style={{
                      background:"transparent", border:"1px solid var(--border)",
                      color:"var(--stone)", borderRadius:8, padding:"6px 12px",
                      fontSize:12, fontWeight:700, cursor:"pointer",
                      fontFamily:"'Plus Jakarta Sans',sans-serif",
                      display:"inline-flex", alignItems:"center", gap:6,
                    }}>
                    <Settings size={13} strokeWidth={2}/> Settings
                  </button>
                )}
              </div>

              {infoChurchVision && (
                <>
                  <div className="sec-lbl">Our Vision</div>
                  <div className="vision-card">
                    <div className="vision-txt">"{infoChurchVision}"</div>
                  </div>
                </>
              )}

              {infoChurchMeetInfo && (
                <>
                  <div className="sec-lbl">When and where we meet</div>
                  <div style={{
                    background:"var(--white)", border:"1px solid var(--border)",
                    borderRadius:10, padding:"14px 16px", fontSize:14, color:"var(--ink)",
                    lineHeight:1.5, whiteSpace:"pre-wrap", marginBottom:14,
                  }}>{infoChurchMeetInfo}</div>
                </>
              )}

              {churchEvents.length > 0 && (
                <>
                  <div className="sec-lbl">Events</div>
                  {churchEvents.map(ev => (
                    <div key={ev.id} style={{
                      background:"var(--white)", border:"1px solid var(--border)",
                      borderRadius:10, padding:"12px 14px", marginBottom:10,
                      display:"flex", alignItems:"flex-start", gap:12,
                    }}>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{fontSize:14, fontWeight:700, color:"var(--ink)"}}>{ev.name}</div>
                        <div style={{fontSize:12, color:"var(--stone)", marginTop:2}}>
                          {ev.event_date ? new Date(ev.event_date).toLocaleDateString("en-GB",{weekday:"long", day:"numeric", month:"short", year:"numeric"}) : ""}
                          {ev.event_time ? ` · ${String(ev.event_time).slice(0,5)}` : ""}
                          {ev.location   ? ` · ${ev.location}` : ""}
                        </div>
                        {ev.description && (
                          <div style={{fontSize:13, color:"var(--stone)", marginTop:6, lineHeight:1.5, whiteSpace:"pre-wrap"}}>{ev.description}</div>
                        )}
                      </div>
                      <button type="button" onClick={() => downloadIcs(ev)}
                        title="Download .ics for this event"
                        style={{
                          background:"rgba(232,200,122,0.12)", color:"var(--gold-ink)",
                          border:"1px solid rgba(232,200,122,0.35)", borderRadius:8,
                          padding:"6px 10px", fontSize:11, fontWeight:700, cursor:"pointer",
                          fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:"nowrap", flexShrink:0,
                        }}>
                        📅 Add to calendar
                      </button>
                    </div>
                  ))}
                </>
              )}

              {latest && (
                <>
                  <div className="sec-lbl">Latest Sermon</div>
                  <div className="latest-sermon-card" style={{cursor:"pointer"}}
                    onClick={()=>openFullSermon(latest, null)}>
                    <div className="ls-thumb">
                      {latestThumb && (
                        <img
                          src={latestThumb}
                          alt={latest.title || "Latest sermon"}
                          className="ls-thumb-img"
                          onError={e=>{e.target.style.display="none";}}
                        />
                      )}
                      <div className="ls-play-overlay">▶</div>
                    </div>
                    <div className="ls-body">
                      {latestSeries && <div className="ls-series">{latestSeries}</div>}
                      <div className="ls-title">{latest.title || "Latest sermon"}</div>
                      {latestRef && <div className="ls-passage">{latestRef}</div>}
                      <div className="ls-actions">
                        {latestWatchUrl && (
                          <button className="ls-watch-btn" onClick={(e)=>{e.stopPropagation(); openExternalUrl(latestWatchUrl);}}>
                            ▶ Watch
                          </button>
                        )}
                        {passageMatch && (
                          <button className="ls-read-btn" onClick={(e)=>{
                            e.stopPropagation();
                            setBook(passageMatch[1]);
                            setChap(parseInt(passageMatch[2], 10));
                            setTab("bible"); setBibleSub("read");
                          }}>
                            Read passage →
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="sec-lbl">Recent Sermons</div>
              {infoSermons.length === 0 && sermonsHydrating ? (
                <div className="empty" style={{padding:"24px 0", textAlign:"center"}}>
                  <div className="eico">✦</div>
                  <div className="esub">Loading sermons…</div>
                </div>
              ) : infoSermons.length === 0 ? (
                <div className="empty" style={{padding:"24px 0", textAlign:"center"}}>
                  <div className="eico">🎙️</div>
                  <div className="esub">No sermons</div>
                  {isModerator && isViewingHome && (
                    <button type="button" onClick={()=>setSermonSettingsOpen(true)}
                      style={{
                        marginTop:14, background:"var(--gold)", color:"var(--ink)",
                        border:"none", borderRadius:10, padding:"11px 20px",
                        fontSize:14, fontWeight:800, cursor:"pointer",
                        fontFamily:"'Plus Jakarta Sans',sans-serif",
                      }}>
                      + Add a sermon
                    </button>
                  )}
                </div>
              ) : infoSermons.slice(0,3).map(s=>{
                const dateLbl = s.date ? new Date(s.date).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : "";
                // Open THIS sermon's detail sheet (with its Bible refs), not the
                // generic sermons list. Same shape the Sermons-tab rows build, so
                // it renders identically. `s` is already scoped to the church the
                // user is viewing on the Info page, so it respects the church
                // picker rather than defaulting to the home church.
                const legacy = {
                  id:        s.id,
                  church_id: s.church_id || null,
                  series:    s.series || "",
                  title:     s.title,
                  pastor:    s.preacher || "",
                  date:      dateLbl,
                  rawDate:   s.date || "",
                  ytId:      s.youtube_id || "",
                  ts:        "",
                  notes:     s.transcript || "",
                  refs:      (s.sermon_refs || []).map(r => r.verse_ref),
                  _allRefs:  s.sermon_refs || [],
                };
                return (
                  <div key={s.id} className="srow" onClick={()=>openFullSermon(s, null)}>
                    <div className="srow-ico">🎙️</div>
                    <div>
                      {renderChurchPill(s.church_id)}
                      <div className="srow-title">{s.title || ""}</div>
                      <div className="srow-meta">{dateLbl}</div>
                    </div>
                    <div className="srow-arr">›</div>
                  </div>
                );
              })}

              {infoChurchWebsite && (
                <>
                  <div className="sec-lbl">Church website</div>
                  <div className="srow" onClick={()=>openExternalUrl(infoChurchWebsite)}>
                    <div className="srow-ico">🌐</div>
                    <div>
                      <div className="srow-title">{infoChurch?.name || "Visit website"}</div>
                      <div className="srow-meta">{infoChurchWebsite.replace(/^https?:\/\//, '')}</div>
                    </div>
                    <div className="srow-arr">›</div>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* ══ SERMONS ══ */}
        {tab==="church" && churchSub==="sermons" && (() => {
          // Apply the tab-local church filter on top of the multi-church
          // `churchSermons`. Default is profile.church_id, so non-admin
          // users see their primary church straight away; they can pull
          // in followed churches via the filter dropdown.
          const tabSermons = sermonTabChurchFilter === 'all'
            ? churchSermons
            : churchSermons.filter(s => s.church_id === sermonTabChurchFilter);
          // For pill-overlay purposes — show pill only when the list
          // spans multiple churches.
          const distinctChurchIds = new Set();
          for (const s of tabSermons) if (s.church_id) distinctChurchIds.add(s.church_id);
          const showChurchPill = distinctChurchIds.size > 1;
          // ── Group sermons by playlist for the folder grid ──
          // Shared with the Sermons tab's Series section — see
          // src/lib/sermonSeries.js for the grouping/fallback/thumbnail logic.
          const seriesGroups = groupSermonsBySeries(tabSermons);

          // Drill-in: filter sermons to the chosen series
          const inSeries = selectedSeries === "__all__"
            ? tabSermons
            : selectedSeries
              ? (seriesGroups.find(g => g.name === selectedSeries)?.sermons || [])
              : null;

          // Build the church-filter dropdown options from every church the
          // user follows (active + extras) — Map preserves insertion order.
          const filterOptions = (() => {
            const seen = new Set();
            const out  = [];
            for (const id of effectiveSermonChurchIds) {
              if (!id || seen.has(id)) continue;
              seen.add(id);
              const c = churches?.find(x => x.id === id);
              if (c) out.push(c);
            }
            return out;
          })();
          const currentFilterChurch = filterOptions.find(c => c.id === sermonTabChurchFilter);
          const filterLabel = sermonTabChurchFilter === 'all'
            ? 'All churches'
            : (currentFilterChurch?.name || 'Church');

          return (
            <div className="slist">
              {isModerator && (
                <div style={{
                  margin:"12px 0 0",
                  padding:"8px 12px",
                  background:"rgba(232,200,122,0.10)",
                  border:"1px solid rgba(232,200,122,0.30)",
                  borderRadius:8,
                  fontSize:12, lineHeight:1.5, color:"var(--gold-ink)",
                  fontFamily:"'Plus Jakarta Sans',sans-serif",
                  display:"flex", alignItems:"center", gap:10, flexWrap:"wrap",
                }}>
                  <span style={{flex:1, minWidth:0}}>🛡 You have admin rights so can upload and edit sermons</span>
                </div>
              )}

              {/* Church filter — only useful if the user follows more
                  than one church; otherwise hide entirely. */}
              {filterOptions.length > 1 && (
                <div style={{position:"relative", margin:"14px 0 0"}}>
                  <button type="button"
                    onClick={() => setSermonChurchMenuOpen(o => !o)}
                    style={{
                      display:"inline-flex", alignItems:"center", gap:6,
                      background:"var(--white)", border:"1px solid var(--border-strong)",
                      borderRadius:8, padding:"7px 12px",
                      fontSize:12, fontWeight:700, color:"var(--ink)",
                      letterSpacing:"0.04em", cursor:"pointer",
                      fontFamily:"'Plus Jakarta Sans',sans-serif",
                    }}>
                    Church: {filterLabel}
                    <span style={{fontSize:10, opacity:0.6}}>▾</span>
                  </button>
                  {sermonChurchMenuOpen && (
                    <>
                      <div onClick={()=>setSermonChurchMenuOpen(false)}
                        style={{position:"fixed", inset:0, zIndex:90}}/>
                      <div style={{
                        position:"absolute", top:"100%", left:0, marginTop:6, zIndex:91,
                        background:"var(--white)", border:"1px solid var(--border-strong)",
                        borderRadius:10, minWidth:200, overflow:"hidden",
                        boxShadow:"0 10px 24px rgba(0,0,0,0.2)",
                      }}>
                        <div onClick={()=>{ setSermonTabChurchFilter('all'); setSermonChurchMenuOpen(false); setSelectedSeries("__all__"); }}
                          style={{
                            padding:"10px 12px", cursor:"pointer", fontSize:13, fontWeight:600,
                            color: sermonTabChurchFilter === 'all' ? "var(--gold-ink)" : "var(--ink)",
                            background: sermonTabChurchFilter === 'all' ? "rgba(232,200,122,0.10)" : "transparent",
                            borderBottom:"1px solid var(--border)",
                          }}>
                          All churches
                        </div>
                        {filterOptions.map(c => (
                          <div key={c.id}
                            onClick={()=>{ setSermonTabChurchFilter(c.id); setSermonChurchMenuOpen(false); setSelectedSeries("__all__"); }}
                            style={{
                              padding:"10px 12px", cursor:"pointer", fontSize:13, fontWeight:600,
                              color: sermonTabChurchFilter === c.id ? "var(--gold-ink)" : "var(--ink)",
                              background: sermonTabChurchFilter === c.id ? "rgba(232,200,122,0.10)" : "transparent",
                            }}>
                            {c.name}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,margin:"16px 0 12px",flexWrap:"wrap"}}>
                <div className="sec-lbl" style={{margin:0}}>
                  {selectedSeries === null
                    ? `Series · ${seriesGroups.length}`
                    : selectedSeries === "__all__"
                      ? `All sermons · ${tabSermons.length}`
                      : `${selectedSeries} · ${inSeries.length}`}
                </div>
                {/* Inside a specific playlist → back to the grid. Otherwise a
                    "View by playlist" toggle: on = playlist grid, off = all. */}
                {(selectedSeries !== null && selectedSeries !== "__all__") ? (
                  <button
                    onClick={()=>setSelectedSeries(null)}
                    style={{background:"var(--white)",border:"1px solid var(--border-strong)",borderRadius:8,padding:"7px 13px",fontSize:12,fontWeight:700,color:"var(--stone)",cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                    ← Back to series
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={()=>setSelectedSeries(selectedSeries === null ? "__all__" : null)}
                    aria-pressed={selectedSeries === null}
                    style={{display:"inline-flex",alignItems:"center",gap:8,background:"none",border:"none",padding:"4px 0",cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                    <span style={{fontSize:12,fontWeight:700,color:"var(--stone)"}}>View by series</span>
                    <span style={{
                      width:38,height:22,borderRadius:11,padding:2,flexShrink:0,
                      background: selectedSeries === null ? "var(--gold)" : "var(--white)",
                      border:"1px solid var(--border-strong)",
                      display:"inline-flex",alignItems:"center",
                      transition:"background 0.2s",
                    }}>
                      <span style={{
                        width:16,height:16,borderRadius:"50%",background:"#fff",display:"block",
                        transform: selectedSeries === null ? "translateX(16px)" : "translateX(0)",
                        transition:"transform 0.2s",
                      }}/>
                    </span>
                  </button>
                )}
              </div>

              {tabSermons.length === 0 && sermonsHydrating ? (
                <div className="empty">
                  <div className="eico">✦</div>
                  <div className="etit">Loading sermons…</div>
                </div>
              ) : tabSermons.length === 0 ? (
                <div className="empty">
                  <div className="eico">🎙️</div>
                  <div className="etit">No sermons yet</div>
                  {isModerator ? (
                    <button
                      type="button"
                      onClick={()=>setSermonSettingsOpen(true)}
                      style={{
                        marginTop:12, background:"var(--gold)", color:"var(--ink)",
                        border:"none", borderRadius:8, padding:"10px 18px",
                        fontSize:13, fontWeight:800, cursor:"pointer",
                        fontFamily:"'Plus Jakarta Sans',sans-serif",
                      }}>
                      + Add sermons
                    </button>
                  ) : (
                    <div className="esub">Check back soon.</div>
                  )}
                </div>
              ) : selectedSeries === null ? (
                // ── PLAYLIST GRID ──
                <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:14,paddingBottom:24}}>
                  {seriesGroups.length === 0 && (
                    <div style={{gridColumn:"1 / -1",color:"var(--stone-dim)",fontSize:13,padding:"8px 2px"}}>No series yet.</div>
                  )}
                  {seriesGroups.map(({ name, sermons, thumb }) => (
                    <SeriesFolder
                      key={name}
                      name={name}
                      count={sermons.length}
                      thumb={thumb}
                      onClick={()=>setSelectedSeries(name)}/>
                  ))}
                  {isModerator && (
                    <AddSermonTile onClick={()=>setSermonSettingsOpen(true)}/>
                  )}
                </div>
              ) : (
                // ── SERMON LIST FOR ONE SERIES ──
                inSeries.map(s => {
                  const allRefs = (s.sermon_refs || []).map(r => r.verse_ref);
                  const dateLbl = s.date ? new Date(s.date).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : "";
                  const legacy = {
                    id:        s.id,
                    church_id: s.church_id || null,
                    series:    s.series || "",
                    title:     s.title,
                    pastor:    s.preacher || "",
                    date:      dateLbl,
                    rawDate:   s.date || "",
                    ytId:      s.youtube_id || "",
                    ts:        "",
                    notes:     s.transcript || "",
                    refs:      allRefs,
                    _allRefs:  s.sermon_refs || [],
                  };
                  // Church-name pill overlay — only when the list spans
                  // more than one church (otherwise the row is redundant).
                  const pillName = showChurchPill
                    ? churches?.find(c => c.id === s.church_id)?.name
                    : null;
                  return (
                    <div key={s.id} className="sermon-row" onClick={()=>openFullSermon(s, null)}>
                      <div className="sermon-row-thumb" style={{position:"relative"}}>
                        {s.youtube_id ? (
                          <img src={`https://i.ytimg.com/vi/${s.youtube_id}/mqdefault.jpg`}
                            alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}
                            onError={e=>{e.target.style.display="none"}}/>
                        ) : (
                          <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>🎙️</div>
                        )}
                        {pillName && (
                          <div style={{
                            position:"absolute", bottom:6, left:6,
                            background:"rgba(0,0,0,0.7)",
                            border:"1px solid var(--border-strong)",
                            color:"#fff",
                            fontSize:9, fontWeight:700, letterSpacing:"0.06em",
                            padding:"3px 8px", borderRadius:4,
                            textTransform:"uppercase",
                            maxWidth:"calc(100% - 12px)",
                            whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                          }}>{pillName}</div>
                        )}
                      </div>
                      <div className="sermon-row-info">
                        <div className="sermon-row-date">{dateLbl}</div>
                        <div className="sermon-row-title">{displayTitleOf(s)}</div>
                        <div className="sermon-row-refs">
                          {allRefs.slice(0,3).map(r=>(
                            <span key={r} className="ref-tag" style={{fontSize:10,padding:"2px 6px"}}>{r}</span>
                          ))}
                          {allRefs.length > 3 && <span style={{fontSize:10,color:"var(--stone-dim)"}}>+{allRefs.length-3} more</span>}
                        </div>
                      </div>
                      <div style={{fontSize:18,color:"var(--stone-dim)",flexShrink:0}}>›</div>
                    </div>
                  );
                })
              )}
            </div>
          );
        })()}

        {/* ══ NOTES ══ */}
        {tab==="notes" && (
          <div className="hlscroll">
            <div className="sec-lbl" style={{marginTop:16}}>My Highlights · {Object.keys(highlights).length}</div>
            {Object.keys(highlights).length===0 ? (
              <div className="empty"><div className="eico">✏️</div><div className="etit">No Highlights Yet</div><div className="esub">Tap any verse while reading to highlight it in three colours.</div></div>
            ) : Object.entries(highlights).map(([ref,color])=>(
              <div key={ref} className={`hlitem ${color}`}>
                <div className="hldot" style={{background:colMap[color]}}/>
                <div>
                  <div className="hlref" style={{color:colMap[color]}}>{ref}</div>
                  <div className="hltxt">{verseText(ref)}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ══ BIBLE › PLANS (docs/designs/PlansTab.dc.html) ══ */}
        {tab==="bible" && bibleSub==="plans" && (
          <div style={{display:"flex",flexDirection:"column",flex:1,overflow:"hidden"}}>
            <PlansTab
              churchId={profile?.church_id || null}
              churchName={activeChurchName}
              followedChurchIds={effectiveSermonChurchIds}
              churchById={new Map((churches || []).map(c => [c.id, c]))}
              userId={user?.id}
              isModerator={isModerator}
              onOpenPlanDay={(plan, follow, dayNumber) => setOpenPlan({ plan, follow, initialDay: dayNumber })}
              onOpenSermon={(sermon) => openFullSermon(sermon, null)}
              onOpenLeaders={() => setLeadersPageOpen(true)}
              onFindChurch={() => openAccessRequest("plan")}
            />
          </div>
        )}

        {/* ══ COMMUNITY COMMENTS (moved from Connect) ══ */}
        {tab==="bible" && bibleSub==="comments" && !isVisitor && (
          <div style={{display:"flex",flexDirection:"column",flex:1,overflow:"hidden"}}>
            {(() => {
              const repliesUnread = notifications.filter(n => n.type === 'verse_comment_reply' && !n.read).length;
              const scopeLabel = commentsScope === 'chapter' ? `${book} ${chap}` : commentsScope === 'book' ? book : 'Whole Bible';
              return (
                <div style={{display:"flex",alignItems:"center",gap:6,padding:"0 16px 10px",flexShrink:0}}>
                  {[['all','All'], ['mine','Mine'], ...(repliesUnread>0 ? [['replies',`Replies to me · ${repliesUnread}`]] : [])].map(([id,label]) => (
                    <button key={id} type="button" onClick={()=>setCommentsFilterTab(id)}
                      style={{borderRadius:999,padding:"7px 13px",fontSize:13,fontWeight:700,whiteSpace:"nowrap",border:"none",cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif",background:commentsFilterTab===id?"var(--gold)":"var(--white)",color:commentsFilterTab===id?"var(--ink)":"var(--stone)"}}>
                      {label}
                    </button>
                  ))}
                  <div style={{marginLeft:"auto",position:"relative",flexShrink:0}}>
                    <button type="button" onClick={()=>setCommentsScopeMenuOpen(o=>!o)}
                      style={{display:"flex",alignItems:"center",gap:5,fontSize:13,fontWeight:700,color:"var(--gold-ink)",background:"none",border:"none",cursor:"pointer",whiteSpace:"nowrap",fontFamily:"'Plus Jakarta Sans',sans-serif",padding:"4px 2px"}}>
                      {scopeLabel}
                      <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg>
                    </button>
                    {commentsScopeMenuOpen && (
                      <>
                        <div onClick={()=>setCommentsScopeMenuOpen(false)} style={{position:"fixed",inset:0,zIndex:90}}/>
                        <div style={{position:"absolute",top:"100%",right:0,marginTop:6,zIndex:91,background:"var(--white)",border:"1px solid var(--border-strong)",borderRadius:10,minWidth:150,overflow:"hidden",boxShadow:"0 10px 24px rgba(0,0,0,0.2)"}}>
                          {[['chapter',`This chapter (${book} ${chap})`], ['book',`This book (${book})`], ['bible','Whole Bible']].map(([id,label]) => (
                            <div key={id} onClick={()=>{ setCommentsScope(id); setCommentsScopeMenuOpen(false); }}
                              style={{padding:"10px 12px",cursor:"pointer",fontSize:13,fontWeight:600,color:commentsScope===id?"var(--gold-ink)":"var(--ink)",background:commentsScope===id?"rgba(232,200,122,0.10)":"transparent"}}>
                              {label}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
            <div className="flag-scroll" style={{paddingTop:0}}>
              {(() => {
                // Count only verses that actually carry a question, from the SAME
                // data the Elders dashboard renders: the church-wide question set
                // for moderators, the user's own flagged questions for members.
                // Previously this counted every row in `flagged` (the user's own
                // flags — including ones with no question text and from other
                // churches), so the banner could read "3 verses with questions"
                // while the dashboard's church-scoped, question-only query showed
                // none.
                const questionVerseCount = isModerator
                  ? new Set(churchQuestions.map(q => q.verse_ref)).size
                  : Object.values(flagged).filter(f => f?.question).length;
                if (questionVerseCount === 0) return null;
                return (
                  <div className="elder-banner" style={{cursor:"pointer"}} onClick={()=>{ setEldersSubTab("questions"); setEldersSheetOpen(true); }}>
                    <div className="elder-banner-icon" style={{display:"flex",alignItems:"center"}}><ColouredCompass size={26}/></div>
                    <div style={{flex:1}}>
                      <div className="elder-banner-title">{questionVerseCount} verse{questionVerseCount>1?"s":""} with questions for elders</div>
                      <div className="elder-banner-sub">Tap to open Elders only dashboard →</div>
                    </div>
                  </div>
                );
              })()}
              {(() => {
                // Flatten to one array of top-level comments (replies stay
                // nested on .replies, rendered inline by renderVerseCommentCard).
                let allTop = [];
                for (const [vk, cms] of Object.entries(comments)) {
                  for (const cm of cms) if (!cm.private) allTop.push({ ...cm, vk });
                }
                const totalBeforeScope = allTop.length;
                allTop = allTop.filter(cm => {
                  const m = cm.vk.match(/^(.+)\s(\d+):\d+$/);
                  if (!m) return false;
                  if (commentsScope === 'chapter') return m[1] === book && +m[2] === chap;
                  if (commentsScope === 'book') return m[1] === book;
                  return true;
                });
                if (commentsFilterTab === 'mine') allTop = allTop.filter(cm => cm.authorId === user?.id);
                if (commentsFilterTab === 'replies') allTop = allTop.filter(cm => cm.authorId === user?.id && (cm.replies || []).length > 0);
                // Newest activity first — a new reply bumps its card up.
                const activityMs = cm => Math.max(
                  new Date(cm.created_at || 0).getTime(),
                  ...(cm.replies || []).map(r => new Date(r.created_at || 0).getTime()),
                  0
                );
                allTop.sort((a, b) => activityMs(b) - activityMs(a));
                // Trigger verse-text loads for every chapter shown (same
                // fetch-on-demand helper the old expand-toggle used to call).
                const loadedChapters = new Set();
                for (const cm of allTop) {
                  const m = cm.vk.match(/^(.+)\s(\d+):\d+$/);
                  if (!m) continue;
                  const key = `${m[1]} ${m[2]}`;
                  if (loadedChapters.has(key)) continue;
                  loadedChapters.add(key);
                  ensureChapterLoaded(m[1], parseInt(m[2]));
                }
                if (allTop.length === 0) {
                  if (commentsFilterTab === 'mine') {
                    return <div className="empty"><div className="eico">💬</div><div className="etit">You haven't commented yet.</div></div>;
                  }
                  if (commentsFilterTab === 'replies') {
                    return <div className="empty"><div className="eico">💬</div><div className="etit">No replies to you yet.</div></div>;
                  }
                  return (
                    <div className="empty">
                      <div className="eico">💬</div>
                      <div className="etit">{totalBeforeScope===0 ? "No Comments Yet" : `No comments on ${commentsScope === 'chapter' ? `${book} ${chap}` : commentsScope === 'book' ? book : 'the whole Bible'} yet.`}</div>
                      <div className="esub">
                        Tap a verse while reading to add one.
                        {commentsScope !== 'bible' && (
                          <>{" "}<span style={{color:"var(--gold-ink)",fontWeight:700,cursor:"pointer"}} onClick={()=>setCommentsScope('bible')}>See comments across the whole Bible</span></>
                        )}
                      </div>
                    </div>
                  );
                }
                return allTop.map(cm => {
                  const vText = verseText(cm.vk);
                  const shortQuote = vText ? (vText.length > 90 ? vText.slice(0,90).trimEnd() + "…" : vText) : null;
                  const m = cm.vk.match(/^(.+)\s(\d+):(\d+)$/);
                  return (
                    <div key={cm.id} style={{background:"var(--white)",borderRadius:22,padding:14,marginBottom:10}}>
                      <div style={{display:"flex",gap:10,alignItems:"flex-start",borderLeft:"3px solid var(--sky)",paddingLeft:10,marginBottom:10,cursor:"pointer"}}
                        onClick={()=>{ if (m) { setBook(m[1]); setChap(parseInt(m[2])); setSelection([parseInt(m[3])]); setTab("bible"); setBibleSub("read"); } }}>
                        <div style={{display:"flex",flexDirection:"column",gap:2}}>
                          <span style={{fontSize:12,fontWeight:800,color:"var(--sky-ink)"}}>{cm.vk}</span>
                          {shortQuote && <span style={{fontFamily:"'Source Serif 4',Georgia,serif",fontSize:14,lineHeight:1.45,color:"var(--stone)"}}>"{shortQuote}"</span>}
                        </div>
                      </div>
                      {renderVerseCommentCard(cm, cm.vk)}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {/* ══ SAVED ══ */}
        {tab==="bible" && bibleSub==="saved" && (() => {
          // Which vks the view should consider before book/chapter filtering.
          const sourceVks = (() => {
            if (savedView === "highlighted") {
              return Object.keys(saved).filter(vk => !!highlights[vk]);
            }
            if (savedView === "questions") {
              const fromFlagged = Object.entries(flagged).filter(([,f])=>f?.question).map(([vk])=>vk);
              const fromSaved   = Object.keys(saved).filter(vk => flagged[vk]?.question);
              return [...new Set([...fromFlagged, ...fromSaved])];
            }
            if (savedView === "notes") {
              return Object.keys(saved).filter(vk =>
                (comments[vk] || []).some(c => c.authorId === user?.id)
              );
            }
            return Object.keys(saved);
          })();
          const viewLabel = savedView === "highlighted" ? "Highlighted"
            : savedView === "questions" ? "My questions"
            : savedView === "notes" ? "My notes"
            : "All";
          return (
          <div style={{display:"flex",flexDirection:"column",flex:1,overflow:"hidden"}}>
            {/* View filter chips */}
            <div className="saved-filter-row">
              {[
                { id:"all",         label:"All" },
                { id:"highlighted", label:"Highlighted" },
                { id:"questions",   label:"My questions" },
                { id:"notes",       label:"My notes" },
              ].map(opt => (
                <button key={opt.id}
                  className={`saved-chip ${savedView===opt.id?"on":""}`}
                  onClick={()=>setSavedView(opt.id)}>
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Book / chapter filter selectors */}
            <div className="filter-bar">
              <select className="filter-select" value={filterBook} onChange={e=>{setFilterBook(e.target.value);setFilterChap("all");}}>
                <option value="all">All books</option>
                {[...new Set(sourceVks.map(vk=>vk.match(/^(.+)\s\d+:\d+$/)?.[1]).filter(Boolean))].sort().map(b=>(
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              <select className="filter-select" value={filterChap} onChange={e=>setFilterChap(e.target.value)} disabled={filterBook==="all"}>
                <option value="all">All chapters</option>
                {filterBook!=="all" && [...new Set(
                  sourceVks
                    .filter(vk=>vk.startsWith(filterBook+" "))
                    .map(vk=>vk.match(/\s(\d+):/)?.[1])
                    .filter(Boolean)
                )].sort((a,b)=>+a-+b).map(c=>(
                  <option key={c} value={c}>Chapter {c}</option>
                ))}
              </select>
            </div>

            <div className="saved-scroll" style={{flex:1,paddingTop:8}}>
              {(() => {
                const filtered = sourceVks
                  .map(vk => [vk, saved[vk] || { text: flagged[vk]?.text || "", ref: vk, note: "", savedAt: flagged[vk]?.flaggedAt || "" }])
                  .filter(([vk])=>{
                    const m = vk.match(/^(.+)\s(\d+):\d+$/);
                    if (!m) return true;
                    if (filterBook!=="all" && m[1]!==filterBook) return false;
                    if (filterChap!=="all" && m[2]!==filterChap) return false;
                    return true;
                  });
                const label = filterBook==="all" ? `${viewLabel} · ${sourceVks.length}`
                  : filterChap==="all" ? `${filterBook} · ${filtered.length}`
                  : `${filterBook} ${filterChap} · ${filtered.length}`;
                return <>
                  <div className="sec-lbl" style={{marginTop:0}}>Saved Verses · {label}</div>
                  {filtered.length===0 ? (
                    <div className="empty">
                      <div className="eico"><Bookmark size={44} strokeWidth={1.5} color="var(--stone-dim)"/></div>
                      <div className="etit">No saved verses here</div>
                      <div className="esub">Try a different book or chapter filter.</div>
                    </div>
                  ) : filtered.map(([vk, sv_])=>{
                    const myPrivateNotes = (comments[vk] || []).filter(c => c.private && c.authorId === user?.id);
                    const allComments    = (comments[vk] || []).filter(c => !c.private);
                    return (
                      <div key={vk} className="saved-card">
                        <div className="saved-ref">
                          <span>{vk}</span>
                          <div className="saved-ref-actions">
                            <span className="saved-date">{sv_.savedAt}</span>
                            <button className="goto-btn" onClick={()=>{
                              const parts = vk.match(/^(.+)\s(\d+):(\d+)$/);
                              if (parts) { setBook(parts[1]); setChap(parseInt(parts[2])); setTab("bible"); setBibleSub("read"); }
                            }}>Read →</button>
                            <button className="unsave-btn" title="Remove" onClick={()=>setSaved(s=>{const n={...s};delete n[vk];return n;})}>✕</button>
                          </div>
                        </div>
                        <div className="saved-verse-txt">"{sv_.text}"</div>

                        {flagged[vk]?.question && (
                          <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)"}}>
                            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.15em",textTransform:"uppercase",color:"#C8372A"}}>
                                ❓ Your question
                              </div>
                              <button onClick={()=>{
                                const parts = vk.match(/^(.+)\s(\d+):(\d+)$/);
                                if (parts) {
                                  setBook(parts[1]); setChap(parseInt(parts[2]));
                                  setSelection([parseInt(parts[3])]);
                                  setQuestionSheet({
                                    vk,
                                    book: parts[1],
                                    chap: parseInt(parts[2]),
                                    verseNum: parseInt(parts[3]),
                                    text: sv_.text,
                                    draft: flagged[vk]?.question || "",
                                    isEditing: true,
                                  });
                                }
                              }} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:11,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                                Edit
                              </button>
                            </div>
                            <div style={{background:"rgba(249,112,102,0.08)",border:"1px solid rgba(249,112,102,0.25)",borderRadius:8,padding:"10px 12px",fontSize:14,color:"var(--ink)",lineHeight:1.5}}>
                              {flagged[vk].question}
                            </div>
                          </div>
                        )}

                        {myPrivateNotes.length > 0 && (
                          <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)"}}>
                            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.15em",textTransform:"uppercase",color:"var(--stone)",marginBottom:8}}>
                              🔒 Your private notes
                            </div>
                            {myPrivateNotes.map(n => (
                              <div key={n.id} style={{background:"var(--white)",border:"1px solid var(--border)",borderRadius:8,padding:"10px 12px",marginBottom:6}}>
                                <div style={{fontSize:10,color:"var(--stone-dim)",marginBottom:4}}>{n.date}</div>
                                <div style={{fontFamily:"Georgia,serif",fontSize:15,lineHeight:1.55,color:"var(--ink)"}}>{n.text}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {allComments.length > 0 && (
                          <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)"}}>
                            <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.15em",textTransform:"uppercase",color:"var(--stone-dim)",marginBottom:8}}>
                              💬 Community ({allComments.length})
                            </div>
                            {allComments.slice(0,3).map(c => (
                              <div key={c.id} style={{padding:"6px 0",borderBottom:"1px dashed var(--border)"}}>
                                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                                  <Avatar url={c.authorAvatar} name={c.author} size={18}/>
                                  <div style={{fontSize:11,fontWeight:700,color:"var(--stone)"}}>{c.author}</div>
                                </div>
                                <div style={{fontSize:13,color:"var(--ink)",lineHeight:1.5}}>{c.text}</div>
                              </div>
                            ))}
                            {allComments.length > 3 && (
                              <div style={{fontSize:11,color:"var(--stone-dim)",marginTop:6}}>
                                + {allComments.length - 3} more - open the verse to read them all
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>;
              })()}
            </div>
          </div>
          );
        })()}

        {/* ══ QUESTIONS - now inside COMMUNITY tab ══ */}
        {tab==="flagged" && (() => {
          // Moderators see every question across the church (anonymised).
          // Members fall back to their own - RLS already enforces this.
          const rows = isModerator ? churchQuestions : Object.entries(flagged)
            .filter(([,f]) => f?.question)
            .map(([vk, f]) => ({
              id: vk,
              verse_ref: vk,
              book: f.book,
              chapter: f.chap,
              verse: f.verse,
              verse_text: f.text,
              question: f.question,
              created_at: null,
            }));
          const fmtDate = (iso) => {
            if (!iso) return '';
            try { return new Date(iso).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}); }
            catch { return ''; }
          };
          const uniqueVerseCount = new Set(rows.map(r => r.verse_ref)).size;
          const bookCounts = (() => {
            const m = new Map();
            for (const r of rows) m.set(r.book, (m.get(r.book) || 0) + 1);
            return [...m.entries()].sort((a, b) => b[1] - a[1]);
          })();
          return (
          <div className="flag-scroll">
            <div className="sec-lbl" style={{marginTop:16}}>{isModerator ? "Anonymous questions from members" : "My questions"}</div>
            <div className="elder-banner">
              <div className="elder-banner-icon">👁️</div>
              <div>
                <div className="elder-banner-title">{isModerator ? "Elder View" : "Your questions"}</div>
                <div className="elder-banner-sub">
                  {rows.length === 0
                    ? "No questions yet"
                    : `${rows.length} question${rows.length>1?"s":""} across ${uniqueVerseCount} verse${uniqueVerseCount>1?"s":""}`}
                </div>
              </div>
            </div>
            {rows.length === 0 ? (
              <div className="empty">
                <div className="eico">❓</div>
                <div className="etit">No Questions Yet</div>
                <div className="esub">When members tap "I've got questions" on a verse, it appears here.</div>
              </div>
            ) : (
              <>
                <div style={{background:"var(--white)",border:"1px solid var(--border)",borderRadius:12,padding:"14px 16px",marginBottom:14}}>
                  <div className="sec-lbl" style={{marginTop:0,marginBottom:8}}>Books with questions</div>
                  {bookCounts.map(([b, count]) => (
                    <div key={b} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:"1px solid var(--border)"}}>
                      <span style={{fontSize:14,color:"var(--ink)"}}>{b}</span>
                      <span style={{fontSize:12,fontWeight:700,color:"#C8372A",background:"rgba(249,112,102,0.12)",padding:"2px 8px",borderRadius:6}}>{count} question{count>1?"s":""}</span>
                    </div>
                  ))}
                </div>
                {rows.map(r => {
                  const authorName = r.author?.full_name
                    || r.author?.email?.split('@')[0]
                    || (isModerator ? "Unknown member" : "You");
                  return (
                    <div key={r.id} className="flag-card">
                      <div className="flag-ref">{r.verse_ref}</div>
                      <div className="flag-text">"{r.verse_text}"</div>
                      <div style={{marginTop:8,padding:"10px 12px",background:"rgba(249,112,102,0.08)",border:"1px solid rgba(249,112,102,0.25)",borderRadius:8,fontSize:14,color:"var(--ink)",lineHeight:1.5}}>
                        <strong style={{fontWeight:700}}>Comment:</strong> {r.question}
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
                        <div className="flag-date">{fmtDate(r.created_at) ? `${authorName} · ${fmtDate(r.created_at)}` : authorName}</div>
                        <button onClick={()=>{
                          setBook(r.book); setChap(r.chapter); setSelection([r.verse]); setTab("bible"); setBibleSub("read");
                        }} style={{background:"none",border:"none",color:"var(--gold-ink)",fontSize:12,fontWeight:600,cursor:"pointer"}}>Go to verse →</button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
          );
        })()}

        {/* ══ ELDERS DASHBOARD - now a sheet opened from the profile ══ */}
        {eldersSheetOpen && isModerator && (() => {
          const eldersOnlyPrayers = (prayers || []).filter(p => p.visibility === "elders");
          const goToVerse = (book, chap, verse) => {
            setEldersSheetOpen(false);
            setBook(book); setChap(chap); setSelection([verse]); setTab("bible"); setBibleSub("read");
          };
          return (
          <>
            <div className="overlay" onClick={()=>setEldersSheetOpen(false)}/>
            {/* Fixed height so the sheet doesn't jump when switching sub-tabs
                with different amounts of content. */}
            <div className="sheet" style={{height:"92vh",maxHeight:"92vh",display:"flex",flexDirection:"column"}}>
              <div className="shandle"/>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 16px 0"}}>
                <div className="shd" style={{padding:0}}>Elders only dashboard</div>
                <button onClick={()=>setEldersSheetOpen(false)} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:20,cursor:"pointer",padding:4}}>✕</button>
              </div>
              <div style={{display:"flex",flexDirection:"column",flex:1,overflow:"hidden",marginTop:6}}>
            {/* Elder sub-tab bar - mirrors the Community sub-tabs */}
            <div className="comm-tabs" style={{overflowX:"auto",flexWrap:"nowrap"}}>
              <div className={`comm-tab ${eldersSubTab==="questions"?"on":""}`} onClick={()=>setEldersSubTab("questions")}>
                Questions {churchQuestions.length > 0 && `· ${churchQuestions.length}`}
              </div>
              <div className={`comm-tab ${eldersSubTab==="prayer"?"on":""}`} onClick={()=>setEldersSubTab("prayer")}>
                Prayer {eldersOnlyPrayers.length > 0 && `· ${eldersOnlyPrayers.length}`}
              </div>
            </div>

            {/* ── QUESTIONS sub-tab ── */}
            {eldersSubTab === "questions" && (
              <div className="flag-scroll" style={{paddingTop:12}}>
                {/* Collapsible Question themes (AI summary) */}
                <div style={{background:"var(--white)",border:"1px solid var(--border)",borderRadius:12,marginBottom:14,overflow:"hidden"}}>
                  <button type="button"
                    onClick={()=>setQuestionThemesOpen(o=>!o)}
                    style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,padding:"12px 14px",background:"none",border:"none",cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:14}}>✨</span>
                      <span style={{fontSize:11,fontWeight:700,letterSpacing:"0.14em",textTransform:"uppercase",color:"var(--gold-ink)"}}>Question themes</span>
                    </div>
                    <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:30,height:30,borderRadius:8,background:"var(--white)",border:"1px solid var(--border)",color:"var(--gold-ink)"}}>
                      {questionThemesOpen ? <ChevronDown size={20} strokeWidth={2.2}/> : <ChevronRight size={20} strokeWidth={2.2}/>}
                    </span>
                  </button>
                  {questionThemesOpen && (
                    <div style={{padding:"4px 16px 14px",borderTop:"1px solid var(--border)",fontSize:14,color:"var(--ink)",lineHeight:1.6}}>
                      {eldersAiSummary.loading ? (
                        <div style={{display:"flex",alignItems:"center",gap:8,color:"var(--stone)",paddingTop:10}}>
                          <span style={{display:"inline-block",width:14,height:14,border:"2px solid var(--gold)",borderTopColor:"transparent",borderRadius:"50%",animation:"sermon-spin 0.8s linear infinite"}}/>
                          <span>Looking for patterns…</span>
                        </div>
                      ) : eldersAiSummary.error ? (
                        <div style={{fontSize:13,color:"#C8372A",paddingTop:10}}>Couldn't generate summary: {eldersAiSummary.error}</div>
                      ) : eldersAiSummary.text ? (
                        <div style={{whiteSpace:"pre-wrap",paddingTop:10}}>{eldersAiSummary.text}</div>
                      ) : (
                        <div style={{fontSize:13,color:"var(--stone-dim)",paddingTop:10}}>No questions yet - once members raise some, a theme summary will appear here.</div>
                      )}
                      {churchQuestions.length > 0 && !eldersAiSummary.loading && (
                        <button type="button"
                          onClick={()=>setEldersAiSummary({ count: -1, text: "", loading: false, error: "" })}
                          style={{marginTop:10,background:"none",border:"none",color:"var(--stone-dim)",fontSize:11,cursor:"pointer",textDecoration:"underline",fontFamily:"'Plus Jakarta Sans',sans-serif",padding:0}}>
                          Refresh
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="sec-lbl" style={{marginTop:0}}>Questions · {churchQuestions.length}</div>
                {churchQuestions.length === 0 ? (
                  <div className="empty" style={{padding:"20px 0"}}>
                    <div className="eico">❓</div>
                    <div className="etit">No questions yet</div>
                    <div className="esub">Members can mark a verse "I've got questions" and they'll appear here anonymously.</div>
                  </div>
                ) : churchQuestions.map(q => {
                  const authorName = q.author?.full_name
                    || q.author?.email?.split('@')[0]
                    || "Unknown member";
                  const dateStr = q.created_at
                    ? new Date(q.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})
                    : "";
                  return (
                    <div key={q.id} className="flag-card">
                      <div className="flag-ref">{q.verse_ref}</div>
                      <div className="flag-text">"{q.verse_text}"</div>
                      <div style={{marginTop:8,padding:"10px 12px",background:"rgba(249,112,102,0.08)",border:"1px solid rgba(249,112,102,0.25)",borderRadius:8,fontSize:14,color:"var(--ink)",lineHeight:1.5}}>
                        <strong style={{fontWeight:700}}>Comment:</strong> {q.question}
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
                        <div className="flag-date">{dateStr ? `${authorName} · ${dateStr}` : authorName}</div>
                        <button onClick={()=>goToVerse(q.book, q.chapter, q.verse)}
                          style={{background:"none",border:"none",color:"var(--gold-ink)",fontSize:12,fontWeight:600,cursor:"pointer"}}>Go to verse →</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── PRAYER REQUESTS sub-tab ── */}
            {eldersSubTab === "prayer" && (
              <div className="flag-scroll" style={{paddingTop:12}}>
                <div className="sec-lbl" style={{marginTop:0}}>Private prayer requests · {eldersOnlyPrayers.length}</div>
                {eldersOnlyPrayers.length === 0 ? (
                  <div className="empty" style={{padding:"20px 0"}}>
                    <div className="eico">🙏</div>
                    <div className="etit">No private requests</div>
                    <div className="esub">Members can send a prayer request marked "Elders only" - those will show up here instead of the public Prayer feed.</div>
                  </div>
                ) : eldersOnlyPrayers.map(pr => (
                  <div key={pr.id} className="prayer-card" style={{borderLeft:"3px solid var(--gold)"}}>
                    <div className="prayer-header">
                      <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                        <Avatar url={pr.authorAvatar} name={pr.author} size={28}/>
                        <span className="prayer-author">{pr.author}</span>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span className="prayer-date">{pr.date}</span>
                        <ContentActionsMenu
                          canDelete={canDelete(pr.authorId)}
                          isOwn={pr.authorId === user?.id}
                          authorName={pr.author}
                          onDelete={()=>handleDeletePrayer(pr)}
                          onReport={()=>openReport("prayer", pr.id)}
                        />
                      </div>
                    </div>
                    <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.14em",textTransform:"uppercase",color:"var(--gold-ink)",marginBottom:6}}>
                      👑 Elders only
                    </div>
                    <div className="prayer-text">{pr.text}</div>
                    <div className="prayer-actions">
                      <div className={`pray-btn ${pr.prayed?"prayed":""}`} onClick={()=>togglePray(pr.id)}>
                        <span>🙏</span>
                        <span>{pr.prayed?"Praying":"Pray"}</span>
                        {pr.prayCount>0 && <span style={{fontWeight:800,marginLeft:2}}>{pr.prayCount}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

              </div>
            </div>
          </>
          );
        })()}

        {/* ══ SERMONS (ex-Explore, absorbs past-plans browsing) ══ */}
        {tab==="sermons" && (
          <div style={{flex:1, overflowY:"auto"}}>
            <SermonsTab
              churchIds={effectiveSermonChurchIds}
              churches={churches}
              activeChurchId={activeChurchId || profile?.church_id || ''}
              sermonNoteChurchIds={sermonNoteChurchIds}
              setSermonNoteChurchIds={setSermonNoteChurchIds}
              user={{ id: user?.id, email: user?.email, full_name: profile?.full_name }}
              isModerator={isModerator}
              onOpenVerse={(book, chapter, verse, scope) => openVerseSheetInPlace(book, chapter, verse, scope)}
              onOpenChurch={(e) => setProfileEntity(e)}
              onOpenPlanDay={(plan, follow, dayNumber) => setOpenPlan({ plan, follow, initialDay: dayNumber })}
              onOpenSermon={(sermon) => { setOpenPlan(null); openFullSermon(sermon, null); }}
              openBibleMapSignal={bibleMapSignal}
              searchOpen={sermonsSearchOpen}
              onToggleSearch={() => setSermonsSearchOpen(o => !o)}
            />
          </div>
        )}

        {/* ══ THIS WEEK (ex-Plans) ══ */}
        {tab==="week" && (
          <div style={{flex:1, overflowY:"auto"}}>
            <ThisWeekTab
              churchId={profile?.church_id || ''}
              churchName={activeChurchName}
              userId={user?.id}
              isModerator={isModerator}
              onOpenPlanDay={(plan, follow, dayNumber) => setOpenPlan({ plan, follow, initialDay: dayNumber })}
              onOpenSermon={(sermon) => { setOpenPlan(null); openFullSermon(sermon, null); }}
              onOpenLeaders={() => setLeadersPageOpen(true)}
              onOpenVerse={(book, chapter, verse, scope) => openVerseSheetInPlace(book, chapter, verse, scope)}
              onOpenBible={(bk, ch) => { if (bk && ch) { setBook(bk); setChap(ch); } setTab("bible"); setBibleSub("read"); }}
              onOpenCommunity={() => setTab("community")}
              onAddEvent={() => setEventEditorTarget({ event: null, occurrenceDate: null })}
              onEditEvent={(event, occurrenceDate) => setEventEditorTarget({ event, occurrenceDate })}
              onEventsRefresh={(fn) => { eventsRefreshRef.current = fn; }}
            />
          </div>
        )}

        {/* ══ COMMUNITY (ex-Connect) ══ */}
        {tab==="community" && (
          <div style={{flex:1, overflowY:"auto"}}>
            <CommunityTab
              activeChurchId={activeChurchId}
              churchName={activeChurchName}
              userId={user?.id}
              isModerator={isModerator}
              prayers={prayers}
              addPrayer={addPrayerSupabase}
              togglePray={togglePrayedSupabase}
              addPrayerComment={addPrayerCommentSupabase}
              likePrayerComment={togglePrayerCommentLikeSupabase}
              removePrayer={removePrayerSupabase}
              encouragements={encouragements}
              toggleEncouragementLike={toggleEncouragementLikeSupabase}
              removeEncouragement={removeEncouragementSupabase}
              addEncComment={addEncComment}
              likeEncComment={likeEncComment}
              removeEncComment={removeEncComment}
              refreshEncouragements={refreshEncouragements}
              onOpenVerse={(book, chapter, verse) => {
                const bk = canonicalizeBook(book) || book;
                setBook(bk); setChap(chapter); setSelection(verse ? [verse] : []);
                setTab("bible"); setBibleSub("read");
              }}
              onReport={openReport}
              chip={chip}
              setChip={setChip}
              deepLinkHighlight={deepLinkHighlight}
            />
          </div>
        )}

        {/* ── BOTTOM NAV ── */}
        <div className="bnav">
          {[
            {
              id:"week",
              lbl:"This Week",
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 8a4 4 0 1 0 0 8a4 4 0 1 0 0-8M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>
                </svg>
              ),
            },
            {
              id:"bible",
              lbl:"Bible",
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5zM4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5"/>
                </svg>
              ),
            },
            {
              id:"sermons",
              lbl:"Sermons",
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM19 10v1a7 7 0 0 1-14 0v-1M12 18v4M8 22h8"/>
                </svg>
              ),
            },
            {
              id:"community",
              lbl:"Community",
              icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 11.5a3.5 3.5 0 1 0 0-7a3.5 3.5 0 1 0 0 7M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6M17 10.5a2.5 2.5 0 1 0 0-5M21.5 20c0-2.8-1.6-4.7-4-5.4"/>
                </svg>
              ),
            },
          ].map(n=>{
            const communityLocked = isVisitor && n.id === "community";
            const spot =
              (walkthroughStep === 6 && n.id === "community")
                ? WALKTHROUGH_SPOTLIGHT
                : null;
            return (
              <div key={n.id}
                className={`btab ${tab===n.id?"on":""} ${communityLocked?"locked":""}`}
                style={spot || undefined}
                onClick={()=>{
                  if (walkthroughActive) return; // disable nav during the tour
                  if (communityLocked) { openAccessRequest("community"); return; }
                  setTab(n.id);
                  setFilterBook("all"); setFilterChap("all");
                }}>
                <div className="bico">
                  {n.icon}
                  {communityLocked && (
                    <span className="lock-badge" title="Members only">🔒</span>
                  )}
                </div>
                <div className="blbl">{n.lbl}</div>
              </div>
            );
          })}
        </div>

        {/* ── PROFILE SHEET ── */}
        {profileOpen && (
          <ProfileSheet
            onClose={()=>setProfileOpen(false)}
            activeChurchId={activeChurchId}
            setActiveChurchId={setActiveChurchId}
            sermonNoteChurchIds={effectiveSermonChurchIds}
            setSermonNoteChurchIds={setSermonNoteChurchIds}
            savedCount={Object.keys(saved).length}
            onOpenSaved={()=>{ setProfileOpen(false); setFilterBook("all"); setFilterChap("all"); setTab("bible"); setBibleSub("saved"); }}
            onOpenLeaders={()=>{ setProfileOpen(false); setLeadersPageOpen(true); }}
            onStartWalkthrough={()=>{ setProfileOpen(false); setWalkthroughStep(1); }}
            onReportProblem={()=>{ setProfileOpen(false); setReportTarget({ content_type:"general", content_id:null }); }}
            notifications={notifications}
            notifUnread={notifUnread}
            onOpenNotifications={()=>{ setProfileOpen(false); setNotificationsOpen(true); }}
            onOpenReadingSettings={()=>{ setProfileOpen(false); setTab("bible"); setBibleSub("read"); setReadingSettingsOpen(true); }}
          />
        )}

        {/* ── MODERATION SHEET (moderator only) ── */}
        {removedPostsOpen && isModerator && (
          <ModerationSheet
            items={removedPostsList}
            activeChurchId={activeChurchId}
            initialTab={moderationInitialTab}
            onClose={()=>{ setRemovedPostsOpen(false); setModerationInitialTab(null); }}
            onRestore={restoreRemovedPost}
            onRefresh={loadRemovedPosts}
          />
        )}

        {/* ── EVENT EDITOR (This Week → Coming up, moderator only) ── */}
        {eventEditorTarget && isModerator && (
          <EventEditorSheet
            event={eventEditorTarget.event}
            occurrenceDate={eventEditorTarget.occurrenceDate}
            churchId={activeChurchId}
            onClose={() => setEventEditorTarget(null)}
            onSaved={() => eventsRefreshRef.current?.()}
          />
        )}

        {/* ── LEADERS PAGE (redesign Phase 7 — moderator only) ── */}
        {leadersPageOpen && isModerator && (
          <LeadersPage
            churchId={activeChurchId}
            churchSlug={activeChurch?.slug}
            churchName={activeChurch?.name}
            churches={churches}
            sermonNoteChurchIds={sermonNoteChurchIds}
            user={{ id: user?.id, email: user?.email, full_name: profile?.full_name }}
            isModerator={isModerator}
            pendingAccess={pendingAccess}
            accessActionState={accessActionState}
            onApproveAccess={approveAccessRequest}
            onDeclineAccess={declineAccessRequest}
            onOpenSermon={(sermon) => { setLeadersPageOpen(false); openFullSermon(sermon, null); }}
            onOpenReports={()=>{ setModerationInitialTab("reports"); setRemovedPostsOpen(true); }}
            onOpenRemoved={()=>{ setModerationInitialTab("removed"); setRemovedPostsOpen(true); }}
            onOpenAddSermons={()=>{ setLeadersPageOpen(false); setSermonSettingsOpen(true); }}
            onOpenChurchSettings={()=>{ setLeadersPageOpen(false); setChurchSettingsOpen(true); }}
            onAddEvent={()=>{ setLeadersPageOpen(false); setEventEditorTarget({ event: null, occurrenceDate: null }); }}
            onClose={()=>setLeadersPageOpen(false)}
          />
        )}

        {/* ── NOTIFICATIONS PAGE ── */}
        {notificationsOpen && (
          <NotificationsPage
            onClose={()=>{ setNotificationsOpen(false); setNotifAccessScroll(false); }}
            items={notifications}
            markAsRead={markNotificationRead}
            markAllRead={markAllNotificationsRead}
            isModerator={isModerator}
            scrollToAccess={notifAccessScroll}
            accessActionState={accessActionState}
            onApproveRequest={(req, notifId)=>{ approveAccessRequest(req); markNotificationRead(notifId); }}
            onDeclineRequest={(req, notifId)=>{ declineAccessRequest(req); markNotificationRead(notifId); }}
            onNavigate={(n)=>{
              setNotificationsOpen(false);
              // Super admins can be "acting as" a church other than the one
              // this notification belongs to (and elders/admins are pinned to
              // their own church anyway). Scope the active church to the
              // notification's church so the destination — e.g. the elders
              // questions dashboard, which filters flagged_verses by
              // activeChurchId — actually contains the item they tapped.
              if (n.church_id) setActiveChurchId(n.church_id);
              switch (n.type) {
                case 'prayer_reply':
                case 'prayer_reaction':
                  setTab("community"); setChip("prayer"); break;
                case 'elder_prayer':
                  setEldersSubTab("prayer"); setEldersSheetOpen(true); break;
                case 'verse_comment_reply':
                  setTab("bible"); setBibleSub("comments"); break;
                case 'board_post_reply':
                case 'community_post_comment':
                  setTab("community"); setChip("board"); break;
                case 'question_flagged':
                  setEldersSubTab("questions"); setEldersSheetOpen(true); break;
                case 'access_approved':
                case 'invite_received':
                  setProfileOpen(true); break;
                default: break;
              }
            }}
          />
        )}

        {/* ── DELETE CONFIRMATION SHEET (cross-author) ── */}
        {pendingDelete && (
          <ConfirmDeleteSheet
            title={pendingDelete.title}
            subtitle={pendingDelete.subtitle}
            onCancel={()=>setPendingDelete(null)}
            onConfirm={pendingDelete.onConfirm}
          />
        )}

        {reportTarget && (
          <ReportSheet
            target={reportTarget}
            busy={reportBusy}
            onClose={()=>setReportTarget(null)}
            onSubmit={submitReport}
          />
        )}

        {/* ── FIRST-RUN WALKTHROUGH ── */}
        {walkthroughActive && (
          <WalkthroughOverlay
            step={walkthroughStep}
            onNext={nextWalkthroughStep}
            onSkip={finishWalkthrough}
            churchName={activeChurchName}
          />
        )}

        {/* ── BIBLE-READER TOAST (translation fallback etc.) ── */}
        {bibleToast && (
          <div style={{
            position:"fixed", left:"50%", bottom:88, transform:"translateX(-50%)",
            background:"var(--white)",
            border:"1px solid var(--border-strong)",
            color:"var(--ink)", padding:"10px 16px",
            borderRadius:10, fontSize:13, fontWeight:600,
            boxShadow:"0 10px 24px rgba(0,0,0,0.25)",
            zIndex:120, fontFamily:"'Plus Jakarta Sans',sans-serif",
            maxWidth:"calc(100vw - 32px)",
          }}>
            {bibleToast}
          </div>
        )}

        {/* ── SERMON SETTINGS SHEET ── */}
        {sermonSettingsOpen && (
          <SermonSettingsSheet
            onClose={()=>setSermonSettingsOpen(false)}
            churchId={sermonUploadChurchId}
            onSermonsChanged={refreshSermons}
            onScanUpload={()=>{ setSermonSettingsOpen(false); setScanSermonOpen(true); }}
          />
        )}

        {/* ── CHURCH INFO SETTINGS SHEET ── */}
        {churchSettingsOpen && activeChurch && (
          <ChurchInfoSettingsSheet
            onClose={()=>{ setChurchSettingsOpen(false); loadChurchEvents(); }}
            church={activeChurch}
            onSaved={refreshChurches}
          />
        )}

        {/* ── ENCOURAGEMENT SETTINGS SHEET ── */}
        {encourSettingsOpen && (
          <EncouragementSettingsSheet
            onClose={()=>{ setEncourSettingsOpen(false); setEditingEncouragement(null); }}
            churchId={activeChurchId || profile?.church_id || ''}
            editing={editingEncouragement}
            onChanged={refreshEncouragements}
          />
        )}

        {/* ── VISITOR ACCESS-REQUEST MODAL ── */}
        {accessFeature && (
          <AccessRequestModal
            feature={accessFeature}
            churchId={profile?.church_id || activeChurchId}
            churches={churches}
            requestAccess={requestAccess}
            onClose={()=>setAccessFeature(null)}
          />
        )}

        {/* ── FULL SERMON SHEET (sermon-sheet feature) ── */}
        {fullSermon && (() => {
          const fsChurch = churches?.find(c => c.id === fullSermon.church_id);
          return (
            <FullSermonSheet
              sermon={fullSermon}
              churchName={fsChurch?.name || activeChurchName || "Church"}
              churchLogo={fsChurch?.logo_url || null}
              originVerse={fullSermonOrigin}
              startAtSeconds={fullSermonStartAt}
              initialTab={fullSermonInitialTab}
              initialScrollTop={fullSermonInitialScrollTop}
              isAdmin={isModerator}
              userId={user?.id}
              userChurchId={profile?.church_id || null}
              onClose={closeFullSermon}
              onOpenRefAndPause={onOpenRefAndPause}
              onShare={(s) => {
                const url = `https://thebiblecommunity.co.uk/sermon/${s.id}`;
                if (navigator.share) { navigator.share({ title: s.title || 'Sermon', url }).catch(()=>{}); }
                else { try { navigator.clipboard.writeText(url); } catch (_e) {} }
              }}
            />
          );
        })()}

        {/* ── READING PLAN DAY SHEET (opened from Plans tab or a push deep link) ── */}
        {openPlan && (
          <ReadingPlanDaySheet
            plan={openPlan.plan}
            days={openPlan.plan.reading_plan_days || []}
            follow={openPlan.follow}
            initialDay={openPlan.initialDay}
            sermon={openPlan.plan.sermons}
            userId={user?.id}
            userChurchId={profile?.church_id || null}
            onClose={() => setOpenPlan(null)}
            onOpenSermon={() => { const s = openPlan.plan.sermons; setOpenPlan(null); if (s) openFullSermon(s, null); }}
            onFollowChange={(f) => {
              const prevCount = (openPlan?.follow?.days_completed || []).length;
              const newCount  = (f?.days_completed || []).length;
              setOpenPlan(p => p ? { ...p, follow: f } : p);
              if (newCount > prevCount && !reminderPromptOpen && profile) {
                const promptedAt    = profile.reminder_prompted_at;
                const promptCount   = profile.reminder_prompt_count || 0;
                const neverAsked    = !promptedAt;
                const declinedBefore = !!promptedAt && !profile.reminder_time;
                const reaskEligible = declinedBefore && promptCount < 2 && newCount === 3;
                if (neverAsked || reaskEligible) {
                  openReminderPromptFor(f.last_opened_day, openPlan?.plan?.id);
                }
              }
            }}
          />
        )}

        {/* ── FIRST STEP DONE + REMINDER PROMPT (redesign Phase 8a) ── */}
        {reminderPromptOpen && (
          <ReminderPromptSheet
            firstName={(profile?.full_name || '').split(' ')[0] || 'there'}
            churchName={activeChurchName}
            otherCount={reminderOtherCount}
            onRemind={(time) => { handleReminderAccept(time); }}
            onNotNow={handleReminderNotNow}
            onCarryOn={() => setReminderPromptOpen(false)}
            onBackToWeek={() => { setReminderPromptOpen(false); setOpenPlan(null); }}
          />
        )}

        {/* ── CHURCH / EVENT / NETWORK PROFILE PAGE ── */}
        {profileEntity && (
          <ChurchProfilePage
            entity={profileEntity}
            activeChurchId={activeChurchId || profile?.church_id || ''}
            sermonNoteChurchIds={sermonNoteChurchIds}
            setSermonNoteChurchIds={setSermonNoteChurchIds}
            isAdmin={isModerator}
            user={{ id: user?.id, email: user?.email, full_name: profile?.full_name }}
            onOpenSermon={(s)=>openFullSermon(s, null)}
            onOpenEntity={(e)=>setProfileEntity(e)}
            onOpenBibleMap={()=>{ setProfileEntity(null); setTab("sermons"); setBibleMapSignal(n=>n+1); }}
            onEditChurch={()=>{ setProfileEntity(null); setChurchSettingsOpen(true); }}
            onClose={()=>setProfileEntity(null)}
          />
        )}

        {/* ── SERMON DETAIL SHEET ── */}
        {selectedSermon && (
          <>
            <div className="overlay" onClick={()=>setSelectedSermon(null)}/>
            <div className="sheet sermon-detail">
              <div className="shandle"/>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 16px 12px"}}>
                <div className="shd">{selectedSermon.series || "Sermon"}</div>
                <button onClick={()=>setSelectedSermon(null)} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:20,cursor:"pointer",padding:4}}>✕</button>
              </div>

              <div className="sermon-detail-scroll">
                {/* Church pill — only shown in multi-church mode */}
                {renderChurchPill(selectedSermon.church_id)}

                {/* Watch-the-moment image (also the play CTA — no separate
                    button below). Tapping opens YouTube at the timestamp. */}
                {selectedSermon.ytId && (() => {
                  const secs = tsToSeconds(selectedSermon.ts);
                  return (
                    <button type="button" onClick={()=>openExternalUrl(youtubeAt(selectedSermon.ytId, secs))}
                      style={{display:"block",width:"100%",padding:0,border:"none",background:"none",borderRadius:8,overflow:"hidden",cursor:"pointer",marginBottom:14}}>
                      <div style={{position:"relative",width:"100%",aspectRatio:"16/9",background:"var(--white)"}}>
                        <img src={`https://i.ytimg.com/vi/${selectedSermon.ytId}/hqdefault.jpg`} alt=""
                          onError={e=>{e.currentTarget.style.display="none";}}
                          style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                        <div style={{position:"absolute",inset:0,background:"linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.1) 55%, rgba(0,0,0,0.3))"}}/>
                        <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10}}>
                          <div style={{width:56,height:56,borderRadius:"50%",background:"rgba(0,0,0,0.55)",border:"2px solid rgba(255,255,255,0.92)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                            <span style={{color:"#fff",fontSize:20,marginLeft:4}}>▶</span>
                          </div>
                          <div style={{color:"#fff",fontSize:14,fontWeight:800,fontFamily:"'Plus Jakarta Sans',sans-serif"}}>Watch the sermon</div>
                        </div>
                      </div>
                    </button>
                  );
                })()}

                {/* Header */}
                {selectedSermon.series && (
                  <div className="sermon-detail-series">
                    <span className="sermon-detail-series-label">SERIES: </span>
                    <span className="sermon-detail-series-value">{selectedSermon.series}</span>
                  </div>
                )}
                {/* Title — editable for elders/admins/super-admins */}
                {sermonEdit?.field === "title" ? (
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                    <input
                      autoFocus
                      value={sermonEdit.value}
                      onChange={e=>setSermonEdit(s=>({...s,value:e.target.value}))}
                      onKeyDown={e=>{ if(e.key==="Enter") saveSermonEdit(); if(e.key==="Escape") setSermonEdit(null); }}
                      style={{flex:1,background:"var(--white)",border:"1px solid var(--border-strong)",borderRadius:8,padding:"8px 10px",color:"var(--ink)",fontSize:16,fontWeight:700,fontFamily:"'Plus Jakarta Sans',sans-serif",outline:"none"}}
                    />
                    <button onClick={saveSermonEdit} disabled={sermonEditSaving} style={{background:"var(--gold)",color:"var(--ink)",border:"none",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif",flexShrink:0}}>{sermonEditSaving?"…":"Save"}</button>
                    <button onClick={()=>setSermonEdit(null)} style={{background:"none",border:"1px solid var(--border-strong)",borderRadius:8,padding:"8px 10px",fontSize:13,color:"var(--stone)",cursor:"pointer",flexShrink:0}}>✕</button>
                  </div>
                ) : (
                  <div className="sermon-detail-title" style={{display:"flex",alignItems:"flex-start",gap:8}}>
                    <span style={{flex:1}}>{cleanSermonTitle(selectedSermon.title)}</span>
                    {isModerator && (
                      <button onClick={()=>startSermonEdit("title")} title="Edit title" style={{background:"none",border:"none",color:"var(--stone-dim)",cursor:"pointer",padding:4,display:"inline-flex",alignItems:"center",flexShrink:0,marginTop:4}}>
                        <Pencil size={15}/>
                      </button>
                    )}
                  </div>
                )}

                {/* Meta (preacher · date) — date editable for elders/admins/super-admins */}
                {sermonEdit?.field === "date" ? (
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                    <input
                      type="date"
                      autoFocus
                      value={sermonEdit.value}
                      onChange={e=>setSermonEdit(s=>({...s,value:e.target.value}))}
                      style={{background:"var(--white)",border:"1px solid var(--border-strong)",borderRadius:8,padding:"7px 10px",color:"var(--ink)",fontSize:13,fontFamily:"'Plus Jakarta Sans',sans-serif",outline:"none",colorScheme:"dark"}}
                    />
                    <button onClick={saveSermonEdit} disabled={sermonEditSaving} style={{background:"var(--gold)",color:"var(--ink)",border:"none",borderRadius:8,padding:"7px 12px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif",flexShrink:0}}>{sermonEditSaving?"…":"Save"}</button>
                    <button onClick={()=>setSermonEdit(null)} style={{background:"none",border:"1px solid var(--border-strong)",borderRadius:8,padding:"7px 10px",fontSize:13,color:"var(--stone)",cursor:"pointer",flexShrink:0}}>✕</button>
                  </div>
                ) : (
                  <div className="sermon-detail-meta" style={{display:"flex",alignItems:"center",gap:8}}>
                    <span>
                      {selectedSermon.pastor ? `${selectedSermon.pastor}  ·  ${selectedSermon.date}` : selectedSermon.date}
                      {selectedSermon.occasion ? `  ·  ${selectedSermon.occasion}` : ""}
                    </span>
                    {isModerator && (
                      <button onClick={()=>startSermonEdit("date")} title="Edit date" style={{background:"none",border:"none",color:"var(--stone-dim)",cursor:"pointer",padding:4,display:"inline-flex",alignItems:"center",flexShrink:0}}>
                        <Pencil size={13}/>
                      </button>
                    )}
                  </div>
                )}

                {/* All verse refs for this sermon */}
                <div className="sermon-detail-refs">
                  {(selectedSermon.refs || []).map(r=>(
                    <span key={r} className="ref-tag" style={{cursor:"pointer"}}
                      onClick={()=>{
                        const m = r.match(/^(.+)\s(\d+):/);
                        if (m) { setBook(m[1]); setChap(parseInt(m[2])); setTab("bible"); setBibleSub("read"); setSelectedSermon(null); }
                      }}>{r}</span>
                  ))}
                </div>

                {/* Per-verse notes for this sermon */}
                {(selectedSermon._allRefs || []).length > 0 && (
                  <div style={{marginTop:16}}>
                    <div className="sermon-ref-label">SERMON NOTES</div>
                    {(selectedSermon._allRefs || []).map(r=>(
                      <div key={r.id} style={{padding:"12px 0",borderBottom:"1px solid var(--border)"}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                          <span className="ref-tag" style={{cursor:"pointer"}}
                            onClick={()=>{
                              const m = r.verse_ref.match(/^(.+)\s(\d+):/);
                              if (m) {
                                const bk = canonicalizeBook(m[1].trim()) || m[1].trim();
                                setBook(bk); setChap(parseInt(m[2])); setTab("bible"); setBibleSub("read"); setSelectedSermon(null);
                              }
                            }}>{r.verse_ref}</span>
                          {r.timestamp && selectedSermon.ytId && (
                            <button style={{background:"none",border:"none",color:"var(--ink)",fontSize:12,fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6,flexShrink:0}}
                              onClick={()=>openExternalUrl(
                                youtubeAt(selectedSermon.ytId,
                                  typeof r.timestamp_s === "number" ? r.timestamp_s : tsToSeconds(r.timestamp)))}>
                              <YtGlyph/> Watch the moment
                            </button>
                          )}
                        </div>
                        {r.notes && <div className="sermon-detail-notes" style={{fontSize:14,color:"var(--stone)"}}>{r.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Delete — elders/admins/super-admins only (RLS enforces too) */}
                {isModerator && (
                  <div style={{marginTop:24,paddingTop:16,borderTop:"1px solid var(--border)"}}>
                    {!sermonDeleteConfirm ? (
                      <button
                        onClick={()=>setSermonDeleteConfirm(true)}
                        style={{background:"none",border:"1px solid rgba(249,112,102,0.4)",borderRadius:10,padding:"11px",width:"100%",color:"#C8372A",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                        Delete sermon
                      </button>
                    ) : (
                      <div>
                        <div style={{fontSize:13,color:"var(--stone)",lineHeight:1.5,marginBottom:10,textAlign:"center"}}>
                          Delete this sermon and its notes? This can't be undone.
                        </div>
                        <div style={{display:"flex",gap:10}}>
                          <button
                            onClick={()=>setSermonDeleteConfirm(false)}
                            disabled={sermonDeleting}
                            style={{flex:1,background:"var(--white)",border:"1px solid var(--border-strong)",borderRadius:10,padding:"11px",color:"var(--ink)",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                            Cancel
                          </button>
                          <button
                            onClick={deleteSelectedSermon}
                            disabled={sermonDeleting}
                            style={{flex:1,background:"#F97066",border:"none",borderRadius:10,padding:"11px",color:"#fff",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                            {sermonDeleting ? "Deleting…" : "Delete"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── COMMENT THREAD SHEET ── */}
        {commentVk && (() => {
          // The selection bar isn't gated by this sheet — it reappears on its
          // own once this closes, since it's purely selection.length-driven.
          const goBack = () => {
            setCommentVk(null);
            setCommentError("");
          };
          // Tapping outside the sheet fully dismisses - closes the sub-sheet
          // and clears the underlying selection so the bar disappears too.
          const dismissAll = () => {
            setCommentVk(null);
            setCommentError("");
            setSelection([]);
          };
          return (
          <>
            <div className="overlay" onClick={dismissAll}/>
            <div className="sheet comment-sheet">
              <div className="shandle"/>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 16px 0"}}>
                <div onClick={goBack}
                  style={{display:"flex",alignItems:"center",gap:4,padding:"6px 10px",fontSize:13,fontWeight:600,background:"var(--white)",border:"1px solid var(--border)",borderRadius:8,color:"var(--stone)",cursor:"pointer"}}>
                  ← Back
                </div>
                <div className="shd" style={{padding:0}}>Comments</div>
                <button onClick={()=>setCommentVk(null)} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:20,cursor:"pointer",padding:4}}>✕</button>
              </div>
              <div className="comment-verse-ref">{commentVk}</div>
              {/* show verse text if we have it cached */}
              {(() => {
                const m = commentVk.match(/^(.+)\s(\d+):(\d+)$/);
                const t = m ? chapterCache[`${ver}/${m[1]}/${m[2]}`]?.find(v=>v.v===parseInt(m[3]))?.t : null;
                return t ? <div className="comment-verse-preview">"{t}"</div> : null;
              })()}

              <div className="comment-list">
                {(comments[commentVk]||[]).length === 0 && (
                  <div className="empty" style={{paddingTop:30}}>
                    <div className="eico">💬</div>
                    <div className="etit">No comments yet</div>
                    <div className="esub">Share a reflection - keep it private or share it with the church.</div>
                  </div>
                )}
                {(comments[commentVk]||[]).map(cm=>renderVerseCommentCard(cm, commentVk))}
              </div>

              {commentError && (
                <div style={{margin:"0 16px 8px",padding:"10px 12px",background:"rgba(249,112,102,0.1)",border:"1px solid rgba(249,112,102,0.25)",borderRadius:8,color:"#C8372A",fontSize:12}}>
                  {commentError}
                </div>
              )}

              {/* Composer - single Post button; audience picked via dropdown pill above the textarea */}
              {(() => {
                const churchName = churches?.find(c => c.id === activeChurchId)?.name || "your church";
                return (
                <div style={{padding:"12px 16px 14px",borderTop:"1px solid var(--border)",background:"var(--cream)"}}>
                  {/* Audience pill + dropdown */}
                  <div style={{position:"relative",marginBottom:8}}>
                    <button type="button" onClick={()=>setAudienceOpen(o=>!o)}
                      style={{
                        display:"inline-flex", alignItems:"center", gap:6,
                        padding:"6px 10px", borderRadius:999, cursor:"pointer",
                        background: commentPrivate ? "var(--white)" : "rgba(232,200,122,0.18)",
                        border: `1px solid ${commentPrivate ? "var(--border)" : "rgba(232,200,122,0.5)"}`,
                        color:  commentPrivate ? "var(--stone)" : "var(--gold-ink)",
                        fontSize:12, fontWeight:700, fontFamily:"'Plus Jakarta Sans',sans-serif",
                      }}>
                      <span style={{fontSize:13}}>{commentPrivate ? "🔒" : "👥"}</span>
                      <span>{commentPrivate ? "Only you" : `${churchName} members`}</span>
                      <span style={{fontSize:10,opacity:0.8}}>▾</span>
                    </button>
                    {audienceOpen && (
                      <>
                        {/* Capture outside-click to close the popover */}
                        <div onClick={()=>setAudienceOpen(false)}
                          style={{position:"fixed",inset:0,zIndex:4}}/>
                        <div style={{
                          position:"absolute", top:"100%", left:0, marginTop:6, zIndex:5,
                          background:"var(--white)", border:"1px solid var(--border-strong)",
                          borderRadius:10, minWidth:240,
                          boxShadow:"0 10px 24px rgba(0,0,0,0.18)", overflow:"hidden",
                        }}>
                          <div onClick={()=>{ setCommentPrivate(true); setAudienceOpen(false); }}
                            style={{padding:"10px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:8,
                              background: commentPrivate ? "rgba(232,200,122,0.08)" : "transparent",
                              borderBottom:"1px solid var(--border)"}}>
                            <span style={{fontSize:14}}>🔒</span>
                            <span style={{fontSize:13,color: commentPrivate ? "var(--gold-ink)" : "var(--ink)"}}>Only you</span>
                            {commentPrivate && <span style={{marginLeft:"auto",color:"var(--gold-ink)",fontSize:13}}>✓</span>}
                          </div>
                          <div onClick={()=>{
                            if (isVisitor) { setAudienceOpen(false); openAccessRequest("comment"); return; }
                            setCommentPrivate(false); setAudienceOpen(false);
                          }}
                            style={{padding:"10px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:8,
                              background: !commentPrivate ? "rgba(232,200,122,0.08)" : "transparent",
                              opacity: isVisitor ? 0.65 : 1}}>
                            <span style={{fontSize:14}}>{isVisitor ? "🔒" : "👥"}</span>
                            <span style={{fontSize:13,color: !commentPrivate ? "var(--gold-ink)" : "var(--ink)"}}>{churchName} members</span>
                            {!commentPrivate && !isVisitor && <span style={{marginLeft:"auto",color:"var(--gold-ink)",fontSize:13}}>✓</span>}
                            {isVisitor && <span style={{marginLeft:"auto",fontSize:11,color:"var(--stone-dim)"}}>Members only</span>}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <textarea
                    className="comment-input"
                    placeholder={commentPrivate ? "Write a personal note or reflection…" : "Share a reflection with your church…"}
                    value={commentIn}
                    rows={3}
                    style={{width:"100%",resize:"vertical",minHeight:72,boxSizing:"border-box"}}
                    onChange={e=>setCommentIn(e.target.value)}
                    onKeyDown={e=>{
                      if (e.key==="Enter" && (e.metaKey||e.ctrlKey)) { e.preventDefault(); addComment(commentPrivate); }
                    }}
                  />

                  <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:10,marginTop:10}}>
                    <button type="button"
                      disabled={!commentIn.trim()}
                      onClick={()=>addComment(commentPrivate)}
                      style={{
                        background:"var(--gold)", color:"var(--ink)", border:"none",
                        borderRadius:10, padding:"10px 18px",
                        fontSize:13, fontWeight:800, fontFamily:"'Plus Jakarta Sans',sans-serif",
                        cursor: commentIn.trim() ? "pointer" : "default",
                        opacity: commentIn.trim() ? 1 : 0.5,
                        whiteSpace:"nowrap", flexShrink:0,
                      }}>
                      Post
                    </button>
                  </div>
                </div>
                );
              })()}
            </div>
          </>
          );
        })()}

        {/* ── QUESTION SHEET (anonymous question for Elders) ── */}
        {questionSheet && (() => {
          const goBack = () => {
            setQuestionSheet(null);
          };
          const dismissAll = () => {
            setQuestionSheet(null);
            setSelection([]);
          };
          return (
          <>
            <div className="overlay" onClick={dismissAll}/>
            <div className="sheet" style={{maxHeight:"auto"}}>
              <div className="shandle"/>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 16px 0"}}>
                <div onClick={goBack}
                  style={{display:"flex",alignItems:"center",gap:4,padding:"6px 10px",fontSize:13,fontWeight:600,background:"var(--white)",border:"1px solid var(--border)",borderRadius:8,color:"var(--stone)",cursor:"pointer"}}>
                  ← Back
                </div>
                <div className="shd" style={{padding:0}}>Ask anonymously</div>
                <button onClick={dismissAll} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:20,cursor:"pointer",padding:4}}>✕</button>
              </div>
              <div style={{padding:"6px 18px 4px",fontSize:11,fontWeight:700,letterSpacing:"0.14em",textTransform:"uppercase",color:"var(--gold-ink)"}}>{questionSheet.vk}</div>
              {questionSheet.text && (
                <div style={{margin:"0 16px 12px",padding:"10px 12px",background:"var(--white)",border:"1px solid var(--border)",borderRadius:10,fontFamily:"Georgia,serif",fontSize:14,lineHeight:1.55,color:"var(--stone)",fontStyle:"italic"}}>"{questionSheet.text}"</div>
              )}
              <div style={{padding:"0 16px 4px",fontSize:11,color:"var(--stone-dim)"}}>
                Your question is shown to Elders without your name attached.
              </div>
              <textarea
                style={{
                  width:"calc(100% - 32px)", margin:"8px 16px 0",
                  background:"var(--white)", border:"1px solid var(--border)",
                  borderRadius:10, padding:"11px 14px", color:"var(--ink)",
                  fontSize:14, fontFamily:"'Plus Jakarta Sans',sans-serif",
                  outline:"none", resize:"vertical", minHeight:90, boxSizing:"border-box",
                }}
                autoFocus
                onFocus={e => {
                  const v = e.target.value;
                  // Cursor at the end so the user can append without retyping.
                  e.target.setSelectionRange(v.length, v.length);
                }}
                value={questionSheet.draft}
                onChange={e=>setQuestionSheet(q => q ? { ...q, draft: e.target.value } : q)}
              />
              <div style={{display:"flex",gap:8,padding:"12px 16px 16px"}}>
                <button onClick={saveQuestionFromSheet}
                  style={{flex:1,background:"var(--gold)",color:"var(--ink)",border:"none",borderRadius:10,padding:"11px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                  {questionSheet.isEditing ? "Update question" : "Save question"}
                </button>
                {questionSheet.isEditing && (
                  <button onClick={removeQuestionFromSheet}
                    style={{background:"none",color:"#C8372A",border:"1px solid rgba(249,112,102,0.35)",borderRadius:10,padding:"11px 14px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          </>
          );
        })()}

        {/* ── EXPANDED ENCOURAGEMENT SHEET ("Read more" → full transcript) ── */}
        {expandedEncouragement && (() => {
          const en = expandedEncouragement;
          const close = () => setExpandedEncouragement(null);
          const churchName = churches?.find(c => c.id === en.church_id)?.name || '';
          const ytId = (() => {
            const url = String(en.link || '');
            const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([A-Za-z0-9_-]{6,})/);
            return m ? m[1] : null;
          })();
          const ytLink = ytId ? en.link : null;
          return (
            <>
              {/* z-index lifted above the encouragement preview sub-sheet
                  (overlay 200 / sheet 201) so "Read more" opens ON TOP of the
                  preview, which stays mounted underneath — Back returns to it. */}
              <div className="overlay" onClick={close} style={{zIndex:220}}/>
              <div className="sheet" style={{maxHeight:"85vh", display:"flex", flexDirection:"column", zIndex:221}}>
                <div style={{padding:"10px 0 4px"}}>
                  <div className="shandle" style={{margin:"0 auto"}}/>
                </div>
                {/* Back / close row — matches the pattern used by the
                    comments + encouragement sub-sheets so users get the
                    same control in the top-left wherever they're reading. */}
                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding:"4px 16px 8px"}}>
                  <div onClick={close}
                    style={{display:"flex",alignItems:"center",gap:4,padding:"6px 10px",fontSize:13,fontWeight:600,background:"var(--white)",border:"1px solid var(--border)",borderRadius:8,color:"var(--stone)",cursor:"pointer"}}>
                    ← Back
                  </div>
                  <button onClick={close} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:20,cursor:"pointer",padding:4}}>✕</button>
                </div>
                <div style={{padding:"6px 20px 14px", borderBottom:"1px solid var(--border)"}}>
                  <div style={{fontSize:10, fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:"#FF6B9D", marginBottom:6}}>
                    Word of encouragement
                  </div>
                  <div style={{fontFamily:"Georgia,serif", fontSize:20, lineHeight:1.25, color:"var(--ink)", marginBottom:8}}>
                    {en.name || en.title || 'Encouragement'}
                  </div>
                  <div style={{fontSize:12, color:"var(--stone)", display:"flex", flexWrap:"wrap", gap:6, alignItems:"center"}}>
                    {en.sharedBy && <span style={{fontWeight:700, color:"var(--ink)"}}>{en.sharedBy}</span>}
                    {en.date && <><span style={{color:"var(--stone-dim)"}}>·</span><span>{en.date}</span></>}
                    {churchName && <><span style={{color:"var(--stone-dim)"}}>·</span><span>{churchName}</span></>}
                  </div>
                </div>
                <div style={{flex:1, overflowY:"auto", padding:"16px 20px"}}>
                  <div style={{fontFamily:"Georgia,serif", fontSize:15, lineHeight:1.65, color:"var(--ink)", whiteSpace:"pre-wrap"}}>
                    {en.text || ''}
                  </div>
                </div>
                {ytLink && (
                  <div style={{padding:"12px 16px 16px", borderTop:"1px solid var(--border)", display:"flex", gap:8}}>
                    <a href={ytLink} target="_blank" rel="noreferrer"
                      style={{
                        flex:1, textAlign:"center",
                        background:"rgba(220,38,38,0.18)", border:"1px solid rgba(220,38,38,0.35)",
                        color:"#C8372A", borderRadius:10, padding:"11px",
                        fontSize:14, fontWeight:700, textDecoration:"none",
                        fontFamily:"'Plus Jakarta Sans',sans-serif",
                        display:"inline-flex", alignItems:"center", justifyContent:"center", gap:8,
                      }}>
                      <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:20,height:14,borderRadius:3,background:"#FF0000"}}>
                        <svg width="8" height="8" viewBox="0 0 10 10" aria-hidden="true">
                          <path d="M2.5 1.5 L8 5 L2.5 8.5 Z" fill="#fff"/>
                        </svg>
                      </span>
                      {en.linkLabel || 'Watch the moment'}
                    </a>
                  </div>
                )}
              </div>
            </>
          );
        })()}

        {/* ── ENCOURAGEMENT SUB-SHEET (for the current selection) ── */}
        {encourageSheetVks && (() => {
          const goBack = () => {
            setEncourageSheetVks(null);
          };
          // Tapping outside the sheet fully dismisses - closes the sub-sheet
          // and clears the underlying selection so the bar disappears too.
          const dismissAll = () => {
            setEncourageSheetVks(null);
            setSelection([]);
          };
          const vksSet = new Set(encourageSheetVks);
          // Selected verses are always in the same chapter - derive the
          // chapter key from any of them to also catch whole-chapter refs.
          const chapKey = (() => {
            const m = encourageSheetVks[0]?.match(/^(.+\s\d+):/);
            return m ? m[1] : null;
          })();
          const matching = encouragements.filter(en => {
            for (const vk of en.verseKeys || []) {
              if (vksSet.has(vk)) return true;
            }
            return chapKey && en.chapterKeys?.has(chapKey);
          });
          const label = encourageSheetVks.length === 1
            ? encourageSheetVks[0]
            : `${encourageSheetVks.length} verses`;
          return (
            <>
              <div className="overlay" onClick={dismissAll}/>
              <div className="sheet comment-sheet">
                <div className="shandle"/>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 16px 0"}}>
                  <div onClick={goBack}
                    style={{display:"flex",alignItems:"center",gap:4,padding:"6px 10px",fontSize:13,fontWeight:600,background:"var(--white)",border:"1px solid var(--border)",borderRadius:8,color:"var(--stone)",cursor:"pointer"}}>
                    ← Back
                  </div>
                  <div className="shd" style={{padding:0}}>Encouragements</div>
                  <button onClick={()=>setEncourageSheetVks(null)} style={{background:"none",border:"none",color:"var(--stone-dim)",fontSize:20,cursor:"pointer",padding:4}}>✕</button>
                </div>
                <div className="comment-verse-ref">{label}</div>

                <div className="comment-list">
                  {matching.length === 0 ? (
                    <div className="empty" style={{paddingTop:30}}>
                      <div className="eico">❤️</div>
                      <div className="etit">No encouragements yet</div>
                      <div className="esub">No encouragements reference {encourageSheetVks.length > 1 ? "any of these verses" : "this verse"} yet.</div>
                    </div>
                  ) : matching.map(en => (
                    <div key={en.id} className="enc-card">
                      <div className="comment-header">
                        {en.sharedBy && <span className="comment-author">{en.sharedBy}</span>}
                        <span className="comment-date">{en.date}</span>
                        {isModerator && (
                          <button type="button" aria-label="Edit encouragement"
                            onClick={()=>{ setEditingEncouragement(en); setEncourSettingsOpen(true); }}
                            style={{marginLeft:"auto",background:"none",border:"none",padding:4,cursor:"pointer",color:"var(--stone-dim)",display:"inline-flex",alignItems:"center"}}>
                            <Pencil size={14} strokeWidth={2}/>
                          </button>
                        )}
                        {canDelete(en.authorId) && (
                          <button type="button" aria-label="Delete encouragement"
                            onClick={()=>handleDeleteEncouragement(en)}
                            style={{marginLeft: isModerator ? 2 : "auto",background:"none",border:"none",padding:4,cursor:"pointer",color:"var(--stone-dim)",display:"inline-flex",alignItems:"center"}}>
                            <Trash2 size={14} strokeWidth={2}/>
                          </button>
                        )}
                      </div>
                      {(en.name || en.title) && (
                        <div style={{fontSize:15,fontWeight:700,color:"var(--ink)",margin:"2px 0 6px"}}>{en.name || en.title}</div>
                      )}
                      {(() => {
                        const fullText = String(en.text || '');
                        const isLong = fullText.length > 200;
                        const preview = isLong
                          ? (fullText.slice(0, 200).replace(/\s+\S*$/, '').trim() + '…')
                          : fullText;
                        return (
                          <div className="comment-text" style={{marginBottom:en.link||en.refs?.length?4:10,whiteSpace:"pre-wrap"}}>
                            {preview}
                            {isLong && (
                              <>
                                {' '}
                                <button
                                  type="button"
                                  onClick={() => setExpandedEncouragement(en)}
                                  style={{
                                    background:"none", border:"none", padding:0,
                                    color:"var(--gold-ink)", fontSize:13, fontWeight:700,
                                    cursor:"pointer", fontFamily:"'Plus Jakarta Sans',sans-serif",
                                    textDecoration:"underline",
                                  }}>
                                  Read more
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })()}
                      {en.refs && en.refs.length > 0 && (
                        <div style={{display:"flex",flexWrap:"wrap",gap:6,margin:"8px 0"}}>
                          {en.refs.map(r => (
                            <span key={r.id}
                              role="button" tabIndex={0}
                              title={`Open ${r.verseRef} in the Bible`}
                              onClick={() => {
                                const m = String(r.verseRef || '').match(/^(.+?)\s+(\d+)(?::(\d+))?/);
                                if (!m) return;
                                const bk = canonicalizeBook(m[1].trim()) || m[1].trim();
                                setBook(bk);
                                setChap(parseInt(m[2], 10));
                                setSelection(m[3] ? [parseInt(m[3], 10)] : []);
                                setTab("bible"); setBibleSub("read");
                                dismissAll();
                              }}
                              style={{background: vksSet.has(r.verseRef) ? "rgba(232,200,122,0.18)" : "rgba(232,200,122,0.1)",border:"1px solid rgba(232,200,122,0.25)",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700,color:"var(--gold-ink)",letterSpacing:"0.02em",cursor:"pointer"}}>
                              {r.verseRef}
                            </span>
                          ))}
                        </div>
                      )}
                      {en.link && (() => {
                        const isYt = en.link.includes("youtube") || en.link.includes("youtu.be");
                        const label = isYt ? 'Watch the moment' : (en.linkLabel || en.link);
                        return (
                          <a href={en.link} target="_blank" rel="noreferrer" className="enc-link">
                            {isYt ? (
                              <span style={{display:"flex",alignItems:"center",justifyContent:"center",width:30,height:22,borderRadius:5,background:"#FF0000",flexShrink:0}}>
                                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                                  <path d="M2.5 1.5 L8 5 L2.5 8.5 Z" fill="#fff"/>
                                </svg>
                              </span>
                            ) : (
                              <span className="enc-link-icon">🔗</span>
                            )}
                            <div>
                              <div className="enc-link-label">{label}</div>
                              <div className="enc-link-url">{en.link}</div>
                            </div>
                          </a>
                        );
                      })()}
                      <div className={`heart-btn ${en.liked?"hearted":""}`} onClick={()=>likeEncouragement(en.id)}>
                        <span>{en.liked?"❤️":"🤍"}</span>
                        {en.likes>0&&<span>{en.likes}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          );
        })()}

        {/* ── BOOK / CHAPTER PICKER (single "Romans 8 ▾" pill, docs/designs/Bible) ── */}
        {picker && (
          <>
            <div className="overlay" onClick={()=>setPicker(null)}/>
            <div className="sheet">
              <div className="shandle"/>
              <div className="shd">
                {picker === "chapter" ? (
                  <div onClick={()=>setPicker("book")}
                    style={{display:"flex",alignItems:"center",gap:4,padding:"6px 10px",fontSize:13,fontWeight:600,background:"var(--white)",border:"1px solid var(--border)",borderRadius:8,color:"var(--stone)",cursor:"pointer"}}>
                    ← Back
                  </div>
                ) : (
                  <div className="shtitle">CHOOSE A BOOK</div>
                )}
                <div className="shx" onClick={()=>setPicker(null)}>×</div>
              </div>
              <div className="shbody">
                {picker === "book" ? (
                  ["OT","NT"].map(t=>(
                    <div key={t}>
                      <div className="divider-lbl">{t==="OT"?"Old Testament":"New Testament"}</div>
                      <div className="bgrid">
                        {BOOKS.filter(b=>b.t===t).map(b=>(
                          <div key={b.name} className={`bcard ${book===b.name?"on":""}`}
                            onClick={()=>{setPickerBook(b.name);setPicker("chapter");}}>
                            <div className="bcard-n">{b.name}</div>
                            <div className="bcard-c">{b.ch} ch</div>
                            <div style={{display:"flex",gap:4,marginTop:4,alignItems:"center",minHeight:8}}>
                              {booksWithSermons.has(b.name)        && <span style={{width:6,height:6,borderRadius:"50%",background:"#7C6BC4"}}/>}
                              {booksWithComments.has(b.name)       && <span style={{width:6,height:6,borderRadius:"50%",background:"#4A90E2"}}/>}
                              {booksWithEncouragements.has(b.name) && (
                                <svg width="9" height="9" viewBox="0 0 24 24" aria-hidden="true">
                                  <path fill="#FF6B9D" d="M12 21s-7.5-4.6-9.6-9.2C.9 8.1 3.4 4 7.5 4c2 0 3.4 1 4.5 2.4C13.1 5 14.5 4 16.5 4c4.1 0 6.6 4.1 5.1 7.8C19.5 16.4 12 21 12 21z"/>
                                </svg>
                              )}
                              {booksWithQuestions.has(b.name)      && <span style={{fontSize:9,color:"#9B6FD4",fontWeight:800,lineHeight:1}}>?</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div>
                    <div className="divider-lbl">{pickerBook}</div>
                    <div className="chgrid">
                      {Array.from({length: BOOKS.find(b=>b.name===pickerBook)?.ch || 1}, (_,i)=>i+1).map(c=>(
                        <div key={c} className={`bcard ${book===pickerBook && chap===c?"on":""}`}
                          onClick={()=>{setBook(pickerBook);setChap(c);setPicker(null);}}>
                          <div className="bcard-n">{c}</div>
                          <div style={{display:"flex",gap:4,marginTop:4,alignItems:"center",justifyContent:"center",minHeight:8}}>
                            {chaptersWithSermons.get(pickerBook)?.has(c)        && <span style={{width:6,height:6,borderRadius:"50%",background:"#7C6BC4"}}/>}
                            {chaptersWithComments.get(pickerBook)?.has(c)       && <span style={{width:6,height:6,borderRadius:"50%",background:"#4A90E2"}}/>}
                            {chaptersWithEncouragements.get(pickerBook)?.has(c) && (
                              <svg width="9" height="9" viewBox="0 0 24 24" aria-hidden="true">
                                <path fill="#FF6B9D" d="M12 21s-7.5-4.6-9.6-9.2C.9 8.1 3.4 4 7.5 4c2 0 3.4 1 4.5 2.4C13.1 5 14.5 4 16.5 4c4.1 0 6.6 4.1 5.1 7.8C19.5 16.4 12 21 12 21z"/>
                              </svg>
                            )}
                            {chaptersWithQuestions.get(pickerBook)?.has(c)      && <span style={{fontSize:9,color:"#9B6FD4",fontWeight:800,lineHeight:1}}>?</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── VERSE SHEET (docs/designs/VerseClip.dc.html) ── */}
        {sheet && (() => {
          // Tapping outside, or the ✕, fully dismisses — no separate "← Back"
          // per the new design (was two ways to close before; now one).
          const dismissAll = () => {
            setSheet(null);
            setSelection([]);
            setPlayingClipId(null);
            setVerseSheetVisibleCount(5);
          };
          const scopeFilter = sheet.scope === 'home' || sheet.scope === 'following' ? sheet.scope : 'all';
          const allClips = sheet.sermons || [];
          const homeClips = allClips.filter(c => c.isHome);
          const followingClips = allClips.filter(c => !c.isHome);
          const visibleClips = scopeFilter === 'home' ? homeClips : scopeFilter === 'following' ? followingClips : allClips;
          const shown = visibleClips.slice(0, verseSheetVisibleCount);
          const homeChurchName = churches?.find(c => c.id === (activeChurchId || profile?.church_id))?.name || "Home church";
          const verseTextFull = sheet.verse?.t || "";
          const verseTextShort = verseTextFull.length > 160 ? verseTextFull.slice(0, 160).trimEnd() + "…" : verseTextFull;
          const selVk = sheet.vks?.[0] || (selection.length ? `${book} ${chap}:${selection[0]}` : null);
          const scopeEmptyLabel = scopeFilter === 'home' ? homeChurchName : scopeFilter === 'following' ? 'churches you follow' : 'these churches';

          return (
            <>
              {/* When opened in-place from Explore (e.g. over the Bible-Map
                  book overlay), render above that overlay so it's visible. */}
              <div className="overlay" onClick={dismissAll} style={sheet.origin === "explore" ? { zIndex: 340 } : undefined}/>
              <div className="sheet" style={sheet.origin === "explore" ? { zIndex: 341 } : undefined}>
                <div className="shandle"/>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, padding: "4px 20px 2px" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 19, fontWeight: 800, color: "var(--ink)" }}>{sheet.vk}</div>
                    {verseTextShort && (
                      sheet.origin === "explore" ? (
                        <button type="button"
                          onClick={() => { const mm = (sheet.vk || "").match(/^(.+)\s(\d+):(\d+)$/); if (mm) { setSheet(null); applyDeepLink({ screen: "bible", book: mm[1], chapter: mm[2], verse: mm[3] }); } }}
                          style={{ display: "block", textAlign: "left", background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: 4, fontFamily: "'Source Serif 4', Georgia, serif" }}>
                          <div style={{ fontSize: 14.5, lineHeight: 1.5, color: "var(--ink)", fontStyle: "italic" }}>"{verseTextShort}"</div>
                          <div style={{ fontSize: 11.5, color: "var(--gold-ink)", fontWeight: 700, marginTop: 4, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Open in Bible →</div>
                        </button>
                      ) : (
                        <div style={{ fontSize: 14.5, lineHeight: 1.5, color: "var(--ink)", fontStyle: "italic", fontFamily: "'Source Serif 4', Georgia, serif", marginTop: 4 }}>"{verseTextShort}"</div>
                      )
                    )}
                  </div>
                  <button type="button" onClick={dismissAll} aria-label="Close"
                    style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 17, background: "rgba(31,27,22,0.06)", border: "none", color: "var(--stone)", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>✕</button>
                </div>

                <div className="shbody" style={{ paddingTop: 14 }}>
                  {sheet.origin !== "explore" && selVk && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 8, marginBottom: 18 }}>
                      {[
                        [highlights[selVk] === "yellow" ? "Unhighlight" : "Highlight", () => applyHL(highlights[selVk] === "yellow" ? null : "yellow")],
                        [saved[selVk] ? "Saved ✓" : "Save", toggleSavedForSelection],
                        ["Comment", openCommentsForFirst],
                        ["Share", shareSelection],
                      ].map(([label, fn]) => (
                        <button key={label} type="button" onClick={fn}
                          style={{ border: "none", borderRadius: 14, background: "var(--white)", padding: "12px 0", fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 12, fontWeight: 700, color: "var(--ink)", cursor: "pointer" }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}

                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--stone)", marginBottom: 10 }}>
                    Clips on this verse · {allClips.length}
                  </div>

                  <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
                    {[["all", `All · ${allClips.length}`], ["home", `${homeChurchName} · ${homeClips.length}`], ["following", `Following · ${followingClips.length}`]]
                      .filter(([id]) => id === "all" || (id === "home" ? homeClips.length > 0 : followingClips.length > 0))
                      .map(([id, label]) => (
                        <button key={id} type="button"
                          onClick={() => { setSheet(s => ({ ...s, scope: id })); setVerseSheetVisibleCount(5); setPlayingClipId(null); }}
                          style={{ borderRadius: 999, padding: "7px 13px", fontSize: 12.5, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "'Plus Jakarta Sans',sans-serif", background: scopeFilter === id ? "var(--gold)" : "var(--white)", color: scopeFilter === id ? "var(--ink)" : "var(--stone)" }}>
                          {label}
                        </button>
                      ))}
                  </div>

                  {sheet.sermons === null ? (
                    <div className="empty"><div className="eico">✦</div><div className="etit">Loading clips…</div></div>
                  ) : visibleClips.length === 0 ? (
                    <div className="empty"><div className="eico">📜</div><div className="etit">No sermons yet</div>
                      <div className="esub">No sermons from {scopeEmptyLabel} on this verse yet.</div>
                    </div>
                  ) : (
                    <>
                      {shown.map((c, i) => {
                        const isPlaying = playingClipId === c.refId;
                        const kindLabel = c.kind === "main_passage" ? `Main passage · ${c.verseLabel}` : c.kind === "moment" ? "Key moment" : "Mentioned";
                        const initials = (c.churchName || "").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase() || "?";
                        if (isPlaying) {
                          return (
                            <VerseClipPlayingCard key={c.refId} clip={c}
                              onOpenFullSermon={(sec) => openFullSermon(
                                { id: c.id, church_id: c.churchId, title: c.displayTitle, display_title: c.displayTitle, date: c.date, youtube_id: c.youtubeId, duration: c.duration },
                                null, sec,
                              )}
                              onCollapse={() => setPlayingClipId(null)}
                            />
                          );
                        }
                        return (
                          <button key={c.refId} type="button" onClick={() => setPlayingClipId(c.refId)}
                            style={{ display: "flex", gap: 10, alignItems: "center", width: "100%", textAlign: "left", background: "none", border: "none", borderTop: i > 0 ? "1px solid var(--border)" : "none", cursor: "pointer", padding: "12px 2px", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                            <div style={{ position: "relative", width: 76, height: 43, borderRadius: 10, overflow: "hidden", background: "var(--border)", flexShrink: 0 }}>
                              {c.youtubeId && <img src={`https://i.ytimg.com/vi/${c.youtubeId}/mqdefault.jpg`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} onError={e => { e.currentTarget.style.display = "none"; }}/>}
                              <span style={{ position: "absolute", left: "50%", top: "50%", width: 22, height: 22, marginLeft: -11, marginTop: -11, borderRadius: 11, background: "rgba(255,255,255,0.92)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9 }}>▶</span>
                              <span style={{ position: "absolute", left: 5, top: 5, background: "#7C6BC4", color: "#fff", borderRadius: 5, padding: "1px 6px", fontSize: 9.5, fontWeight: 800 }}>Clip</span>
                            </div>
                            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, color: c.isHome ? "var(--lavender-ink)" : "var(--sage-ink)" }}>
                                <span style={{ width: 16, height: 16, borderRadius: 8, background: c.isHome ? "#7C6BC4" : "var(--sage)", color: c.isHome ? "#fff" : "var(--sage-ink)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 800, flexShrink: 0 }}>{initials}</span>
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.churchName}</span>
                              </div>
                              <div style={{ fontSize: 14, fontWeight: 800, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.displayTitle}</div>
                              {c.timestampS != null && <div style={{ fontSize: 12, fontWeight: 600, color: "var(--lavender-ink)" }}>▶ Clip from {fmtClipClock(c.timestampS)}</div>}
                              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5 }}>
                                <span style={{ background: c.isHome ? "var(--lavender)" : "var(--border)", color: c.isHome ? "var(--lavender-ink)" : "var(--stone)", borderRadius: 999, padding: "2px 8px", fontWeight: 700 }}>{kindLabel}</span>
                                <span style={{ color: "var(--stone)" }}>{fmtShortSermonDate(c.date)}</span>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                      {visibleClips.length > shown.length && (
                        <button type="button" onClick={() => setVerseSheetVisibleCount(n => n + 10)}
                          style={{ display: "block", width: "100%", textAlign: "center", background: "none", border: "none", color: "var(--gold-ink)", fontSize: 13, fontWeight: 700, cursor: "pointer", padding: "12px 0", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                          Show {visibleClips.length - shown.length} more
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </>
  );
}
